import {
  useQuery,
  useSuspenseQuery,
  useInfiniteQuery,
  useSuspenseInfiniteQuery,
  useMutation,
  UseQueryOptions,
  UseSuspenseQueryOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
  UseSuspenseInfiniteQueryOptions,
  UseMutationOptions,
} from "@tanstack/react-query"

import { publicFetcher } from "./fetcher"
import * as Types from "./public-types"

export const UtilityFragmentFragmentDoc = `
    fragment UtilityFragment on Utility {
  id
  pid
  name
  shortName
  numTariffs
  dateTariffsRefreshed
  utilityApiName
  isPreferred
  notes
}
    `
export const UserRefFragmentDoc = `
    fragment UserRef on UserRef {
  pid
  email
  name
  firstName
  isAdmin
  avatarUrl
}
    `
export const SmallScenarioFragmentFragmentDoc = `
    fragment SmallScenarioFragment on AdminScenario {
  id
  pid
  name
  dateCreated
  includeStorage
  isPrimary
  isMultifamily
  loadCategory
  solarLocation
  createdBy {
    ...UserRef
  }
  hiddenFromCustomers
  allowMulticonnect
}
    `
export const ScenarioFragmentFragmentDoc = `
    fragment ScenarioFragment on AdminScenario {
  ...SmallScenarioFragment
  activeEtbReport {
    id
    projectName
  }
  helioscope {
    id
    pid
  }
}
    `
export const BasicOrgDetailsFragmentDoc = `
    fragment BasicOrgDetails on AdminOrg {
  id
  pid
  name
  slug
  rawSlug
  description
  logoUrl
  logomarkUrl
}
    `
export const KwhFlowFragmentDoc = `
    fragment KwhFlow on KwhFlow {
  bauLoadKwh
  importsKwh
  exportsKwh
  pvProductionKwh
  pvToLoadKwh
  batteryChargeKwh
  batteryDischargeKwh
}
    `
export const RevampSystemFragmentDoc = `
    fragment RevampSystem on RevampSystem {
  __typename
  ... on PVSystem {
    type
    sizeKw
    bauMeterId
    sitePid
  }
  ... on BatterySystem {
    vendor
    sizeKw
    sizeKwh
    assumptions {
      battChemName
    }
  }
}
    `
export const FinmodelCostFragmentDoc = `
    fragment FinmodelCost on FinmodelCost {
  totalKwh
  totalCostUsd
  usdPerKwh
  items {
    name
    usd
  }
  tariffs {
    utility {
      id
      name
      shortName
    }
    id
    name
    code
    startDate
    endDate
  }
}
    `
export const RevampFinmodelFragmentDoc = `
    fragment RevampFinmodel on RevampFinmodel {
  __typename
  id
  pid
  createdAt
  type
  intervalsAndCostsXlsxHref
  buildingOwnerXlsxHref
  offerStatus
  fmvStr
  totalPvProductionKwh
  totalPvExportKwh
  bauCostPerYear
  derCostPerYear
  goal
  isVnem
  isGreenLease
  thirdPartySupplier {
    pid
    name
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  ppa {
    __typename
    ppaType
    ppaCostPerKwh
    ppaTermYears
    ppaEsc
    srecToInvestor
    offtakerXlsxHref
    product {
      productName
      financierName
    }
    roofRent {
      lifetimeUsd
      annualUsd
      usdPerSqft
      roofRentPerYear
    }
    tenantFlows {
      npv
      discountRate
      totalUtilitySavings
      totalCapex
      totalOpex
      totalNet
      netCashflowPerYear
    }
  }
  loan {
    __typename
    loanRecvrXlsxHref
    loanTermYears
    ownershipTermYears
    interestPct
    srecToInvestor
    product {
      productName
      financierName
      loanDealerFeePct
    }
    flows {
      monthlyPayment
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalLoanFunding
      totalLoanPayments
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  selfInvest {
    __typename
    npv
    discountRate
    selfInvestXlsxHref
    termYears
    srecToInvestor
    irrs {
      postTaxItcDepr
      postTaxItcDeprMultiple
    }
    product {
      productName
      financierName
    }
    flows {
      totalRevenue
      totalUtilitySavings
      totalOpex
      totalCapex
      totalItc
      totalNet
      netCashflowPerYear
      greenLeaseTenantFlows {
        totalNet
        netCashflowPerYear
        totalCapex
        totalOpex
        totalUtilitySavings
        npv
        discountRate
      }
    }
  }
  meters {
    name
    loadCategory
    meterSource
    bau {
      totalKwhFlow {
        ...KwhFlow
      }
    }
    der {
      totalKwhFlow {
        ...KwhFlow
      }
      averageDayKwhFlow {
        ...KwhFlow
      }
    }
  }
  systems {
    ...RevampSystem
  }
  utilityCosts {
    bau {
      ...FinmodelCost
    }
    der {
      ...FinmodelCost
    }
  }
}
    `
export const AdminFinmodelDetailFragmentDoc = `
    fragment AdminFinmodelDetail on RevampFinmodel {
  __typename
  ...RevampFinmodel
  financing {
    id
    name
    type
    financierName
    financierPid
    isThirdParty
    status
  }
  scenario {
    pid
    name
    loadCategory
    includeStorage
    solarLocation
  }
  investorDetails {
    investorXlsxHref
    irrs {
      preTax
      postTaxItcDepr
    }
  }
  moreDetails {
    cashPurchaseNpv
    totalInstallCost
    pvCostPerWatt
    finmodelInputs
    finmodelOutputs
    luxId
    failureId
  }
  importReductionPct
}
    `
export const SiteLabelRefFragmentDoc = `
    fragment SiteLabelRef on SiteLabel {
  pid
  name
  themeColor
  sites
}
    `
export const SiteLabelDetailFragmentDoc = `
    fragment SiteLabelDetail on SiteLabel {
  ...SiteLabelRef
  createdBy {
    ...UserRef
  }
  perms {
    canAdd
    canRemove
  }
}
    `
export const PolicyBriefFragmentDoc = `
    fragment PolicyBrief on Policy {
  pid
  name
  publicName
  description
  publicDescription
  sources {
    text
    url
  }
  userVisible
  application {
    status
  }
}
    `
export const SiteConfigFragmentDoc = `
    fragment SiteConfig on SiteConfig {
  createdAt
  createdBy {
    ...UserRef
  }
  scenario {
    pid
    name
    loadCategory
    solarLocation
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
}
    `
export const FootprintFragmentDoc = `
    fragment Footprint on Footprint {
  latitude
  longitude
  bounds
  obstructionShape
  roofShape
  roofShapeGeojson
  roofShapeUpdatedAt
  roofShapeUpdatedBy {
    ...UserRef
  }
  roofSetbackShape
  roofSetbackShapeGeojson
  roofSetbackShapeUpdatedAt
  roofSetbackShapeUpdatedBy {
    ...UserRef
  }
  carportShape
  carportShapeGeojson
  carportShapeUpdatedAt
  carportShapeUpdatedBy {
    ...UserRef
  }
  groundmountShape
  groundmountShapeGeojson
  groundmountShapeUpdatedAt
  groundmountShapeUpdatedBy {
    ...UserRef
  }
  keepoutsShape
  keepoutsShapeGeojson
  keepoutsShapeUpdatedAt
  keepoutsShapeUpdatedBy {
    ...UserRef
  }
  batteryPadShape
  batteryPadShapeGeojson
  batteryPadShapeUpdatedAt
  batteryPadShapeUpdatedBy {
    ...UserRef
  }
}
    `
export const RevampSiteBasicsFragmentDoc = `
    fragment RevampSiteBasics on RevampSite {
  pid
  name
  isBehindTheMeter
  isEnergyCommunity
  isLowIncomeCommunity
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  metering
  assetManager
  vnemAllowed
  buildingType
  usingBillingMeters
  numParkingSpaces
  selectedConfig {
    ...SiteConfig
  }
  documents {
    pid
    downloadHref
    fileName
    tag
  }
  energyThings {
    canRooftopSolar
    canCarportSolar
    canGroundmountSolar
    canStorage
  }
  financingThings {
    canSelfInvest
    canLoan
    canPpa
    canCommunitySolar
  }
  physicalCheck {
    existingSolar
    unobstructedFlatRoof
    badRoof
  }
  siteDetails {
    leaseApprovers
    externalLeaseApprovers
    needsTenantApproval
    roofRightsHolder
    propertyLenders
    propertyInsurer
    constructionEmail
    propertySiteVisitContacts
    leasePropertyLegalDescription
    leaseLegalOwnerName
    signatureBlocks
    isFannieMaeLender
    isFmGlobalInsurer
    roofReplacedYr
    roofCondition
    salePlans
    buildingLegalName
    propertyFundName
    leaseSignatory
    counselApproval
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  usableRoofSqft
  usableCarportFt
  impliedCarportLinearFt
  impliedUsableRoofSqft
  impliedUsableRoofSqftExplanation
  groundAvailableSqft
  hideBuildingFootprint
  keepoutsFootprintSqft
  batteryPadFootprintSqft
  roofMaxPvKw
  carportMaxPvKw
  groundmountMaxPvKw
  maxPvKwPolicyLimited
  isMultifamily
  occupancyType
  leaseType
  tenantLeaseTerm
  tenantLeaseEnds
  tenantInterest
  mortgageLender
  notes {
    text
  }
  buildingClass
  buildingData {
    numStories
    numUnits
    yearBuilt
    sqft
    roofSqft
    numParkingSpaces
    occupancyRate
    totalBuildings
    yearRenovated
    parentCompany
    lastSalePrice
    leedCertified
    energyStar
    buildingOpex
    capRate
  }
  footprint {
    ...Footprint
  }
  thirdPartySupplier {
    pid
    name
  }
  meterSet {
    loadOrigin
  }
  targetUtility {
    name
    pid
    id
    eiaGridEsc {
      cagrBlended
      fromYear
      toYear
    }
    hostingCapacityMaps {
      url
      requiresLogin
      types
    }
  }
  hasSubmittedEnergyData
}
    `
export const BillingMeterOptionFragmentDoc = `
    fragment BillingMeterOption on BillingMeter {
  pid
  bauMeterPid
  view {
    name
    isArchived
    calc2 {
      __typename
      ... on Projected {
        annualKwh
      }
    }
  }
}
    `
export const BundleRowFragmentDoc = `
    fragment BundleRow on BundleRow {
  id
  site {
    ...RevampSiteBasics
    billingMeters {
      ...BillingMeterOption
    }
  }
  type
}
    `
export const DecodedRateFragmentDoc = `
    fragment DecodedRate on DecodedRate {
  id
  name
  chargeClass
}
    `
export const CostOrTaxItemFragmentDoc = `
    fragment CostOrTaxItem on CostItemTaxItem {
  __typename
  ... on CostItem {
    rateObj {
      ...DecodedRate
    }
    name
    rateId
    chargeType
    chargeCategory
    volume
    rate
    cost
  }
  ... on TaxItem {
    taxId
    subtotal
    cost
    percentage
  }
}
    `
export const BillSectionFragmentDoc = `
    fragment BillSection on BillSection {
  name
  rates {
    ...DecodedRate
  }
  costs {
    ...CostOrTaxItem
  }
}
    `
export const FormattedBillFragmentDoc = `
    fragment FormattedBill on FormattedBills {
  rate {
    tariffId
    startDate
    endDate
  }
  bills {
    month
    charges {
      ...BillSection
    }
    exports {
      ...BillSection
    }
    kwhSupplyCharge
    kwhCharge
  }
}
    `
export const SiteBauMeterFragmentDoc = `
    fragment SiteBauMeter on BauMeter {
  id
  pid
  userVisibleLabel
  loadOrigin
  loadCategory
  formattedBill {
    ...FormattedBill
  }
  tariff {
    id
    name
    code
  }
  rollup {
    season
    days
    numbers
  }
  intervals {
    maxKw
    year
    units
    totalKwh
  }
  tariffUsdPerKwh
  totalCost
  importsKwh
}
    `
export const SiteMeterSetFragmentDoc = `
    fragment SiteMeterSet on RevampSite {
  meterSet {
    intervalsBauXlsxHref
    loadKwh
    loadMaxKw
    loadOrigin
    totalCost
    rollup {
      season
      days
      numbers
    }
    meters {
      ...SiteBauMeter
    }
  }
}
    `
export const ShineStatsFragmentDoc = `
    fragment ShineStats on ShineStats {
  numProperties
  pvKwInstalled
  totalCo2ReducedTonnes
  totalGrossRevenue
  totalPvProductionKwh
}
    `
export const ShineUnitFragmentDoc = `
    fragment ShineUnit on ShineUnit {
  duration
  start
  actualPvProductionKwh
  expectedPvProductionKwh
  actualGrossRevenue
  expectedGrossRevenue
  co2EmissionsReductionTonnes
}
    `
export const DirtyRowDataFragmentDoc = `
    fragment DirtyRowData on DirtyRowData {
  name
  assetManager
  customerId
  numStories
  streetAddress
  city
  state
  zipCode
  buildingType
  sqft
  yearBuilt
}
    `
export const CsvImportLabelsFragmentDoc = `
    fragment CsvImportLabels on CsvImport {
  pid
  labels {
    pid
    themeColor
    name
  }
}
    `
export const FancyFloatFragmentDoc = `
    fragment FancyFloat on FancyFloat {
  source
  number
}
    `
export const ProviderFragmentDoc = `
    fragment Provider on Provider {
  id
  createdAt
  user {
    ...UserRef
  }
  document {
    pid
    fileName
    contentType
    downloadHref
    viewHref
  }
  espmConsumption {
    id
    createdAt
    lastUpdatedAt
    externalConsumptionId
    externalData
    start
    end
    usageKwh
    costUsd
  }
}
    `
export const BillingMonthViewFragmentDoc = `
    fragment BillingMonthView on BillingMonthView {
  id
  start
  end
  usageKwh {
    ...FancyFloat
  }
  peakKw {
    ...FancyFloat
  }
  totalCostUsd {
    ...FancyFloat
  }
  supplyCostUsd {
    ...FancyFloat
  }
  transmissionCostUsd {
    ...FancyFloat
  }
  provider {
    ...Provider
  }
  supplyProvider {
    ...Provider
  }
}
    `
export const GroupedLineItemFragmentDoc = `
    fragment GroupedLineItem on GroupedLineItem {
  cost
  usage
  name
  rate
  group
  unit
}
    `
export const BillGroupFragmentDoc = `
    fragment BillGroup on BillGroup {
  group
  items {
    ...GroupedLineItem
  }
}
    `
export const BillingMeterProjectedFragmentDoc = `
    fragment BillingMeterProjected on Projected {
  year
  annualKwh
  peakKw
  annualCostUsd
  monthlyUsageKwh
  monthlyCostUsd
  kwh8760 {
    timeZone
    start
    year
  }
  costAndKwhDownloadHref
  tariff {
    id
    pid
    name
    code
  }
  monthlyBills {
    start
    end
    sections {
      ...BillGroup
    }
  }
}
    `
export const BillingIntervalViewFragmentDoc = `
    fragment BillingIntervalView on BillingIntervalView {
  id
  start
  end
  timeZone
  units
  numbers
  timestamps
  document {
    id
    pid
    fileName
    downloadHref
  }
}
    `
export const BillingMeterFragmentDoc = `
    fragment BillingMeter on BillingMeter {
  id
  pid
  view {
    name
    utilityMeterId
    supplyUtilityMeterId
    buildingType
    loadCategory
    isDisabled
    isArchived
    utility {
      pid
      name
      shortName
    }
    tariff {
      id
      pid
      name
      code
    }
    tenantDetails {
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    numBills
    numIntervals
    totalAnnualKwh {
      ...FancyFloat
    }
    peakAnnualKw {
      ...FancyFloat
    }
    weeklyRollup
    months {
      ...BillingMonthView
    }
    calc2 {
      __typename
      ... on E2Error {
        msg
      }
      ... on Projected {
        ...BillingMeterProjected
      }
    }
    intervals {
      ...BillingIntervalView
    }
  }
}
    `
export const PublicTariffFragmentDoc = `
    fragment PublicTariff on PublicTariff {
  id
  pid
  name
  code
  tariffType
  isPreferred
  isNem2
  isClosed
  isPrimaryConnection
  customTariffId
  notes
  updatedAt
  trashedAt
  requiresPv
  requiresStorage
  hasTouRates
  hasNetMetering
  customerLikelihood
  customerCount
  customerClass
  minMonthlyKw
  maxMonthlyKw
  minMonthlyKwh
  maxMonthlyKwh
  latestRateId
}
    `
export const BasicBundleParentFragmentDoc = `
    fragment BasicBundleParent on BundleParent {
  id
  createdAt
  pid
  name
  type
  note
  pricingNote
  shortPricingNote
  state
  biddingOpenAt
  biddingClosedAt
  opsOwner {
    ...UserRef
  }
  recvr {
    pid
    name
  }
  canAddNewBids
  canEditBids
  hiddenFromRecvr
  hiddenFromFinancier
  showAllBidsReport
}
    `
export const PvSystemSpecFragmentDoc = `
    fragment PvSystemSpec on PVSpec {
  id
  createdAt
  pvTypes
  sizeKwDc
  sizeKwAc
  helioscope2Id
  etbMeterId
  productionOrigin
  helioscopePdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
  allocations {
    id
    createdAt
    percentage
    meter {
      pid
      view {
        name
      }
    }
  }
}
    `
export const SystemFragmentDoc = `
    fragment System on System {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
    notes
    etbMeterName
    document {
      id
      pid
      fileName
      downloadHref
    }
    meter {
      pid
      view {
        name
      }
    }
  }
}
    `
export const PpaBidSpecFragmentDoc = `
    fragment PpaBidSpec on PPABidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  system {
    ...System
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
}
    `
export const SystemLiteFragmentDoc = `
    fragment SystemLite on SystemLite {
  id
  pid
  createdAt
  totalPvSizeKw
  pvSpecs {
    ...PvSystemSpec
  }
  batterySpecs {
    id
    sizeKwh
    sizeKwDc
    notes
  }
}
    `
export const CsBidSpecFragmentDoc = `
    fragment CsBidSpec on CSBidSpec {
  __typename
  id
  pid
  name
  termYears
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  roofRentY1Usd
  roofRentEscalator
  buildingOwnerExtraPaymentsUsd
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  recOwner
  systemLite {
    ...SystemLite
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  ftmProgram {
    id
    name
  }
}
    `
export const CashBidSpecFragmentDoc = `
    fragment CashBidSpec on CashBidSpec {
  __typename
  id
  pid
  name
  note
  archivedAt
  archivedBy {
    ...UserRef
  }
  itcPercentageAssumption
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
}
    `
export const InvestorBidSpecOptOutFragmentDoc = `
    fragment InvestorBidSpecOptOut on InvestorBidSpecOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    `
export const BidSpecFragmentDoc = `
    fragment BidSpec on BidSpec {
  ppa {
    ...PpaBidSpec
  }
  cs {
    ...CsBidSpec
  }
  cash {
    ...CashBidSpec
  }
  optOut {
    ...InvestorBidSpecOptOut
  }
}
    `
export const BundleParentFragmentDoc = `
    fragment BundleParent on BundleParent {
  ...BasicBundleParent
  bundles {
    name
    pid
    financier {
      pid
      name
      orgSlug
    }
  }
  rows {
    specs {
      ...BidSpec
    }
    site {
      pid
      name
      streetAddress
      city
      state
      zipCode
    }
    notes
    status
    closedAt
    closeReason
    closeCustomReason
    awardedFinancier {
      pid
      name
      slug
      logoUrl
      logomarkUrl
    }
    recommendedBid {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
    energyProject {
      pid
      name
      changeOrderSent
      scopeOfWorkSent
      hostAwardSent
    }
  }
  documents {
    id
    pid
    tag
    fileName
    contentType
    dateCreated
    downloadHref
    canDelete
    ownerName
  }
  adminOnlyNote
  notionPageUrl
  bidRemindersSent
  lossNoticesSent
}
    `
export const CustomBidReportFragmentDoc = `
    fragment CustomBidReport on CustomBidReport {
  pid
  name
  notes
  buildingOwnerNotes
  visibleToDevelopers
  visibleToBuildingOwners
  bids {
    ppa {
      pid
      financier {
        orgSlug
      }
    }
    cs {
      pid
      financier {
        orgSlug
      }
    }
  }
}
    `
export const DocumentParseRequestFragmentDoc = `
    fragment DocumentParseRequest on DocumentParseRequest {
  id
  createdAt
  status
  errorClass
  errorMessage
  document {
    id
    pid
    fileName
    contentType
    downloadHref
    viewHref
    ownerName
    ownerEmail
    dateCreated
  }
  results {
    parse {
      id
      start
      end
      totalKwh
      peakKw
      totalCostUsd
      supplyCostUsd
      transmissionCostUsd
      meterId
      tariffLabel
      kind
    }
    target {
      id
      pid
      label
      utilityMeterId
    }
    kind
  }
}
    `
export const InvestorOptOutFragmentDoc = `
    fragment InvestorOptOut on InvestorOptOut {
  __typename
  createdAt
  createdBy {
    ...UserRef
  }
  archivedAt
  archivedBy {
    ...UserRef
  }
  reason
  customReason
}
    `
export const BundleListItemFragmentDoc = `
    fragment BundleListItem on Bundle2 {
  pid
  name
  type
  note
  pricingNote
  state
  archivedAt
  lastViewed
  biddingOpenAt
  biddingClosedAt
  hasVisibleReports
  optOut {
    ...InvestorOptOut
  }
  totalBids
  recvr {
    pid
    name
    orgSlug
  }
  financier {
    pid
    name
    orgSlug
  }
  bidSubmissionStatus {
    bidStatus
    submittedBidCount
    totalRequestedBids
  }
  awardStatus {
    awardStatus
    awardedBidCount
    totalPossibleAwards
  }
}
    `
export const InvestorProfileFragmentDoc = `
    fragment InvestorProfile on InvestorProfile {
  hq
  yearFounded
  website
  description
  committedFunding
  distinguishingFeatures
  investorBacking
  managementTeam
  longTermOwnershipApproach
  subscriptionMgmtApproach
  experiences {
    state
    ftmEnergizedPvKw
    ftmAwardedPvKw
    btmEnergizedPvKw
    btmAwardedPvKw
  }
  buyBoxes {
    state
    ftmMinPortfolioSize
    ftmMinProjectSize
    ftmSeekingProjects
    btmMinPortfolioSize
    btmMinProjectSize
    btmSeekingProjects
  }
}
    `
export const PpaInvestorBidFragmentDoc = `
    fragment PpaInvestorBid on PPAInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedCod
  estimatedNtp
  buildingOwnerExtraPaymentsUsd
  recOwner
  otherNotes
  lumenNotes
  extraPaymentsNotes
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  status
  system {
    ...System
  }
  result {
    ...RevampFinmodel
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    `
export const RevampCsFragmentDoc = `
    fragment RevampCS on RevampCS {
  __typename
  id
  termYears
  createdAt
  goal
  systems {
    ...RevampSystem
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
  }
  buildingOwner {
    noiYearOne
    noiLifetime
    noiAnnual
  }
  roofRent {
    lifetimeUsd
    annualUsd
    usdPerSqft
    npv
    discountRate
    roofRentPerYear
  }
  pricing {
    utility {
      id
      name
    }
    usdPerKwPerYear
    escalator
    minPvKw
    maxPvKw
  }
  leaseProjections {
    systemSizeKw
    annualRentMinimumUsd
    annualRentLowerRangeUsd
    annualRentMedianUsd
    annualRentUpperRangeUsd
    annualRentMaximumUsd
    annualRentP10Usd
    annualRentP90Usd
    avgAwardedAnnualRentUsd
    methodology
  }
}
    `
export const CsInvestorBidFragmentDoc = `
    fragment CsInvestorBid on CSInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  financier {
    pid
    name
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
  name
  termYears
  roofRentEscalator
  roofRentY1Usd
  buildingOwnerExtraPaymentsUsd
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  estimatedAwardDate
  estimatedCod
  estimatedNtp
  recOwner
  incentivesNotes
  interconnectionNotes
  interconnectionRepriceProcessNotes
  extraPaymentsNotes
  otherNotes
  lumenNotes
  status
  systemLite {
    ...SystemLite
  }
  result {
    ...RevampCS
  }
  roofReplacementYear
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  subscriberDiscountPct
  ftmProgram {
    id
    name
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    `
export const CashInvestorBidFragmentDoc = `
    fragment CashInvestorBid on CashInvestorBid {
  __typename
  pid
  spec {
    pid
  }
  name
  estimatedCod
  estimatedNtp
  interconnectionCostUsdPerWatt
  interconnectionCostUtilityMinUsd
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  interconnectionNotes
  incentivesNotes
  otherNotes
  status
  system {
    ...System
  }
  itcAdders
  itcObbbStrategy
  itcObbbCustomStrategy
  recValueUsdPerMwh
  estimatedIxApproval
  totalPvCostUsd
  totalStorageCostUsd
  srecApplied
  annualOmCostUsd
  omCostNotes
  lumenNotes
  result {
    ...RevampFinmodel
  }
  documents {
    pid
    fileName
    downloadHref
    tag
  }
}
    `
export const InvestorBidFragmentDoc = `
    fragment InvestorBid on InvestorBid {
  ppa {
    ...PpaInvestorBid
  }
  cs {
    ...CsInvestorBid
  }
  cash {
    ...CashInvestorBid
  }
}
    `
export const BundleDetailRowFragmentDoc = `
    fragment BundleDetailRow on BundleRow2 {
  id
  ppaType
  state
  reason
  customReason
  notes
  optOut {
    ...InvestorOptOut
  }
  bids {
    ...InvestorBid
  }
  specs {
    ...BidSpec
  }
}
    `
export const BoxplotStatsFragmentDoc = `
    fragment BoxplotStats on BoxplotStats {
  min
  q1
  median
  q3
  max
  average
}
    `
export const CsSiteBidReportFragmentDoc = `
    fragment CSSiteBidReport on CSSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedAwardDate
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  roofRentEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
    `
export const PpaSiteBidReportFragmentDoc = `
    fragment PPASiteBidReport on PPASiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    termYears
    roofRentEscalator
    roofRentY1Usd
    buildingOwnerExtraPaymentsUsd
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    recOwner
    incentivesNotes
    interconnectionNotes
    interconnectionRepriceProcessNotes
    extraPaymentsNotes
    otherNotes
    result {
      buildingOwner {
        noiLifetime
      }
    }
  }
  noi {
    ...BoxplotStats
  }
  y1RoofRent {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  ppaPriceEscalator {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
  termYears {
    ...BoxplotStats
  }
}
    `
export const CashSiteBidReportFragmentDoc = `
    fragment CashSiteBidReport on CashSiteBidReport {
  __typename
  site {
    pid
    name
    streetAddress
    city
    state
    zipCode
    county
    fullAddress
  }
  awarded
  awardedPid
  totalBids
  bids {
    __typename
    name
    pid
    financier {
      pid
      name
      logoUrl
      logomarkUrl
      profile {
        ...InvestorProfile
      }
    }
    name
    interconnectionCostUsdPerWatt
    interconnectionCostUtilityMinUsd
    interconnectionCostUtilityMaxUsd
    interconnectionCostNonUtilityUsd
    itcPercentageAssumption
    estimatedCod
    estimatedNtp
    incentivesNotes
    interconnectionNotes
    otherNotes
    result {
      selfInvest {
        npv
        irrs {
          postTaxItcDepr
        }
        flows {
          totalCapex
          totalUtilitySavings
        }
      }
    }
  }
  itcPercentageAssumption {
    ...BoxplotStats
  }
  totalLifetimeSavings {
    ...BoxplotStats
  }
  totalProjectCost {
    ...BoxplotStats
  }
  systemSize {
    ...BoxplotStats
  }
  npv {
    ...BoxplotStats
  }
}
    `
export const BidReportFragmentDoc = `
    fragment BidReport on PackageBidReport {
  pid
  name
  notes
  visible
  totalDevelopers
  totalBids
  cs {
    ...CSSiteBidReport
  }
  ppa {
    ...PPASiteBidReport
  }
  cash {
    ...CashSiteBidReport
  }
}
    `
export const BundleDetailFragmentDoc = `
    fragment BundleDetail on Bundle2 {
  ...BundleListItem
  opsOwner {
    ...UserRef
    meetingSchedulingUrl
  }
  parentRef {
    pid
  }
  documents {
    canDelete
    contentType
    dateCreated
    fileName
    ownerName
    pid
    tag
    downloadHref
    viewHref
    belongsToBundleParent
    investorBid {
      pid
      name
    }
    ownerOrg {
      orgSlug
    }
  }
  downloadBundleDocsZipHref
  downloadHelioscopesDotZipHref
  downloadBundleDotXlsxHref
  rows {
    ...BundleDetailRow
    site {
      pid
      name
      fullAddress
      streetAddress
      city
      state
      zipCode
      county
      cbsaName
      buildingData {
        ownerName
      }
      buildingType
      siteDetails {
        roofReplacedYr
        roofCondition
      }
      yearBuilt
      targetUtility {
        pid
        name
        hostingCapacityMaps {
          url
          requiresLogin
          types
        }
      }
      isEnergyCommunity
      isLowIncomeCommunity
      sqft
      roofSqft
      roofMaxPvKw
      numStories
      buildingClass
    }
  }
  bidReports {
    ...BidReport
  }
}
    `
export const BundleRowDetailFragmentDoc = `
    fragment BundleRowDetail on BundleRow2 {
  ...BundleDetailRow
  site {
    ...RevampSiteBasics
    meterSet {
      totalCost
      loadKwh
    }
    billingMeters {
      ...BillingMeterOption
    }
  }
}
    `
export const Helioscope2RefFragmentDoc = `
    fragment Helioscope2Ref on Helioscope2 {
  id
  pdf {
    downloadHref
  }
  roofImage {
    viewHref
  }
  productionCsv {
    downloadHref
  }
}
    `
export const BasicBidPackageFragmentDoc = `
    fragment BasicBidPackage on BidPackage {
  pid
  name
  biddingOpenAt
  biddingClosedAt
  state
  type
  totalRows
  financiers {
    pid
    name
    orgSlug
    logoUrl
    logomarkUrl
    profile {
      ...InvestorProfile
    }
  }
}
    `
export const PackageDocumentFragmentDoc = `
    fragment PackageDocument on Document {
  canDelete
  contentType
  dateCreated
  fileName
  ownerName
  pid
  tag
  downloadHref
  belongsToBundleParent
  ownerOrg {
    orgSlug
    name
  }
  investorBid {
    pid
    name
  }
}
    `
export const BidPackageDetailFragmentDoc = `
    fragment BidPackageDetail on BidPackage {
  ...BasicBidPackage
  documents {
    ...PackageDocument
  }
  bidDocuments {
    ...PackageDocument
  }
}
    `
export const BidReviewFragmentDoc = `
    fragment BidReview on BidReview {
  recommendedBidPids
  reports {
    properties
    bidders
    uniqueBids
    avgRoofRentY1Usd
    isCustom
    report {
      ...BidReport
    }
  }
}
    `
export const RateBandFragmentDoc = `
    fragment RateBand on RateBand {
  tariffRateBandId
  rateUnit
  price
  isCredit
  applicabilityValue
}
    `
export const SeasonFragmentDoc = `
    fragment Season on Season {
  seasonFromDay
  seasonFromMonth
  seasonToDay
  seasonToMonth
}
    `
export const RateFragmentDoc = `
    fragment Rate on Rate {
  name
  groupName
  tariffRateId
  chargeClass
  chargeType
  transactionType
  chargePeriod
  applicabilityKey
  applicabilityValues
  quantityKey
  applicableBands {
    ...RateBand
  }
  bands {
    ...RateBand
  }
  tou {
    touName
    touType
    touPeriods {
      fromHour
      fromMinute
      fromDayOfWeek
      toHour
      toMinute
      toDayOfWeek
    }
    season {
      ...Season
    }
  }
  season {
    ...Season
  }
}
    `
export const CustomRateFragmentDoc = `
    fragment CustomRate on CustomRate {
  name
  chargeClass
  chargeType
  transactionType
  price
  rateUnit
  chargePeriod
}
    `
export const TariffOpFragmentDoc = `
    fragment TariffOp on AddRateSubRateOverrideProperty {
  __typename
  ... on AddRate {
    rate {
      ...CustomRate
    }
    op
  }
  ... on SubRate {
    chargeClass
    rateId
    op
  }
  ... on OverrideProperty {
    keyName
    propertyValue
    op
  }
}
    `
export const ChangeLogEntryFragmentDoc = `
    fragment ChangeLogEntry on ChangeLogEntry {
  source
  sourceId
  oldValue
  newValue
  columnType
  changedAt
}
    `
export const FieldChangeLogHistoryFragmentDoc = `
    fragment FieldChangeLogHistory on FieldChangeLogHistory {
  latest {
    ...ChangeLogEntry
  }
  history {
    ...ChangeLogEntry
  }
}
    `
export const SiteFieldChangeLogFragmentDoc = `
    fragment SiteFieldChangeLog on SiteFieldChangeLog {
  occupancyType {
    ...FieldChangeLogHistory
  }
  leaseType {
    ...FieldChangeLogHistory
  }
  targetUtilityId {
    ...FieldChangeLogHistory
  }
  tenantLeaseTerm {
    ...FieldChangeLogHistory
  }
}
    `
export const SiteDetailsFieldChangeLogFragmentDoc = `
    fragment SiteDetailsFieldChangeLog on SiteDetailsFieldChangeLog {
  buildingLegalName {
    ...FieldChangeLogHistory
  }
  constructionEmail {
    ...FieldChangeLogHistory
  }
  counselApproval {
    ...FieldChangeLogHistory
  }
  externalLeaseApprovers {
    ...FieldChangeLogHistory
  }
  isFannieMaeLender {
    ...FieldChangeLogHistory
  }
  isFmGlobalInsurer {
    ...FieldChangeLogHistory
  }
  leaseApprovers {
    ...FieldChangeLogHistory
  }
  leaseLegalOwnerName {
    ...FieldChangeLogHistory
  }
  leasePropertyLegalDescription {
    ...FieldChangeLogHistory
  }
  leaseSignatory {
    ...FieldChangeLogHistory
  }
  needsTenantApproval {
    ...FieldChangeLogHistory
  }
  propertyFundName {
    ...FieldChangeLogHistory
  }
  propertyInsurer {
    ...FieldChangeLogHistory
  }
  propertyLenders {
    ...FieldChangeLogHistory
  }
  propertySiteVisitContacts {
    ...FieldChangeLogHistory
  }
  roofCondition {
    ...FieldChangeLogHistory
  }
  roofReplacedYr {
    ...FieldChangeLogHistory
  }
  roofRightsHolder {
    ...FieldChangeLogHistory
  }
  salePlannedYear {
    ...FieldChangeLogHistory
  }
  salePlans {
    ...FieldChangeLogHistory
  }
  signatureBlocks {
    ...FieldChangeLogHistory
  }
}
    `
export const Helioscope2FragmentDoc = `
    fragment Helioscope2 on Helioscope2 {
  pid
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  groundPvKw
  dcNameplateKw
  acNameplateKw
  annualProductionAcKwh
  notes
  helioscopeKw
  pdfHref
  roofImageHref
  roofImageName
  roofImage {
    id
    fileName
    downloadHref
  }
  pdf {
    id
    fileName
    downloadHref
    contentType
  }
}
    `
export const EnergyProjectCsSpecificationFragmentDoc = `
    fragment EnergyProjectCSSpecification on EnergyProjectCSSpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  roofRentY1Usd
  roofRentEscalator
  systemLite {
    ...SystemLite
  }
}
    `
export const EnergyProjectPpaSpecificationFragmentDoc = `
    fragment EnergyProjectPPASpecification on EnergyProjectPPASpecification {
  finproductType
  interconnectionCostUtilityMaxUsd
  interconnectionCostNonUtilityUsd
  itcPercentageAssumption
  buildingOwnerExtraPaymentsUsd
  recOwner
  termYears
  ppaPriceY1Usd
  ppaPriceEscalator
  roofRentY1Usd
  roofRentEscalator
  system {
    ...System
  }
}
    `
export const MilestoneSummaryFragmentDoc = `
    fragment MilestoneSummary on MilestoneSummary {
  rofoSignedEstimated
  rofoSignedActual
  biddingOpenedEstimated
  biddingOpenedActual
  biddingClosedEstimated
  biddingClosedActual
  bidsPresentedEstimated
  bidsPresentedActual
  ixSubmittedEstimated
  ixSubmittedActual
  ixApprovedEstimated
  ixApprovedActual
  csProgramSubmittedEstimated
  csProgramSubmittedActual
  csProgramApprovedEstimated
  csProgramApprovedActual
  awardedToDeveloperEstimated
  awardedToDeveloperActual
  leaseSignedEstimated
  leaseSignedActual
  ntpEstimated
  ntpActual
  codEstimated
  codActual
}
    `
export const ProgressNotesFragmentDoc = `
    fragment ProgressNotes on ProgressNotesType {
  pid
  notes
  actionItem
  priority
}
    `
export const EnergyProjectFragmentDoc = `
    fragment EnergyProject on EnergyProject {
  pid
  name
  description
  hubspotRecordId
  hubspotRevenueRecordId
  status
  closedAt
  closeReason
  closeCustomReason
  lumenFeeUsdPerWatt
  lumenRevenue
  archivedAt
  archivedBy {
    ...UserRef
  }
  site {
    pid
    streetAddress
  }
  projectSpecification {
    cs {
      ...EnergyProjectCSSpecification
    }
    ppa {
      ...EnergyProjectPPASpecification
    }
  }
  milestoneSummary {
    ...MilestoneSummary
  }
  developer {
    name
    pid
  }
  progressNotes {
    ...ProgressNotes
  }
}
    `
export const AdminEnergyProjectFragmentDoc = `
    fragment AdminEnergyProject on EnergyProject {
  ...EnergyProject
  bundleParentRow {
    id
    status
    notes
    parent {
      pid
      name
      recvr {
        slug
        name
      }
    }
  }
  awardedBid {
    ppa {
      pid
      name
      bidPackagePid
      bidPackageOrgSlug
      system {
        ...System
      }
    }
    cs {
      pid
      name
      bidPackagePid
      bidPackageOrgSlug
      systemLite {
        ...SystemLite
      }
    }
  }
}
    `
export const OrgUserFragmentDoc = `
    fragment OrgUser on OrgUser {
  user {
    ...UserRef
  }
  userRoles {
    role
  }
}
    `
export const MutBillingMeterFragmentDoc = `
    fragment MutBillingMeter on BillingMeter {
  __typename
  ... on BillingMeter {
    pid
    label
    config {
      loadCategory
      buildingType
    }
  }
}
    `
export const EtbExtractFragmentDoc = `
    fragment EtbExtract on ETBExtract {
  id
  document {
    id
    pid
    fileName
    downloadHref
  }
  pv {
    sizeKwDc
    sizeKwAc
    etbMeterId
    etbMeterName
  }
  battery {
    sizeKwDc
    sizeKwh
    etbMeterId
    etbMeterName
  }
}
    `
export const ResultRefFragmentDoc = `
    fragment ResultRef on Result {
  id
  pid
  createdAt
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
}
    `
export const HelioscopeFragmentDoc = `
    fragment Helioscope on Helioscope {
  pid
  roofPvKw
  carportPvKw
  groundPvKw
  systemProductivityKwhPerKw
  roofImage {
    id
    name
    downloadHref
  }
  pdf {
    downloadHref
    contentType
    name
    id
  }
  roofImageHref
  roofImageName
  pdfHref
  helioscopeKw
  dcNameplateKw
  acNameplateKw
  loadRatio
  moduleType
  inverterType
  rackingTiltDegrees
  rackingManufacturer
  rackingConnectionType
  notes
}
    `
export const ResultFragmentDoc = `
    fragment Result on Result {
  id
  pid
  createdAt
  salesPdfReportHref
  scenario {
    pid
    name
    solarLocation
    loadCategory
    includeStorage
  }
  financing {
    id
    name
    type
    financierName
    financierPid
    financierLogo
    financierLogomark
    isThirdParty
    status
  }
  result {
    __typename
    ... on PhysicalCheck {
      createdAt
      roofIsGood
      carportIsGood
      existingSolar
      badRoof
      groundmountIsGood
    }
    ... on PolicyCheck {
      createdAt
      productType
      isGood
      disqualReason
    }
    ... on RevampFinmodelFailure {
      id
      createdAt
      fmvStr
      details {
        label
        description
      }
    }
    ... on RevampFinmodel {
      ...RevampFinmodel
      iterations {
        cashPurchaseNpv
        pvKw
        marginalCo2Averted
        storagePvKw
        storagePvKwh
      }
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
    ... on RevampCS {
      ...RevampCS
    }
    ... on Viability {
      isAllowed
      viabilityType: type
      reason
      level
    }
  }
  helioscope {
    ...Helioscope
  }
}
    `
export const ResultWithEnergyDataFragmentDoc = `
    fragment ResultWithEnergyData on Result {
  id
  result {
    ... on RevampFinmodel {
      meters {
        loadCategory
        meterSource
        bau {
          totalKwhFlow {
            ...KwhFlow
          }
        }
        der {
          totalKwhFlow {
            ...KwhFlow
          }
          averageDayKwhFlow {
            ...KwhFlow
          }
        }
      }
      emissions {
        bauCo2Tons
        derCo2Tons
        bauAverageDayCo2Tons
        derAverageDayCo2Tons
      }
    }
  }
}
    `
export const BidRevampSiteFragmentDoc = `
    fragment BidRevampSite on RevampSite {
  pid
  name
  streetAddress
  city
  state
  zipCode
  county
  fullAddress
  assetManager
  buildingType
  meterSet {
    totalCost
    loadKwh
  }
  yearBuilt
  numStories
  sqft
  roofSqft
  siteDetails {
    salePlans
    roofCondition
    roofReplacedYr
  }
  roofMaxPvKw
  buildingClass
  targetUtility {
    name
    pid
    id
  }
  ppaType
}
    `
export const AdminUserRefFragmentDoc = `
    fragment AdminUserRef on AdminUser {
  id
  pid
  email
  name
  isAdmin
  avatarUrl
}
    `
export const SiteVisualsFragmentDoc = `
    fragment SiteVisuals on SiteVisuals {
  solarPanels {
    latitude
    longitude
    angle
  }
}
    `
export const BasicEvCalcFragmentDoc = `
    fragment BasicEVCalc on EVCalc {
  id
  createdAt
  projectInputs {
    projectStartDate
    installType
    termYears
    chargerKw
    yr0NumTenantEvs
    discountRate
  }
  outputs {
    irr
    npv
    yr0NumTenantEvs
    breakevenYear
    numChargersDemandedSeries
    cashflowSeries
    revenueSeries
    incentiveUsd
  }
  numChargers
  optimalNumChargers
  npv
  markupUsdPerKwh
  tariffRate {
    parent {
      name
      code
    }
  }
  utility {
    shortName
  }
  timeZone
  dayRates
  seasonalDayRates {
    name
    startMonth
    endMonth
    dayRates
  }
  downloadHourlyRatesHref
  xlsx
  downloadXlsxHref
  optIters {
    numChargers
    npv
  }
  yr3Revenue
  yr3Cashflow
  yr3NumEvs
  parkingRatio
  year3Expenses {
    label
    value
  }
  installCosts {
    label
    value
  }
}
    `
export const DocumentFragmentDoc = `
    fragment Document on Document {
  id
  pid
  contentType
  dateCreated
  fileName
  ownerName
  ownerEmail
  downloadHref
  viewHref
  tag
}
    `
export const BauTariffFragmentDoc = `
    fragment BauTariff on Tariff {
  id
  name
  code
  masterTariffId
}
    `
export const BauMeterFragmentDoc = `
    fragment BauMeter on BauMeter {
  id
  createdAt
  createdBy {
    email
  }
  label
  loadOrigin
  loadCategory
  meterSource
  utility {
    id
    name
  }
  tariff {
    ...BauTariff
  }
  tariffSetBy {
    pid
    email
  }
  underHumanControl
  tariffsUnderHumanControl
  postSolarTariff {
    ...BauTariff
  }
  postSolarTariffSetBy {
    pid
    email
  }
  postBatteryTariff {
    ...BauTariff
  }
  postBatteryTariffSetBy {
    pid
    email
  }
  archivedAt
  archivedBy {
    pid
    email
  }
  archivedReason
  rollup {
    season
    days
    numbers
  }
}
    `
export const BauMeterIntervalsFragmentDoc = `
    fragment BauMeterIntervals on BauMeter {
  id
  tariffUsdPerKwh
  rollup {
    season
    days
    numbers
  }
  intervals {
    units
    year
    timeZone
    maxKw
    totalKwh
    kwh8760
  }
}
    `
export const SitesTableFragmentDoc = `
    fragment SitesTable on Site {
  pid
  propertyName
  fullAddress
  state
  buildingType
  sqft
  roofSqft
  occupancyType
  leaseType
  metering
  mortgageLender
  roofReplacedYr
  hiddenFromCustomers
  portfolio {
    pid
    title
    org {
      slug
    }
  }
  roofImage {
    id
  }
  roofMaxPvKw
  carportMaxPvKw
  primaryScenario {
    pid
    solarLocation
    includeStorage
  }
  currentStatus {
    pid
    status
  }
  createdAt
}
    `
export const ReonomyContactFragmentDoc = `
    fragment ReonomyContact on ReonomyContact {
  firstName
  lastName
  company
  jobTitle
  emailAddresses
  phoneNumbers
  fullAddresses
}
    `
export const UserListUserFragmentDoc = `
    fragment UserListUser on AdminUser {
  id
  pid
  email
  name
  affiliation
  isAdmin
  isEnabled
  avatarUrl
  meetingSchedulingUrl
  portfoliosWithRole {
    pid
    title
  }
  unclaimedInvitations {
    isClaimed
    isExpired
    expiresAt
    portfolio {
      pid
      title
    }
  }
  licenseAgreements {
    forOrg {
      pid
      name
    }
    acceptedAt
  }
  org {
    pid
    name
    slug
  }
  lastSeenAt
}
    `
export const ChangelogFragmentDoc = `
    fragment Changelog on Changelog {
  field
  updatedAt
  updatedBy {
    ...UserRef
  }
}
    `
export const RuleValueFragmentDoc = `
    fragment RuleValue on RuleValue {
  __typename
  ... on YesNoValue {
    yes
  }
  ... on NumberValue {
    number
  }
  ... on CurrencyValue {
    usd
  }
  ... on IntegerValue {
    integer
  }
  ... on TextValue {
    text
  }
}
    `
export const PolicyConditionFragmentDoc = `
    fragment PolicyCondition on PolicyCondition {
  __typename
  ... on BuildingSuperTypeCondition {
    buildingSuperType
  }
  ... on VnemEligibilityCondition {
    vnemEligibilityRequired
  }
}
    `
export const RuleFragmentDoc = `
    fragment Rule on Rule {
  id
  description
  units
  value {
    ...RuleValue
  }
  application {
    status
    detail
  }
  conditions {
    ...PolicyCondition
  }
}
    `
export const PolicyListItemFragmentDoc = `
    fragment PolicyListItem on Policy {
  pid
  name
  jurisdiction {
    scope
    name
  }
  status
  startDate
  endDate
  userVisible
  application {
    status
  }
  rules {
    ...Rule
  }
}
    `
export const OverrideFragmentDoc = `
    fragment Override on FinmodelInputOverride {
  id
  dateCreated
  dateTrashed
  inputName
  inputValue
  source
  createdBy
}
    `
export const FinRelFragmentDoc = `
    fragment FinRel on AdminFinRel {
  disabledAt
  finproduct {
    id
    type
    name
    financier {
      name
      pid
    }
  }
  recvr {
    pid
  }
}
    `
export const ScenarioRefFragmentDoc = `
    fragment ScenarioRef on ScenarioRef {
  pid
  name
}
    `
export const AttachmentFragmentFragmentDoc = `
    fragment AttachmentFragment on Attachment {
  id
  size
  name
  contentType
  downloadHref
  createdAt
  trashedAt
  isTrashable
  isRoofImageCandidate
  isEtb
  createdBy {
    ...UserRef
  }
}
    `
export const ScenarioHelioscopeFragmentFragmentDoc = `
    fragment ScenarioHelioscopeFragment on Helioscope {
  id
  pid
  createdAt
  isTrashed
  roofPvKw
  carportPvKw
  pdf {
    ...AttachmentFragment
  }
  roofImage {
    ...AttachmentFragment
  }
}
    `
export const ScenarioEtbFragmentFragmentDoc = `
    fragment ScenarioEtbFragment on ETBReport {
  id
  dateCreated
  projectName
  roofPvKw
  carportPvKw
  groundPvKw
  batteryKw
  batteryKwh
}
    `
export const TariffDetailFragmentFragmentDoc = `
    fragment TariffDetailFragment on TariffDetails {
  tariff {
    id
    code
    name
    customerClass
  }
  version {
    tariffId
    effectiveDate
    endDate
  }
  properties {
    keyName
    dataType
    propertyValue
    formulaDetail
  }
  rawJson
  rates {
    tariffRateId
    name
    classes
    chargeClass
    chargeType
    chargePeriod
    transactionType
    applicabilityKey
    quantityKey
    isDemandCharge
    isFixedPrice
    isQuantity
    isTou
    variableLimitValue {
      keyName
      formulaDetail
    }
    bands {
      tariffRateBandId
      rateUnit
      price
      isCredit
      hasConsumptionLimit
      propertyUpperLimit
      consumptionUpperLimit
      demandUpperLimit
      calculationFactor
      applicabilityValue
    }
    season {
      seasonFromMonth
      seasonToMonth
      seasonFromDay
      seasonToDay
    }
    tou {
      touName
      season {
        seasonFromMonth
        seasonToMonth
        seasonFromDay
        seasonToDay
      }
      touPeriods {
        fromDayOfWeek
        toDayOfWeek
        fromHour
        toHour
        fromMinute
        toMinute
      }
    }
  }
}
    `
export const MeteringFragmentDoc = `
    fragment Metering on Site {
  metering
  meteringSetDate
  meteringSetBy {
    ...UserRef
  }
}
    `
export const EtbScenarioRefFragmentDoc = `
    fragment ETBScenarioRef on ETBScenarioRef {
  pid
  name
  isPrimary
  solarLocation
  includeStorage
  loadCategory
}
    `
export const CostsFragmentFragmentDoc = `
    fragment CostsFragment on ETBCosts {
  totalCost
  totalIncentiveSavings
  netCost
  energyConsumptionKwhYear1
  netEnergyConsumptionKwhYear1
  utilityCostPerKwh
  averageLoadFactor
  energyCostBefore
  energyCostAfter
  energyCostSavings
  demandCostBefore
  demandCostAfter
  demandCostSavings
  totalCostBefore
  totalCostAfter
  totalCostSavings
  pvTotalCost
  pvNetCost
  pvPowerRatingKwDc
  pvPowerRatingKwAc
  pvDegradationRate
  pvGenerationKwhYear1
  pvKwhPerKwDc
  pvSavingsPerKwh
  pvGenerationPercentExported
  storageTotalCost
  storageNetCost
  storageCapacityKwh
  storageMaxDischargePowerKw
  storageTwoHourPowerRatingKw
  storageDegradationRate
  storageDischargeYear1
  storageConsumptionKwhYear1
  storageSavingsPerKw
  storageSavingsPerKwh
}
    `
export const PvConfigFragmentDoc = `
    fragment PVConfig on PVConfig {
  pvType
  origin
  sizeKw
  productionKwh
}
    `
export const BatteryConfigFragmentDoc = `
    fragment BatteryConfig on BatteryConfig {
  vendor
  sizeKwh
  sizeKw
  chargeStrategy
  dischargeStrategy
}
    `
export const SlimLuxFragmentDoc = `
    fragment SlimLux on Lux {
  id
  etbReport {
    id
  }
  createdAt
  fmvStr
  goal
  maxOffsetPct
  pvKw
  roofPvKw
  carportPvThreeHighKw
  carportPvSixHighKw
  battKw
  battKwh
  meterSet {
    id
  }
  meters {
    bauMeter {
      id
    }
    postSolarMeter {
      pv {
        ...PVConfig
      }
    }
    postBatteryMeter {
      batt {
        ...BatteryConfig
      }
    }
  }
}
    `
export const CostSummaryFragmentDoc = `
    fragment CostSummary on CostSummary {
  totalCost
  totalEnergyUsage
  energyCost
  demandCost
  fixedCost
  nbcCost
  exportBenefit
}
    `
export const LuxMeterFragmentDoc = `
    fragment LuxMeter on LuxMeter {
  id
  gridMeter {
    preSolar {
      costSummary {
        ...CostSummary
      }
    }
    postSolar {
      costSummary {
        ...CostSummary
      }
    }
    postBattery {
      costSummary {
        ...CostSummary
      }
    }
  }
  dayIntervals {
    pvToBldg
    battDischarge
    gridToBldg
  }
  bauMeter {
    id
    label
    importsKwh
    tariff {
      name
      code
    }
  }
  postSolarMeter {
    pv {
      ...PVConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
  postBatteryMeter {
    batt {
      ...BatteryConfig
    }
    tariff {
      parent {
        name
        code
      }
    }
  }
}
    `
export const LuxFragmentDoc = `
    fragment Lux on Lux {
  ...SlimLux
  meters {
    ...LuxMeter
  }
  iterations {
    meters {
      bauMeterId
      totalPvKw
      pv {
        pvType
        sizeKw
      }
    }
    metric
    postTaxItcDeprIrr
  }
}
    `
export const DeltaFragmentDoc = `
    fragment Delta on Delta {
  a
  b
  diff
  pct
}
    `
export const NameThatTariffResultFragmentDoc = `
    fragment NameThatTariffResult on NameThatTariffResult {
  notTheCorrectRateVersion
  rate {
    startDate
    endDate
    tariffId
  }
  expected {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  actual {
    start
    end
    totalCostUsd
    totalKwh
    peakKw
    lineItems {
      chargeType
      costUsd
    }
  }
  delta {
    usd {
      ...Delta
    }
    kwh {
      ...Delta
    }
    kw {
      ...Delta
    }
  }
  document {
    pid
    contentType
    viewHref
  }
}
    `
export const AdminSitesTableViewDocument = `
    query AdminSitesTableView($filterBy: AdminSitesTableFilter, $sortBy: [AdminSitesTableSort!]) {
  admin {
    allSites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableView"]
        : ["AdminSitesTableView", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminSitesTableView"] : ["AdminSitesTableView", variables]

useAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useSuspenseAdminSitesTableViewQuery = <
  TData = Types.AdminSitesTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableViewSuspense"]
        : ["AdminSitesTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableViewQuery,
      Types.AdminSitesTableViewQueryVariables
    >(AdminSitesTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableViewSuspense"]
    : ["AdminSitesTableViewSuspense", variables]

useSuspenseAdminSitesTableViewQuery.rootKey = "AdminSitesTableView"

export const useInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infinite"]
            : ["AdminSitesTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infinite"]
    : ["AdminSitesTableView.infinite", variables]

useInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

export const useSuspenseInfiniteAdminSitesTableViewQuery = <
  TData = InfiniteData<Types.AdminSitesTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableView.infiniteSuspense"]
            : ["AdminSitesTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableViewQuery,
            Types.AdminSitesTableViewQueryVariables
          >(AdminSitesTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableViewQuery.getKey = (
  variables?: Types.AdminSitesTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableView.infiniteSuspense"]
    : ["AdminSitesTableView.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableViewQuery.rootKey = "AdminSitesTableView.infinite"

useAdminSitesTableViewQuery.fetcher = (
  variables?: Types.AdminSitesTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableViewQuery,
    Types.AdminSitesTableViewQueryVariables
  >(AdminSitesTableViewDocument, variables, options)

export const AdminSitesTableStatsDocument = `
    query AdminSitesTableStats {
  admin {
    allSites {
      view {
        stats {
          orgNames
          portfolios
          utilityNames
          cities
        }
      }
    }
  }
}
    `

export const useAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStats"]
        : ["AdminSitesTableStats", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats"]
    : ["AdminSitesTableStats", variables]

useAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useSuspenseAdminSitesTableStatsQuery = <
  TData = Types.AdminSitesTableStatsQuery,
  TError = unknown,
>(
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableStatsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSitesTableStatsSuspense"]
        : ["AdminSitesTableStatsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableStatsQuery,
      Types.AdminSitesTableStatsQueryVariables
    >(AdminSitesTableStatsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStatsSuspense"]
    : ["AdminSitesTableStatsSuspense", variables]

useSuspenseAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats"

export const useInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infinite"]
            : ["AdminSitesTableStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infinite"]
    : ["AdminSitesTableStats.infinite", variables]

useInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

export const useSuspenseInfiniteAdminSitesTableStatsQuery = <
  TData = InfiniteData<Types.AdminSitesTableStatsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSitesTableStats.infiniteSuspense"]
            : ["AdminSitesTableStats.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableStatsQuery,
            Types.AdminSitesTableStatsQueryVariables
          >(AdminSitesTableStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableStatsQuery.getKey = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
) =>
  variables === undefined
    ? ["AdminSitesTableStats.infiniteSuspense"]
    : ["AdminSitesTableStats.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableStatsQuery.rootKey = "AdminSitesTableStats.infinite"

useAdminSitesTableStatsQuery.fetcher = (
  variables?: Types.AdminSitesTableStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableStatsQuery,
    Types.AdminSitesTableStatsQueryVariables
  >(AdminSitesTableStatsDocument, variables, options)

export const AdminSitesTableRowsDocument = `
    query AdminSitesTableRows($keys: [Int!]!) {
  admin {
    allSites {
      rows(keys: $keys) {
        id
        orgName
        orgPid
        orgSlug
        portfolioPid
        portfolioName
        sitePid
        createdAt
        status
        statusReason
        statusCustomReason
        name
        customerId
        streetAddress
        city
        state
        zipCode
        roofMaxPvKw
        utilityId
        utilityShortName
        utilityName
        buildingFootprintSqft
        parcelFootprintSqft
        freeSpaceSqft
        buildingType
      }
    }
  }
}
    `

export const useAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows", variables]

useAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useSuspenseAdminSitesTableRowsQuery = <
  TData = Types.AdminSitesTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSitesTableRowsQuery, TError, TData>({
    queryKey: ["AdminSitesTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSitesTableRowsQuery,
      Types.AdminSitesTableRowsQueryVariables
    >(AdminSitesTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRowsSuspense", variables]

useSuspenseAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows"

export const useInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSitesTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infinite", variables]

useInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

export const useSuspenseInfiniteAdminSitesTableRowsQuery = <
  TData = InfiniteData<Types.AdminSitesTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSitesTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSitesTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSitesTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSitesTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminSitesTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSitesTableRowsQuery,
            Types.AdminSitesTableRowsQueryVariables
          >(AdminSitesTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSitesTableRowsQuery.getKey = (
  variables: Types.AdminSitesTableRowsQueryVariables,
) => ["AdminSitesTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminSitesTableRowsQuery.rootKey = "AdminSitesTableRows.infinite"

useAdminSitesTableRowsQuery.fetcher = (
  variables: Types.AdminSitesTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSitesTableRowsQuery,
    Types.AdminSitesTableRowsQueryVariables
  >(AdminSitesTableRowsDocument, variables, options)

export const AdminBidsTableViewDocument = `
    query AdminBidsTableView($filterBy: AdminBidsTableFilter, $sortBy: [AdminBidsTableSort!]) {
  admin {
    allBids {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          buildingOwnerNames
          financierNames
        }
      }
    }
  }
}
    `

export const useAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableView"]
        : ["AdminBidsTableView", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined ? ["AdminBidsTableView"] : ["AdminBidsTableView", variables]

useAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useSuspenseAdminBidsTableViewQuery = <
  TData = Types.AdminBidsTableViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBidsTableViewSuspense"]
        : ["AdminBidsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableViewQuery,
      Types.AdminBidsTableViewQueryVariables
    >(AdminBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableViewSuspense"]
    : ["AdminBidsTableViewSuspense", variables]

useSuspenseAdminBidsTableViewQuery.rootKey = "AdminBidsTableView"

export const useInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infinite"]
            : ["AdminBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infinite"]
    : ["AdminBidsTableView.infinite", variables]

useInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

export const useSuspenseInfiniteAdminBidsTableViewQuery = <
  TData = InfiniteData<Types.AdminBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBidsTableView.infiniteSuspense"]
            : ["AdminBidsTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableViewQuery,
            Types.AdminBidsTableViewQueryVariables
          >(AdminBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableViewQuery.getKey = (
  variables?: Types.AdminBidsTableViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBidsTableView.infiniteSuspense"]
    : ["AdminBidsTableView.infiniteSuspense", variables]

useSuspenseInfiniteAdminBidsTableViewQuery.rootKey = "AdminBidsTableView.infinite"

useAdminBidsTableViewQuery.fetcher = (
  variables?: Types.AdminBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminBidsTableViewQuery, Types.AdminBidsTableViewQueryVariables>(
    AdminBidsTableViewDocument,
    variables,
    options,
  )

export const AdminBidsTableRowsDocument = `
    query AdminBidsTableRows($keys: [Int!]!) {
  admin {
    allBids {
      rows(keys: $keys) {
        id
        bidPackagePid
        bundlePid
        bidPackageName
        name
        createdAt
        isAwarded
        isCustom
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        buildingOwnerPid
        buildingOwnerName
        financierPid
        financierName
        sitePid
        siteName
        streetAddress
        city
        state
        zipCode
        utilityId
        utilityName
        utilityShortName
        termYears
        roofRentY1Usd
        roofRentEscalator
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        buildingOwnerNoiLifetimeUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostUtilityMinUsd
        interconnectionCostNonUtilityUsd
        interconnectionCostUsdPerWatt
        interconnectionNotes
        itcPercentageAssumption
        recOwner
        incentivesNotes
        otherNotes
        finmodelId
      }
    }
  }
}
    `

export const useAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows", variables]

useAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useSuspenseAdminBidsTableRowsQuery = <
  TData = Types.AdminBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBidsTableRowsQuery, TError, TData>({
    queryKey: ["AdminBidsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBidsTableRowsQuery,
      Types.AdminBidsTableRowsQueryVariables
    >(AdminBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRowsSuspense", variables]

useSuspenseAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows"

export const useInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infinite", variables]

useInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

export const useSuspenseInfiniteAdminBidsTableRowsQuery = <
  TData = InfiniteData<Types.AdminBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBidsTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBidsTableRowsQuery,
            Types.AdminBidsTableRowsQueryVariables
          >(AdminBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBidsTableRowsQuery.getKey = (
  variables: Types.AdminBidsTableRowsQueryVariables,
) => ["AdminBidsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminBidsTableRowsQuery.rootKey = "AdminBidsTableRows.infinite"

useAdminBidsTableRowsQuery.fetcher = (
  variables: Types.AdminBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminBidsTableRowsQuery, Types.AdminBidsTableRowsQueryVariables>(
    AdminBidsTableRowsDocument,
    variables,
    options,
  )

export const AdminPortfolioListDocument = `
    query AdminPortfolioList {
  admin {
    portfolios {
      pid
      title
      numSites
      org {
        pid
        name
      }
    }
  }
}
    `

export const useAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioList"]
        : ["AdminPortfolioList", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined ? ["AdminPortfolioList"] : ["AdminPortfolioList", variables]

useAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useSuspenseAdminPortfolioListQuery = <
  TData = Types.AdminPortfolioListQuery,
  TError = unknown,
>(
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminPortfolioListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminPortfolioListSuspense"]
        : ["AdminPortfolioListSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioListQuery,
      Types.AdminPortfolioListQueryVariables
    >(AdminPortfolioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioListSuspense"]
    : ["AdminPortfolioListSuspense", variables]

useSuspenseAdminPortfolioListQuery.rootKey = "AdminPortfolioList"

export const useInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infinite"]
            : ["AdminPortfolioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infinite"]
    : ["AdminPortfolioList.infinite", variables]

useInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

export const useSuspenseInfiniteAdminPortfolioListQuery = <
  TData = InfiniteData<Types.AdminPortfolioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminPortfolioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminPortfolioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminPortfolioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminPortfolioList.infiniteSuspense"]
            : ["AdminPortfolioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioListQuery,
            Types.AdminPortfolioListQueryVariables
          >(AdminPortfolioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminPortfolioListQuery.getKey = (
  variables?: Types.AdminPortfolioListQueryVariables,
) =>
  variables === undefined
    ? ["AdminPortfolioList.infiniteSuspense"]
    : ["AdminPortfolioList.infiniteSuspense", variables]

useSuspenseInfiniteAdminPortfolioListQuery.rootKey = "AdminPortfolioList.infinite"

useAdminPortfolioListQuery.fetcher = (
  variables?: Types.AdminPortfolioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminPortfolioListQuery, Types.AdminPortfolioListQueryVariables>(
    AdminPortfolioListDocument,
    variables,
    options,
  )

export const AdminOrgBySlugDocument = `
    query AdminOrgBySlug($slug: String, $pid: String) {
  admin {
    orgBySlug(slug: $slug, pid: $pid) {
      pid
      slug
    }
  }
}
    `

export const useAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables],
    queryFn: publicFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useAdminOrgBySlugQuery.getKey = (variables?: Types.AdminOrgBySlugQueryVariables) =>
  variables === undefined ? ["AdminOrgBySlug"] : ["AdminOrgBySlug", variables]

useAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useSuspenseAdminOrgBySlugQuery = <
  TData = Types.AdminOrgBySlugQuery,
  TError = unknown,
>(
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminOrgBySlugSuspense"]
        : ["AdminOrgBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminOrgBySlugQuery,
      Types.AdminOrgBySlugQueryVariables
    >(AdminOrgBySlugDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlugSuspense"]
    : ["AdminOrgBySlugSuspense", variables]

useSuspenseAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug"

export const useInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infinite"]
            : ["AdminOrgBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infinite"]
    : ["AdminOrgBySlug.infinite", variables]

useInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

export const useSuspenseInfiniteAdminOrgBySlugQuery = <
  TData = InfiniteData<Types.AdminOrgBySlugQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminOrgBySlug.infiniteSuspense"]
            : ["AdminOrgBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
            AdminOrgBySlugDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgBySlugQuery.getKey = (
  variables?: Types.AdminOrgBySlugQueryVariables,
) =>
  variables === undefined
    ? ["AdminOrgBySlug.infiniteSuspense"]
    : ["AdminOrgBySlug.infiniteSuspense", variables]

useSuspenseInfiniteAdminOrgBySlugQuery.rootKey = "AdminOrgBySlug.infinite"

useAdminOrgBySlugQuery.fetcher = (
  variables?: Types.AdminOrgBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgBySlugQuery, Types.AdminOrgBySlugQueryVariables>(
    AdminOrgBySlugDocument,
    variables,
    options,
  )

export const OrgsAndPortfoliosDocument = `
    query OrgsAndPortfolios {
  admin {
    orgs {
      pid
      name
    }
    portfolios {
      pid
      title
      numSites
      org {
        pid
      }
    }
  }
}
    `

export const useOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfolios"]
        : ["OrgsAndPortfolios", variables],
    queryFn: publicFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined ? ["OrgsAndPortfolios"] : ["OrgsAndPortfolios", variables]

useOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useSuspenseOrgsAndPortfoliosQuery = <
  TData = Types.OrgsAndPortfoliosQuery,
  TError = unknown,
>(
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsAndPortfoliosQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsAndPortfoliosSuspense"]
        : ["OrgsAndPortfoliosSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgsAndPortfoliosQuery,
      Types.OrgsAndPortfoliosQueryVariables
    >(OrgsAndPortfoliosDocument, variables),
    ...options,
  })
}

useSuspenseOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfoliosSuspense"]
    : ["OrgsAndPortfoliosSuspense", variables]

useSuspenseOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios"

export const useInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infinite"]
            : ["OrgsAndPortfolios.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infinite"]
    : ["OrgsAndPortfolios.infinite", variables]

useInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

export const useSuspenseInfiniteOrgsAndPortfoliosQuery = <
  TData = InfiniteData<Types.OrgsAndPortfoliosQuery>,
  TError = unknown,
>(
  variables: Types.OrgsAndPortfoliosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsAndPortfoliosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsAndPortfoliosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsAndPortfoliosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsAndPortfolios.infiniteSuspense"]
            : ["OrgsAndPortfolios.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgsAndPortfoliosQuery,
            Types.OrgsAndPortfoliosQueryVariables
          >(OrgsAndPortfoliosDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsAndPortfoliosQuery.getKey = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
) =>
  variables === undefined
    ? ["OrgsAndPortfolios.infiniteSuspense"]
    : ["OrgsAndPortfolios.infiniteSuspense", variables]

useSuspenseInfiniteOrgsAndPortfoliosQuery.rootKey = "OrgsAndPortfolios.infinite"

useOrgsAndPortfoliosQuery.fetcher = (
  variables?: Types.OrgsAndPortfoliosQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsAndPortfoliosQuery, Types.OrgsAndPortfoliosQueryVariables>(
    OrgsAndPortfoliosDocument,
    variables,
    options,
  )

export const GetEnergyProductionDocument = `
    query GetEnergyProduction {
  admin {
    energy {
      sites {
        name
        daysActive
        kwhLifetime
      }
      kwhPerDay {
        day
        kwh
      }
      currentTime
      kwhAllTime
      kwhToday
      projectedNextInterval
    }
  }
}
    `

export const useGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProduction"]
        : ["GetEnergyProduction", variables],
    queryFn: publicFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined ? ["GetEnergyProduction"] : ["GetEnergyProduction", variables]

useGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useSuspenseGetEnergyProductionQuery = <
  TData = Types.GetEnergyProductionQuery,
  TError = unknown,
>(
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEnergyProductionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetEnergyProductionSuspense"]
        : ["GetEnergyProductionSuspense", variables],
    queryFn: publicFetcher<
      Types.GetEnergyProductionQuery,
      Types.GetEnergyProductionQueryVariables
    >(GetEnergyProductionDocument, variables),
    ...options,
  })
}

useSuspenseGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProductionSuspense"]
    : ["GetEnergyProductionSuspense", variables]

useSuspenseGetEnergyProductionQuery.rootKey = "GetEnergyProduction"

export const useInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infinite"]
            : ["GetEnergyProduction.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infinite"]
    : ["GetEnergyProduction.infinite", variables]

useInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

export const useSuspenseInfiniteGetEnergyProductionQuery = <
  TData = InfiniteData<Types.GetEnergyProductionQuery>,
  TError = unknown,
>(
  variables: Types.GetEnergyProductionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEnergyProductionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEnergyProductionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEnergyProductionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetEnergyProduction.infiniteSuspense"]
            : ["GetEnergyProduction.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetEnergyProductionQuery,
            Types.GetEnergyProductionQueryVariables
          >(GetEnergyProductionDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEnergyProductionQuery.getKey = (
  variables?: Types.GetEnergyProductionQueryVariables,
) =>
  variables === undefined
    ? ["GetEnergyProduction.infiniteSuspense"]
    : ["GetEnergyProduction.infiniteSuspense", variables]

useSuspenseInfiniteGetEnergyProductionQuery.rootKey = "GetEnergyProduction.infinite"

useGetEnergyProductionQuery.fetcher = (
  variables?: Types.GetEnergyProductionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetEnergyProductionQuery,
    Types.GetEnergyProductionQueryVariables
  >(GetEnergyProductionDocument, variables, options)

export const MultiPortfolioSitesDocument = `
    query MultiPortfolioSites($portfolioPids: [String!]!, $statuses: [SiteStatus!]!) {
  admin {
    sitesInPortfolios(portfolioPids: $portfolioPids, statuses: $statuses) {
      ...SitesTable
      portfolio {
        pid
        title
      }
    }
  }
}
    ${SitesTableFragmentDoc}`

export const useMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSites", variables],
    queryFn: publicFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites", variables]

useMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useSuspenseMultiPortfolioSitesQuery = <
  TData = Types.MultiPortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiPortfolioSitesQuery, TError, TData>({
    queryKey: ["MultiPortfolioSitesSuspense", variables],
    queryFn: publicFetcher<
      Types.MultiPortfolioSitesQuery,
      Types.MultiPortfolioSitesQueryVariables
    >(MultiPortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspenseMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSitesSuspense", variables]

useSuspenseMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites"

export const useInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiPortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infinite", variables]

useInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

export const useSuspenseInfiniteMultiPortfolioSitesQuery = <
  TData = InfiniteData<Types.MultiPortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.MultiPortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiPortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiPortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiPortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MultiPortfolioSites.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiPortfolioSitesQuery,
            Types.MultiPortfolioSitesQueryVariables
          >(MultiPortfolioSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiPortfolioSitesQuery.getKey = (
  variables: Types.MultiPortfolioSitesQueryVariables,
) => ["MultiPortfolioSites.infiniteSuspense", variables]

useSuspenseInfiniteMultiPortfolioSitesQuery.rootKey = "MultiPortfolioSites.infinite"

useMultiPortfolioSitesQuery.fetcher = (
  variables: Types.MultiPortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MultiPortfolioSitesQuery,
    Types.MultiPortfolioSitesQueryVariables
  >(MultiPortfolioSitesDocument, variables, options)

export const PortfolioSitesDocument = `
    query PortfolioSites($pid: String!, $sitePids: [String!]!) {
  admin {
    portfolio(pid: $pid) {
      pid
      sites(sitePids: $sitePids) {
        ...SitesTable
      }
    }
  }
}
    ${SitesTableFragmentDoc}`

export const usePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSites", variables],
    queryFn: publicFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

usePortfolioSitesQuery.getKey = (variables: Types.PortfolioSitesQueryVariables) => [
  "PortfolioSites",
  variables,
]

usePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useSuspensePortfolioSitesQuery = <
  TData = Types.PortfolioSitesQuery,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioSitesQuery, TError, TData>({
    queryKey: ["PortfolioSitesSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioSitesQuery,
      Types.PortfolioSitesQueryVariables
    >(PortfolioSitesDocument, variables),
    ...options,
  })
}

useSuspensePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSitesSuspense", variables]

useSuspensePortfolioSitesQuery.rootKey = "PortfolioSites"

export const useInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infinite", variables]

useInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

export const useSuspenseInfinitePortfolioSitesQuery = <
  TData = InfiniteData<Types.PortfolioSitesQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioSites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
            PortfolioSitesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioSitesQuery.getKey = (
  variables: Types.PortfolioSitesQueryVariables,
) => ["PortfolioSites.infiniteSuspense", variables]

useSuspenseInfinitePortfolioSitesQuery.rootKey = "PortfolioSites.infinite"

usePortfolioSitesQuery.fetcher = (
  variables: Types.PortfolioSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioSitesQuery, Types.PortfolioSitesQueryVariables>(
    PortfolioSitesDocument,
    variables,
    options,
  )

export const SitesDocument = `
    query Sites($sitePids: [String!]!) {
  admin {
    sites(sitePids: $sitePids) {
      ...SitesTable
    }
  }
}
    ${SitesTableFragmentDoc}`

export const useSitesQuery = <TData = Types.SitesQuery, TError = unknown>(
  variables: Types.SitesQueryVariables,
  options?: Omit<UseQueryOptions<Types.SitesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SitesQuery, TError, TData>({
    queryKey: ["Sites", variables],
    queryFn: publicFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
      SitesDocument,
      variables,
    ),
    ...options,
  })
}

useSitesQuery.getKey = (variables: Types.SitesQueryVariables) => ["Sites", variables]

useSitesQuery.rootKey = "Sites"

export const useSuspenseSitesQuery = <TData = Types.SitesQuery, TError = unknown>(
  variables: Types.SitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitesQuery, TError, TData>({
    queryKey: ["SitesSuspense", variables],
    queryFn: publicFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
      SitesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "SitesSuspense",
  variables,
]

useSuspenseSitesQuery.rootKey = "Sites"

export const useInfiniteSitesQuery = <
  TData = InfiniteData<Types.SitesQuery>,
  TError = unknown,
>(
  variables: Types.SitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Sites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitesQuery, Types.SitesQueryVariables>(SitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "Sites.infinite",
  variables,
]

useInfiniteSitesQuery.rootKey = "Sites.infinite"

export const useSuspenseInfiniteSitesQuery = <
  TData = InfiniteData<Types.SitesQuery>,
  TError = unknown,
>(
  variables: Types.SitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Sites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitesQuery, Types.SitesQueryVariables>(SitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitesQuery.getKey = (variables: Types.SitesQueryVariables) => [
  "Sites.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSitesQuery.rootKey = "Sites.infinite"

useSitesQuery.fetcher = (
  variables: Types.SitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SitesQuery, Types.SitesQueryVariables>(
    SitesDocument,
    variables,
    options,
  )

export const GetPortfolioSiteRefsDocument = `
    query GetPortfolioSiteRefs($pid: String!) {
  admin {
    portfolio(pid: $pid) {
      pid
      siteRefs {
        pid
        name
        streetAddress
      }
    }
  }
}
    `

export const useGetPortfolioSiteRefsQuery = <
  TData = Types.GetPortfolioSiteRefsQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>({
    queryKey: ["GetPortfolioSiteRefs", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioSiteRefsQuery,
      Types.GetPortfolioSiteRefsQueryVariables
    >(GetPortfolioSiteRefsDocument, variables),
    ...options,
  })
}

useGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs", variables]

useGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs"

export const useSuspenseGetPortfolioSiteRefsQuery = <
  TData = Types.GetPortfolioSiteRefsQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>({
    queryKey: ["GetPortfolioSiteRefsSuspense", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioSiteRefsQuery,
      Types.GetPortfolioSiteRefsQueryVariables
    >(GetPortfolioSiteRefsDocument, variables),
    ...options,
  })
}

useSuspenseGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefsSuspense", variables]

useSuspenseGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs"

export const useInfiniteGetPortfolioSiteRefsQuery = <
  TData = InfiniteData<Types.GetPortfolioSiteRefsQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioSiteRefs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioSiteRefsQuery,
            Types.GetPortfolioSiteRefsQueryVariables
          >(GetPortfolioSiteRefsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs.infinite", variables]

useInfiniteGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs.infinite"

export const useSuspenseInfiniteGetPortfolioSiteRefsQuery = <
  TData = InfiniteData<Types.GetPortfolioSiteRefsQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetPortfolioSiteRefsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetPortfolioSiteRefsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetPortfolioSiteRefsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetPortfolioSiteRefs.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioSiteRefsQuery,
            Types.GetPortfolioSiteRefsQueryVariables
          >(GetPortfolioSiteRefsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetPortfolioSiteRefsQuery.getKey = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
) => ["GetPortfolioSiteRefs.infiniteSuspense", variables]

useSuspenseInfiniteGetPortfolioSiteRefsQuery.rootKey = "GetPortfolioSiteRefs.infinite"

useGetPortfolioSiteRefsQuery.fetcher = (
  variables: Types.GetPortfolioSiteRefsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetPortfolioSiteRefsQuery,
    Types.GetPortfolioSiteRefsQueryVariables
  >(GetPortfolioSiteRefsDocument, variables, options)

export const SiteBuildingDataDocument = `
    query SiteBuildingData($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      customerId
      assetManager
      propertyName
      capRate
      buildingClass
      streetAddress
      city
      state
      zipCode
      county
      latitude
      longitude
      sqft
      yearBuilt
      numStories
      numParkingSpaces
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
      occupancyType
      leaseType
      metering
      meteringSetBy {
        ...UserRef
      }
      meteringSetDate
      buildingType
      costarPropertyType
      costarSecondaryType
      mappedBuildingType
      buildingClass
      leaseType
      occupancyType
      mortgageLender
      roofReplacedYr
      roofCondition
      cbsaId
      cbsaName
      timeZone
      roofSqft
      roofMaxPvKw
      carportMaxPvKw
      groundMaxPvKw
      computedRoofMaxPvKw
      computedCarportMaxPvKw
      computedGroundMaxPvKw
      buildingData {
        id
        affordableType
        source
        sourceId
        streetAddress
        city
        state
        zipCode
        county
        latitude
        longitude
        sqft
        totalBuildings
        numStories
        numParkingSpaces
        costarPropertyType
        costarSecondaryType
        buildingType
        yearBuilt
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
        yearRenovated
        parentCompany
        buildingClass
        buildingOpex
        energyStar
        leedCertified
        lastSalePrice
        electricPowerRating
        zoning
        propertyName
        capRate
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
        numVacantUnitsStudio
        numVacantUnits1Bd
        numVacantUnits2Bd
        numVacantUnits3Bd
        numVacantUnits4Bd
        avgSqftStudio
        avgSqft1Bd
        avgSqft2Bd
        avgSqft3Bd
        avgSqft4Bd
        numElevators
        taxIdPrintFormat
        parcelNumberMin
        parcelNumberMax
        rentType
        reportedOwnerName
        reportedContactName
        reportedOwnerAddress
        reportedOwnerCity
        reportedOwnerState
        reportedOwnerZipCode
        reportedOwnerPhone
        ownerName
        ownerFirstName
        ownerLastName
        contactAddress
        contactCity
        contactState
        contactZipCode
        contactPhone
        buildingClass
        tenantContacts {
          ...ReonomyContact
        }
        ownerContacts {
          ...ReonomyContact
        }
      }
    }
  }
}
    ${UserRefFragmentDoc}
${ReonomyContactFragmentDoc}`

export const useSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSiteBuildingDataQuery.getKey = (variables: Types.SiteBuildingDataQueryVariables) => [
  "SiteBuildingData",
  variables,
]

useSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useSuspenseSiteBuildingDataQuery = <
  TData = Types.SiteBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBuildingDataQuery, TError, TData>({
    queryKey: ["SiteBuildingDataSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteBuildingDataQuery,
      Types.SiteBuildingDataQueryVariables
    >(SiteBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingDataSuspense", variables]

useSuspenseSiteBuildingDataQuery.rootKey = "SiteBuildingData"

export const useInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infinite", variables]

useInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

export const useSuspenseInfiniteSiteBuildingDataQuery = <
  TData = InfiniteData<Types.SiteBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBuildingData.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBuildingDataQuery,
            Types.SiteBuildingDataQueryVariables
          >(SiteBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBuildingDataQuery.getKey = (
  variables: Types.SiteBuildingDataQueryVariables,
) => ["SiteBuildingData.infiniteSuspense", variables]

useSuspenseInfiniteSiteBuildingDataQuery.rootKey = "SiteBuildingData.infinite"

useSiteBuildingDataQuery.fetcher = (
  variables: Types.SiteBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBuildingDataQuery, Types.SiteBuildingDataQueryVariables>(
    SiteBuildingDataDocument,
    variables,
    options,
  )

export const SitePossibleTariffsDocument = `
    query SitePossibleTariffs($pid: String!, $loadCategory: LoadCategory, $preferred: Boolean! = true, $includeUnpreferred: Boolean! = false, $includeCustom: Boolean! = false) {
  admin {
    site(pid: $pid) {
      pid
      possibleTariffs(
        loadCategory: $loadCategory
        preferred: $preferred
        includeUnpreferred: $includeUnpreferred
        includeCustom: $includeCustom
      ) {
        id
        name
        code
        tariffType
        customerClass
        customerCount
        customerLikelihood
        masterTariffId
        isPreferred
        hasNetMetering
        requiresPv
        requiresStorage
        minMonthlyKw
        maxMonthlyKw
        minMonthlyKwh
        maxMonthlyKwh
      }
    }
  }
}
    `

export const useSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffs", variables],
    queryFn: publicFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs", variables]

useSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useSuspenseSitePossibleTariffsQuery = <
  TData = Types.SitePossibleTariffsQuery,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePossibleTariffsQuery, TError, TData>({
    queryKey: ["SitePossibleTariffsSuspense", variables],
    queryFn: publicFetcher<
      Types.SitePossibleTariffsQuery,
      Types.SitePossibleTariffsQueryVariables
    >(SitePossibleTariffsDocument, variables),
    ...options,
  })
}

useSuspenseSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffsSuspense", variables]

useSuspenseSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs"

export const useInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePossibleTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infinite", variables]

useInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

export const useSuspenseInfiniteSitePossibleTariffsQuery = <
  TData = InfiniteData<Types.SitePossibleTariffsQuery>,
  TError = unknown,
>(
  variables: Types.SitePossibleTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePossibleTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePossibleTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePossibleTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SitePossibleTariffs.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePossibleTariffsQuery,
            Types.SitePossibleTariffsQueryVariables
          >(SitePossibleTariffsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePossibleTariffsQuery.getKey = (
  variables: Types.SitePossibleTariffsQueryVariables,
) => ["SitePossibleTariffs.infiniteSuspense", variables]

useSuspenseInfiniteSitePossibleTariffsQuery.rootKey = "SitePossibleTariffs.infinite"

useSitePossibleTariffsQuery.fetcher = (
  variables: Types.SitePossibleTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SitePossibleTariffsQuery,
    Types.SitePossibleTariffsQueryVariables
  >(SitePossibleTariffsDocument, variables, options)

export const GetSiteAdminDocument = `
    query GetSiteAdmin($pid: String!) {
  admin {
    site(pid: $pid) {
      id
      pid
      fullAddress
      hiddenFromCustomers
      hideFakePanels
      portfolio {
        pid
        title
        org {
          pid
          slug
        }
      }
      buildingData {
        id
        reonomyPropertyLink
      }
      primaryScenario {
        pid
        name
      }
      currentStatus {
        pid
        displayText
        dateCreated
        createdBy {
          ...UserRef
        }
      }
      footprint {
        ...Footprint
        parcelShape
        parcelShapeGeojson
        parcelShapeUpdatedAt
        parcelShapeUpdatedBy {
          ...UserRef
        }
      }
      buildingFootprintNeedsReview
      physicalCheck {
        existingSolar
        badRoof
      }
      currentPolicyChecks {
        productType
        isGood
        disqualReason
        violatedPolicy {
          pid
          name
        }
      }
      roofSqft
      roofMaxPvKw
      computedRoofMaxPvKw
      groundMaxPvKw
      carportMaxPvKw
      parcelFootprintSqft
      buildingFootprintSqft
      buildingFootprintCount
      roofObstructionsSqft
      roofUsableSqft
      roofImage {
        id
        name
      }
      attachments {
        ...AttachmentFragment
      }
      duplicateSites {
        pid
        displayName
        portfolio {
          pid
          title
          org {
            pid
            slug
            name
          }
        }
      }
    }
  }
}
    ${UserRefFragmentDoc}
${FootprintFragmentDoc}
${AttachmentFragmentFragmentDoc}`

export const useGetSiteAdminQuery = <TData = Types.GetSiteAdminQuery, TError = unknown>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteAdminQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdmin", variables],
    queryFn: publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin",
  variables,
]

useGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useSuspenseGetSiteAdminQuery = <
  TData = Types.GetSiteAdminQuery,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteAdminQuery, TError, TData>({
    queryKey: ["GetSiteAdminSuspense", variables],
    queryFn: publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
      GetSiteAdminDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdminSuspense",
  variables,
]

useSuspenseGetSiteAdminQuery.rootKey = "GetSiteAdmin"

export const useInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteAdminQuery.getKey = (variables: Types.GetSiteAdminQueryVariables) => [
  "GetSiteAdmin.infinite",
  variables,
]

useInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

export const useSuspenseInfiniteGetSiteAdminQuery = <
  TData = InfiniteData<Types.GetSiteAdminQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteAdminQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteAdminQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteAdminQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteAdminQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteAdmin.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
            GetSiteAdminDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteAdminQuery.getKey = (
  variables: Types.GetSiteAdminQueryVariables,
) => ["GetSiteAdmin.infiniteSuspense", variables]

useSuspenseInfiniteGetSiteAdminQuery.rootKey = "GetSiteAdmin.infinite"

useGetSiteAdminQuery.fetcher = (
  variables: Types.GetSiteAdminQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetSiteAdminQuery, Types.GetSiteAdminQueryVariables>(
    GetSiteAdminDocument,
    variables,
    options,
  )

export const UserListDocument = `
    query UserList {
  admin {
    users {
      ...UserListUser
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UserListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UserListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UserList"] : ["UserList", variables],
    queryFn: publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList"] : ["UserList", variables]

useUserListQuery.rootKey = "UserList"

export const useSuspenseUserListQuery = <TData = Types.UserListQuery, TError = unknown>(
  variables?: Types.UserListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UserListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["UserListSuspense"] : ["UserListSuspense", variables],
    queryFn: publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
      UserListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserListSuspense"] : ["UserListSuspense", variables]

useSuspenseUserListQuery.rootKey = "UserList"

export const useInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.UserListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infinite"]
            : ["UserList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined ? ["UserList.infinite"] : ["UserList.infinite", variables]

useInfiniteUserListQuery.rootKey = "UserList.infinite"

export const useSuspenseInfiniteUserListQuery = <
  TData = InfiniteData<Types.UserListQuery>,
  TError = unknown,
>(
  variables: Types.UserListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UserListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UserListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UserListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UserList.infiniteSuspense"]
            : ["UserList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
            UserListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUserListQuery.getKey = (variables?: Types.UserListQueryVariables) =>
  variables === undefined
    ? ["UserList.infiniteSuspense"]
    : ["UserList.infiniteSuspense", variables]

useSuspenseInfiniteUserListQuery.rootKey = "UserList.infinite"

useUserListQuery.fetcher = (
  variables?: Types.UserListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UserListQuery, Types.UserListQueryVariables>(
    UserListDocument,
    variables,
    options,
  )

export const PortfolioDetailDocument = `
    query PortfolioDetail($pid: String!) {
  admin {
    portfolio(pid: $pid) {
      pid
      title
      logoUrl
      minKw
      minKwSetBy
      minKwSetDate
      view {
        sitePids
      }
      org {
        pid
        name
        slug
      }
    }
  }
}
    `

export const usePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetail", variables],
    queryFn: publicFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

usePortfolioDetailQuery.getKey = (variables: Types.PortfolioDetailQueryVariables) => [
  "PortfolioDetail",
  variables,
]

usePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useSuspensePortfolioDetailQuery = <
  TData = Types.PortfolioDetailQuery,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioDetailQuery, TError, TData>({
    queryKey: ["PortfolioDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioDetailQuery,
      Types.PortfolioDetailQueryVariables
    >(PortfolioDetailDocument, variables),
    ...options,
  })
}

useSuspensePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetailSuspense", variables]

useSuspensePortfolioDetailQuery.rootKey = "PortfolioDetail"

export const useInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioDetailQuery,
            Types.PortfolioDetailQueryVariables
          >(PortfolioDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infinite", variables]

useInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

export const useSuspenseInfinitePortfolioDetailQuery = <
  TData = InfiniteData<Types.PortfolioDetailQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioDetailQuery,
            Types.PortfolioDetailQueryVariables
          >(PortfolioDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioDetailQuery.getKey = (
  variables: Types.PortfolioDetailQueryVariables,
) => ["PortfolioDetail.infiniteSuspense", variables]

useSuspenseInfinitePortfolioDetailQuery.rootKey = "PortfolioDetail.infinite"

usePortfolioDetailQuery.fetcher = (
  variables: Types.PortfolioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioDetailQuery, Types.PortfolioDetailQueryVariables>(
    PortfolioDetailDocument,
    variables,
    options,
  )

export const UtilityListDocument = `
    query UtilityList($zipCode: String) {
  admin {
    tariffs {
      utilities(zipCode: $zipCode) {
        ...UtilityFragment
      }
    }
  }
}
    ${UtilityFragmentFragmentDoc}`

export const useUtilityListQuery = <TData = Types.UtilityListQuery, TError = unknown>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<UseQueryOptions<Types.UtilityListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.UtilityListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityListQuery, TError, TData>({
    queryKey: variables === undefined ? ["UtilityList"] : ["UtilityList", variables],
    queryFn: publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityList"] : ["UtilityList", variables]

useUtilityListQuery.rootKey = "UtilityList"

export const useSuspenseUtilityListQuery = <
  TData = Types.UtilityListQuery,
  TError = unknown,
>(
  variables?: Types.UtilityListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["UtilityListSuspense"]
        : ["UtilityListSuspense", variables],
    queryFn: publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
      UtilityListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined ? ["UtilityListSuspense"] : ["UtilityListSuspense", variables]

useSuspenseUtilityListQuery.rootKey = "UtilityList"

export const useInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infinite"]
            : ["UtilityList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityListQuery.getKey = (variables?: Types.UtilityListQueryVariables) =>
  variables === undefined
    ? ["UtilityList.infinite"]
    : ["UtilityList.infinite", variables]

useInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

export const useSuspenseInfiniteUtilityListQuery = <
  TData = InfiniteData<Types.UtilityListQuery>,
  TError = unknown,
>(
  variables: Types.UtilityListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["UtilityList.infiniteSuspense"]
            : ["UtilityList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
            UtilityListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityListQuery.getKey = (
  variables?: Types.UtilityListQueryVariables,
) =>
  variables === undefined
    ? ["UtilityList.infiniteSuspense"]
    : ["UtilityList.infiniteSuspense", variables]

useSuspenseInfiniteUtilityListQuery.rootKey = "UtilityList.infinite"

useUtilityListQuery.fetcher = (
  variables?: Types.UtilityListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityListQuery, Types.UtilityListQueryVariables>(
    UtilityListDocument,
    variables,
    options,
  )

export const UtilityDetailDocument = `
    query UtilityDetail($id: Int!) {
  admin {
    tariffs {
      utility(id: $id) {
        ...UtilityFragment
      }
    }
  }
}
    ${UtilityFragmentFragmentDoc}`

export const useUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetail", variables],
    queryFn: publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useUtilityDetailQuery.getKey = (variables: Types.UtilityDetailQueryVariables) => [
  "UtilityDetail",
  variables,
]

useUtilityDetailQuery.rootKey = "UtilityDetail"

export const useSuspenseUtilityDetailQuery = <
  TData = Types.UtilityDetailQuery,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityDetailQuery, TError, TData>({
    queryKey: ["UtilityDetailSuspense", variables],
    queryFn: publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
      UtilityDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetailSuspense", variables]

useSuspenseUtilityDetailQuery.rootKey = "UtilityDetail"

export const useInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infinite", variables]

useInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

export const useSuspenseInfiniteUtilityDetailQuery = <
  TData = InfiniteData<Types.UtilityDetailQuery>,
  TError = unknown,
>(
  variables: Types.UtilityDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
            UtilityDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityDetailQuery.getKey = (
  variables: Types.UtilityDetailQueryVariables,
) => ["UtilityDetail.infiniteSuspense", variables]

useSuspenseInfiniteUtilityDetailQuery.rootKey = "UtilityDetail.infinite"

useUtilityDetailQuery.fetcher = (
  variables: Types.UtilityDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityDetailQuery, Types.UtilityDetailQueryVariables>(
    UtilityDetailDocument,
    variables,
    options,
  )

export const TariffListDocument = `
    query TariffList($id: Int!) {
  admin {
    tariffs {
      utility(id: $id) {
        id
        shortName
        name
        tariffs {
          id
          masterTariffId
          code
          name
          tariffType
          customerClass
          isPreferred
          requiresPv
          requiresStorage
          customerLikelihood
          hasNetMetering
          hasTouRates
          minMonthlyKwh
          maxMonthlyKwh
          minMonthlyKw
          maxMonthlyKw
        }
      }
    }
  }
}
    `

export const useTariffListQuery = <TData = Types.TariffListQuery, TError = unknown>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<UseQueryOptions<Types.TariffListQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffList", variables],
    queryFn: publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList",
  variables,
]

useTariffListQuery.rootKey = "TariffList"

export const useSuspenseTariffListQuery = <
  TData = Types.TariffListQuery,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffListQuery, TError, TData>({
    queryKey: ["TariffListSuspense", variables],
    queryFn: publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
      TariffListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffListSuspense",
  variables,
]

useSuspenseTariffListQuery.rootKey = "TariffList"

export const useInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffListQuery.getKey = (variables: Types.TariffListQueryVariables) => [
  "TariffList.infinite",
  variables,
]

useInfiniteTariffListQuery.rootKey = "TariffList.infinite"

export const useSuspenseInfiniteTariffListQuery = <
  TData = InfiniteData<Types.TariffListQuery>,
  TError = unknown,
>(
  variables: Types.TariffListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
            TariffListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffListQuery.getKey = (
  variables: Types.TariffListQueryVariables,
) => ["TariffList.infiniteSuspense", variables]

useSuspenseInfiniteTariffListQuery.rootKey = "TariffList.infinite"

useTariffListQuery.fetcher = (
  variables: Types.TariffListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffListQuery, Types.TariffListQueryVariables>(
    TariffListDocument,
    variables,
    options,
  )

export const CustomTariffListDocument = `
    query CustomTariffList($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      displayName
      targetUtility {
        id
        name
      }
      customTariffs {
        id
        dateCreated
        name
        code
        customerClass
        parent {
          id
        }
        utility {
          id
          name
        }
        createdBy {
          pid
          email
        }
        archivedAt
      }
    }
  }
}
    `

export const useCustomTariffListQuery = <
  TData = Types.CustomTariffListQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffListQuery, TError, TData>({
    queryKey: ["CustomTariffList", variables],
    queryFn: publicFetcher<
      Types.CustomTariffListQuery,
      Types.CustomTariffListQueryVariables
    >(CustomTariffListDocument, variables),
    ...options,
  })
}

useCustomTariffListQuery.getKey = (variables: Types.CustomTariffListQueryVariables) => [
  "CustomTariffList",
  variables,
]

useCustomTariffListQuery.rootKey = "CustomTariffList"

export const useSuspenseCustomTariffListQuery = <
  TData = Types.CustomTariffListQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffListQuery, TError, TData>({
    queryKey: ["CustomTariffListSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffListQuery,
      Types.CustomTariffListQueryVariables
    >(CustomTariffListDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffListSuspense", variables]

useSuspenseCustomTariffListQuery.rootKey = "CustomTariffList"

export const useInfiniteCustomTariffListQuery = <
  TData = InfiniteData<Types.CustomTariffListQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffListQuery,
            Types.CustomTariffListQueryVariables
          >(CustomTariffListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffList.infinite", variables]

useInfiniteCustomTariffListQuery.rootKey = "CustomTariffList.infinite"

export const useSuspenseInfiniteCustomTariffListQuery = <
  TData = InfiniteData<Types.CustomTariffListQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffListQuery,
            Types.CustomTariffListQueryVariables
          >(CustomTariffListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffListQuery.getKey = (
  variables: Types.CustomTariffListQueryVariables,
) => ["CustomTariffList.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffListQuery.rootKey = "CustomTariffList.infinite"

useCustomTariffListQuery.fetcher = (
  variables: Types.CustomTariffListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffListQuery, Types.CustomTariffListQueryVariables>(
    CustomTariffListDocument,
    variables,
    options,
  )

export const TariffDetailDocument = `
    query TariffDetail($tariffId: Int!) {
  admin {
    tariffs {
      tariff(id: $tariffId) {
        id
        masterTariffId
        code
        name
        tariffType
        customerClass
        customerCount
        customerLikelihood
        minMonthlyKwh
        maxMonthlyKwh
        minMonthlyKw
        maxMonthlyKw
        hasTouRates
        hasNetMetering
        isPreferred
        notes
        requiresPv
        requiresStorage
        utility {
          id
          name
        }
        rateDetails {
          ...TariffDetailFragment
        }
        history {
          dateLastUpdated
          versions {
            isDownloaded
            tariffId
            effectiveDate
            endDate
          }
        }
      }
    }
  }
}
    ${TariffDetailFragmentFragmentDoc}`

export const useTariffDetailQuery = <TData = Types.TariffDetailQuery, TError = unknown>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetail", variables],
    queryFn: publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail",
  variables,
]

useTariffDetailQuery.rootKey = "TariffDetail"

export const useSuspenseTariffDetailQuery = <
  TData = Types.TariffDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffDetailQuery, TError, TData>({
    queryKey: ["TariffDetailSuspense", variables],
    queryFn: publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
      TariffDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetailSuspense",
  variables,
]

useSuspenseTariffDetailQuery.rootKey = "TariffDetail"

export const useInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffDetailQuery.getKey = (variables: Types.TariffDetailQueryVariables) => [
  "TariffDetail.infinite",
  variables,
]

useInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

export const useSuspenseInfiniteTariffDetailQuery = <
  TData = InfiniteData<Types.TariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
            TariffDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffDetailQuery.getKey = (
  variables: Types.TariffDetailQueryVariables,
) => ["TariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteTariffDetailQuery.rootKey = "TariffDetail.infinite"

useTariffDetailQuery.fetcher = (
  variables: Types.TariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffDetailQuery, Types.TariffDetailQueryVariables>(
    TariffDetailDocument,
    variables,
    options,
  )

export const SiteHeaderDocument = `
    query SiteHeader($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      id
      pid
      customerId
      propertyName
      streetAddress
      city
      state
      zipCode
      portfolio {
        pid
        title
        org {
          pid
          name
          slug
        }
      }
      status
      hiddenFromCustomers
      statusCheckPassed
      policyViolations {
        policy {
          pid
        }
      }
    }
  }
}
    `

export const useSiteHeaderQuery = <TData = Types.SiteHeaderQuery, TError = unknown>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteHeaderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeader", variables],
    queryFn: publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader",
  variables,
]

useSiteHeaderQuery.rootKey = "SiteHeader"

export const useSuspenseSiteHeaderQuery = <
  TData = Types.SiteHeaderQuery,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteHeaderQuery, TError, TData>({
    queryKey: ["SiteHeaderSuspense", variables],
    queryFn: publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
      SiteHeaderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeaderSuspense",
  variables,
]

useSuspenseSiteHeaderQuery.rootKey = "SiteHeader"

export const useInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteHeaderQuery.getKey = (variables: Types.SiteHeaderQueryVariables) => [
  "SiteHeader.infinite",
  variables,
]

useInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

export const useSuspenseInfiniteSiteHeaderQuery = <
  TData = InfiniteData<Types.SiteHeaderQuery>,
  TError = unknown,
>(
  variables: Types.SiteHeaderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteHeaderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteHeaderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteHeaderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteHeader.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
            SiteHeaderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteHeaderQuery.getKey = (
  variables: Types.SiteHeaderQueryVariables,
) => ["SiteHeader.infiniteSuspense", variables]

useSuspenseInfiniteSiteHeaderQuery.rootKey = "SiteHeader.infinite"

useSiteHeaderQuery.fetcher = (
  variables: Types.SiteHeaderQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteHeaderQuery, Types.SiteHeaderQueryVariables>(
    SiteHeaderDocument,
    variables,
    options,
  )

export const GetEtbDetailDocument = `
    query GetEtbDetail($sitePid: String!, $etbId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      activeMeters {
        id
        meters {
          ...BauMeter
        }
      }
      etbReport(id: $etbId) {
        id
        dateCreated
        projectName
        proposalDate
        taxType
        ownerSector
        federalIncomeTaxRate
        stateIncomeTaxRate
        discountRate
        electricityEscalationRate
        scenario {
          ...ETBScenarioRef
        }
        combinedCosts {
          ...CostsFragment
        }
        meters {
          id
          name
          streetAddress
          city
          state
          zipCode
          utilityName
          genabilityUtility: utility {
            id
            name
            shortName
          }
          tariffFullDescriptionBefore
          genabilityTariffBefore: tariffBefore {
            id
            name
            code
          }
          tariffFullDescriptionAfter
          genabilityTariffAfter: tariffAfter {
            id
            name
            code
          }
          costs {
            ...CostsFragment
          }
          pv {
            cost
            powerRatingKwDc
            powerRatingKwAc
            degradationRate
            tilt
            azimuth
          }
          pvType
          bauMeter {
            id
            archivedAt
          }
        }
        transactions {
          name
          termYears
          irr10Year
          irr20Year
          irrTerm
          lcoeBefore
          lcoeAfter
          lcoePv
          npvTerm
          paybackYears
          roiTerm
          demandSavingsTerm
          energySavingsTerm
          totalSavingsTerm
          pvGenerationKwhTerm
          storageDischargeKwhTerm
          storageConsumptionKwh
          totalCost
          totalIncentives
          netCost
          upfrontCost
          ppaTerm
          ppaStartingRate
          ppaEscalationRate
          series {
            name
            series
          }
        }
        roofPvKw
        carportPvKw
      }
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${EtbScenarioRefFragmentDoc}
${CostsFragmentFragmentDoc}`

export const useGetEtbDetailQuery = <TData = Types.GetEtbDetailQuery, TError = unknown>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetEtbDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetail", variables],
    queryFn: publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail",
  variables,
]

useGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useSuspenseGetEtbDetailQuery = <
  TData = Types.GetEtbDetailQuery,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetEtbDetailQuery, TError, TData>({
    queryKey: ["GetEtbDetailSuspense", variables],
    queryFn: publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
      GetEtbDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetailSuspense",
  variables,
]

useSuspenseGetEtbDetailQuery.rootKey = "GetEtbDetail"

export const useInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetEtbDetailQuery.getKey = (variables: Types.GetEtbDetailQueryVariables) => [
  "GetEtbDetail.infinite",
  variables,
]

useInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

export const useSuspenseInfiniteGetEtbDetailQuery = <
  TData = InfiniteData<Types.GetEtbDetailQuery>,
  TError = unknown,
>(
  variables: Types.GetEtbDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetEtbDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetEtbDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetEtbDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetEtbDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
            GetEtbDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetEtbDetailQuery.getKey = (
  variables: Types.GetEtbDetailQueryVariables,
) => ["GetEtbDetail.infiniteSuspense", variables]

useSuspenseInfiniteGetEtbDetailQuery.rootKey = "GetEtbDetail.infinite"

useGetEtbDetailQuery.fetcher = (
  variables: Types.GetEtbDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetEtbDetailQuery, Types.GetEtbDetailQueryVariables>(
    GetEtbDetailDocument,
    variables,
    options,
  )

export const EtbIndexDocument = `
    query EtbIndex($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      etbReports {
        id
        projectName
        dateCreated
        proposalDate
        scenario {
          ...ETBScenarioRef
        }
      }
      etbScenarioOptions {
        ...ETBScenarioRef
      }
    }
  }
}
    ${EtbScenarioRefFragmentDoc}`

export const useEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<UseQueryOptions<Types.EtbIndexQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndex", variables],
    queryFn: publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex",
  variables,
]

useEtbIndexQuery.rootKey = "EtbIndex"

export const useSuspenseEtbIndexQuery = <TData = Types.EtbIndexQuery, TError = unknown>(
  variables: Types.EtbIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EtbIndexQuery, TError, TData>({
    queryKey: ["EtbIndexSuspense", variables],
    queryFn: publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
      EtbIndexDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndexSuspense",
  variables,
]

useSuspenseEtbIndexQuery.rootKey = "EtbIndex"

export const useInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infinite",
  variables,
]

useInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

export const useSuspenseInfiniteEtbIndexQuery = <
  TData = InfiniteData<Types.EtbIndexQuery>,
  TError = unknown,
>(
  variables: Types.EtbIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EtbIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EtbIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EtbIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EtbIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
            EtbIndexDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEtbIndexQuery.getKey = (variables: Types.EtbIndexQueryVariables) => [
  "EtbIndex.infiniteSuspense",
  variables,
]

useSuspenseInfiniteEtbIndexQuery.rootKey = "EtbIndex.infinite"

useEtbIndexQuery.fetcher = (
  variables: Types.EtbIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EtbIndexQuery, Types.EtbIndexQueryVariables>(
    EtbIndexDocument,
    variables,
    options,
  )

export const HelioscopeIndexDocument = `
    query HelioscopeIndex($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      activeMeters {
        meters {
          label
        }
      }
      helioscopes {
        id
        pid
        kind
        createdAt
        createdBy {
          ...UserRef
        }
        isTrashed
        roofPvKw
        carportPvKw
        carportPvThreeHighKw
        carportPvSixHighKw
        groundPvKw
        roofIsMax
        carportIsMax
        annualProductionAcKwh
        monthlyProductionAcKwh
        systemProductivityKwhPerKw
        allocations
        pvSpecs {
          pvType
          sizeKw
        }
        pdf {
          ...AttachmentFragment
        }
        roofImage {
          ...AttachmentFragment
        }
        roofImageHref
        productionCsv {
          ...AttachmentFragment
        }
        dcNameplateKw
        acNameplateKw
        loadRatio
        moduleType
        inverterType
        rackingTiltDegrees
        rackingManufacturer
        rackingConnectionType
        notes
      }
    }
  }
}
    ${UserRefFragmentDoc}
${AttachmentFragmentFragmentDoc}`

export const useHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndex", variables],
    queryFn: publicFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useHelioscopeIndexQuery.getKey = (variables: Types.HelioscopeIndexQueryVariables) => [
  "HelioscopeIndex",
  variables,
]

useHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useSuspenseHelioscopeIndexQuery = <
  TData = Types.HelioscopeIndexQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeIndexQuery, TError, TData>({
    queryKey: ["HelioscopeIndexSuspense", variables],
    queryFn: publicFetcher<
      Types.HelioscopeIndexQuery,
      Types.HelioscopeIndexQueryVariables
    >(HelioscopeIndexDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndexSuspense", variables]

useSuspenseHelioscopeIndexQuery.rootKey = "HelioscopeIndex"

export const useInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeIndexQuery,
            Types.HelioscopeIndexQueryVariables
          >(HelioscopeIndexDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infinite", variables]

useInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

export const useSuspenseInfiniteHelioscopeIndexQuery = <
  TData = InfiniteData<Types.HelioscopeIndexQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeIndexQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeIndexQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeIndexQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeIndexQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeIndex.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeIndexQuery,
            Types.HelioscopeIndexQueryVariables
          >(HelioscopeIndexDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeIndexQuery.getKey = (
  variables: Types.HelioscopeIndexQueryVariables,
) => ["HelioscopeIndex.infiniteSuspense", variables]

useSuspenseInfiniteHelioscopeIndexQuery.rootKey = "HelioscopeIndex.infinite"

useHelioscopeIndexQuery.fetcher = (
  variables: Types.HelioscopeIndexQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HelioscopeIndexQuery, Types.HelioscopeIndexQueryVariables>(
    HelioscopeIndexDocument,
    variables,
    options,
  )

export const HelioscopeUploadDocument = `
    query HelioscopeUpload($pid: String!) {
  admin {
    site(pid: $pid) {
      pid
      scenarios {
        ...SmallScenarioFragment
      }
    }
  }
}
    ${SmallScenarioFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUpload", variables],
    queryFn: publicFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useHelioscopeUploadQuery.getKey = (variables: Types.HelioscopeUploadQueryVariables) => [
  "HelioscopeUpload",
  variables,
]

useHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useSuspenseHelioscopeUploadQuery = <
  TData = Types.HelioscopeUploadQuery,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HelioscopeUploadQuery, TError, TData>({
    queryKey: ["HelioscopeUploadSuspense", variables],
    queryFn: publicFetcher<
      Types.HelioscopeUploadQuery,
      Types.HelioscopeUploadQueryVariables
    >(HelioscopeUploadDocument, variables),
    ...options,
  })
}

useSuspenseHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUploadSuspense", variables]

useSuspenseHelioscopeUploadQuery.rootKey = "HelioscopeUpload"

export const useInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infinite", variables]

useInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

export const useSuspenseInfiniteHelioscopeUploadQuery = <
  TData = InfiniteData<Types.HelioscopeUploadQuery>,
  TError = unknown,
>(
  variables: Types.HelioscopeUploadQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HelioscopeUploadQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HelioscopeUploadQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HelioscopeUploadQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HelioscopeUpload.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HelioscopeUploadQuery,
            Types.HelioscopeUploadQueryVariables
          >(HelioscopeUploadDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHelioscopeUploadQuery.getKey = (
  variables: Types.HelioscopeUploadQueryVariables,
) => ["HelioscopeUpload.infiniteSuspense", variables]

useSuspenseInfiniteHelioscopeUploadQuery.rootKey = "HelioscopeUpload.infinite"

useHelioscopeUploadQuery.fetcher = (
  variables: Types.HelioscopeUploadQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HelioscopeUploadQuery, Types.HelioscopeUploadQueryVariables>(
    HelioscopeUploadDocument,
    variables,
    options,
  )

export const LuxDetailDocument = `
    query LuxDetail($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      lux(id: $id) {
        ...Lux
      }
    }
  }
}
    ${LuxFragmentDoc}
${SlimLuxFragmentDoc}
${PvConfigFragmentDoc}
${BatteryConfigFragmentDoc}
${LuxMeterFragmentDoc}
${CostSummaryFragmentDoc}`

export const useLuxDetailQuery = <TData = Types.LuxDetailQuery, TError = unknown>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.LuxDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetail", variables],
    queryFn: publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail",
  variables,
]

useLuxDetailQuery.rootKey = "LuxDetail"

export const useSuspenseLuxDetailQuery = <
  TData = Types.LuxDetailQuery,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LuxDetailQuery, TError, TData>({
    queryKey: ["LuxDetailSuspense", variables],
    queryFn: publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
      LuxDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetailSuspense",
  variables,
]

useSuspenseLuxDetailQuery.rootKey = "LuxDetail"

export const useInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLuxDetailQuery.getKey = (variables: Types.LuxDetailQueryVariables) => [
  "LuxDetail.infinite",
  variables,
]

useInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

export const useSuspenseInfiniteLuxDetailQuery = <
  TData = InfiniteData<Types.LuxDetailQuery>,
  TError = unknown,
>(
  variables: Types.LuxDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.LuxDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LuxDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.LuxDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["LuxDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
            LuxDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLuxDetailQuery.getKey = (
  variables: Types.LuxDetailQueryVariables,
) => ["LuxDetail.infiniteSuspense", variables]

useSuspenseInfiniteLuxDetailQuery.rootKey = "LuxDetail.infinite"

useLuxDetailQuery.fetcher = (
  variables: Types.LuxDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.LuxDetailQuery, Types.LuxDetailQueryVariables>(
    LuxDetailDocument,
    variables,
    options,
  )

export const GetPortfolioOverridesDocument = `
    query GetPortfolioOverrides($portfolioPid: String!) {
  admin {
    portfolio(pid: $portfolioPid) {
      pid
      overrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverrides", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides", variables]

useGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useSuspenseGetPortfolioOverridesQuery = <
  TData = Types.GetPortfolioOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetPortfolioOverridesQuery, TError, TData>({
    queryKey: ["GetPortfolioOverridesSuspense", variables],
    queryFn: publicFetcher<
      Types.GetPortfolioOverridesQuery,
      Types.GetPortfolioOverridesQueryVariables
    >(GetPortfolioOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverridesSuspense", variables]

useSuspenseGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides"

export const useInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetPortfolioOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infinite", variables]

useInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

export const useSuspenseInfiniteGetPortfolioOverridesQuery = <
  TData = InfiniteData<Types.GetPortfolioOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetPortfolioOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetPortfolioOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetPortfolioOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetPortfolioOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetPortfolioOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetPortfolioOverridesQuery,
            Types.GetPortfolioOverridesQueryVariables
          >(GetPortfolioOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetPortfolioOverridesQuery.getKey = (
  variables: Types.GetPortfolioOverridesQueryVariables,
) => ["GetPortfolioOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetPortfolioOverridesQuery.rootKey = "GetPortfolioOverrides.infinite"

useGetPortfolioOverridesQuery.fetcher = (
  variables: Types.GetPortfolioOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetPortfolioOverridesQuery,
    Types.GetPortfolioOverridesQueryVariables
  >(GetPortfolioOverridesDocument, variables, options)

export const GetSiteOverridesDocument = `
    query GetSiteOverrides($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      overrides {
        ...Override
      }
      portfolio {
        pid
        overrides {
          ...Override
        }
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverrides", variables],
    queryFn: publicFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useGetSiteOverridesQuery.getKey = (variables: Types.GetSiteOverridesQueryVariables) => [
  "GetSiteOverrides",
  variables,
]

useGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useSuspenseGetSiteOverridesQuery = <
  TData = Types.GetSiteOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetSiteOverridesQuery, TError, TData>({
    queryKey: ["GetSiteOverridesSuspense", variables],
    queryFn: publicFetcher<
      Types.GetSiteOverridesQuery,
      Types.GetSiteOverridesQueryVariables
    >(GetSiteOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverridesSuspense", variables]

useSuspenseGetSiteOverridesQuery.rootKey = "GetSiteOverrides"

export const useInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infinite", variables]

useInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

export const useSuspenseInfiniteGetSiteOverridesQuery = <
  TData = InfiniteData<Types.GetSiteOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetSiteOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetSiteOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetSiteOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetSiteOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetSiteOverrides.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetSiteOverridesQuery,
            Types.GetSiteOverridesQueryVariables
          >(GetSiteOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetSiteOverridesQuery.getKey = (
  variables: Types.GetSiteOverridesQueryVariables,
) => ["GetSiteOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetSiteOverridesQuery.rootKey = "GetSiteOverrides.infinite"

useGetSiteOverridesQuery.fetcher = (
  variables: Types.GetSiteOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetSiteOverridesQuery, Types.GetSiteOverridesQueryVariables>(
    GetSiteOverridesDocument,
    variables,
    options,
  )

export const ResolvedOverridesDocument = `
    query ResolvedOverrides($sitePid: String!, $finproductId: Int) {
  admin {
    site(pid: $sitePid) {
      resolvedOverrides(finproductId: $finproductId) {
        ...Override
      }
    }
  }
}
    ${OverrideFragmentDoc}`

export const useResolvedOverridesQuery = <
  TData = Types.ResolvedOverridesQuery,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResolvedOverridesQuery, TError, TData>({
    queryKey: ["ResolvedOverrides", variables],
    queryFn: publicFetcher<
      Types.ResolvedOverridesQuery,
      Types.ResolvedOverridesQueryVariables
    >(ResolvedOverridesDocument, variables),
    ...options,
  })
}

useResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides", variables]

useResolvedOverridesQuery.rootKey = "ResolvedOverrides"

export const useSuspenseResolvedOverridesQuery = <
  TData = Types.ResolvedOverridesQuery,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResolvedOverridesQuery, TError, TData>({
    queryKey: ["ResolvedOverridesSuspense", variables],
    queryFn: publicFetcher<
      Types.ResolvedOverridesQuery,
      Types.ResolvedOverridesQueryVariables
    >(ResolvedOverridesDocument, variables),
    ...options,
  })
}

useSuspenseResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverridesSuspense", variables]

useSuspenseResolvedOverridesQuery.rootKey = "ResolvedOverrides"

export const useInfiniteResolvedOverridesQuery = <
  TData = InfiniteData<Types.ResolvedOverridesQuery>,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResolvedOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResolvedOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResolvedOverridesQuery,
            Types.ResolvedOverridesQueryVariables
          >(ResolvedOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides.infinite", variables]

useInfiniteResolvedOverridesQuery.rootKey = "ResolvedOverrides.infinite"

export const useSuspenseInfiniteResolvedOverridesQuery = <
  TData = InfiniteData<Types.ResolvedOverridesQuery>,
  TError = unknown,
>(
  variables: Types.ResolvedOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResolvedOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResolvedOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResolvedOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResolvedOverrides.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResolvedOverridesQuery,
            Types.ResolvedOverridesQueryVariables
          >(ResolvedOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResolvedOverridesQuery.getKey = (
  variables: Types.ResolvedOverridesQueryVariables,
) => ["ResolvedOverrides.infiniteSuspense", variables]

useSuspenseInfiniteResolvedOverridesQuery.rootKey = "ResolvedOverrides.infinite"

useResolvedOverridesQuery.fetcher = (
  variables: Types.ResolvedOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResolvedOverridesQuery, Types.ResolvedOverridesQueryVariables>(
    ResolvedOverridesDocument,
    variables,
    options,
  )

export const GetFinancingGiverOrgOverridesDocument = `
    query GetFinancingGiverOrgOverrides($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      overrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    ${OverrideFragmentDoc}`

export const useGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverrides", variables],
    queryFn: publicFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides", variables]

useGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useSuspenseGetFinancingGiverOrgOverridesQuery = <
  TData = Types.GetFinancingGiverOrgOverridesQuery,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>({
    queryKey: ["GetFinancingGiverOrgOverridesSuspense", variables],
    queryFn: publicFetcher<
      Types.GetFinancingGiverOrgOverridesQuery,
      Types.GetFinancingGiverOrgOverridesQueryVariables
    >(GetFinancingGiverOrgOverridesDocument, variables),
    ...options,
  })
}

useSuspenseGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverridesSuspense", variables]

useSuspenseGetFinancingGiverOrgOverridesQuery.rootKey = "GetFinancingGiverOrgOverrides"

export const useInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetFinancingGiverOrgOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infinite", variables]

useInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

export const useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery = <
  TData = InfiniteData<Types.GetFinancingGiverOrgOverridesQuery>,
  TError = unknown,
>(
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetFinancingGiverOrgOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.GetFinancingGiverOrgOverridesQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "GetFinancingGiverOrgOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GetFinancingGiverOrgOverridesQuery,
            Types.GetFinancingGiverOrgOverridesQueryVariables
          >(GetFinancingGiverOrgOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.getKey = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
) => ["GetFinancingGiverOrgOverrides.infiniteSuspense", variables]

useSuspenseInfiniteGetFinancingGiverOrgOverridesQuery.rootKey =
  "GetFinancingGiverOrgOverrides.infinite"

useGetFinancingGiverOrgOverridesQuery.fetcher = (
  variables: Types.GetFinancingGiverOrgOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.GetFinancingGiverOrgOverridesQuery,
    Types.GetFinancingGiverOrgOverridesQueryVariables
  >(GetFinancingGiverOrgOverridesDocument, variables, options)

export const FinproductOverridesDocument = `
    query FinproductOverrides($finProductId: Int!) {
  admin {
    finproduct(finproductId: $finProductId) {
      id
      finmodelInputOverrides {
        ...Override
      }
    }
    finmodelSettings {
      possibleOverrides {
        name
        type
      }
    }
  }
}
    ${OverrideFragmentDoc}`

export const useFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverrides", variables],
    queryFn: publicFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides", variables]

useFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useSuspenseFinproductOverridesQuery = <
  TData = Types.FinproductOverridesQuery,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinproductOverridesQuery, TError, TData>({
    queryKey: ["FinproductOverridesSuspense", variables],
    queryFn: publicFetcher<
      Types.FinproductOverridesQuery,
      Types.FinproductOverridesQueryVariables
    >(FinproductOverridesDocument, variables),
    ...options,
  })
}

useSuspenseFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverridesSuspense", variables]

useSuspenseFinproductOverridesQuery.rootKey = "FinproductOverrides"

export const useInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinproductOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infinite", variables]

useInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

export const useSuspenseInfiniteFinproductOverridesQuery = <
  TData = InfiniteData<Types.FinproductOverridesQuery>,
  TError = unknown,
>(
  variables: Types.FinproductOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinproductOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinproductOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinproductOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinproductOverrides.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinproductOverridesQuery,
            Types.FinproductOverridesQueryVariables
          >(FinproductOverridesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinproductOverridesQuery.getKey = (
  variables: Types.FinproductOverridesQueryVariables,
) => ["FinproductOverrides.infiniteSuspense", variables]

useSuspenseInfiniteFinproductOverridesQuery.rootKey = "FinproductOverrides.infinite"

useFinproductOverridesQuery.fetcher = (
  variables: Types.FinproductOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinproductOverridesQuery,
    Types.FinproductOverridesQueryVariables
  >(FinproductOverridesDocument, variables, options)

export const SiteUtilityDocument = `
    query SiteUtility($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      zipCode
      thirdPartySupplier {
        id
        name
      }
      targetUtility {
        id
        name
        shortName
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
      utilityName
      probableUtilities {
        id
        name
      }
      isMultifamily
      ...Metering
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    ${UserRefFragmentDoc}
${MeteringFragmentDoc}`

export const useSiteUtilityQuery = <TData = Types.SiteUtilityQuery, TError = unknown>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteUtilityQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteUtilityQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtility", variables],
    queryFn: publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility",
  variables,
]

useSiteUtilityQuery.rootKey = "SiteUtility"

export const useSuspenseSiteUtilityQuery = <
  TData = Types.SiteUtilityQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityQuery, TError, TData>({
    queryKey: ["SiteUtilitySuspense", variables],
    queryFn: publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
      SiteUtilityDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtilitySuspense",
  variables,
]

useSuspenseSiteUtilityQuery.rootKey = "SiteUtility"

export const useInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityQuery.getKey = (variables: Types.SiteUtilityQueryVariables) => [
  "SiteUtility.infinite",
  variables,
]

useInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

export const useSuspenseInfiniteSiteUtilityQuery = <
  TData = InfiniteData<Types.SiteUtilityQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtility.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
            SiteUtilityDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityQuery.getKey = (
  variables: Types.SiteUtilityQueryVariables,
) => ["SiteUtility.infiniteSuspense", variables]

useSuspenseInfiniteSiteUtilityQuery.rootKey = "SiteUtility.infinite"

useSiteUtilityQuery.fetcher = (
  variables: Types.SiteUtilityQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteUtilityQuery, Types.SiteUtilityQueryVariables>(
    SiteUtilityDocument,
    variables,
    options,
  )

export const UtilitySearchDocument = `
    query UtilitySearch($name: String!) {
  admin {
    tariffs {
      searchGenabilityByUtilityName(name: $name) {
        id
        name
      }
    }
  }
}
    `

export const useUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilitySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearch", variables],
    queryFn: publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useUtilitySearchQuery.getKey = (variables: Types.UtilitySearchQueryVariables) => [
  "UtilitySearch",
  variables,
]

useUtilitySearchQuery.rootKey = "UtilitySearch"

export const useSuspenseUtilitySearchQuery = <
  TData = Types.UtilitySearchQuery,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilitySearchQuery, TError, TData>({
    queryKey: ["UtilitySearchSuspense", variables],
    queryFn: publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
      UtilitySearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearchSuspense", variables]

useSuspenseUtilitySearchQuery.rootKey = "UtilitySearch"

export const useInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infinite", variables]

useInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

export const useSuspenseInfiniteUtilitySearchQuery = <
  TData = InfiniteData<Types.UtilitySearchQuery>,
  TError = unknown,
>(
  variables: Types.UtilitySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilitySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilitySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilitySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilitySearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
            UtilitySearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilitySearchQuery.getKey = (
  variables: Types.UtilitySearchQueryVariables,
) => ["UtilitySearch.infiniteSuspense", variables]

useSuspenseInfiniteUtilitySearchQuery.rootKey = "UtilitySearch.infinite"

useUtilitySearchQuery.fetcher = (
  variables: Types.UtilitySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilitySearchQuery, Types.UtilitySearchQueryVariables>(
    UtilitySearchDocument,
    variables,
    options,
  )

export const SiteUtilityByLabelDocument = `
    query SiteUtilityByLabel($sitePid: String!, $label: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      utilityByLabel(label: $label) {
        id
        name
      }
      probableUtilities {
        id
        name
      }
    }
  }
}
    `

export const useSiteUtilityByLabelQuery = <
  TData = Types.SiteUtilityByLabelQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteUtilityByLabelQuery, TError, TData>({
    queryKey: ["SiteUtilityByLabel", variables],
    queryFn: publicFetcher<
      Types.SiteUtilityByLabelQuery,
      Types.SiteUtilityByLabelQueryVariables
    >(SiteUtilityByLabelDocument, variables),
    ...options,
  })
}

useSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel", variables]

useSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel"

export const useSuspenseSiteUtilityByLabelQuery = <
  TData = Types.SiteUtilityByLabelQuery,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteUtilityByLabelQuery, TError, TData>({
    queryKey: ["SiteUtilityByLabelSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteUtilityByLabelQuery,
      Types.SiteUtilityByLabelQueryVariables
    >(SiteUtilityByLabelDocument, variables),
    ...options,
  })
}

useSuspenseSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabelSuspense", variables]

useSuspenseSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel"

export const useInfiniteSiteUtilityByLabelQuery = <
  TData = InfiniteData<Types.SiteUtilityByLabelQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteUtilityByLabelQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityByLabel.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteUtilityByLabelQuery,
            Types.SiteUtilityByLabelQueryVariables
          >(SiteUtilityByLabelDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel.infinite", variables]

useInfiniteSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel.infinite"

export const useSuspenseInfiniteSiteUtilityByLabelQuery = <
  TData = InfiniteData<Types.SiteUtilityByLabelQuery>,
  TError = unknown,
>(
  variables: Types.SiteUtilityByLabelQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteUtilityByLabelQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteUtilityByLabelQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteUtilityByLabelQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteUtilityByLabel.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteUtilityByLabelQuery,
            Types.SiteUtilityByLabelQueryVariables
          >(SiteUtilityByLabelDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteUtilityByLabelQuery.getKey = (
  variables: Types.SiteUtilityByLabelQueryVariables,
) => ["SiteUtilityByLabel.infiniteSuspense", variables]

useSuspenseInfiniteSiteUtilityByLabelQuery.rootKey = "SiteUtilityByLabel.infinite"

useSiteUtilityByLabelQuery.fetcher = (
  variables: Types.SiteUtilityByLabelQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteUtilityByLabelQuery, Types.SiteUtilityByLabelQueryVariables>(
    SiteUtilityByLabelDocument,
    variables,
    options,
  )

export const TariffRateDetailDocument = `
    query TariffRateDetail($sitePid: String!, $tariffParentId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      tariffDetail(tariffParentId: $tariffParentId) {
        ...TariffDetailFragment
      }
    }
  }
}
    ${TariffDetailFragmentFragmentDoc}`

export const useTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.TariffRateDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetail", variables],
    queryFn: publicFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useTariffRateDetailQuery.getKey = (variables: Types.TariffRateDetailQueryVariables) => [
  "TariffRateDetail",
  variables,
]

useTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useSuspenseTariffRateDetailQuery = <
  TData = Types.TariffRateDetailQuery,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.TariffRateDetailQuery, TError, TData>({
    queryKey: ["TariffRateDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.TariffRateDetailQuery,
      Types.TariffRateDetailQueryVariables
    >(TariffRateDetailDocument, variables),
    ...options,
  })
}

useSuspenseTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetailSuspense", variables]

useSuspenseTariffRateDetailQuery.rootKey = "TariffRateDetail"

export const useInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infinite", variables]

useInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

export const useSuspenseInfiniteTariffRateDetailQuery = <
  TData = InfiniteData<Types.TariffRateDetailQuery>,
  TError = unknown,
>(
  variables: Types.TariffRateDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.TariffRateDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.TariffRateDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.TariffRateDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["TariffRateDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.TariffRateDetailQuery,
            Types.TariffRateDetailQueryVariables
          >(TariffRateDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteTariffRateDetailQuery.getKey = (
  variables: Types.TariffRateDetailQueryVariables,
) => ["TariffRateDetail.infiniteSuspense", variables]

useSuspenseInfiniteTariffRateDetailQuery.rootKey = "TariffRateDetail.infinite"

useTariffRateDetailQuery.fetcher = (
  variables: Types.TariffRateDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TariffRateDetailQuery, Types.TariffRateDetailQueryVariables>(
    TariffRateDetailDocument,
    variables,
    options,
  )

export const AdminSearchDocument = `
    query AdminSearch($q: String!) {
  admin {
    search(q: $q) {
      id
      pid
      name
      fullAddress
      portfolio {
        pid
        title
      }
    }
  }
}
    `

export const useAdminSearchQuery = <TData = Types.AdminSearchQuery, TError = unknown>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminSearchQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminSearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearch", variables],
    queryFn: publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch",
  variables,
]

useAdminSearchQuery.rootKey = "AdminSearch"

export const useSuspenseAdminSearchQuery = <
  TData = Types.AdminSearchQuery,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSearchQuery, TError, TData>({
    queryKey: ["AdminSearchSuspense", variables],
    queryFn: publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
      AdminSearchDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearchSuspense",
  variables,
]

useSuspenseAdminSearchQuery.rootKey = "AdminSearch"

export const useInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSearchQuery.getKey = (variables: Types.AdminSearchQueryVariables) => [
  "AdminSearch.infinite",
  variables,
]

useInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

export const useSuspenseInfiniteAdminSearchQuery = <
  TData = InfiniteData<Types.AdminSearchQuery>,
  TError = unknown,
>(
  variables: Types.AdminSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
            AdminSearchDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSearchQuery.getKey = (
  variables: Types.AdminSearchQueryVariables,
) => ["AdminSearch.infiniteSuspense", variables]

useSuspenseInfiniteAdminSearchQuery.rootKey = "AdminSearch.infinite"

useAdminSearchQuery.fetcher = (
  variables: Types.AdminSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminSearchQuery, Types.AdminSearchQueryVariables>(
    AdminSearchDocument,
    variables,
    options,
  )

export const CostarOwnerNameSearchDocument = `
    query CostarOwnerNameSearch($q: String!) {
  admin {
    searchForOwnerName(q: $q) {
      ownerName
      countSitesInPortfolio
      countSitesNotInPortfolio
      primaryState
    }
  }
}
    `

export const useCostarOwnerNameSearchQuery = <
  TData = Types.CostarOwnerNameSearchQuery,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CostarOwnerNameSearchQuery, TError, TData>({
    queryKey: ["CostarOwnerNameSearch", variables],
    queryFn: publicFetcher<
      Types.CostarOwnerNameSearchQuery,
      Types.CostarOwnerNameSearchQueryVariables
    >(CostarOwnerNameSearchDocument, variables),
    ...options,
  })
}

useCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch", variables]

useCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch"

export const useSuspenseCostarOwnerNameSearchQuery = <
  TData = Types.CostarOwnerNameSearchQuery,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CostarOwnerNameSearchQuery, TError, TData>({
    queryKey: ["CostarOwnerNameSearchSuspense", variables],
    queryFn: publicFetcher<
      Types.CostarOwnerNameSearchQuery,
      Types.CostarOwnerNameSearchQueryVariables
    >(CostarOwnerNameSearchDocument, variables),
    ...options,
  })
}

useSuspenseCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearchSuspense", variables]

useSuspenseCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch"

export const useInfiniteCostarOwnerNameSearchQuery = <
  TData = InfiniteData<Types.CostarOwnerNameSearchQuery>,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CostarOwnerNameSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CostarOwnerNameSearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CostarOwnerNameSearchQuery,
            Types.CostarOwnerNameSearchQueryVariables
          >(CostarOwnerNameSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch.infinite", variables]

useInfiniteCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch.infinite"

export const useSuspenseInfiniteCostarOwnerNameSearchQuery = <
  TData = InfiniteData<Types.CostarOwnerNameSearchQuery>,
  TError = unknown,
>(
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CostarOwnerNameSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CostarOwnerNameSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CostarOwnerNameSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CostarOwnerNameSearch.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CostarOwnerNameSearchQuery,
            Types.CostarOwnerNameSearchQueryVariables
          >(CostarOwnerNameSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCostarOwnerNameSearchQuery.getKey = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
) => ["CostarOwnerNameSearch.infiniteSuspense", variables]

useSuspenseInfiniteCostarOwnerNameSearchQuery.rootKey = "CostarOwnerNameSearch.infinite"

useCostarOwnerNameSearchQuery.fetcher = (
  variables: Types.CostarOwnerNameSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CostarOwnerNameSearchQuery,
    Types.CostarOwnerNameSearchQueryVariables
  >(CostarOwnerNameSearchDocument, variables, options)

export const CostarSitesByOwnerNameDocument = `
    query CostarSitesByOwnerName($name: String!) {
  admin {
    sitesByOwnerName(name: $name) {
      pid
      displayName
      costarPropertyType
      city
      state
    }
  }
}
    `

export const useCostarSitesByOwnerNameQuery = <
  TData = Types.CostarSitesByOwnerNameQuery,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>({
    queryKey: ["CostarSitesByOwnerName", variables],
    queryFn: publicFetcher<
      Types.CostarSitesByOwnerNameQuery,
      Types.CostarSitesByOwnerNameQueryVariables
    >(CostarSitesByOwnerNameDocument, variables),
    ...options,
  })
}

useCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName", variables]

useCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName"

export const useSuspenseCostarSitesByOwnerNameQuery = <
  TData = Types.CostarSitesByOwnerNameQuery,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>({
    queryKey: ["CostarSitesByOwnerNameSuspense", variables],
    queryFn: publicFetcher<
      Types.CostarSitesByOwnerNameQuery,
      Types.CostarSitesByOwnerNameQueryVariables
    >(CostarSitesByOwnerNameDocument, variables),
    ...options,
  })
}

useSuspenseCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerNameSuspense", variables]

useSuspenseCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName"

export const useInfiniteCostarSitesByOwnerNameQuery = <
  TData = InfiniteData<Types.CostarSitesByOwnerNameQuery>,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CostarSitesByOwnerName.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CostarSitesByOwnerNameQuery,
            Types.CostarSitesByOwnerNameQueryVariables
          >(CostarSitesByOwnerNameDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName.infinite", variables]

useInfiniteCostarSitesByOwnerNameQuery.rootKey = "CostarSitesByOwnerName.infinite"

export const useSuspenseInfiniteCostarSitesByOwnerNameQuery = <
  TData = InfiniteData<Types.CostarSitesByOwnerNameQuery>,
  TError = unknown,
>(
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CostarSitesByOwnerNameQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CostarSitesByOwnerNameQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CostarSitesByOwnerNameQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CostarSitesByOwnerName.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CostarSitesByOwnerNameQuery,
            Types.CostarSitesByOwnerNameQueryVariables
          >(CostarSitesByOwnerNameDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCostarSitesByOwnerNameQuery.getKey = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
) => ["CostarSitesByOwnerName.infiniteSuspense", variables]

useSuspenseInfiniteCostarSitesByOwnerNameQuery.rootKey =
  "CostarSitesByOwnerName.infinite"

useCostarSitesByOwnerNameQuery.fetcher = (
  variables: Types.CostarSitesByOwnerNameQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CostarSitesByOwnerNameQuery,
    Types.CostarSitesByOwnerNameQueryVariables
  >(CostarSitesByOwnerNameDocument, variables, options)

export const AdminScenarioListDocument = `
    query AdminScenarioList($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      scenarios {
        ...ScenarioFragment
      }
    }
  }
}
    ${ScenarioFragmentFragmentDoc}
${SmallScenarioFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useAdminScenarioListQuery = <
  TData = Types.AdminScenarioListQuery,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminScenarioListQuery, TError, TData>({
    queryKey: ["AdminScenarioList", variables],
    queryFn: publicFetcher<
      Types.AdminScenarioListQuery,
      Types.AdminScenarioListQueryVariables
    >(AdminScenarioListDocument, variables),
    ...options,
  })
}

useAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList", variables]

useAdminScenarioListQuery.rootKey = "AdminScenarioList"

export const useSuspenseAdminScenarioListQuery = <
  TData = Types.AdminScenarioListQuery,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminScenarioListQuery, TError, TData>({
    queryKey: ["AdminScenarioListSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminScenarioListQuery,
      Types.AdminScenarioListQueryVariables
    >(AdminScenarioListDocument, variables),
    ...options,
  })
}

useSuspenseAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioListSuspense", variables]

useSuspenseAdminScenarioListQuery.rootKey = "AdminScenarioList"

export const useInfiniteAdminScenarioListQuery = <
  TData = InfiniteData<Types.AdminScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminScenarioListQuery,
            Types.AdminScenarioListQueryVariables
          >(AdminScenarioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList.infinite", variables]

useInfiniteAdminScenarioListQuery.rootKey = "AdminScenarioList.infinite"

export const useSuspenseInfiniteAdminScenarioListQuery = <
  TData = InfiniteData<Types.AdminScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.AdminScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminScenarioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminScenarioListQuery,
            Types.AdminScenarioListQueryVariables
          >(AdminScenarioListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminScenarioListQuery.getKey = (
  variables: Types.AdminScenarioListQueryVariables,
) => ["AdminScenarioList.infiniteSuspense", variables]

useSuspenseInfiniteAdminScenarioListQuery.rootKey = "AdminScenarioList.infinite"

useAdminScenarioListQuery.fetcher = (
  variables: Types.AdminScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminScenarioListQuery, Types.AdminScenarioListQueryVariables>(
    AdminScenarioListDocument,
    variables,
    options,
  )

export const ScenarioDetailDocument = `
    query ScenarioDetail($sitePid: String!, $scenarioPid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      attachments {
        ...AttachmentFragment
      }
      helioscopes {
        ...ScenarioHelioscopeFragment
      }
      etbReports {
        ...ScenarioEtbFragment
      }
      scenario(pid: $scenarioPid) {
        ...ScenarioFragment
        etbs {
          id
          projectName
          dateCreated
        }
        configs {
          id
          createdAt
          finproduct {
            __typename
            financier {
              pid
              name
            }
            id
            createdAt
            name
            type
          }
          finproductType
          goal
          roofKw
          carportKw
          groundKw
          battery {
            sizeKw
            sizeKwh
            vendor
            equipmentCost
            laborCost
          }
          helioscope {
            ...ScenarioHelioscopeFragment
          }
          etb {
            ...ScenarioEtbFragment
          }
        }
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}
${ScenarioHelioscopeFragmentFragmentDoc}
${ScenarioEtbFragmentFragmentDoc}
${ScenarioFragmentFragmentDoc}
${SmallScenarioFragmentFragmentDoc}`

export const useScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetail", variables],
    queryFn: publicFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useScenarioDetailQuery.getKey = (variables: Types.ScenarioDetailQueryVariables) => [
  "ScenarioDetail",
  variables,
]

useScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useSuspenseScenarioDetailQuery = <
  TData = Types.ScenarioDetailQuery,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioDetailQuery, TError, TData>({
    queryKey: ["ScenarioDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.ScenarioDetailQuery,
      Types.ScenarioDetailQueryVariables
    >(ScenarioDetailDocument, variables),
    ...options,
  })
}

useSuspenseScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetailSuspense", variables]

useSuspenseScenarioDetailQuery.rootKey = "ScenarioDetail"

export const useInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infinite", variables]

useInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

export const useSuspenseInfiniteScenarioDetailQuery = <
  TData = InfiniteData<Types.ScenarioDetailQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
            ScenarioDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioDetailQuery.getKey = (
  variables: Types.ScenarioDetailQueryVariables,
) => ["ScenarioDetail.infiniteSuspense", variables]

useSuspenseInfiniteScenarioDetailQuery.rootKey = "ScenarioDetail.infinite"

useScenarioDetailQuery.fetcher = (
  variables: Types.ScenarioDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ScenarioDetailQuery, Types.ScenarioDetailQueryVariables>(
    ScenarioDetailDocument,
    variables,
    options,
  )

export const SiteChangelogDocument = `
    query SiteChangelog($sitePid: String!) {
  admin {
    changelogs {
      site(sitePid: $sitePid) {
        id
        tableName
        transactionId
        dateCreated
        columnName
        oldVal
        newVal
        valueRedacted
        changedBy {
          ...UserRef
        }
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteChangelogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelog", variables],
    queryFn: publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSiteChangelogQuery.getKey = (variables: Types.SiteChangelogQueryVariables) => [
  "SiteChangelog",
  variables,
]

useSiteChangelogQuery.rootKey = "SiteChangelog"

export const useSuspenseSiteChangelogQuery = <
  TData = Types.SiteChangelogQuery,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteChangelogQuery, TError, TData>({
    queryKey: ["SiteChangelogSuspense", variables],
    queryFn: publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
      SiteChangelogDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelogSuspense", variables]

useSuspenseSiteChangelogQuery.rootKey = "SiteChangelog"

export const useInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infinite", variables]

useInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

export const useSuspenseInfiniteSiteChangelogQuery = <
  TData = InfiniteData<Types.SiteChangelogQuery>,
  TError = unknown,
>(
  variables: Types.SiteChangelogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteChangelogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteChangelogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteChangelogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteChangelog.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
            SiteChangelogDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteChangelogQuery.getKey = (
  variables: Types.SiteChangelogQueryVariables,
) => ["SiteChangelog.infiniteSuspense", variables]

useSuspenseInfiniteSiteChangelogQuery.rootKey = "SiteChangelog.infinite"

useSiteChangelogQuery.fetcher = (
  variables: Types.SiteChangelogQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteChangelogQuery, Types.SiteChangelogQueryVariables>(
    SiteChangelogDocument,
    variables,
    options,
  )

export const SiteAttachmentsDocument = `
    query SiteAttachments($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      attachments {
        ...AttachmentFragment
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useSiteAttachmentsQuery = <
  TData = Types.SiteAttachmentsQuery,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteAttachmentsQuery, TError, TData>({
    queryKey: ["SiteAttachments", variables],
    queryFn: publicFetcher<
      Types.SiteAttachmentsQuery,
      Types.SiteAttachmentsQueryVariables
    >(SiteAttachmentsDocument, variables),
    ...options,
  })
}

useSiteAttachmentsQuery.getKey = (variables: Types.SiteAttachmentsQueryVariables) => [
  "SiteAttachments",
  variables,
]

useSiteAttachmentsQuery.rootKey = "SiteAttachments"

export const useSuspenseSiteAttachmentsQuery = <
  TData = Types.SiteAttachmentsQuery,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteAttachmentsQuery, TError, TData>({
    queryKey: ["SiteAttachmentsSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteAttachmentsQuery,
      Types.SiteAttachmentsQueryVariables
    >(SiteAttachmentsDocument, variables),
    ...options,
  })
}

useSuspenseSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachmentsSuspense", variables]

useSuspenseSiteAttachmentsQuery.rootKey = "SiteAttachments"

export const useInfiniteSiteAttachmentsQuery = <
  TData = InfiniteData<Types.SiteAttachmentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteAttachmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteAttachments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteAttachmentsQuery,
            Types.SiteAttachmentsQueryVariables
          >(SiteAttachmentsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachments.infinite", variables]

useInfiniteSiteAttachmentsQuery.rootKey = "SiteAttachments.infinite"

export const useSuspenseInfiniteSiteAttachmentsQuery = <
  TData = InfiniteData<Types.SiteAttachmentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteAttachmentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteAttachmentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteAttachmentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteAttachmentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteAttachments.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteAttachmentsQuery,
            Types.SiteAttachmentsQueryVariables
          >(SiteAttachmentsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteAttachmentsQuery.getKey = (
  variables: Types.SiteAttachmentsQueryVariables,
) => ["SiteAttachments.infiniteSuspense", variables]

useSuspenseInfiniteSiteAttachmentsQuery.rootKey = "SiteAttachments.infinite"

useSiteAttachmentsQuery.fetcher = (
  variables: Types.SiteAttachmentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteAttachmentsQuery, Types.SiteAttachmentsQueryVariables>(
    SiteAttachmentsDocument,
    variables,
    options,
  )

export const AttachmentAsCsvDocument = `
    query AttachmentAsCsv($sitePid: String!, $attachmentId: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      attachment(id: $attachmentId) {
        ...AttachmentFragment
        csvPeek
      }
    }
  }
}
    ${AttachmentFragmentFragmentDoc}
${UserRefFragmentDoc}`

export const useAttachmentAsCsvQuery = <
  TData = Types.AttachmentAsCsvQuery,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AttachmentAsCsvQuery, TError, TData>({
    queryKey: ["AttachmentAsCsv", variables],
    queryFn: publicFetcher<
      Types.AttachmentAsCsvQuery,
      Types.AttachmentAsCsvQueryVariables
    >(AttachmentAsCsvDocument, variables),
    ...options,
  })
}

useAttachmentAsCsvQuery.getKey = (variables: Types.AttachmentAsCsvQueryVariables) => [
  "AttachmentAsCsv",
  variables,
]

useAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv"

export const useSuspenseAttachmentAsCsvQuery = <
  TData = Types.AttachmentAsCsvQuery,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AttachmentAsCsvQuery, TError, TData>({
    queryKey: ["AttachmentAsCsvSuspense", variables],
    queryFn: publicFetcher<
      Types.AttachmentAsCsvQuery,
      Types.AttachmentAsCsvQueryVariables
    >(AttachmentAsCsvDocument, variables),
    ...options,
  })
}

useSuspenseAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsvSuspense", variables]

useSuspenseAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv"

export const useInfiniteAttachmentAsCsvQuery = <
  TData = InfiniteData<Types.AttachmentAsCsvQuery>,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AttachmentAsCsvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AttachmentAsCsv.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AttachmentAsCsvQuery,
            Types.AttachmentAsCsvQueryVariables
          >(AttachmentAsCsvDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsv.infinite", variables]

useInfiniteAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv.infinite"

export const useSuspenseInfiniteAttachmentAsCsvQuery = <
  TData = InfiniteData<Types.AttachmentAsCsvQuery>,
  TError = unknown,
>(
  variables: Types.AttachmentAsCsvQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AttachmentAsCsvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AttachmentAsCsvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AttachmentAsCsvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AttachmentAsCsv.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AttachmentAsCsvQuery,
            Types.AttachmentAsCsvQueryVariables
          >(AttachmentAsCsvDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAttachmentAsCsvQuery.getKey = (
  variables: Types.AttachmentAsCsvQueryVariables,
) => ["AttachmentAsCsv.infiniteSuspense", variables]

useSuspenseInfiniteAttachmentAsCsvQuery.rootKey = "AttachmentAsCsv.infinite"

useAttachmentAsCsvQuery.fetcher = (
  variables: Types.AttachmentAsCsvQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AttachmentAsCsvQuery, Types.AttachmentAsCsvQueryVariables>(
    AttachmentAsCsvDocument,
    variables,
    options,
  )

export const CleanMetersDocument = `
    query CleanMeters($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      cleanMeters {
        all {
          id
          dateCreated
          source
          label
          address
          dirtyIntervals {
            units
            totalKwh
            rollup {
              season
              days
              numbers
            }
          }
        }
      }
    }
  }
}
    `

export const useCleanMetersQuery = <TData = Types.CleanMetersQuery, TError = unknown>(
  variables: Types.CleanMetersQueryVariables,
  options?: Omit<UseQueryOptions<Types.CleanMetersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CleanMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CleanMetersQuery, TError, TData>({
    queryKey: ["CleanMeters", variables],
    queryFn: publicFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
      CleanMetersDocument,
      variables,
    ),
    ...options,
  })
}

useCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMeters",
  variables,
]

useCleanMetersQuery.rootKey = "CleanMeters"

export const useSuspenseCleanMetersQuery = <
  TData = Types.CleanMetersQuery,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CleanMetersQuery, TError, TData>({
    queryKey: ["CleanMetersSuspense", variables],
    queryFn: publicFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
      CleanMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMetersSuspense",
  variables,
]

useSuspenseCleanMetersQuery.rootKey = "CleanMeters"

export const useInfiniteCleanMetersQuery = <
  TData = InfiniteData<Types.CleanMetersQuery>,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CleanMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CleanMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
            CleanMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCleanMetersQuery.getKey = (variables: Types.CleanMetersQueryVariables) => [
  "CleanMeters.infinite",
  variables,
]

useInfiniteCleanMetersQuery.rootKey = "CleanMeters.infinite"

export const useSuspenseInfiniteCleanMetersQuery = <
  TData = InfiniteData<Types.CleanMetersQuery>,
  TError = unknown,
>(
  variables: Types.CleanMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CleanMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CleanMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CleanMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CleanMeters.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
            CleanMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCleanMetersQuery.getKey = (
  variables: Types.CleanMetersQueryVariables,
) => ["CleanMeters.infiniteSuspense", variables]

useSuspenseInfiniteCleanMetersQuery.rootKey = "CleanMeters.infinite"

useCleanMetersQuery.fetcher = (
  variables: Types.CleanMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CleanMetersQuery, Types.CleanMetersQueryVariables>(
    CleanMetersDocument,
    variables,
    options,
  )

export const SiteStatusCheckDocument = `
    query SiteStatusCheck($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      statusCheck {
        desc
        hint
        status
      }
    }
  }
}
    `

export const useSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheck", variables],
    queryFn: publicFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSiteStatusCheckQuery.getKey = (variables: Types.SiteStatusCheckQueryVariables) => [
  "SiteStatusCheck",
  variables,
]

useSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useSuspenseSiteStatusCheckQuery = <
  TData = Types.SiteStatusCheckQuery,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteStatusCheckQuery, TError, TData>({
    queryKey: ["SiteStatusCheckSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteStatusCheckQuery,
      Types.SiteStatusCheckQueryVariables
    >(SiteStatusCheckDocument, variables),
    ...options,
  })
}

useSuspenseSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheckSuspense", variables]

useSuspenseSiteStatusCheckQuery.rootKey = "SiteStatusCheck"

export const useInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteStatusCheckQuery,
            Types.SiteStatusCheckQueryVariables
          >(SiteStatusCheckDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infinite", variables]

useInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

export const useSuspenseInfiniteSiteStatusCheckQuery = <
  TData = InfiniteData<Types.SiteStatusCheckQuery>,
  TError = unknown,
>(
  variables: Types.SiteStatusCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteStatusCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteStatusCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteStatusCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteStatusCheck.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteStatusCheckQuery,
            Types.SiteStatusCheckQueryVariables
          >(SiteStatusCheckDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteStatusCheckQuery.getKey = (
  variables: Types.SiteStatusCheckQueryVariables,
) => ["SiteStatusCheck.infiniteSuspense", variables]

useSuspenseInfiniteSiteStatusCheckQuery.rootKey = "SiteStatusCheck.infinite"

useSiteStatusCheckQuery.fetcher = (
  variables: Types.SiteStatusCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteStatusCheckQuery, Types.SiteStatusCheckQueryVariables>(
    SiteStatusCheckDocument,
    variables,
    options,
  )

export const FailuresDocument = `
    query Failures($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      failures {
        __typename
        ... on FinmodelFailure {
          id
          createdAt
          name
          message
          scenario {
            ...ScenarioRef
          }
        }
        ... on GuessTariffFailure {
          createdAt
          name
          message
        }
        ... on BuildingFootprintFailure {
          createdAt
          name
          message
        }
        ... on LoadEstimateFailure {
          createdAt: dateCreated
          errorClass
          errorMessage
        }
        ... on ParcelDataFailure {
          createdAt
          name
          message
        }
      }
    }
  }
}
    ${ScenarioRefFragmentDoc}`

export const useFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<UseQueryOptions<Types.FailuresQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["Failures", variables],
    queryFn: publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures",
  variables,
]

useFailuresQuery.rootKey = "Failures"

export const useSuspenseFailuresQuery = <TData = Types.FailuresQuery, TError = unknown>(
  variables: Types.FailuresQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FailuresQuery, TError, TData>({
    queryKey: ["FailuresSuspense", variables],
    queryFn: publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
      FailuresDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "FailuresSuspense",
  variables,
]

useSuspenseFailuresQuery.rootKey = "Failures"

export const useInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infinite",
  variables,
]

useInfiniteFailuresQuery.rootKey = "Failures.infinite"

export const useSuspenseInfiniteFailuresQuery = <
  TData = InfiniteData<Types.FailuresQuery>,
  TError = unknown,
>(
  variables: Types.FailuresQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FailuresQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FailuresQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FailuresQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Failures.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
            FailuresDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFailuresQuery.getKey = (variables: Types.FailuresQueryVariables) => [
  "Failures.infiniteSuspense",
  variables,
]

useSuspenseInfiniteFailuresQuery.rootKey = "Failures.infinite"

useFailuresQuery.fetcher = (
  variables: Types.FailuresQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FailuresQuery, Types.FailuresQueryVariables>(
    FailuresDocument,
    variables,
    options,
  )

export const BillDocumentsDocument = `
    query BillDocuments($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      targetUtility {
        id
        name
        shortName
      }
      documentsV0 {
        id
        pid
        fileName
        dateCreated
        ownerEmail
        contentType
        tag
        downloadHref
        viewHref
      }
      billSuggestions {
        id
        document {
          pid
        }
      }
    }
  }
}
    `

export const useBillDocumentsQuery = <
  TData = Types.BillDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BillDocumentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BillDocumentsQuery, TError, TData>({
    queryKey: ["BillDocuments", variables],
    queryFn: publicFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
      BillDocumentsDocument,
      variables,
    ),
    ...options,
  })
}

useBillDocumentsQuery.getKey = (variables: Types.BillDocumentsQueryVariables) => [
  "BillDocuments",
  variables,
]

useBillDocumentsQuery.rootKey = "BillDocuments"

export const useSuspenseBillDocumentsQuery = <
  TData = Types.BillDocumentsQuery,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BillDocumentsQuery, TError, TData>({
    queryKey: ["BillDocumentsSuspense", variables],
    queryFn: publicFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
      BillDocumentsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocumentsSuspense", variables]

useSuspenseBillDocumentsQuery.rootKey = "BillDocuments"

export const useInfiniteBillDocumentsQuery = <
  TData = InfiniteData<Types.BillDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BillDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BillDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
            BillDocumentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocuments.infinite", variables]

useInfiniteBillDocumentsQuery.rootKey = "BillDocuments.infinite"

export const useSuspenseInfiniteBillDocumentsQuery = <
  TData = InfiniteData<Types.BillDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.BillDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BillDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BillDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BillDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BillDocuments.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
            BillDocumentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBillDocumentsQuery.getKey = (
  variables: Types.BillDocumentsQueryVariables,
) => ["BillDocuments.infiniteSuspense", variables]

useSuspenseInfiniteBillDocumentsQuery.rootKey = "BillDocuments.infinite"

useBillDocumentsQuery.fetcher = (
  variables: Types.BillDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BillDocumentsQuery, Types.BillDocumentsQueryVariables>(
    BillDocumentsDocument,
    variables,
    options,
  )

export const OrgsDocument = `
    query Orgs {
  admin {
    orgs {
      pid
      name
    }
  }
}
    `

export const useOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["Orgs"] : ["Orgs", variables],
    queryFn: publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs"] : ["Orgs", variables]

useOrgsQuery.rootKey = "Orgs"

export const useSuspenseOrgsQuery = <TData = Types.OrgsQuery, TError = unknown>(
  variables?: Types.OrgsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsQuery, TError, TData>({
    queryKey: variables === undefined ? ["OrgsSuspense"] : ["OrgsSuspense", variables],
    queryFn: publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
      OrgsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["OrgsSuspense"] : ["OrgsSuspense", variables]

useSuspenseOrgsQuery.rootKey = "Orgs"

export const useInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infinite"]
            : ["Orgs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined ? ["Orgs.infinite"] : ["Orgs.infinite", variables]

useInfiniteOrgsQuery.rootKey = "Orgs.infinite"

export const useSuspenseInfiniteOrgsQuery = <
  TData = InfiniteData<Types.OrgsQuery>,
  TError = unknown,
>(
  variables: Types.OrgsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["Orgs.infiniteSuspense"]
            : ["Orgs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(OrgsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsQuery.getKey = (variables?: Types.OrgsQueryVariables) =>
  variables === undefined
    ? ["Orgs.infiniteSuspense"]
    : ["Orgs.infiniteSuspense", variables]

useSuspenseInfiniteOrgsQuery.rootKey = "Orgs.infinite"

useOrgsQuery.fetcher = (
  variables?: Types.OrgsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsQuery, Types.OrgsQueryVariables>(
    OrgsDocument,
    variables,
    options,
  )

export const OrgsDetailListDocument = `
    query OrgsDetailList {
  admin {
    orgs {
      ...BasicOrgDetails
    }
    financierOrgs {
      ...BasicOrgDetails
    }
  }
}
    ${BasicOrgDetailsFragmentDoc}`

export const useOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgsDetailListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables],
    queryFn: publicFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useOrgsDetailListQuery.getKey = (variables?: Types.OrgsDetailListQueryVariables) =>
  variables === undefined ? ["OrgsDetailList"] : ["OrgsDetailList", variables]

useOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useSuspenseOrgsDetailListQuery = <
  TData = Types.OrgsDetailListQuery,
  TError = unknown,
>(
  variables?: Types.OrgsDetailListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgsDetailListQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgsDetailListSuspense"]
        : ["OrgsDetailListSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgsDetailListQuery,
      Types.OrgsDetailListQueryVariables
    >(OrgsDetailListDocument, variables),
    ...options,
  })
}

useSuspenseOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailListSuspense"]
    : ["OrgsDetailListSuspense", variables]

useSuspenseOrgsDetailListQuery.rootKey = "OrgsDetailList"

export const useInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infinite"]
            : ["OrgsDetailList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infinite"]
    : ["OrgsDetailList.infinite", variables]

useInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

export const useSuspenseInfiniteOrgsDetailListQuery = <
  TData = InfiniteData<Types.OrgsDetailListQuery>,
  TError = unknown,
>(
  variables: Types.OrgsDetailListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgsDetailListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgsDetailListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgsDetailListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgsDetailList.infiniteSuspense"]
            : ["OrgsDetailList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
            OrgsDetailListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgsDetailListQuery.getKey = (
  variables?: Types.OrgsDetailListQueryVariables,
) =>
  variables === undefined
    ? ["OrgsDetailList.infiniteSuspense"]
    : ["OrgsDetailList.infiniteSuspense", variables]

useSuspenseInfiniteOrgsDetailListQuery.rootKey = "OrgsDetailList.infinite"

useOrgsDetailListQuery.fetcher = (
  variables?: Types.OrgsDetailListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgsDetailListQuery, Types.OrgsDetailListQueryVariables>(
    OrgsDetailListDocument,
    variables,
    options,
  )

export const SiteIraIncentiveDataDocument = `
    query SiteIraIncentiveData($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isLowIncomeCommunity
      isEnergyCommunity
    }
  }
}
    `

export const useSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveData", variables],
    queryFn: publicFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData", variables]

useSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useSuspenseSiteIraIncentiveDataQuery = <
  TData = Types.SiteIraIncentiveDataQuery,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteIraIncentiveDataQuery, TError, TData>({
    queryKey: ["SiteIraIncentiveDataSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteIraIncentiveDataQuery,
      Types.SiteIraIncentiveDataQueryVariables
    >(SiteIraIncentiveDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveDataSuspense", variables]

useSuspenseSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData"

export const useInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteIraIncentiveData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infinite", variables]

useInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

export const useSuspenseInfiniteSiteIraIncentiveDataQuery = <
  TData = InfiniteData<Types.SiteIraIncentiveDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteIraIncentiveDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteIraIncentiveDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteIraIncentiveDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteIraIncentiveData.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteIraIncentiveDataQuery,
            Types.SiteIraIncentiveDataQueryVariables
          >(SiteIraIncentiveDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteIraIncentiveDataQuery.getKey = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
) => ["SiteIraIncentiveData.infiniteSuspense", variables]

useSuspenseInfiniteSiteIraIncentiveDataQuery.rootKey = "SiteIraIncentiveData.infinite"

useSiteIraIncentiveDataQuery.fetcher = (
  variables: Types.SiteIraIncentiveDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteIraIncentiveDataQuery,
    Types.SiteIraIncentiveDataQueryVariables
  >(SiteIraIncentiveDataDocument, variables, options)

export const SiteLeaseTypeDocument = `
    query SiteLeaseType($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      leaseType
    }
  }
}
    `

export const useSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseType", variables],
    queryFn: publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLeaseTypeQuery.getKey = (variables: Types.SiteLeaseTypeQueryVariables) => [
  "SiteLeaseType",
  variables,
]

useSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useSuspenseSiteLeaseTypeQuery = <
  TData = Types.SiteLeaseTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLeaseTypeQuery, TError, TData>({
    queryKey: ["SiteLeaseTypeSuspense", variables],
    queryFn: publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
      SiteLeaseTypeDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseTypeSuspense", variables]

useSuspenseSiteLeaseTypeQuery.rootKey = "SiteLeaseType"

export const useInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infinite", variables]

useInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

export const useSuspenseInfiniteSiteLeaseTypeQuery = <
  TData = InfiniteData<Types.SiteLeaseTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteLeaseTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLeaseTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLeaseTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLeaseTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLeaseType.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
            SiteLeaseTypeDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLeaseTypeQuery.getKey = (
  variables: Types.SiteLeaseTypeQueryVariables,
) => ["SiteLeaseType.infiniteSuspense", variables]

useSuspenseInfiniteSiteLeaseTypeQuery.rootKey = "SiteLeaseType.infinite"

useSiteLeaseTypeQuery.fetcher = (
  variables: Types.SiteLeaseTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteLeaseTypeQuery, Types.SiteLeaseTypeQueryVariables>(
    SiteLeaseTypeDocument,
    variables,
    options,
  )

export const SiteOccupancyTypeDocument = `
    query SiteOccupancyType($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      occupancyType
    }
  }
}
    `

export const useSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyType", variables],
    queryFn: publicFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType", variables]

useSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useSuspenseSiteOccupancyTypeQuery = <
  TData = Types.SiteOccupancyTypeQuery,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteOccupancyTypeQuery, TError, TData>({
    queryKey: ["SiteOccupancyTypeSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteOccupancyTypeQuery,
      Types.SiteOccupancyTypeQueryVariables
    >(SiteOccupancyTypeDocument, variables),
    ...options,
  })
}

useSuspenseSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyTypeSuspense", variables]

useSuspenseSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType"

export const useInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infinite", variables]

useInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

export const useSuspenseInfiniteSiteOccupancyTypeQuery = <
  TData = InfiniteData<Types.SiteOccupancyTypeQuery>,
  TError = unknown,
>(
  variables: Types.SiteOccupancyTypeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteOccupancyTypeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteOccupancyTypeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteOccupancyTypeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteOccupancyType.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteOccupancyTypeQuery,
            Types.SiteOccupancyTypeQueryVariables
          >(SiteOccupancyTypeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteOccupancyTypeQuery.getKey = (
  variables: Types.SiteOccupancyTypeQueryVariables,
) => ["SiteOccupancyType.infiniteSuspense", variables]

useSuspenseInfiniteSiteOccupancyTypeQuery.rootKey = "SiteOccupancyType.infinite"

useSiteOccupancyTypeQuery.fetcher = (
  variables: Types.SiteOccupancyTypeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteOccupancyTypeQuery, Types.SiteOccupancyTypeQueryVariables>(
    SiteOccupancyTypeDocument,
    variables,
    options,
  )

export const AdminOrgDetailDocument = `
    query AdminOrgDetail($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      ...BasicOrgDetails
      portfolios {
        pid
        title
      }
      allowedEmailDomains
      labels {
        pid
        name
        themeColor
      }
      financing {
        ...FinRel
      }
      finproducts {
        __typename
        financier {
          pid
          name
        }
        id
        createdAt
        name
        type
        activeOverrides {
          ...Override
        }
        targetOrg {
          pid
          name
        }
        targetSite {
          pid
          name
          streetAddress
        }
        disabledAt
        includeStorage
        status
        csPrices {
          utility {
            name
            id
            shortName
          }
          state
          usdPerKwPerYear
          escalator
          termYears
          minPvKw
          maxPvKw
          pvType
        }
      }
    }
  }
}
    ${BasicOrgDetailsFragmentDoc}
${FinRelFragmentDoc}
${OverrideFragmentDoc}`

export const useAdminOrgDetailQuery = <
  TData = Types.AdminOrgDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgDetailQuery, TError, TData>({
    queryKey: ["AdminOrgDetail", variables],
    queryFn: publicFetcher<
      Types.AdminOrgDetailQuery,
      Types.AdminOrgDetailQueryVariables
    >(AdminOrgDetailDocument, variables),
    ...options,
  })
}

useAdminOrgDetailQuery.getKey = (variables: Types.AdminOrgDetailQueryVariables) => [
  "AdminOrgDetail",
  variables,
]

useAdminOrgDetailQuery.rootKey = "AdminOrgDetail"

export const useSuspenseAdminOrgDetailQuery = <
  TData = Types.AdminOrgDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgDetailQuery, TError, TData>({
    queryKey: ["AdminOrgDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminOrgDetailQuery,
      Types.AdminOrgDetailQueryVariables
    >(AdminOrgDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetailSuspense", variables]

useSuspenseAdminOrgDetailQuery.rootKey = "AdminOrgDetail"

export const useInfiniteAdminOrgDetailQuery = <
  TData = InfiniteData<Types.AdminOrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
            AdminOrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetail.infinite", variables]

useInfiniteAdminOrgDetailQuery.rootKey = "AdminOrgDetail.infinite"

export const useSuspenseInfiniteAdminOrgDetailQuery = <
  TData = InfiniteData<Types.AdminOrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
            AdminOrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgDetailQuery.getKey = (
  variables: Types.AdminOrgDetailQueryVariables,
) => ["AdminOrgDetail.infiniteSuspense", variables]

useSuspenseInfiniteAdminOrgDetailQuery.rootKey = "AdminOrgDetail.infinite"

useAdminOrgDetailQuery.fetcher = (
  variables: Types.AdminOrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgDetailQuery, Types.AdminOrgDetailQueryVariables>(
    AdminOrgDetailDocument,
    variables,
    options,
  )

export const AdminOrgUsersDocument = `
    query AdminOrgUsers($orgPid: String!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      name
      logoUrl
      logomarkUrl
      users {
        ...UserListUser
      }
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useAdminOrgUsersQuery = <
  TData = Types.AdminOrgUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminOrgUsersQuery, TError, TData>({
    queryKey: ["AdminOrgUsers", variables],
    queryFn: publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
      AdminOrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useAdminOrgUsersQuery.getKey = (variables: Types.AdminOrgUsersQueryVariables) => [
  "AdminOrgUsers",
  variables,
]

useAdminOrgUsersQuery.rootKey = "AdminOrgUsers"

export const useSuspenseAdminOrgUsersQuery = <
  TData = Types.AdminOrgUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminOrgUsersQuery, TError, TData>({
    queryKey: ["AdminOrgUsersSuspense", variables],
    queryFn: publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
      AdminOrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsersSuspense", variables]

useSuspenseAdminOrgUsersQuery.rootKey = "AdminOrgUsers"

export const useInfiniteAdminOrgUsersQuery = <
  TData = InfiniteData<Types.AdminOrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminOrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
            AdminOrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsers.infinite", variables]

useInfiniteAdminOrgUsersQuery.rootKey = "AdminOrgUsers.infinite"

export const useSuspenseInfiniteAdminOrgUsersQuery = <
  TData = InfiniteData<Types.AdminOrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminOrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminOrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminOrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminOrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminOrgUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
            AdminOrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminOrgUsersQuery.getKey = (
  variables: Types.AdminOrgUsersQueryVariables,
) => ["AdminOrgUsers.infiniteSuspense", variables]

useSuspenseInfiniteAdminOrgUsersQuery.rootKey = "AdminOrgUsers.infinite"

useAdminOrgUsersQuery.fetcher = (
  variables: Types.AdminOrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminOrgUsersQuery, Types.AdminOrgUsersQueryVariables>(
    AdminOrgUsersDocument,
    variables,
    options,
  )

export const SitePoliciesDocument = `
    query SitePolicies($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      state
      targetUtility {
        id
        name
        shortName
      }
      policies {
        ...PolicyListItem
      }
    }
  }
}
    ${PolicyListItemFragmentDoc}
${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const useSitePoliciesQuery = <TData = Types.SitePoliciesQuery, TError = unknown>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SitePoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePolicies", variables],
    queryFn: publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies",
  variables,
]

useSitePoliciesQuery.rootKey = "SitePolicies"

export const useSuspenseSitePoliciesQuery = <
  TData = Types.SitePoliciesQuery,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePoliciesQuery, TError, TData>({
    queryKey: ["SitePoliciesSuspense", variables],
    queryFn: publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
      SitePoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePoliciesSuspense",
  variables,
]

useSuspenseSitePoliciesQuery.rootKey = "SitePolicies"

export const useInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePoliciesQuery.getKey = (variables: Types.SitePoliciesQueryVariables) => [
  "SitePolicies.infinite",
  variables,
]

useInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

export const useSuspenseInfiniteSitePoliciesQuery = <
  TData = InfiniteData<Types.SitePoliciesQuery>,
  TError = unknown,
>(
  variables: Types.SitePoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicies.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
            SitePoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePoliciesQuery.getKey = (
  variables: Types.SitePoliciesQueryVariables,
) => ["SitePolicies.infiniteSuspense", variables]

useSuspenseInfiniteSitePoliciesQuery.rootKey = "SitePolicies.infinite"

useSitePoliciesQuery.fetcher = (
  variables: Types.SitePoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SitePoliciesQuery, Types.SitePoliciesQueryVariables>(
    SitePoliciesDocument,
    variables,
    options,
  )

export const AllPoliciesDocument = `
    query AllPolicies {
  admin {
    policies {
      all {
        ...PolicyListItem
      }
    }
  }
}
    ${PolicyListItemFragmentDoc}
${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const useAllPoliciesQuery = <TData = Types.AllPoliciesQuery, TError = unknown>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AllPoliciesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AllPoliciesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey: variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables],
    queryFn: publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPolicies"] : ["AllPolicies", variables]

useAllPoliciesQuery.rootKey = "AllPolicies"

export const useSuspenseAllPoliciesQuery = <
  TData = Types.AllPoliciesQuery,
  TError = unknown,
>(
  variables?: Types.AllPoliciesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AllPoliciesQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AllPoliciesSuspense"]
        : ["AllPoliciesSuspense", variables],
    queryFn: publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
      AllPoliciesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined ? ["AllPoliciesSuspense"] : ["AllPoliciesSuspense", variables]

useSuspenseAllPoliciesQuery.rootKey = "AllPolicies"

export const useInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infinite"]
            : ["AllPolicies.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAllPoliciesQuery.getKey = (variables?: Types.AllPoliciesQueryVariables) =>
  variables === undefined
    ? ["AllPolicies.infinite"]
    : ["AllPolicies.infinite", variables]

useInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

export const useSuspenseInfiniteAllPoliciesQuery = <
  TData = InfiniteData<Types.AllPoliciesQuery>,
  TError = unknown,
>(
  variables: Types.AllPoliciesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AllPoliciesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AllPoliciesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AllPoliciesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllPolicies.infiniteSuspense"]
            : ["AllPolicies.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
            AllPoliciesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAllPoliciesQuery.getKey = (
  variables?: Types.AllPoliciesQueryVariables,
) =>
  variables === undefined
    ? ["AllPolicies.infiniteSuspense"]
    : ["AllPolicies.infiniteSuspense", variables]

useSuspenseInfiniteAllPoliciesQuery.rootKey = "AllPolicies.infinite"

useAllPoliciesQuery.fetcher = (
  variables?: Types.AllPoliciesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AllPoliciesQuery, Types.AllPoliciesQueryVariables>(
    AllPoliciesDocument,
    variables,
    options,
  )

export const PolicyDetailDocument = `
    query PolicyDetail($policyPid: String!) {
  admin {
    policies {
      policy(policyPid: $policyPid) {
        pid
        name
        userVisible
        description
        publicDescription
        status
        startDate
        endDate
        application {
          status
        }
        jurisdiction {
          scope
          name
        }
        rules {
          ...Rule
        }
        conditions {
          ...PolicyCondition
        }
        sources {
          text
          url
        }
      }
    }
  }
}
    ${RuleFragmentDoc}
${RuleValueFragmentDoc}
${PolicyConditionFragmentDoc}`

export const usePolicyDetailQuery = <TData = Types.PolicyDetailQuery, TError = unknown>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PolicyDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetail", variables],
    queryFn: publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

usePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail",
  variables,
]

usePolicyDetailQuery.rootKey = "PolicyDetail"

export const useSuspensePolicyDetailQuery = <
  TData = Types.PolicyDetailQuery,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PolicyDetailQuery, TError, TData>({
    queryKey: ["PolicyDetailSuspense", variables],
    queryFn: publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
      PolicyDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspensePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetailSuspense",
  variables,
]

useSuspensePolicyDetailQuery.rootKey = "PolicyDetail"

export const useInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePolicyDetailQuery.getKey = (variables: Types.PolicyDetailQueryVariables) => [
  "PolicyDetail.infinite",
  variables,
]

useInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

export const useSuspenseInfinitePolicyDetailQuery = <
  TData = InfiniteData<Types.PolicyDetailQuery>,
  TError = unknown,
>(
  variables: Types.PolicyDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PolicyDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PolicyDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PolicyDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PolicyDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
            PolicyDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePolicyDetailQuery.getKey = (
  variables: Types.PolicyDetailQueryVariables,
) => ["PolicyDetail.infiniteSuspense", variables]

useSuspenseInfinitePolicyDetailQuery.rootKey = "PolicyDetail.infinite"

usePolicyDetailQuery.fetcher = (
  variables: Types.PolicyDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PolicyDetailQuery, Types.PolicyDetailQueryVariables>(
    PolicyDetailDocument,
    variables,
    options,
  )

export const SitePolicyViolationsDocument = `
    query SitePolicyViolations($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      policyViolations {
        policy {
          pid
          name
        }
        violatedRule {
          id
          description
          units
          value {
            ...RuleValue
          }
        }
        value {
          ...RuleValue
        }
      }
    }
  }
}
    ${RuleValueFragmentDoc}`

export const useSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolations", variables],
    queryFn: publicFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations", variables]

useSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useSuspenseSitePolicyViolationsQuery = <
  TData = Types.SitePolicyViolationsQuery,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SitePolicyViolationsQuery, TError, TData>({
    queryKey: ["SitePolicyViolationsSuspense", variables],
    queryFn: publicFetcher<
      Types.SitePolicyViolationsQuery,
      Types.SitePolicyViolationsQueryVariables
    >(SitePolicyViolationsDocument, variables),
    ...options,
  })
}

useSuspenseSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolationsSuspense", variables]

useSuspenseSitePolicyViolationsQuery.rootKey = "SitePolicyViolations"

export const useInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SitePolicyViolations.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infinite", variables]

useInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

export const useSuspenseInfiniteSitePolicyViolationsQuery = <
  TData = InfiniteData<Types.SitePolicyViolationsQuery>,
  TError = unknown,
>(
  variables: Types.SitePolicyViolationsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SitePolicyViolationsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SitePolicyViolationsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SitePolicyViolationsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SitePolicyViolations.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SitePolicyViolationsQuery,
            Types.SitePolicyViolationsQueryVariables
          >(SitePolicyViolationsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSitePolicyViolationsQuery.getKey = (
  variables: Types.SitePolicyViolationsQueryVariables,
) => ["SitePolicyViolations.infiniteSuspense", variables]

useSuspenseInfiniteSitePolicyViolationsQuery.rootKey = "SitePolicyViolations.infinite"

useSitePolicyViolationsQuery.fetcher = (
  variables: Types.SitePolicyViolationsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SitePolicyViolationsQuery,
    Types.SitePolicyViolationsQueryVariables
  >(SitePolicyViolationsDocument, variables, options)

export const MultiMeterDocument = `
    query MultiMeter($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      usingBillingMeters
      orgSlug
      bauMeters {
        ...BauMeter
        hasTruth
      }
      activeMeters {
        id
        createdAt
        createdBy {
          pid
          email
        }
        meters {
          ...BauMeter
        }
      }
      thirdPartySupplier {
        id
        name
      }
      targetUtility {
        id
        name
        shortName
      }
      targetUtilitySetBy {
        ...UserRef
      }
      dateTargetUtilitySet
      utilityName
      probableUtilities {
        id
        name
      }
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${UserRefFragmentDoc}`

export const useMultiMeterQuery = <TData = Types.MultiMeterQuery, TError = unknown>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<UseQueryOptions<Types.MultiMeterQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeter", variables],
    queryFn: publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter",
  variables,
]

useMultiMeterQuery.rootKey = "MultiMeter"

export const useSuspenseMultiMeterQuery = <
  TData = Types.MultiMeterQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterQuery, TError, TData>({
    queryKey: ["MultiMeterSuspense", variables],
    queryFn: publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
      MultiMeterDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeterSuspense",
  variables,
]

useSuspenseMultiMeterQuery.rootKey = "MultiMeter"

export const useInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterQuery.getKey = (variables: Types.MultiMeterQueryVariables) => [
  "MultiMeter.infinite",
  variables,
]

useInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

export const useSuspenseInfiniteMultiMeterQuery = <
  TData = InfiniteData<Types.MultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeter.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
            MultiMeterDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterQuery.getKey = (
  variables: Types.MultiMeterQueryVariables,
) => ["MultiMeter.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterQuery.rootKey = "MultiMeter.infinite"

useMultiMeterQuery.fetcher = (
  variables: Types.MultiMeterQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MultiMeterQuery, Types.MultiMeterQueryVariables>(
    MultiMeterDocument,
    variables,
    options,
  )

export const BauMeterIntervalsDocument = `
    query BauMeterIntervals($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      bauMeters {
        ...BauMeterIntervals
      }
    }
  }
}
    ${BauMeterIntervalsFragmentDoc}`

export const useBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervals", variables],
    queryFn: publicFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals", variables]

useBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useSuspenseBauMeterIntervalsQuery = <
  TData = Types.BauMeterIntervalsQuery,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BauMeterIntervalsQuery, TError, TData>({
    queryKey: ["BauMeterIntervalsSuspense", variables],
    queryFn: publicFetcher<
      Types.BauMeterIntervalsQuery,
      Types.BauMeterIntervalsQueryVariables
    >(BauMeterIntervalsDocument, variables),
    ...options,
  })
}

useSuspenseBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervalsSuspense", variables]

useSuspenseBauMeterIntervalsQuery.rootKey = "BauMeterIntervals"

export const useInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infinite", variables]

useInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

export const useSuspenseInfiniteBauMeterIntervalsQuery = <
  TData = InfiniteData<Types.BauMeterIntervalsQuery>,
  TError = unknown,
>(
  variables: Types.BauMeterIntervalsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BauMeterIntervalsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BauMeterIntervalsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BauMeterIntervalsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BauMeterIntervals.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BauMeterIntervalsQuery,
            Types.BauMeterIntervalsQueryVariables
          >(BauMeterIntervalsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBauMeterIntervalsQuery.getKey = (
  variables: Types.BauMeterIntervalsQueryVariables,
) => ["BauMeterIntervals.infiniteSuspense", variables]

useSuspenseInfiniteBauMeterIntervalsQuery.rootKey = "BauMeterIntervals.infinite"

useBauMeterIntervalsQuery.fetcher = (
  variables: Types.BauMeterIntervalsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BauMeterIntervalsQuery, Types.BauMeterIntervalsQueryVariables>(
    BauMeterIntervalsDocument,
    variables,
    options,
  )

export const MeterSetDocument = `
    query MeterSet($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      pid
      isMultifamily
      meterSet(id: $id) {
        id
        meters {
          hasTruth
          ...BauMeter
          ...BauMeterIntervals
        }
      }
      customTariffs {
        id
        name
        code
        parent {
          id
        }
      }
    }
  }
}
    ${BauMeterFragmentDoc}
${BauTariffFragmentDoc}
${BauMeterIntervalsFragmentDoc}`

export const useMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<UseQueryOptions<Types.MeterSetQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSet", variables],
    queryFn: publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet",
  variables,
]

useMeterSetQuery.rootKey = "MeterSet"

export const useSuspenseMeterSetQuery = <TData = Types.MeterSetQuery, TError = unknown>(
  variables: Types.MeterSetQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MeterSetQuery, TError, TData>({
    queryKey: ["MeterSetSuspense", variables],
    queryFn: publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
      MeterSetDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSetSuspense",
  variables,
]

useSuspenseMeterSetQuery.rootKey = "MeterSet"

export const useInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infinite",
  variables,
]

useInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

export const useSuspenseInfiniteMeterSetQuery = <
  TData = InfiniteData<Types.MeterSetQuery>,
  TError = unknown,
>(
  variables: Types.MeterSetQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MeterSetQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MeterSetQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MeterSetQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MeterSet.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
            MeterSetDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMeterSetQuery.getKey = (variables: Types.MeterSetQueryVariables) => [
  "MeterSet.infiniteSuspense",
  variables,
]

useSuspenseInfiniteMeterSetQuery.rootKey = "MeterSet.infinite"

useMeterSetQuery.fetcher = (
  variables: Types.MeterSetQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MeterSetQuery, Types.MeterSetQueryVariables>(
    MeterSetDocument,
    variables,
    options,
  )

export const AdminNotesDocument = `
    query AdminNotes($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      notes {
        pid
        text
        createdAt
        createdBy {
          ...UserRef
        }
        updatedAt
        updatedBy {
          ...UserRef
        }
        trashedAt
        trashedBy {
          ...UserRef
        }
        hiddenFromCustomers
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useAdminNotesQuery = <TData = Types.AdminNotesQuery, TError = unknown>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminNotesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotes", variables],
    queryFn: publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes",
  variables,
]

useAdminNotesQuery.rootKey = "AdminNotes"

export const useSuspenseAdminNotesQuery = <
  TData = Types.AdminNotesQuery,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminNotesQuery, TError, TData>({
    queryKey: ["AdminNotesSuspense", variables],
    queryFn: publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
      AdminNotesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotesSuspense",
  variables,
]

useSuspenseAdminNotesQuery.rootKey = "AdminNotes"

export const useInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminNotesQuery.getKey = (variables: Types.AdminNotesQueryVariables) => [
  "AdminNotes.infinite",
  variables,
]

useInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

export const useSuspenseInfiniteAdminNotesQuery = <
  TData = InfiniteData<Types.AdminNotesQuery>,
  TError = unknown,
>(
  variables: Types.AdminNotesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminNotesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminNotesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminNotesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminNotes.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
            AdminNotesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminNotesQuery.getKey = (
  variables: Types.AdminNotesQueryVariables,
) => ["AdminNotes.infiniteSuspense", variables]

useSuspenseInfiniteAdminNotesQuery.rootKey = "AdminNotes.infinite"

useAdminNotesQuery.fetcher = (
  variables: Types.AdminNotesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminNotesQuery, Types.AdminNotesQueryVariables>(
    AdminNotesDocument,
    variables,
    options,
  )

export const BatchPhysicalCheckDocument = `
    query BatchPhysicalCheck($orgPid: String!, $propertyPids: [String!]!, $siteFields: [SiteChangelogColumn!]!, $buildingDataFields: [BuildingDataChangelogColumn!]!) {
  admin {
    org(orgPid: $orgPid) {
      pid
      name
    }
    sites(sitePids: $propertyPids) {
      pid
      portfolio {
        name
      }
      changelog(fields: $siteFields) {
        all {
          column
          obj {
            ...Changelog
          }
        }
      }
      buildingData {
        changelog(fields: $buildingDataFields) {
          all {
            column
            obj {
              ...Changelog
            }
          }
        }
      }
      notes {
        pid
        text
        createdAt
        createdBy {
          ...UserRef
        }
        updatedAt
        updatedBy {
          ...UserRef
        }
      }
      currentStatus {
        pid
        status
      }
      slopedRoof
      propertyName
      numberOfBuildings
      sqft
      numStories
      fullAddress
      roofMaxPvKw
      computedRoofMaxPvKw
      explainRoofHeuristic
      explainSlopedHeuristic
      computedCarportMaxPvKw
      explainCarportHeuristic
      explainCarportParkingSpaceHeuristic
      computedGroundMaxPvKw
      explainGroundHeuristic
      impliedCarportLinearFt
      impliedUsableRoofSqft
      impliedUsableRoofSqftExplanation
      roofSqft
      usableRoofPct
      usableRoofSqft
      setbackRoofSqft
      roofKeepoutsSqft
      groundAvailableSqft
      groundMaxPvKw
      usableCarportFt
      numParkingSpaces
      carportMaxPvKw
      existingSolarDetectionScore
      physicalCheck {
        existingSolar
      }
      footprint {
        latitude
        longitude
        roofShape
        roofShapeGeojson
        roofSetbackShape
        roofSetbackShapeGeojson
        keepoutsShape
        keepoutsShapeGeojson
        parcelShape
        parcelShapeGeojson
        carportShape
        carportShapeGeojson
        groundmountShape
        groundmountShapeGeojson
        batteryPadShape
        batteryPadShapeGeojson
        bounds
      }
    }
  }
}
    ${ChangelogFragmentDoc}
${UserRefFragmentDoc}`

export const useBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheck", variables],
    queryFn: publicFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck", variables]

useBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useSuspenseBatchPhysicalCheckQuery = <
  TData = Types.BatchPhysicalCheckQuery,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BatchPhysicalCheckQuery, TError, TData>({
    queryKey: ["BatchPhysicalCheckSuspense", variables],
    queryFn: publicFetcher<
      Types.BatchPhysicalCheckQuery,
      Types.BatchPhysicalCheckQueryVariables
    >(BatchPhysicalCheckDocument, variables),
    ...options,
  })
}

useSuspenseBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheckSuspense", variables]

useSuspenseBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck"

export const useInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infinite", variables]

useInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

export const useSuspenseInfiniteBatchPhysicalCheckQuery = <
  TData = InfiniteData<Types.BatchPhysicalCheckQuery>,
  TError = unknown,
>(
  variables: Types.BatchPhysicalCheckQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BatchPhysicalCheckQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BatchPhysicalCheckQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BatchPhysicalCheckQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BatchPhysicalCheck.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatchPhysicalCheckQuery,
            Types.BatchPhysicalCheckQueryVariables
          >(BatchPhysicalCheckDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBatchPhysicalCheckQuery.getKey = (
  variables: Types.BatchPhysicalCheckQueryVariables,
) => ["BatchPhysicalCheck.infiniteSuspense", variables]

useSuspenseInfiniteBatchPhysicalCheckQuery.rootKey = "BatchPhysicalCheck.infinite"

useBatchPhysicalCheckQuery.fetcher = (
  variables: Types.BatchPhysicalCheckQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BatchPhysicalCheckQuery, Types.BatchPhysicalCheckQueryVariables>(
    BatchPhysicalCheckDocument,
    variables,
    options,
  )

export const ClosestParcelsDocument = `
    query ClosestParcels($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      closestSites {
        pid
        name
        fullAddress
        footprint {
          latitude
          longitude
          parcelShape
        }
      }
    }
  }
}
    `

export const useClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ClosestParcelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcels", variables],
    queryFn: publicFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useClosestParcelsQuery.getKey = (variables: Types.ClosestParcelsQueryVariables) => [
  "ClosestParcels",
  variables,
]

useClosestParcelsQuery.rootKey = "ClosestParcels"

export const useSuspenseClosestParcelsQuery = <
  TData = Types.ClosestParcelsQuery,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ClosestParcelsQuery, TError, TData>({
    queryKey: ["ClosestParcelsSuspense", variables],
    queryFn: publicFetcher<
      Types.ClosestParcelsQuery,
      Types.ClosestParcelsQueryVariables
    >(ClosestParcelsDocument, variables),
    ...options,
  })
}

useSuspenseClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcelsSuspense", variables]

useSuspenseClosestParcelsQuery.rootKey = "ClosestParcels"

export const useInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infinite", variables]

useInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

export const useSuspenseInfiniteClosestParcelsQuery = <
  TData = InfiniteData<Types.ClosestParcelsQuery>,
  TError = unknown,
>(
  variables: Types.ClosestParcelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ClosestParcelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ClosestParcelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ClosestParcelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ClosestParcels.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
            ClosestParcelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteClosestParcelsQuery.getKey = (
  variables: Types.ClosestParcelsQueryVariables,
) => ["ClosestParcels.infiniteSuspense", variables]

useSuspenseInfiniteClosestParcelsQuery.rootKey = "ClosestParcels.infinite"

useClosestParcelsQuery.fetcher = (
  variables: Types.ClosestParcelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ClosestParcelsQuery, Types.ClosestParcelsQueryVariables>(
    ClosestParcelsDocument,
    variables,
    options,
  )

export const GeocodedLocationDocument = `
    query GeocodedLocation($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      fullAddress
      footprint {
        geocodedLocation {
          latitude
          longitude
        }
      }
    }
  }
}
    `

export const useGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.GeocodedLocationQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocation", variables],
    queryFn: publicFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useGeocodedLocationQuery.getKey = (variables: Types.GeocodedLocationQueryVariables) => [
  "GeocodedLocation",
  variables,
]

useGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useSuspenseGeocodedLocationQuery = <
  TData = Types.GeocodedLocationQuery,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GeocodedLocationQuery, TError, TData>({
    queryKey: ["GeocodedLocationSuspense", variables],
    queryFn: publicFetcher<
      Types.GeocodedLocationQuery,
      Types.GeocodedLocationQueryVariables
    >(GeocodedLocationDocument, variables),
    ...options,
  })
}

useSuspenseGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocationSuspense", variables]

useSuspenseGeocodedLocationQuery.rootKey = "GeocodedLocation"

export const useInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infinite", variables]

useInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

export const useSuspenseInfiniteGeocodedLocationQuery = <
  TData = InfiniteData<Types.GeocodedLocationQuery>,
  TError = unknown,
>(
  variables: Types.GeocodedLocationQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GeocodedLocationQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GeocodedLocationQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GeocodedLocationQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GeocodedLocation.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.GeocodedLocationQuery,
            Types.GeocodedLocationQueryVariables
          >(GeocodedLocationDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGeocodedLocationQuery.getKey = (
  variables: Types.GeocodedLocationQueryVariables,
) => ["GeocodedLocation.infiniteSuspense", variables]

useSuspenseInfiniteGeocodedLocationQuery.rootKey = "GeocodedLocation.infinite"

useGeocodedLocationQuery.fetcher = (
  variables: Types.GeocodedLocationQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GeocodedLocationQuery, Types.GeocodedLocationQueryVariables>(
    GeocodedLocationDocument,
    variables,
    options,
  )

export const ActiveResultListDocument = `
    query ActiveResultList($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      activeResults {
        id
        pid
        createdAt
        scenario {
          pid
          name
          solarLocation
          loadCategory
          includeStorage
        }
        financing {
          id
          name
          type
          financierName
          financierPid
          financierLogo
          financierLogomark
          isThirdParty
          status
        }
        result {
          __typename
          ... on PhysicalCheck {
            createdAt
            roofIsGood
            carportIsGood
            existingSolar
            badRoof
            groundmountIsGood
          }
          ... on PolicyCheck {
            createdAt
            productType
            isGood
            disqualReason
          }
          ... on RevampFinmodelFailure {
            id
            createdAt
            fmvStr
            details {
              label
              description
            }
          }
          ... on RevampFinmodel {
            ...RevampFinmodel
            moreDetails {
              pvCostPerWatt
              storageCostPerKwh
              totalInstallCost
            }
          }
          ... on RevampCS {
            ...RevampCS
          }
          ... on Viability {
            isAllowed
            viabilityType: type
            reason
            level
          }
        }
        helioscope {
          ...Helioscope
        }
      }
    }
  }
}
    ${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveResultListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultList", variables],
    queryFn: publicFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useActiveResultListQuery.getKey = (variables: Types.ActiveResultListQueryVariables) => [
  "ActiveResultList",
  variables,
]

useActiveResultListQuery.rootKey = "ActiveResultList"

export const useSuspenseActiveResultListQuery = <
  TData = Types.ActiveResultListQuery,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveResultListQuery, TError, TData>({
    queryKey: ["ActiveResultListSuspense", variables],
    queryFn: publicFetcher<
      Types.ActiveResultListQuery,
      Types.ActiveResultListQueryVariables
    >(ActiveResultListDocument, variables),
    ...options,
  })
}

useSuspenseActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultListSuspense", variables]

useSuspenseActiveResultListQuery.rootKey = "ActiveResultList"

export const useInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infinite", variables]

useInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

export const useSuspenseInfiniteActiveResultListQuery = <
  TData = InfiniteData<Types.ActiveResultListQuery>,
  TError = unknown,
>(
  variables: Types.ActiveResultListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveResultListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveResultListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveResultListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ActiveResultList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ActiveResultListQuery,
            Types.ActiveResultListQueryVariables
          >(ActiveResultListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveResultListQuery.getKey = (
  variables: Types.ActiveResultListQueryVariables,
) => ["ActiveResultList.infiniteSuspense", variables]

useSuspenseInfiniteActiveResultListQuery.rootKey = "ActiveResultList.infinite"

useActiveResultListQuery.fetcher = (
  variables: Types.ActiveResultListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ActiveResultListQuery, Types.ActiveResultListQueryVariables>(
    ActiveResultListDocument,
    variables,
    options,
  )

export const AdminResultDetailDocument = `
    query AdminResultDetail($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      result(id: $id) {
        ...Result
        ...ResultWithEnergyData
      }
    }
  }
}
    ${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultWithEnergyDataFragmentDoc}`

export const useAdminResultDetailQuery = <
  TData = Types.AdminResultDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminResultDetailQuery, TError, TData>({
    queryKey: ["AdminResultDetail", variables],
    queryFn: publicFetcher<
      Types.AdminResultDetailQuery,
      Types.AdminResultDetailQueryVariables
    >(AdminResultDetailDocument, variables),
    ...options,
  })
}

useAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail", variables]

useAdminResultDetailQuery.rootKey = "AdminResultDetail"

export const useSuspenseAdminResultDetailQuery = <
  TData = Types.AdminResultDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminResultDetailQuery, TError, TData>({
    queryKey: ["AdminResultDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminResultDetailQuery,
      Types.AdminResultDetailQueryVariables
    >(AdminResultDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetailSuspense", variables]

useSuspenseAdminResultDetailQuery.rootKey = "AdminResultDetail"

export const useInfiniteAdminResultDetailQuery = <
  TData = InfiniteData<Types.AdminResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminResultDetailQuery,
            Types.AdminResultDetailQueryVariables
          >(AdminResultDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail.infinite", variables]

useInfiniteAdminResultDetailQuery.rootKey = "AdminResultDetail.infinite"

export const useSuspenseInfiniteAdminResultDetailQuery = <
  TData = InfiniteData<Types.AdminResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminResultDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminResultDetailQuery,
            Types.AdminResultDetailQueryVariables
          >(AdminResultDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminResultDetailQuery.getKey = (
  variables: Types.AdminResultDetailQueryVariables,
) => ["AdminResultDetail.infiniteSuspense", variables]

useSuspenseInfiniteAdminResultDetailQuery.rootKey = "AdminResultDetail.infinite"

useAdminResultDetailQuery.fetcher = (
  variables: Types.AdminResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminResultDetailQuery, Types.AdminResultDetailQueryVariables>(
    AdminResultDetailDocument,
    variables,
    options,
  )

export const ResultTableDocument = `
    query ResultTable($slug: String!, $filterBy: ResultTableFilter, $sortBy: [ResultTableSort!]) {
  admin {
    orgBySlug(slug: $slug) {
      resultTable {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
        }
      }
    }
  }
}
    `

export const useResultTableQuery = <TData = Types.ResultTableQuery, TError = unknown>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<UseQueryOptions<Types.ResultTableQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ResultTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTable", variables],
    queryFn: publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable",
  variables,
]

useResultTableQuery.rootKey = "ResultTable"

export const useSuspenseResultTableQuery = <
  TData = Types.ResultTableQuery,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableQuery, TError, TData>({
    queryKey: ["ResultTableSuspense", variables],
    queryFn: publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
      ResultTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTableSuspense",
  variables,
]

useSuspenseResultTableQuery.rootKey = "ResultTable"

export const useInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableQuery.getKey = (variables: Types.ResultTableQueryVariables) => [
  "ResultTable.infinite",
  variables,
]

useInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

export const useSuspenseInfiniteResultTableQuery = <
  TData = InfiniteData<Types.ResultTableQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
            ResultTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableQuery.getKey = (
  variables: Types.ResultTableQueryVariables,
) => ["ResultTable.infiniteSuspense", variables]

useSuspenseInfiniteResultTableQuery.rootKey = "ResultTable.infinite"

useResultTableQuery.fetcher = (
  variables: Types.ResultTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultTableQuery, Types.ResultTableQueryVariables>(
    ResultTableDocument,
    variables,
    options,
  )

export const ResultTableRowsDocument = `
    query ResultTableRows($slug: String!, $ids: [Int!]!) {
  admin {
    orgBySlug(slug: $slug) {
      resultTable {
        rows(keys: $ids) {
          id
          createdAt
          sitePid
          siteName
          siteStreetAddress
          siteCity
          siteState
          siteStatus
          siteHiddenFromCustomers
          scenarioPid
          scenarioName
          scenarioSolarLocation
          scenarioIncludeStorage
          finproductType
          financierName
          financierPid
          finmodelId
          finmodelFmv
          finmodelNoi
          failureId
          failureFmv
          failureName
          failureMessage
          policyCheckIsGood
          physicalCheckRoofIsGood
          physicalCheckCarportIsGood
          physicalCheckGroundmountIsGood
        }
      }
    }
  }
}
    `

export const useResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRows", variables],
    queryFn: publicFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useResultTableRowsQuery.getKey = (variables: Types.ResultTableRowsQueryVariables) => [
  "ResultTableRows",
  variables,
]

useResultTableRowsQuery.rootKey = "ResultTableRows"

export const useSuspenseResultTableRowsQuery = <
  TData = Types.ResultTableRowsQuery,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultTableRowsQuery, TError, TData>({
    queryKey: ["ResultTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.ResultTableRowsQuery,
      Types.ResultTableRowsQueryVariables
    >(ResultTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRowsSuspense", variables]

useSuspenseResultTableRowsQuery.rootKey = "ResultTableRows"

export const useInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResultTableRowsQuery,
            Types.ResultTableRowsQueryVariables
          >(ResultTableRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infinite", variables]

useInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

export const useSuspenseInfiniteResultTableRowsQuery = <
  TData = InfiniteData<Types.ResultTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.ResultTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ResultTableRowsQuery,
            Types.ResultTableRowsQueryVariables
          >(ResultTableRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultTableRowsQuery.getKey = (
  variables: Types.ResultTableRowsQueryVariables,
) => ["ResultTableRows.infiniteSuspense", variables]

useSuspenseInfiniteResultTableRowsQuery.rootKey = "ResultTableRows.infinite"

useResultTableRowsQuery.fetcher = (
  variables: Types.ResultTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultTableRowsQuery, Types.ResultTableRowsQueryVariables>(
    ResultTableRowsDocument,
    variables,
    options,
  )

export const FinmodelTableDocument = `
    query FinmodelTable($sitePid: String!, $filterBy: FinmodelTableFilter, $sortBy: [FinmodelTableSort!]) {
  admin {
    site(pid: $sitePid) {
      finmodelTable {
        view(filterBy: $filterBy, sortBy: $sortBy) {
          keys
        }
      }
    }
  }
}
    `

export const useFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTable", variables],
    queryFn: publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useFinmodelTableQuery.getKey = (variables: Types.FinmodelTableQueryVariables) => [
  "FinmodelTable",
  variables,
]

useFinmodelTableQuery.rootKey = "FinmodelTable"

export const useSuspenseFinmodelTableQuery = <
  TData = Types.FinmodelTableQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableQuery, TError, TData>({
    queryKey: ["FinmodelTableSuspense", variables],
    queryFn: publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
      FinmodelTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTableSuspense", variables]

useSuspenseFinmodelTableQuery.rootKey = "FinmodelTable"

export const useInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infinite", variables]

useInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

export const useSuspenseInfiniteFinmodelTableQuery = <
  TData = InfiniteData<Types.FinmodelTableQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
            FinmodelTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableQuery.getKey = (
  variables: Types.FinmodelTableQueryVariables,
) => ["FinmodelTable.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelTableQuery.rootKey = "FinmodelTable.infinite"

useFinmodelTableQuery.fetcher = (
  variables: Types.FinmodelTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelTableQuery, Types.FinmodelTableQueryVariables>(
    FinmodelTableDocument,
    variables,
    options,
  )

export const FinmodelTableRowsDocument = `
    query FinmodelTableRows($sitePid: String!, $ids: [Int!]!) {
  admin {
    site(pid: $sitePid) {
      finmodelTable {
        rows(keys: $ids) {
          id
          createdAt
          name
          fmvStr
          type
          buildingOwnerNoiLifetimeUsd
          scenarioPid
          scenarioName
          scenarioSolarLocation
          scenarioIncludeStorage
        }
      }
    }
  }
}
    `

export const useFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRows", variables],
    queryFn: publicFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows", variables]

useFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useSuspenseFinmodelTableRowsQuery = <
  TData = Types.FinmodelTableRowsQuery,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelTableRowsQuery, TError, TData>({
    queryKey: ["FinmodelTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.FinmodelTableRowsQuery,
      Types.FinmodelTableRowsQueryVariables
    >(FinmodelTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRowsSuspense", variables]

useSuspenseFinmodelTableRowsQuery.rootKey = "FinmodelTableRows"

export const useInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infinite", variables]

useInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

export const useSuspenseInfiniteFinmodelTableRowsQuery = <
  TData = InfiniteData<Types.FinmodelTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelTableRowsQuery,
            Types.FinmodelTableRowsQueryVariables
          >(FinmodelTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelTableRowsQuery.getKey = (
  variables: Types.FinmodelTableRowsQueryVariables,
) => ["FinmodelTableRows.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelTableRowsQuery.rootKey = "FinmodelTableRows.infinite"

useFinmodelTableRowsQuery.fetcher = (
  variables: Types.FinmodelTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelTableRowsQuery, Types.FinmodelTableRowsQueryVariables>(
    FinmodelTableRowsDocument,
    variables,
    options,
  )

export const FinmodelDetail2Document = `
    query FinmodelDetail2($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      finmodel(id: $id) {
        ...AdminFinmodelDetail
      }
    }
  }
}
    ${AdminFinmodelDetailFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelDetail2Query, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2", variables],
    queryFn: publicFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useFinmodelDetail2Query.getKey = (variables: Types.FinmodelDetail2QueryVariables) => [
  "FinmodelDetail2",
  variables,
]

useFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useSuspenseFinmodelDetail2Query = <
  TData = Types.FinmodelDetail2Query,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelDetail2Query, TError, TData>({
    queryKey: ["FinmodelDetail2Suspense", variables],
    queryFn: publicFetcher<
      Types.FinmodelDetail2Query,
      Types.FinmodelDetail2QueryVariables
    >(FinmodelDetail2Document, variables),
    ...options,
  })
}

useSuspenseFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2Suspense", variables]

useSuspenseFinmodelDetail2Query.rootKey = "FinmodelDetail2"

export const useInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelDetail2Query,
            Types.FinmodelDetail2QueryVariables
          >(FinmodelDetail2Document, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infinite", variables]

useInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

export const useSuspenseInfiniteFinmodelDetail2Query = <
  TData = InfiniteData<Types.FinmodelDetail2Query>,
  TError = unknown,
>(
  variables: Types.FinmodelDetail2QueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelDetail2Query, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelDetail2Query,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelDetail2Query, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelDetail2.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelDetail2Query,
            Types.FinmodelDetail2QueryVariables
          >(FinmodelDetail2Document, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelDetail2Query.getKey = (
  variables: Types.FinmodelDetail2QueryVariables,
) => ["FinmodelDetail2.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelDetail2Query.rootKey = "FinmodelDetail2.infinite"

useFinmodelDetail2Query.fetcher = (
  variables: Types.FinmodelDetail2QueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelDetail2Query, Types.FinmodelDetail2QueryVariables>(
    FinmodelDetail2Document,
    variables,
    options,
  )

export const FinmodelLineageDocument = `
    query FinmodelLineage($sitePid: String!, $id: Int!) {
  admin {
    site(pid: $sitePid) {
      finmodel(id: $id) {
        id
        moreDetails {
          lineage {
            ...AdminFinmodelDetail
          }
        }
      }
    }
  }
}
    ${AdminFinmodelDetailFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinmodelLineageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineage", variables],
    queryFn: publicFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useFinmodelLineageQuery.getKey = (variables: Types.FinmodelLineageQueryVariables) => [
  "FinmodelLineage",
  variables,
]

useFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useSuspenseFinmodelLineageQuery = <
  TData = Types.FinmodelLineageQuery,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinmodelLineageQuery, TError, TData>({
    queryKey: ["FinmodelLineageSuspense", variables],
    queryFn: publicFetcher<
      Types.FinmodelLineageQuery,
      Types.FinmodelLineageQueryVariables
    >(FinmodelLineageDocument, variables),
    ...options,
  })
}

useSuspenseFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineageSuspense", variables]

useSuspenseFinmodelLineageQuery.rootKey = "FinmodelLineage"

export const useInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelLineageQuery,
            Types.FinmodelLineageQueryVariables
          >(FinmodelLineageDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infinite", variables]

useInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

export const useSuspenseInfiniteFinmodelLineageQuery = <
  TData = InfiniteData<Types.FinmodelLineageQuery>,
  TError = unknown,
>(
  variables: Types.FinmodelLineageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinmodelLineageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinmodelLineageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinmodelLineageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinmodelLineage.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinmodelLineageQuery,
            Types.FinmodelLineageQueryVariables
          >(FinmodelLineageDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinmodelLineageQuery.getKey = (
  variables: Types.FinmodelLineageQueryVariables,
) => ["FinmodelLineage.infiniteSuspense", variables]

useSuspenseInfiniteFinmodelLineageQuery.rootKey = "FinmodelLineage.infinite"

useFinmodelLineageQuery.fetcher = (
  variables: Types.FinmodelLineageQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinmodelLineageQuery, Types.FinmodelLineageQueryVariables>(
    FinmodelLineageDocument,
    variables,
    options,
  )

export const SiteLabelsDocument = `
    query SiteLabels($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      labels {
        pid
        name
        themeColor
      }
      portfolio {
        pid
        org {
          labels {
            pid
            name
            themeColor
          }
        }
      }
    }
  }
}
    `

export const useSiteLabelsQuery = <TData = Types.SiteLabelsQuery, TError = unknown>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteLabelsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabels", variables],
    queryFn: publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels",
  variables,
]

useSiteLabelsQuery.rootKey = "SiteLabels"

export const useSuspenseSiteLabelsQuery = <
  TData = Types.SiteLabelsQuery,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteLabelsQuery, TError, TData>({
    queryKey: ["SiteLabelsSuspense", variables],
    queryFn: publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
      SiteLabelsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabelsSuspense",
  variables,
]

useSuspenseSiteLabelsQuery.rootKey = "SiteLabels"

export const useInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteLabelsQuery.getKey = (variables: Types.SiteLabelsQueryVariables) => [
  "SiteLabels.infinite",
  variables,
]

useInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

export const useSuspenseInfiniteSiteLabelsQuery = <
  TData = InfiniteData<Types.SiteLabelsQuery>,
  TError = unknown,
>(
  variables: Types.SiteLabelsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteLabelsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteLabelsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteLabelsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteLabels.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
            SiteLabelsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteLabelsQuery.getKey = (
  variables: Types.SiteLabelsQueryVariables,
) => ["SiteLabels.infiniteSuspense", variables]

useSuspenseInfiniteSiteLabelsQuery.rootKey = "SiteLabels.infinite"

useSiteLabelsQuery.fetcher = (
  variables: Types.SiteLabelsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteLabelsQuery, Types.SiteLabelsQueryVariables>(
    SiteLabelsDocument,
    variables,
    options,
  )

export const ConserviceSearchDocument = `
    query ConserviceSearch($sitePid: String!, $onlyHouse: Boolean!) {
  admin {
    site(pid: $sitePid) {
      portfolio {
        pid
        org {
          pid
        }
      }
      conserviceSearch(onlyHouse: $onlyHouse) {
        name
        count
        matched
      }
    }
  }
}
    `

export const useConserviceSearchQuery = <
  TData = Types.ConserviceSearchQuery,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ConserviceSearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ConserviceSearchQuery, TError, TData>({
    queryKey: ["ConserviceSearch", variables],
    queryFn: publicFetcher<
      Types.ConserviceSearchQuery,
      Types.ConserviceSearchQueryVariables
    >(ConserviceSearchDocument, variables),
    ...options,
  })
}

useConserviceSearchQuery.getKey = (variables: Types.ConserviceSearchQueryVariables) => [
  "ConserviceSearch",
  variables,
]

useConserviceSearchQuery.rootKey = "ConserviceSearch"

export const useSuspenseConserviceSearchQuery = <
  TData = Types.ConserviceSearchQuery,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ConserviceSearchQuery, TError, TData>({
    queryKey: ["ConserviceSearchSuspense", variables],
    queryFn: publicFetcher<
      Types.ConserviceSearchQuery,
      Types.ConserviceSearchQueryVariables
    >(ConserviceSearchDocument, variables),
    ...options,
  })
}

useSuspenseConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearchSuspense", variables]

useSuspenseConserviceSearchQuery.rootKey = "ConserviceSearch"

export const useInfiniteConserviceSearchQuery = <
  TData = InfiniteData<Types.ConserviceSearchQuery>,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ConserviceSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ConserviceSearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ConserviceSearchQuery,
            Types.ConserviceSearchQueryVariables
          >(ConserviceSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearch.infinite", variables]

useInfiniteConserviceSearchQuery.rootKey = "ConserviceSearch.infinite"

export const useSuspenseInfiniteConserviceSearchQuery = <
  TData = InfiniteData<Types.ConserviceSearchQuery>,
  TError = unknown,
>(
  variables: Types.ConserviceSearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ConserviceSearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ConserviceSearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ConserviceSearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ConserviceSearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ConserviceSearchQuery,
            Types.ConserviceSearchQueryVariables
          >(ConserviceSearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteConserviceSearchQuery.getKey = (
  variables: Types.ConserviceSearchQueryVariables,
) => ["ConserviceSearch.infiniteSuspense", variables]

useSuspenseInfiniteConserviceSearchQuery.rootKey = "ConserviceSearch.infinite"

useConserviceSearchQuery.fetcher = (
  variables: Types.ConserviceSearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ConserviceSearchQuery, Types.ConserviceSearchQueryVariables>(
    ConserviceSearchDocument,
    variables,
    options,
  )

export const SiteResultConfigDocument = `
    query SiteResultConfig($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      resultConfig {
        finproduct {
          id
          type
          name
          financier {
            name
            pid
          }
        }
        disabledAt
        finrelDisabledAt
      }
    }
  }
}
    `

export const useSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteResultConfigQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfig", variables],
    queryFn: publicFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSiteResultConfigQuery.getKey = (variables: Types.SiteResultConfigQueryVariables) => [
  "SiteResultConfig",
  variables,
]

useSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useSuspenseSiteResultConfigQuery = <
  TData = Types.SiteResultConfigQuery,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteResultConfigQuery, TError, TData>({
    queryKey: ["SiteResultConfigSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteResultConfigQuery,
      Types.SiteResultConfigQueryVariables
    >(SiteResultConfigDocument, variables),
    ...options,
  })
}

useSuspenseSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfigSuspense", variables]

useSuspenseSiteResultConfigQuery.rootKey = "SiteResultConfig"

export const useInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infinite", variables]

useInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

export const useSuspenseInfiniteSiteResultConfigQuery = <
  TData = InfiniteData<Types.SiteResultConfigQuery>,
  TError = unknown,
>(
  variables: Types.SiteResultConfigQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteResultConfigQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteResultConfigQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteResultConfigQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteResultConfig.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteResultConfigQuery,
            Types.SiteResultConfigQueryVariables
          >(SiteResultConfigDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteResultConfigQuery.getKey = (
  variables: Types.SiteResultConfigQueryVariables,
) => ["SiteResultConfig.infiniteSuspense", variables]

useSuspenseInfiniteSiteResultConfigQuery.rootKey = "SiteResultConfig.infinite"

useSiteResultConfigQuery.fetcher = (
  variables: Types.SiteResultConfigQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteResultConfigQuery, Types.SiteResultConfigQueryVariables>(
    SiteResultConfigDocument,
    variables,
    options,
  )

export const SiteTodosDocument = `
    query SiteTodos($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      id
      pid
      todos {
        id
        content
        index
        createdAt
        createdBy {
          pid
          email
        }
        updatedAt
        updatedBy {
          pid
          email
        }
        completedAt
        completedBy {
          pid
          email
        }
        section {
          id
          name
          index
        }
      }
    }
  }
}
    `

export const useSiteTodosQuery = <TData = Types.SiteTodosQuery, TError = unknown>(
  variables: Types.SiteTodosQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteTodosQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteTodosQuery, TError, TData>({
    queryKey: ["SiteTodos", variables],
    queryFn: publicFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
      SiteTodosDocument,
      variables,
    ),
    ...options,
  })
}

useSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodos",
  variables,
]

useSiteTodosQuery.rootKey = "SiteTodos"

export const useSuspenseSiteTodosQuery = <
  TData = Types.SiteTodosQuery,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteTodosQuery, TError, TData>({
    queryKey: ["SiteTodosSuspense", variables],
    queryFn: publicFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
      SiteTodosDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodosSuspense",
  variables,
]

useSuspenseSiteTodosQuery.rootKey = "SiteTodos"

export const useInfiniteSiteTodosQuery = <
  TData = InfiniteData<Types.SiteTodosQuery>,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteTodosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteTodos.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
            SiteTodosDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteTodosQuery.getKey = (variables: Types.SiteTodosQueryVariables) => [
  "SiteTodos.infinite",
  variables,
]

useInfiniteSiteTodosQuery.rootKey = "SiteTodos.infinite"

export const useSuspenseInfiniteSiteTodosQuery = <
  TData = InfiniteData<Types.SiteTodosQuery>,
  TError = unknown,
>(
  variables: Types.SiteTodosQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteTodosQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteTodosQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteTodosQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteTodos.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
            SiteTodosDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteTodosQuery.getKey = (
  variables: Types.SiteTodosQueryVariables,
) => ["SiteTodos.infiniteSuspense", variables]

useSuspenseInfiniteSiteTodosQuery.rootKey = "SiteTodos.infinite"

useSiteTodosQuery.fetcher = (
  variables: Types.SiteTodosQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteTodosQuery, Types.SiteTodosQueryVariables>(
    SiteTodosDocument,
    variables,
    options,
  )

export const SiteEspmDocument = `
    query SiteEspm($sitePid: String!) {
  admin {
    site(pid: $sitePid) {
      pid
      espm {
        id
        createdAt
        lastUpdatedAt
        externalPropertyId
        xml
        accounts {
          id
          createdAt
          lastUpdatedAt
          externalAccountId
          xml
        }
        espmMeters {
          id
          createdAt
          lastUpdatedAt
          externalMeterId
          xml
          unitOfMeasure
          meterType
          espmConsumptions {
            id
            createdAt
            lastUpdatedAt
            externalConsumptionId
            xml
            start
            end
            usageKwh
            costUsd
          }
        }
      }
    }
  }
}
    `

export const useSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEspmQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspm", variables],
    queryFn: publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm",
  variables,
]

useSiteEspmQuery.rootKey = "SiteEspm"

export const useSuspenseSiteEspmQuery = <TData = Types.SiteEspmQuery, TError = unknown>(
  variables: Types.SiteEspmQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEspmQuery, TError, TData>({
    queryKey: ["SiteEspmSuspense", variables],
    queryFn: publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
      SiteEspmDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspmSuspense",
  variables,
]

useSuspenseSiteEspmQuery.rootKey = "SiteEspm"

export const useInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infinite",
  variables,
]

useInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

export const useSuspenseInfiniteSiteEspmQuery = <
  TData = InfiniteData<Types.SiteEspmQuery>,
  TError = unknown,
>(
  variables: Types.SiteEspmQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEspmQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEspmQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEspmQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEspm.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
            SiteEspmDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEspmQuery.getKey = (variables: Types.SiteEspmQueryVariables) => [
  "SiteEspm.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteEspmQuery.rootKey = "SiteEspm.infinite"

useSiteEspmQuery.fetcher = (
  variables: Types.SiteEspmQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEspmQuery, Types.SiteEspmQueryVariables>(
    SiteEspmDocument,
    variables,
    options,
  )

export const SolarCaptchaSitesDocument = `
    query SolarCaptchaSites($sitePids: [String!]!) {
  admin {
    sites(sitePids: $sitePids) {
      pid
      fullAddress
      staticMapUrl
      existingSolarDetectionScore
      latitude
      longitude
      orgSlug
    }
  }
}
    `

export const useSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSites", variables],
    queryFn: publicFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites", variables]

useSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useSuspenseSolarCaptchaSitesQuery = <
  TData = Types.SolarCaptchaSitesQuery,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SolarCaptchaSitesQuery, TError, TData>({
    queryKey: ["SolarCaptchaSitesSuspense", variables],
    queryFn: publicFetcher<
      Types.SolarCaptchaSitesQuery,
      Types.SolarCaptchaSitesQueryVariables
    >(SolarCaptchaSitesDocument, variables),
    ...options,
  })
}

useSuspenseSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSitesSuspense", variables]

useSuspenseSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites"

export const useInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infinite", variables]

useInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

export const useSuspenseInfiniteSolarCaptchaSitesQuery = <
  TData = InfiniteData<Types.SolarCaptchaSitesQuery>,
  TError = unknown,
>(
  variables: Types.SolarCaptchaSitesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SolarCaptchaSitesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SolarCaptchaSitesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SolarCaptchaSitesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SolarCaptchaSites.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SolarCaptchaSitesQuery,
            Types.SolarCaptchaSitesQueryVariables
          >(SolarCaptchaSitesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSolarCaptchaSitesQuery.getKey = (
  variables: Types.SolarCaptchaSitesQueryVariables,
) => ["SolarCaptchaSites.infiniteSuspense", variables]

useSuspenseInfiniteSolarCaptchaSitesQuery.rootKey = "SolarCaptchaSites.infinite"

useSolarCaptchaSitesQuery.fetcher = (
  variables: Types.SolarCaptchaSitesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SolarCaptchaSitesQuery, Types.SolarCaptchaSitesQueryVariables>(
    SolarCaptchaSitesDocument,
    variables,
    options,
  )

export const PreviewBuildingFootprintsDocument = `
    query PreviewBuildingFootprints($sitePid: String!, $strategy: FootprintStrategy!) {
  admin {
    site(pid: $sitePid) {
      previewBuildingFootprints(strategy: $strategy) {
        __typename
        ... on Footprint {
          latitude
          longitude
          bounds
          roofShape
          roofShapeGeojson
          parcelShape
          parcelShapeGeojson
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const usePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprints", variables],
    queryFn: publicFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

usePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints", variables]

usePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useSuspensePreviewBuildingFootprintsQuery = <
  TData = Types.PreviewBuildingFootprintsQuery,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>({
    queryKey: ["PreviewBuildingFootprintsSuspense", variables],
    queryFn: publicFetcher<
      Types.PreviewBuildingFootprintsQuery,
      Types.PreviewBuildingFootprintsQueryVariables
    >(PreviewBuildingFootprintsDocument, variables),
    ...options,
  })
}

useSuspensePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprintsSuspense", variables]

useSuspensePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints"

export const useInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PreviewBuildingFootprintsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PreviewBuildingFootprints.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infinite", variables]

useInfinitePreviewBuildingFootprintsQuery.rootKey = "PreviewBuildingFootprints.infinite"

export const useSuspenseInfinitePreviewBuildingFootprintsQuery = <
  TData = InfiniteData<Types.PreviewBuildingFootprintsQuery>,
  TError = unknown,
>(
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PreviewBuildingFootprintsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PreviewBuildingFootprintsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PreviewBuildingFootprints.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PreviewBuildingFootprintsQuery,
            Types.PreviewBuildingFootprintsQueryVariables
          >(PreviewBuildingFootprintsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePreviewBuildingFootprintsQuery.getKey = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
) => ["PreviewBuildingFootprints.infiniteSuspense", variables]

useSuspenseInfinitePreviewBuildingFootprintsQuery.rootKey =
  "PreviewBuildingFootprints.infinite"

usePreviewBuildingFootprintsQuery.fetcher = (
  variables: Types.PreviewBuildingFootprintsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PreviewBuildingFootprintsQuery,
    Types.PreviewBuildingFootprintsQueryVariables
  >(PreviewBuildingFootprintsDocument, variables, options)

export const ListHostingCapacityMapsDocument = `
    query ListHostingCapacityMaps {
  admin {
    hostingCapacityMaps {
      all {
        id
        utility {
          id
          pid
          name
          shortName
        }
        url
        requiresLogin
        types
      }
    }
  }
}
    `

export const useListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMaps"]
        : ["ListHostingCapacityMaps", variables],
    queryFn: publicFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps"]
    : ["ListHostingCapacityMaps", variables]

useListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useSuspenseListHostingCapacityMapsQuery = <
  TData = Types.ListHostingCapacityMapsQuery,
  TError = unknown,
>(
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListHostingCapacityMapsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ListHostingCapacityMapsSuspense"]
        : ["ListHostingCapacityMapsSuspense", variables],
    queryFn: publicFetcher<
      Types.ListHostingCapacityMapsQuery,
      Types.ListHostingCapacityMapsQueryVariables
    >(ListHostingCapacityMapsDocument, variables),
    ...options,
  })
}

useSuspenseListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMapsSuspense"]
    : ["ListHostingCapacityMapsSuspense", variables]

useSuspenseListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps"

export const useInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infinite"]
            : ["ListHostingCapacityMaps.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infinite"]
    : ["ListHostingCapacityMaps.infinite", variables]

useInfiniteListHostingCapacityMapsQuery.rootKey = "ListHostingCapacityMaps.infinite"

export const useSuspenseInfiniteListHostingCapacityMapsQuery = <
  TData = InfiniteData<Types.ListHostingCapacityMapsQuery>,
  TError = unknown,
>(
  variables: Types.ListHostingCapacityMapsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListHostingCapacityMapsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListHostingCapacityMapsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListHostingCapacityMapsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ListHostingCapacityMaps.infiniteSuspense"]
            : ["ListHostingCapacityMaps.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ListHostingCapacityMapsQuery,
            Types.ListHostingCapacityMapsQueryVariables
          >(ListHostingCapacityMapsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListHostingCapacityMapsQuery.getKey = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
) =>
  variables === undefined
    ? ["ListHostingCapacityMaps.infiniteSuspense"]
    : ["ListHostingCapacityMaps.infiniteSuspense", variables]

useSuspenseInfiniteListHostingCapacityMapsQuery.rootKey =
  "ListHostingCapacityMaps.infinite"

useListHostingCapacityMapsQuery.fetcher = (
  variables?: Types.ListHostingCapacityMapsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ListHostingCapacityMapsQuery,
    Types.ListHostingCapacityMapsQueryVariables
  >(ListHostingCapacityMapsDocument, variables, options)

export const AdminMarketplaceViewDocument = `
    query AdminMarketplaceView($filterBy: AdminMarketplaceTableFilter, $sortBy: [AdminMarketplaceTableSort!]) {
  admin {
    marketplace {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          buildingOwners
          assetManagers
          bidPackageNames
          siteStates
          financiers
          portfolios
          salesOwners
          opsOwners
        }
      }
    }
  }
}
    `

export const useAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceView"]
        : ["AdminMarketplaceView", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView"]
    : ["AdminMarketplaceView", variables]

useAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useSuspenseAdminMarketplaceViewQuery = <
  TData = Types.AdminMarketplaceViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminMarketplaceViewSuspense"]
        : ["AdminMarketplaceViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceViewQuery,
      Types.AdminMarketplaceViewQueryVariables
    >(AdminMarketplaceViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceViewSuspense"]
    : ["AdminMarketplaceViewSuspense", variables]

useSuspenseAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView"

export const useInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infinite"]
            : ["AdminMarketplaceView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infinite"]
    : ["AdminMarketplaceView.infinite", variables]

useInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

export const useSuspenseInfiniteAdminMarketplaceViewQuery = <
  TData = InfiniteData<Types.AdminMarketplaceViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminMarketplaceView.infiniteSuspense"]
            : ["AdminMarketplaceView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceViewQuery,
            Types.AdminMarketplaceViewQueryVariables
          >(AdminMarketplaceViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceViewQuery.getKey = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminMarketplaceView.infiniteSuspense"]
    : ["AdminMarketplaceView.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceViewQuery.rootKey = "AdminMarketplaceView.infinite"

useAdminMarketplaceViewQuery.fetcher = (
  variables?: Types.AdminMarketplaceViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceViewQuery,
    Types.AdminMarketplaceViewQueryVariables
  >(AdminMarketplaceViewDocument, variables, options)

export const AdminMarketplaceRowsDocument = `
    query AdminMarketplaceRows($keys: [Int!]!) {
  admin {
    marketplace {
      rows(keys: $keys) {
        id
        projectPid
        projectName
        orgSlug
        orgName
        portfolioName
        sitePid
        bidPackagePid
        bidPackageName
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        isAwarded
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        salePlannedYear
        salePlans
        buildingLegalName
        propertyFundName
        leaseSignatory
        counselApproval
        leaseApprovers
        externalLeaseApprovers
        needsTenantApproval
        roofRightsHolder
        propertyLenders
        propertyInsurer
        constructionEmail
        propertySiteVisitContacts
        leasePropertyLegalDescription
        leaseLegalOwnerName
        signatureBlocks
        isFannieMaeLender
        isFmGlobalInsurer
        utilityShortName
        finproductType
        specFinproductType
        biddingOpenAt
        biddingClosedAt
        bidCount
        bidderCount
        awardedTo
        roofRentY1Usd
        noiLifetimeUsd
        roofRentEscalator
        pvSizeKwDc
        lumenRevenue
        lumenFeeUsdPerWatt
        hubspotRecordId
        hubspotRevenueRecordId
        salesOwnerEmail
        salesOwnerPid
        opsOwnerEmail
        opsOwnerPid
        rofoSignedDate
        rofoSignedEstimated
        rofoSignedActual
        preBidMeetingEstimated
        preBidMeetingActual
        preBidMeetingDate
        biddingOpenedEstimated
        biddingOpenedActual
        biddingOpenedDate
        biddingClosedEstimated
        biddingClosedActual
        biddingClosedDate
        bidsPresentedEstimated
        bidsPresentedActual
        bidsPresentedDate
        ixSubmittedEstimated
        ixSubmittedActual
        ixSubmittedDate
        ixApprovedEstimated
        ixApprovedActual
        ixApprovedDate
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramSubmittedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        csProgramApprovedDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        awardedToDeveloperDate
        leaseSignedEstimated
        leaseSignedActual
        leaseSignedDate
        ntpEstimated
        ntpActual
        ntpDate
        codEstimated
        codActual
        codDate
        projectPid
        progressNotesPid
        progressNotes
        progressActionItem
        progressPriority
      }
    }
  }
}
    `

export const useAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRows", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows", variables]

useAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useSuspenseAdminMarketplaceRowsQuery = <
  TData = Types.AdminMarketplaceRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceRowsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceRowsQuery,
      Types.AdminMarketplaceRowsQueryVariables
    >(AdminMarketplaceRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRowsSuspense", variables]

useSuspenseAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows"

export const useInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminMarketplaceRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infinite", variables]

useInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

export const useSuspenseInfiniteAdminMarketplaceRowsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminMarketplaceRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminMarketplaceRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceRowsQuery,
            Types.AdminMarketplaceRowsQueryVariables
          >(AdminMarketplaceRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceRowsQuery.getKey = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
) => ["AdminMarketplaceRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceRowsQuery.rootKey = "AdminMarketplaceRows.infinite"

useAdminMarketplaceRowsQuery.fetcher = (
  variables: Types.AdminMarketplaceRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceRowsQuery,
    Types.AdminMarketplaceRowsQueryVariables
  >(AdminMarketplaceRowsDocument, variables, options)

export const AdminMarketplaceSiteDocumentsDocument = `
    query AdminMarketplaceSiteDocuments($keys: [Int!]!) {
  admin {
    bundleRowParentDocuments(
      bundleParentRowIds: $keys
      includeTags: [SITE__ROOF, SITE__ROOF_WARRANTY, SITE__STRUCTURAL_DRAWINGS, SITE__ELECTRICAL_DIAGRAMS, SITE__WIRING_INSTRUCTIONS]
    ) {
      sitePid
      documents {
        pid
        fileName
        downloadHref
        tag
      }
    }
  }
}
    `

export const useAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments", variables]

useAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useSuspenseAdminMarketplaceSiteDocumentsQuery = <
  TData = Types.AdminMarketplaceSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>({
    queryKey: ["AdminMarketplaceSiteDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminMarketplaceSiteDocumentsQuery,
      Types.AdminMarketplaceSiteDocumentsQueryVariables
    >(AdminMarketplaceSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocumentsSuspense", variables]

useSuspenseAdminMarketplaceSiteDocumentsQuery.rootKey = "AdminMarketplaceSiteDocuments"

export const useInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminMarketplaceSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infinite", variables]

useInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

export const useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery = <
  TData = InfiniteData<Types.AdminMarketplaceSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminMarketplaceSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminMarketplaceSiteDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminMarketplaceSiteDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminMarketplaceSiteDocumentsQuery,
            Types.AdminMarketplaceSiteDocumentsQueryVariables
          >(AdminMarketplaceSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.getKey = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
) => ["AdminMarketplaceSiteDocuments.infiniteSuspense", variables]

useSuspenseInfiniteAdminMarketplaceSiteDocumentsQuery.rootKey =
  "AdminMarketplaceSiteDocuments.infinite"

useAdminMarketplaceSiteDocumentsQuery.fetcher = (
  variables: Types.AdminMarketplaceSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminMarketplaceSiteDocumentsQuery,
    Types.AdminMarketplaceSiteDocumentsQueryVariables
  >(AdminMarketplaceSiteDocumentsDocument, variables, options)

export const ActiveAdminsDocument = `
    query ActiveAdmins {
  admin {
    activeAdmins {
      pid
      isAdmin
      name
      email
    }
  }
}
    `

export const useActiveAdminsQuery = <TData = Types.ActiveAdminsQuery, TError = unknown>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ActiveAdminsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey: variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables],
    queryFn: publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined ? ["ActiveAdmins"] : ["ActiveAdmins", variables]

useActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useSuspenseActiveAdminsQuery = <
  TData = Types.ActiveAdminsQuery,
  TError = unknown,
>(
  variables?: Types.ActiveAdminsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ActiveAdminsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ActiveAdminsSuspense"]
        : ["ActiveAdminsSuspense", variables],
    queryFn: publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
      ActiveAdminsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined
    ? ["ActiveAdminsSuspense"]
    : ["ActiveAdminsSuspense", variables]

useSuspenseActiveAdminsQuery.rootKey = "ActiveAdmins"

export const useInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infinite"]
            : ["ActiveAdmins.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteActiveAdminsQuery.getKey = (variables?: Types.ActiveAdminsQueryVariables) =>
  variables === undefined
    ? ["ActiveAdmins.infinite"]
    : ["ActiveAdmins.infinite", variables]

useInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

export const useSuspenseInfiniteActiveAdminsQuery = <
  TData = InfiniteData<Types.ActiveAdminsQuery>,
  TError = unknown,
>(
  variables: Types.ActiveAdminsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ActiveAdminsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ActiveAdminsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ActiveAdminsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ActiveAdmins.infiniteSuspense"]
            : ["ActiveAdmins.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
            ActiveAdminsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteActiveAdminsQuery.getKey = (
  variables?: Types.ActiveAdminsQueryVariables,
) =>
  variables === undefined
    ? ["ActiveAdmins.infiniteSuspense"]
    : ["ActiveAdmins.infiniteSuspense", variables]

useSuspenseInfiniteActiveAdminsQuery.rootKey = "ActiveAdmins.infinite"

useActiveAdminsQuery.fetcher = (
  variables?: Types.ActiveAdminsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ActiveAdminsQuery, Types.ActiveAdminsQueryVariables>(
    ActiveAdminsDocument,
    variables,
    options,
  )

export const AdminUserDetailDocument = `
    query AdminUserDetail($userId: Int!) {
  admin {
    user(userId: $userId) {
      ...UserListUser
      pageViews {
        pathname
        createdAt
      }
    }
  }
}
    ${UserListUserFragmentDoc}`

export const useAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminUserDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetail", variables],
    queryFn: publicFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useAdminUserDetailQuery.getKey = (variables: Types.AdminUserDetailQueryVariables) => [
  "AdminUserDetail",
  variables,
]

useAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useSuspenseAdminUserDetailQuery = <
  TData = Types.AdminUserDetailQuery,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUserDetailQuery, TError, TData>({
    queryKey: ["AdminUserDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminUserDetailQuery,
      Types.AdminUserDetailQueryVariables
    >(AdminUserDetailDocument, variables),
    ...options,
  })
}

useSuspenseAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetailSuspense", variables]

useSuspenseAdminUserDetailQuery.rootKey = "AdminUserDetail"

export const useInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUserDetailQuery,
            Types.AdminUserDetailQueryVariables
          >(AdminUserDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infinite", variables]

useInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

export const useSuspenseInfiniteAdminUserDetailQuery = <
  TData = InfiniteData<Types.AdminUserDetailQuery>,
  TError = unknown,
>(
  variables: Types.AdminUserDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUserDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUserDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUserDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminUserDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminUserDetailQuery,
            Types.AdminUserDetailQueryVariables
          >(AdminUserDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUserDetailQuery.getKey = (
  variables: Types.AdminUserDetailQueryVariables,
) => ["AdminUserDetail.infiniteSuspense", variables]

useSuspenseInfiniteAdminUserDetailQuery.rootKey = "AdminUserDetail.infinite"

useAdminUserDetailQuery.fetcher = (
  variables: Types.AdminUserDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminUserDetailQuery, Types.AdminUserDetailQueryVariables>(
    AdminUserDetailDocument,
    variables,
    options,
  )

export const AdminAllDocumentsViewDocument = `
    query AdminAllDocumentsView($filterBy: AdminAllDocumentsTableFilter, $sortBy: [AdminAllDocumentsTableSort!], $sitePid: String) {
  admin {
    allDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerOrgNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminAllDocumentsViewQuery = <
  TData = Types.AdminAllDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminAllDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminAllDocumentsView"]
        : ["AdminAllDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsViewQuery,
      Types.AdminAllDocumentsViewQueryVariables
    >(AdminAllDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView"]
    : ["AdminAllDocumentsView", variables]

useAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView"

export const useSuspenseAdminAllDocumentsViewQuery = <
  TData = Types.AdminAllDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminAllDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminAllDocumentsViewSuspense"]
        : ["AdminAllDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsViewQuery,
      Types.AdminAllDocumentsViewQueryVariables
    >(AdminAllDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsViewSuspense"]
    : ["AdminAllDocumentsViewSuspense", variables]

useSuspenseAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView"

export const useInfiniteAdminAllDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminAllDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminAllDocumentsView.infinite"]
            : ["AdminAllDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsViewQuery,
            Types.AdminAllDocumentsViewQueryVariables
          >(AdminAllDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView.infinite"]
    : ["AdminAllDocumentsView.infinite", variables]

useInfiniteAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView.infinite"

export const useSuspenseInfiniteAdminAllDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminAllDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminAllDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminAllDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminAllDocumentsView.infiniteSuspense"]
            : ["AdminAllDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsViewQuery,
            Types.AdminAllDocumentsViewQueryVariables
          >(AdminAllDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminAllDocumentsViewQuery.getKey = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminAllDocumentsView.infiniteSuspense"]
    : ["AdminAllDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminAllDocumentsViewQuery.rootKey = "AdminAllDocumentsView.infinite"

useAdminAllDocumentsViewQuery.fetcher = (
  variables?: Types.AdminAllDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminAllDocumentsViewQuery,
    Types.AdminAllDocumentsViewQueryVariables
  >(AdminAllDocumentsViewDocument, variables, options)

export const AdminAllDocumentsRowsDocument = `
    query AdminAllDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    allDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        sitePid
        siteAddress
        bundlePid
        bundleName
        bundleParentPid
        bundleParentName
        investorBidPid
        investorBidName
        ownerOrgName
      }
    }
  }
}
    `

export const useAdminAllDocumentsRowsQuery = <
  TData = Types.AdminAllDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminAllDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsRowsQuery,
      Types.AdminAllDocumentsRowsQueryVariables
    >(AdminAllDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows", variables]

useAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows"

export const useSuspenseAdminAllDocumentsRowsQuery = <
  TData = Types.AdminAllDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminAllDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminAllDocumentsRowsQuery,
      Types.AdminAllDocumentsRowsQueryVariables
    >(AdminAllDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRowsSuspense", variables]

useSuspenseAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows"

export const useInfiniteAdminAllDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminAllDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsRowsQuery,
            Types.AdminAllDocumentsRowsQueryVariables
          >(AdminAllDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows.infinite", variables]

useInfiniteAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows.infinite"

export const useSuspenseInfiniteAdminAllDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminAllDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminAllDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminAllDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminAllDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminAllDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminAllDocumentsRowsQuery,
            Types.AdminAllDocumentsRowsQueryVariables
          >(AdminAllDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminAllDocumentsRowsQuery.getKey = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
) => ["AdminAllDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminAllDocumentsRowsQuery.rootKey = "AdminAllDocumentsRows.infinite"

useAdminAllDocumentsRowsQuery.fetcher = (
  variables: Types.AdminAllDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminAllDocumentsRowsQuery,
    Types.AdminAllDocumentsRowsQueryVariables
  >(AdminAllDocumentsRowsDocument, variables, options)

export const AdminProjectDocumentsViewDocument = `
    query AdminProjectDocumentsView($filterBy: AdminProjectDocumentsTableFilter, $sortBy: [AdminProjectDocumentsTableSort!], $sitePid: String) {
  admin {
    projectDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminProjectDocumentsViewQuery = <
  TData = Types.AdminProjectDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProjectDocumentsView"]
        : ["AdminProjectDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsViewQuery,
      Types.AdminProjectDocumentsViewQueryVariables
    >(AdminProjectDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView"]
    : ["AdminProjectDocumentsView", variables]

useAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView"

export const useSuspenseAdminProjectDocumentsViewQuery = <
  TData = Types.AdminProjectDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminProjectDocumentsViewSuspense"]
        : ["AdminProjectDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsViewQuery,
      Types.AdminProjectDocumentsViewQueryVariables
    >(AdminProjectDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsViewSuspense"]
    : ["AdminProjectDocumentsViewSuspense", variables]

useSuspenseAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView"

export const useInfiniteAdminProjectDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProjectDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProjectDocumentsView.infinite"]
            : ["AdminProjectDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsViewQuery,
            Types.AdminProjectDocumentsViewQueryVariables
          >(AdminProjectDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView.infinite"]
    : ["AdminProjectDocumentsView.infinite", variables]

useInfiniteAdminProjectDocumentsViewQuery.rootKey = "AdminProjectDocumentsView.infinite"

export const useSuspenseInfiniteAdminProjectDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProjectDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminProjectDocumentsView.infiniteSuspense"]
            : ["AdminProjectDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsViewQuery,
            Types.AdminProjectDocumentsViewQueryVariables
          >(AdminProjectDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProjectDocumentsViewQuery.getKey = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminProjectDocumentsView.infiniteSuspense"]
    : ["AdminProjectDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminProjectDocumentsViewQuery.rootKey =
  "AdminProjectDocumentsView.infinite"

useAdminProjectDocumentsViewQuery.fetcher = (
  variables?: Types.AdminProjectDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProjectDocumentsViewQuery,
    Types.AdminProjectDocumentsViewQueryVariables
  >(AdminProjectDocumentsViewDocument, variables, options)

export const AdminProjectDocumentsRowsDocument = `
    query AdminProjectDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    projectDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        projectPid
        projectName
        projectStatus
        siteAddress
        ownerName
        developerName
        viewableByDeveloper
        viewableByBuildingOwner
        createdByName
      }
    }
  }
}
    `

export const useAdminProjectDocumentsRowsQuery = <
  TData = Types.AdminProjectDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminProjectDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsRowsQuery,
      Types.AdminProjectDocumentsRowsQueryVariables
    >(AdminProjectDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows", variables]

useAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows"

export const useSuspenseAdminProjectDocumentsRowsQuery = <
  TData = Types.AdminProjectDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminProjectDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminProjectDocumentsRowsQuery,
      Types.AdminProjectDocumentsRowsQueryVariables
    >(AdminProjectDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRowsSuspense", variables]

useSuspenseAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows"

export const useInfiniteAdminProjectDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminProjectDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminProjectDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsRowsQuery,
            Types.AdminProjectDocumentsRowsQueryVariables
          >(AdminProjectDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows.infinite", variables]

useInfiniteAdminProjectDocumentsRowsQuery.rootKey = "AdminProjectDocumentsRows.infinite"

export const useSuspenseInfiniteAdminProjectDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminProjectDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminProjectDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminProjectDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminProjectDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminProjectDocumentsRowsQuery,
            Types.AdminProjectDocumentsRowsQueryVariables
          >(AdminProjectDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminProjectDocumentsRowsQuery.getKey = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
) => ["AdminProjectDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminProjectDocumentsRowsQuery.rootKey =
  "AdminProjectDocumentsRows.infinite"

useAdminProjectDocumentsRowsQuery.fetcher = (
  variables: Types.AdminProjectDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminProjectDocumentsRowsQuery,
    Types.AdminProjectDocumentsRowsQueryVariables
  >(AdminProjectDocumentsRowsDocument, variables, options)

export const AdminSiteDocumentsViewDocument = `
    query AdminSiteDocumentsView($filterBy: AdminSiteDocumentsTableFilter, $sortBy: [AdminSiteDocumentsTableSort!], $sitePid: String) {
  admin {
    siteDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminSiteDocumentsViewQuery = <
  TData = Types.AdminSiteDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSiteDocumentsView"]
        : ["AdminSiteDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsViewQuery,
      Types.AdminSiteDocumentsViewQueryVariables
    >(AdminSiteDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView"]
    : ["AdminSiteDocumentsView", variables]

useAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView"

export const useSuspenseAdminSiteDocumentsViewQuery = <
  TData = Types.AdminSiteDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminSiteDocumentsViewSuspense"]
        : ["AdminSiteDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsViewQuery,
      Types.AdminSiteDocumentsViewQueryVariables
    >(AdminSiteDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsViewSuspense"]
    : ["AdminSiteDocumentsViewSuspense", variables]

useSuspenseAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView"

export const useInfiniteAdminSiteDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSiteDocumentsView.infinite"]
            : ["AdminSiteDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsViewQuery,
            Types.AdminSiteDocumentsViewQueryVariables
          >(AdminSiteDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView.infinite"]
    : ["AdminSiteDocumentsView.infinite", variables]

useInfiniteAdminSiteDocumentsViewQuery.rootKey = "AdminSiteDocumentsView.infinite"

export const useSuspenseInfiniteAdminSiteDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminSiteDocumentsView.infiniteSuspense"]
            : ["AdminSiteDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsViewQuery,
            Types.AdminSiteDocumentsViewQueryVariables
          >(AdminSiteDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteDocumentsViewQuery.getKey = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminSiteDocumentsView.infiniteSuspense"]
    : ["AdminSiteDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminSiteDocumentsViewQuery.rootKey =
  "AdminSiteDocumentsView.infinite"

useAdminSiteDocumentsViewQuery.fetcher = (
  variables?: Types.AdminSiteDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteDocumentsViewQuery,
    Types.AdminSiteDocumentsViewQueryVariables
  >(AdminSiteDocumentsViewDocument, variables, options)

export const AdminSiteDocumentsRowsDocument = `
    query AdminSiteDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    siteDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        sitePid
        siteAddress
        siteCity
        siteState
        siteZip
        ownerName
      }
    }
  }
}
    `

export const useAdminSiteDocumentsRowsQuery = <
  TData = Types.AdminSiteDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminSiteDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsRowsQuery,
      Types.AdminSiteDocumentsRowsQueryVariables
    >(AdminSiteDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows", variables]

useAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows"

export const useSuspenseAdminSiteDocumentsRowsQuery = <
  TData = Types.AdminSiteDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminSiteDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSiteDocumentsRowsQuery,
      Types.AdminSiteDocumentsRowsQueryVariables
    >(AdminSiteDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRowsSuspense", variables]

useSuspenseAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows"

export const useInfiniteAdminSiteDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsRowsQuery,
            Types.AdminSiteDocumentsRowsQueryVariables
          >(AdminSiteDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows.infinite", variables]

useInfiniteAdminSiteDocumentsRowsQuery.rootKey = "AdminSiteDocumentsRows.infinite"

export const useSuspenseInfiniteAdminSiteDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminSiteDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminSiteDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteDocumentsRowsQuery,
            Types.AdminSiteDocumentsRowsQueryVariables
          >(AdminSiteDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteDocumentsRowsQuery.getKey = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
) => ["AdminSiteDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminSiteDocumentsRowsQuery.rootKey =
  "AdminSiteDocumentsRows.infinite"

useAdminSiteDocumentsRowsQuery.fetcher = (
  variables: Types.AdminSiteDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminSiteDocumentsRowsQuery,
    Types.AdminSiteDocumentsRowsQueryVariables
  >(AdminSiteDocumentsRowsDocument, variables, options)

export const AdminBundleDocumentsViewDocument = `
    query AdminBundleDocumentsView($filterBy: AdminBundleDocumentsTableFilter, $sortBy: [AdminBundleDocumentsTableSort!], $sitePid: String) {
  admin {
    bundleDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          receiverNames
          developerNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminBundleDocumentsViewQuery = <
  TData = Types.AdminBundleDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleDocumentsView"]
        : ["AdminBundleDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsViewQuery,
      Types.AdminBundleDocumentsViewQueryVariables
    >(AdminBundleDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView"]
    : ["AdminBundleDocumentsView", variables]

useAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView"

export const useSuspenseAdminBundleDocumentsViewQuery = <
  TData = Types.AdminBundleDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleDocumentsViewSuspense"]
        : ["AdminBundleDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsViewQuery,
      Types.AdminBundleDocumentsViewQueryVariables
    >(AdminBundleDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsViewSuspense"]
    : ["AdminBundleDocumentsViewSuspense", variables]

useSuspenseAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView"

export const useInfiniteAdminBundleDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleDocumentsView.infinite"]
            : ["AdminBundleDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsViewQuery,
            Types.AdminBundleDocumentsViewQueryVariables
          >(AdminBundleDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView.infinite"]
    : ["AdminBundleDocumentsView.infinite", variables]

useInfiniteAdminBundleDocumentsViewQuery.rootKey = "AdminBundleDocumentsView.infinite"

export const useSuspenseInfiniteAdminBundleDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleDocumentsView.infiniteSuspense"]
            : ["AdminBundleDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsViewQuery,
            Types.AdminBundleDocumentsViewQueryVariables
          >(AdminBundleDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleDocumentsView.infiniteSuspense"]
    : ["AdminBundleDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleDocumentsViewQuery.rootKey =
  "AdminBundleDocumentsView.infinite"

useAdminBundleDocumentsViewQuery.fetcher = (
  variables?: Types.AdminBundleDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleDocumentsViewQuery,
    Types.AdminBundleDocumentsViewQueryVariables
  >(AdminBundleDocumentsViewDocument, variables, options)

export const AdminBundleDocumentsRowsDocument = `
    query AdminBundleDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    bundleDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        bundlePid
        bundleName
        bundleType
        receiverName
        developerName
      }
    }
  }
}
    `

export const useAdminBundleDocumentsRowsQuery = <
  TData = Types.AdminBundleDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsRowsQuery,
      Types.AdminBundleDocumentsRowsQueryVariables
    >(AdminBundleDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows", variables]

useAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows"

export const useSuspenseAdminBundleDocumentsRowsQuery = <
  TData = Types.AdminBundleDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBundleDocumentsRowsQuery,
      Types.AdminBundleDocumentsRowsQueryVariables
    >(AdminBundleDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRowsSuspense", variables]

useSuspenseAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows"

export const useInfiniteAdminBundleDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminBundleDocumentsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsRowsQuery,
            Types.AdminBundleDocumentsRowsQueryVariables
          >(AdminBundleDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows.infinite", variables]

useInfiniteAdminBundleDocumentsRowsQuery.rootKey = "AdminBundleDocumentsRows.infinite"

export const useSuspenseInfiniteAdminBundleDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminBundleDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminBundleDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBundleDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleDocumentsRowsQuery,
            Types.AdminBundleDocumentsRowsQueryVariables
          >(AdminBundleDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
) => ["AdminBundleDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleDocumentsRowsQuery.rootKey =
  "AdminBundleDocumentsRows.infinite"

useAdminBundleDocumentsRowsQuery.fetcher = (
  variables: Types.AdminBundleDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleDocumentsRowsQuery,
    Types.AdminBundleDocumentsRowsQueryVariables
  >(AdminBundleDocumentsRowsDocument, variables, options)

export const AdminBundleParentDocumentsViewDocument = `
    query AdminBundleParentDocumentsView($filterBy: AdminBundleParentDocumentsTableFilter, $sortBy: [AdminBundleParentDocumentsTableSort!], $sitePid: String) {
  admin {
    bundleParentDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          ownerNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminBundleParentDocumentsViewQuery = <
  TData = Types.AdminBundleParentDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleParentDocumentsView"]
        : ["AdminBundleParentDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsViewQuery,
      Types.AdminBundleParentDocumentsViewQueryVariables
    >(AdminBundleParentDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView"]
    : ["AdminBundleParentDocumentsView", variables]

useAdminBundleParentDocumentsViewQuery.rootKey = "AdminBundleParentDocumentsView"

export const useSuspenseAdminBundleParentDocumentsViewQuery = <
  TData = Types.AdminBundleParentDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminBundleParentDocumentsViewSuspense"]
        : ["AdminBundleParentDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsViewQuery,
      Types.AdminBundleParentDocumentsViewQueryVariables
    >(AdminBundleParentDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsViewSuspense"]
    : ["AdminBundleParentDocumentsViewSuspense", variables]

useSuspenseAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView"

export const useInfiniteAdminBundleParentDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleParentDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleParentDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleParentDocumentsView.infinite"]
            : ["AdminBundleParentDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsViewQuery,
            Types.AdminBundleParentDocumentsViewQueryVariables
          >(AdminBundleParentDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView.infinite"]
    : ["AdminBundleParentDocumentsView.infinite", variables]

useInfiniteAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView.infinite"

export const useSuspenseInfiniteAdminBundleParentDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBundleParentDocumentsViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminBundleParentDocumentsView.infiniteSuspense"]
            : ["AdminBundleParentDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsViewQuery,
            Types.AdminBundleParentDocumentsViewQueryVariables
          >(AdminBundleParentDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleParentDocumentsViewQuery.getKey = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminBundleParentDocumentsView.infiniteSuspense"]
    : ["AdminBundleParentDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleParentDocumentsViewQuery.rootKey =
  "AdminBundleParentDocumentsView.infinite"

useAdminBundleParentDocumentsViewQuery.fetcher = (
  variables?: Types.AdminBundleParentDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleParentDocumentsViewQuery,
    Types.AdminBundleParentDocumentsViewQueryVariables
  >(AdminBundleParentDocumentsViewDocument, variables, options)

export const AdminBundleParentDocumentsRowsDocument = `
    query AdminBundleParentDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    bundleParentDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        bundleParentPid
        bundleParentName
        bundleParentType
        ownerName
      }
    }
  }
}
    `

export const useAdminBundleParentDocumentsRowsQuery = <
  TData = Types.AdminBundleParentDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleParentDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsRowsQuery,
      Types.AdminBundleParentDocumentsRowsQueryVariables
    >(AdminBundleParentDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows", variables]

useAdminBundleParentDocumentsRowsQuery.rootKey = "AdminBundleParentDocumentsRows"

export const useSuspenseAdminBundleParentDocumentsRowsQuery = <
  TData = Types.AdminBundleParentDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminBundleParentDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminBundleParentDocumentsRowsQuery,
      Types.AdminBundleParentDocumentsRowsQueryVariables
    >(AdminBundleParentDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRowsSuspense", variables]

useSuspenseAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows"

export const useInfiniteAdminBundleParentDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminBundleParentDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBundleParentDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsRowsQuery,
            Types.AdminBundleParentDocumentsRowsQueryVariables
          >(AdminBundleParentDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows.infinite", variables]

useInfiniteAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows.infinite"

export const useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminBundleParentDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminBundleParentDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminBundleParentDocumentsRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminBundleParentDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminBundleParentDocumentsRowsQuery,
            Types.AdminBundleParentDocumentsRowsQueryVariables
          >(AdminBundleParentDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery.getKey = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
) => ["AdminBundleParentDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminBundleParentDocumentsRowsQuery.rootKey =
  "AdminBundleParentDocumentsRows.infinite"

useAdminBundleParentDocumentsRowsQuery.fetcher = (
  variables: Types.AdminBundleParentDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminBundleParentDocumentsRowsQuery,
    Types.AdminBundleParentDocumentsRowsQueryVariables
  >(AdminBundleParentDocumentsRowsDocument, variables, options)

export const AdminInvestorBidDocumentsViewDocument = `
    query AdminInvestorBidDocumentsView($filterBy: AdminInvestorBidDocumentsTableFilter, $sortBy: [AdminInvestorBidDocumentsTableSort!], $sitePid: String) {
  admin {
    investorBidDocuments(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        count
        keys
        stats {
          receiverNames
          developerNames
          createdByNames
        }
      }
    }
  }
}
    `

export const useAdminInvestorBidDocumentsViewQuery = <
  TData = Types.AdminInvestorBidDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInvestorBidDocumentsView"]
        : ["AdminInvestorBidDocumentsView", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsViewQuery,
      Types.AdminInvestorBidDocumentsViewQueryVariables
    >(AdminInvestorBidDocumentsViewDocument, variables),
    ...options,
  })
}

useAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView"]
    : ["AdminInvestorBidDocumentsView", variables]

useAdminInvestorBidDocumentsViewQuery.rootKey = "AdminInvestorBidDocumentsView"

export const useSuspenseAdminInvestorBidDocumentsViewQuery = <
  TData = Types.AdminInvestorBidDocumentsViewQuery,
  TError = unknown,
>(
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminInvestorBidDocumentsViewSuspense"]
        : ["AdminInvestorBidDocumentsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsViewQuery,
      Types.AdminInvestorBidDocumentsViewQueryVariables
    >(AdminInvestorBidDocumentsViewDocument, variables),
    ...options,
  })
}

useSuspenseAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsViewSuspense"]
    : ["AdminInvestorBidDocumentsViewSuspense", variables]

useSuspenseAdminInvestorBidDocumentsViewQuery.rootKey = "AdminInvestorBidDocumentsView"

export const useInfiniteAdminInvestorBidDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminInvestorBidDocumentsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInvestorBidDocumentsView.infinite"]
            : ["AdminInvestorBidDocumentsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsViewQuery,
            Types.AdminInvestorBidDocumentsViewQueryVariables
          >(AdminInvestorBidDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView.infinite"]
    : ["AdminInvestorBidDocumentsView.infinite", variables]

useInfiniteAdminInvestorBidDocumentsViewQuery.rootKey =
  "AdminInvestorBidDocumentsView.infinite"

export const useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsViewQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInvestorBidDocumentsViewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminInvestorBidDocumentsView.infiniteSuspense"]
            : ["AdminInvestorBidDocumentsView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsViewQuery,
            Types.AdminInvestorBidDocumentsViewQueryVariables
          >(AdminInvestorBidDocumentsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery.getKey = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
) =>
  variables === undefined
    ? ["AdminInvestorBidDocumentsView.infiniteSuspense"]
    : ["AdminInvestorBidDocumentsView.infiniteSuspense", variables]

useSuspenseInfiniteAdminInvestorBidDocumentsViewQuery.rootKey =
  "AdminInvestorBidDocumentsView.infinite"

useAdminInvestorBidDocumentsViewQuery.fetcher = (
  variables?: Types.AdminInvestorBidDocumentsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInvestorBidDocumentsViewQuery,
    Types.AdminInvestorBidDocumentsViewQueryVariables
  >(AdminInvestorBidDocumentsViewDocument, variables, options)

export const AdminInvestorBidDocumentsRowsDocument = `
    query AdminInvestorBidDocumentsRows($keys: [Int!]!, $sitePid: String) {
  admin {
    investorBidDocuments(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        documentPid
        fileName
        tag
        dateCreated
        createdByName
        investorBidPid
        investorBidName
        bundlePid
        bundleName
        receiverName
        developerName
      }
    }
  }
}
    `

export const useAdminInvestorBidDocumentsRowsQuery = <
  TData = Types.AdminInvestorBidDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminInvestorBidDocumentsRows", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsRowsQuery,
      Types.AdminInvestorBidDocumentsRowsQueryVariables
    >(AdminInvestorBidDocumentsRowsDocument, variables),
    ...options,
  })
}

useAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows", variables]

useAdminInvestorBidDocumentsRowsQuery.rootKey = "AdminInvestorBidDocumentsRows"

export const useSuspenseAdminInvestorBidDocumentsRowsQuery = <
  TData = Types.AdminInvestorBidDocumentsRowsQuery,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>({
    queryKey: ["AdminInvestorBidDocumentsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminInvestorBidDocumentsRowsQuery,
      Types.AdminInvestorBidDocumentsRowsQueryVariables
    >(AdminInvestorBidDocumentsRowsDocument, variables),
    ...options,
  })
}

useSuspenseAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRowsSuspense", variables]

useSuspenseAdminInvestorBidDocumentsRowsQuery.rootKey = "AdminInvestorBidDocumentsRows"

export const useInfiniteAdminInvestorBidDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminInvestorBidDocumentsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInvestorBidDocumentsRows.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsRowsQuery,
            Types.AdminInvestorBidDocumentsRowsQueryVariables
          >(AdminInvestorBidDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows.infinite", variables]

useInfiniteAdminInvestorBidDocumentsRowsQuery.rootKey =
  "AdminInvestorBidDocumentsRows.infinite"

export const useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery = <
  TData = InfiniteData<Types.AdminInvestorBidDocumentsRowsQuery>,
  TError = unknown,
>(
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminInvestorBidDocumentsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.AdminInvestorBidDocumentsRowsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminInvestorBidDocumentsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminInvestorBidDocumentsRowsQuery,
            Types.AdminInvestorBidDocumentsRowsQueryVariables
          >(AdminInvestorBidDocumentsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery.getKey = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
) => ["AdminInvestorBidDocumentsRows.infiniteSuspense", variables]

useSuspenseInfiniteAdminInvestorBidDocumentsRowsQuery.rootKey =
  "AdminInvestorBidDocumentsRows.infinite"

useAdminInvestorBidDocumentsRowsQuery.fetcher = (
  variables: Types.AdminInvestorBidDocumentsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminInvestorBidDocumentsRowsQuery,
    Types.AdminInvestorBidDocumentsRowsQueryVariables
  >(AdminInvestorBidDocumentsRowsDocument, variables, options)

export const TrackEventDocument = `
    mutation TrackEvent($input: RecordUserEventInput!) {
  recordUserEvent(input: $input) {
    __typename
    message
  }
}
    `

export const useTrackEventMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.TrackEventMutation,
    TError,
    Types.TrackEventMutationVariables,
    TContext
  >({
    mutationKey: ["TrackEvent"],
    mutationFn: (variables?: Types.TrackEventMutationVariables) =>
      publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
        TrackEventDocument,
        variables,
      )(),
    ...options,
  })
}

useTrackEventMutation.fetcher = (
  variables: Types.TrackEventMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.TrackEventMutation, Types.TrackEventMutationVariables>(
    TrackEventDocument,
    variables,
    options,
  )

export const LogPageViewDocument = `
    mutation LogPageView($input: LogPageViewInput!) {
  logPageView(input: $input) {
    __typename
    message
  }
}
    `

export const useLogPageViewMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LogPageViewMutation,
    TError,
    Types.LogPageViewMutationVariables,
    TContext
  >({
    mutationKey: ["LogPageView"],
    mutationFn: (variables?: Types.LogPageViewMutationVariables) =>
      publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
        LogPageViewDocument,
        variables,
      )(),
    ...options,
  })
}

useLogPageViewMutation.fetcher = (
  variables: Types.LogPageViewMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.LogPageViewMutation, Types.LogPageViewMutationVariables>(
    LogPageViewDocument,
    variables,
    options,
  )

export const InviteToOrgDocument = `
    mutation InviteToOrg($input: InviteToOrgInput!) {
  inviteToOrg(input: $input) {
    __typename
    pid
  }
}
    `

export const useInviteToOrgMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToOrgMutation,
    TError,
    Types.InviteToOrgMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToOrg"],
    mutationFn: (variables?: Types.InviteToOrgMutationVariables) =>
      publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
        InviteToOrgDocument,
        variables,
      )(),
    ...options,
  })
}

useInviteToOrgMutation.fetcher = (
  variables: Types.InviteToOrgMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InviteToOrgMutation, Types.InviteToOrgMutationVariables>(
    InviteToOrgDocument,
    variables,
    options,
  )

export const InviteToPortfolioDocument = `
    mutation InviteToPortfolio($input: InviteToPortfolioInput!) {
  inviteToPortfolio(input: $input) {
    __typename
    ... on Portfolio {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useInviteToPortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.InviteToPortfolioMutation,
    TError,
    Types.InviteToPortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["InviteToPortfolio"],
    mutationFn: (variables?: Types.InviteToPortfolioMutationVariables) =>
      publicFetcher<
        Types.InviteToPortfolioMutation,
        Types.InviteToPortfolioMutationVariables
      >(InviteToPortfolioDocument, variables)(),
    ...options,
  })
}

useInviteToPortfolioMutation.fetcher = (
  variables: Types.InviteToPortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InviteToPortfolioMutation,
    Types.InviteToPortfolioMutationVariables
  >(InviteToPortfolioDocument, variables, options)

export const CreateAndApplySiteLabelDocument = `
    mutation CreateAndApplySiteLabel($input: CreateAndApplySiteLabelInput!) {
  createAndApplySiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
    ... on CreateAndApplySiteLabelOutput {
      label {
        ...SiteLabelDetail
      }
    }
  }
}
    ${SiteLabelDetailFragmentDoc}
${SiteLabelRefFragmentDoc}
${UserRefFragmentDoc}`

export const useCreateAndApplySiteLabelMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateAndApplySiteLabelMutation,
    TError,
    Types.CreateAndApplySiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["CreateAndApplySiteLabel"],
    mutationFn: (variables?: Types.CreateAndApplySiteLabelMutationVariables) =>
      publicFetcher<
        Types.CreateAndApplySiteLabelMutation,
        Types.CreateAndApplySiteLabelMutationVariables
      >(CreateAndApplySiteLabelDocument, variables)(),
    ...options,
  })
}

useCreateAndApplySiteLabelMutation.fetcher = (
  variables: Types.CreateAndApplySiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateAndApplySiteLabelMutation,
    Types.CreateAndApplySiteLabelMutationVariables
  >(CreateAndApplySiteLabelDocument, variables, options)

export const ApplyLabelToSitesDocument = `
    mutation ApplyLabelToSites($input: ApplyLabelToSitesInput!) {
  applyLabelToSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useApplyLabelToSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ApplyLabelToSitesMutation,
    TError,
    Types.ApplyLabelToSitesMutationVariables,
    TContext
  >({
    mutationKey: ["ApplyLabelToSites"],
    mutationFn: (variables?: Types.ApplyLabelToSitesMutationVariables) =>
      publicFetcher<
        Types.ApplyLabelToSitesMutation,
        Types.ApplyLabelToSitesMutationVariables
      >(ApplyLabelToSitesDocument, variables)(),
    ...options,
  })
}

useApplyLabelToSitesMutation.fetcher = (
  variables: Types.ApplyLabelToSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ApplyLabelToSitesMutation,
    Types.ApplyLabelToSitesMutationVariables
  >(ApplyLabelToSitesDocument, variables, options)

export const RemoveLabelFromSitesDocument = `
    mutation RemoveLabelFromSites($input: RemoveLabelFromSitesInput!) {
  removeLabelFromSites(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useRemoveLabelFromSitesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromSitesMutation,
    TError,
    Types.RemoveLabelFromSitesMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromSites"],
    mutationFn: (variables?: Types.RemoveLabelFromSitesMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromSitesMutation,
        Types.RemoveLabelFromSitesMutationVariables
      >(RemoveLabelFromSitesDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromSitesMutation.fetcher = (
  variables: Types.RemoveLabelFromSitesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromSitesMutation,
    Types.RemoveLabelFromSitesMutationVariables
  >(RemoveLabelFromSitesDocument, variables, options)

export const DeleteSiteLabelDocument = `
    mutation DeleteSiteLabel($input: DeleteSiteLabelInput!) {
  deleteSiteLabel(input: $input) {
    __typename
    ... on Error {
      message
    }
  }
}
    `

export const useDeleteSiteLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteSiteLabelMutation,
    TError,
    Types.DeleteSiteLabelMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteSiteLabel"],
    mutationFn: (variables?: Types.DeleteSiteLabelMutationVariables) =>
      publicFetcher<
        Types.DeleteSiteLabelMutation,
        Types.DeleteSiteLabelMutationVariables
      >(DeleteSiteLabelDocument, variables)(),
    ...options,
  })
}

useDeleteSiteLabelMutation.fetcher = (
  variables: Types.DeleteSiteLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteSiteLabelMutation, Types.DeleteSiteLabelMutationVariables>(
    DeleteSiteLabelDocument,
    variables,
    options,
  )

export const UploadDocumentDocument = `
    mutation UploadDocument($file: Upload!, $tag: DocumentTag, $associations: DocumentAssociationsInput!) {
  documents {
    uploadDocument(upload: $file, tag: $tag, associations: $associations) {
      __typename
      ... on Document {
        ...Document
        csvPeek
      }
      ... on Error {
        message
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useUploadDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadDocumentMutation,
    TError,
    Types.UploadDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["UploadDocument"],
    mutationFn: (variables?: Types.UploadDocumentMutationVariables) =>
      publicFetcher<
        Types.UploadDocumentMutation,
        Types.UploadDocumentMutationVariables
      >(UploadDocumentDocument, variables)(),
    ...options,
  })
}

useUploadDocumentMutation.fetcher = (
  variables: Types.UploadDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UploadDocumentMutation, Types.UploadDocumentMutationVariables>(
    UploadDocumentDocument,
    variables,
    options,
  )

export const DeleteDocumentDocument = `
    mutation DeleteDocument($documentPid: String!) {
  documents {
    deleteDocument(documentPid: $documentPid) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useDeleteDocumentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteDocumentMutation,
    TError,
    Types.DeleteDocumentMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteDocument"],
    mutationFn: (variables?: Types.DeleteDocumentMutationVariables) =>
      publicFetcher<
        Types.DeleteDocumentMutation,
        Types.DeleteDocumentMutationVariables
      >(DeleteDocumentDocument, variables)(),
    ...options,
  })
}

useDeleteDocumentMutation.fetcher = (
  variables: Types.DeleteDocumentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.DeleteDocumentMutation, Types.DeleteDocumentMutationVariables>(
    DeleteDocumentDocument,
    variables,
    options,
  )

export const SetDocumentTagDocument = `
    mutation SetDocumentTag($pid: String!, $tag: DocumentTag!) {
  documents {
    setDocumentTag(pid: $pid, tag: $tag) {
      __typename
      ... on Document {
        pid
        tag
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useSetDocumentTagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetDocumentTagMutation,
    TError,
    Types.SetDocumentTagMutationVariables,
    TContext
  >({
    mutationKey: ["SetDocumentTag"],
    mutationFn: (variables?: Types.SetDocumentTagMutationVariables) =>
      publicFetcher<
        Types.SetDocumentTagMutation,
        Types.SetDocumentTagMutationVariables
      >(SetDocumentTagDocument, variables)(),
    ...options,
  })
}

useSetDocumentTagMutation.fetcher = (
  variables: Types.SetDocumentTagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetDocumentTagMutation, Types.SetDocumentTagMutationVariables>(
    SetDocumentTagDocument,
    variables,
    options,
  )

export const MoveDocumentToProjectDocument = `
    mutation MoveDocumentToProject($documentPid: String!, $projectPid: String!) {
  documents {
    moveDocumentToProject(documentPid: $documentPid, projectPid: $projectPid) {
      __typename
      ... on Document {
        ...Document
      }
      ... on Error {
        message
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useMoveDocumentToProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MoveDocumentToProjectMutation,
    TError,
    Types.MoveDocumentToProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentToProjectMutation,
    TError,
    Types.MoveDocumentToProjectMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentToProject"],
    mutationFn: (variables?: Types.MoveDocumentToProjectMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentToProjectMutation,
        Types.MoveDocumentToProjectMutationVariables
      >(MoveDocumentToProjectDocument, variables)(),
    ...options,
  })
}

useMoveDocumentToProjectMutation.fetcher = (
  variables: Types.MoveDocumentToProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentToProjectMutation,
    Types.MoveDocumentToProjectMutationVariables
  >(MoveDocumentToProjectDocument, variables, options)

export const MoveDocumentToSiteDocument = `
    mutation MoveDocumentToSite($documentPid: String!, $sitePid: String!, $projectPid: String!) {
  documents {
    moveDocumentToSite(
      documentPid: $documentPid
      sitePid: $sitePid
      projectPid: $projectPid
    ) {
      __typename
      ... on Document {
        ...Document
      }
      ... on Error {
        message
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useMoveDocumentToSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.MoveDocumentToSiteMutation,
    TError,
    Types.MoveDocumentToSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentToSiteMutation,
    TError,
    Types.MoveDocumentToSiteMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentToSite"],
    mutationFn: (variables?: Types.MoveDocumentToSiteMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentToSiteMutation,
        Types.MoveDocumentToSiteMutationVariables
      >(MoveDocumentToSiteDocument, variables)(),
    ...options,
  })
}

useMoveDocumentToSiteMutation.fetcher = (
  variables: Types.MoveDocumentToSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentToSiteMutation,
    Types.MoveDocumentToSiteMutationVariables
  >(MoveDocumentToSiteDocument, variables, options)

export const BundleUpsertSiteDocument = `
    mutation BundleUpsertSite($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        upsertSite(sitePid: $sitePid) {
          site {
            pid
          }
        }
      }
    }
  }
}
    `

export const useBundleUpsertSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleUpsertSiteMutation,
    TError,
    Types.BundleUpsertSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleUpsertSiteMutation,
    TError,
    Types.BundleUpsertSiteMutationVariables,
    TContext
  >({
    mutationKey: ["BundleUpsertSite"],
    mutationFn: (variables?: Types.BundleUpsertSiteMutationVariables) =>
      publicFetcher<
        Types.BundleUpsertSiteMutation,
        Types.BundleUpsertSiteMutationVariables
      >(BundleUpsertSiteDocument, variables)(),
    ...options,
  })
}

useBundleUpsertSiteMutation.fetcher = (
  variables: Types.BundleUpsertSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleUpsertSiteMutation,
    Types.BundleUpsertSiteMutationVariables
  >(BundleUpsertSiteDocument, variables, options)

export const BundleRemoveSiteDocument = `
    mutation BundleRemoveSite($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        removeSite(sitePid: $sitePid)
      }
    }
  }
}
    `

export const useBundleRemoveSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleRemoveSiteMutation,
    TError,
    Types.BundleRemoveSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleRemoveSiteMutation,
    TError,
    Types.BundleRemoveSiteMutationVariables,
    TContext
  >({
    mutationKey: ["BundleRemoveSite"],
    mutationFn: (variables?: Types.BundleRemoveSiteMutationVariables) =>
      publicFetcher<
        Types.BundleRemoveSiteMutation,
        Types.BundleRemoveSiteMutationVariables
      >(BundleRemoveSiteDocument, variables)(),
    ...options,
  })
}

useBundleRemoveSiteMutation.fetcher = (
  variables: Types.BundleRemoveSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleRemoveSiteMutation,
    Types.BundleRemoveSiteMutationVariables
  >(BundleRemoveSiteDocument, variables, options)

export const BundleArchiveBundleDocument = `
    mutation BundleArchiveBundle($orgSlug: String!, $bundlePid: String!, $archive: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      edit(pid: $bundlePid) {
        archiveBundle(archive: $archive)
      }
    }
  }
}
    `

export const useBundleArchiveBundleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BundleArchiveBundleMutation,
    TError,
    Types.BundleArchiveBundleMutationVariables,
    TContext
  >({
    mutationKey: ["BundleArchiveBundle"],
    mutationFn: (variables?: Types.BundleArchiveBundleMutationVariables) =>
      publicFetcher<
        Types.BundleArchiveBundleMutation,
        Types.BundleArchiveBundleMutationVariables
      >(BundleArchiveBundleDocument, variables)(),
    ...options,
  })
}

useBundleArchiveBundleMutation.fetcher = (
  variables: Types.BundleArchiveBundleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleArchiveBundleMutation,
    Types.BundleArchiveBundleMutationVariables
  >(BundleArchiveBundleDocument, variables, options)

export const SetMeteringDocument = `
    mutation SetMetering($input: SetMeteringInput!) {
  setMetering(input: $input)
}
    `

export const useSetMeteringMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetMeteringMutation,
    TError,
    Types.SetMeteringMutationVariables,
    TContext
  >({
    mutationKey: ["SetMetering"],
    mutationFn: (variables?: Types.SetMeteringMutationVariables) =>
      publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
        SetMeteringDocument,
        variables,
      )(),
    ...options,
  })
}

useSetMeteringMutation.fetcher = (
  variables: Types.SetMeteringMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetMeteringMutation, Types.SetMeteringMutationVariables>(
    SetMeteringDocument,
    variables,
    options,
  )

export const SaveSiteShapesDocument = `
    mutation SaveSiteShapes($input: SaveSiteShapesInput!) {
  saveSiteShapes(input: $input)
}
    `

export const useSaveSiteShapesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SaveSiteShapesMutation,
    TError,
    Types.SaveSiteShapesMutationVariables,
    TContext
  >({
    mutationKey: ["SaveSiteShapes"],
    mutationFn: (variables?: Types.SaveSiteShapesMutationVariables) =>
      publicFetcher<
        Types.SaveSiteShapesMutation,
        Types.SaveSiteShapesMutationVariables
      >(SaveSiteShapesDocument, variables)(),
    ...options,
  })
}

useSaveSiteShapesMutation.fetcher = (
  variables: Types.SaveSiteShapesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SaveSiteShapesMutation, Types.SaveSiteShapesMutationVariables>(
    SaveSiteShapesDocument,
    variables,
    options,
  )

export const UpdatePropertyDataDocument = `
    mutation UpdatePropertyData($input: UpdatePropertyDataInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  updatePropertyData(input: $input, sourceType: $sourceType, sourceId: $sourceId)
}
    `

export const useUpdatePropertyDataMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdatePropertyDataMutation,
    TError,
    Types.UpdatePropertyDataMutationVariables,
    TContext
  >({
    mutationKey: ["UpdatePropertyData"],
    mutationFn: (variables?: Types.UpdatePropertyDataMutationVariables) =>
      publicFetcher<
        Types.UpdatePropertyDataMutation,
        Types.UpdatePropertyDataMutationVariables
      >(UpdatePropertyDataDocument, variables)(),
    ...options,
  })
}

useUpdatePropertyDataMutation.fetcher = (
  variables: Types.UpdatePropertyDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdatePropertyDataMutation,
    Types.UpdatePropertyDataMutationVariables
  >(UpdatePropertyDataDocument, variables, options)

export const BulkUpdatePropertyDataDocument = `
    mutation BulkUpdatePropertyData($input: BulkUpdatePropertyDataInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  bulkUpdatePropertyData(
    input: $input
    sourceType: $sourceType
    sourceId: $sourceId
  )
}
    `

export const useBulkUpdatePropertyDataMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.BulkUpdatePropertyDataMutation,
    TError,
    Types.BulkUpdatePropertyDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BulkUpdatePropertyDataMutation,
    TError,
    Types.BulkUpdatePropertyDataMutationVariables,
    TContext
  >({
    mutationKey: ["BulkUpdatePropertyData"],
    mutationFn: (variables?: Types.BulkUpdatePropertyDataMutationVariables) =>
      publicFetcher<
        Types.BulkUpdatePropertyDataMutation,
        Types.BulkUpdatePropertyDataMutationVariables
      >(BulkUpdatePropertyDataDocument, variables)(),
    ...options,
  })
}

useBulkUpdatePropertyDataMutation.fetcher = (
  variables: Types.BulkUpdatePropertyDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BulkUpdatePropertyDataMutation,
    Types.BulkUpdatePropertyDataMutationVariables
  >(BulkUpdatePropertyDataDocument, variables, options)

export const SetSiteStatusDocument = `
    mutation SetSiteStatus($orgSlug: String!, $sitePids: [String!]!, $status: SiteStatus!, $reason: StatusReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    setStatus(
      status: $status
      sitePids: $sitePids
      reason: $reason
      customReason: $customReason
    ) {
      message
    }
  }
}
    `

export const useSetSiteStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetSiteStatusMutation,
    TError,
    Types.SetSiteStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetSiteStatus"],
    mutationFn: (variables?: Types.SetSiteStatusMutationVariables) =>
      publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
        SetSiteStatusDocument,
        variables,
      )(),
    ...options,
  })
}

useSetSiteStatusMutation.fetcher = (
  variables: Types.SetSiteStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetSiteStatusMutation, Types.SetSiteStatusMutationVariables>(
    SetSiteStatusDocument,
    variables,
    options,
  )

export const NewCsvImportDocument = `
    mutation NewCsvImport($portfolioPid: String!, $upload: Upload!) {
  newCsvImport(portfolioPid: $portfolioPid, upload: $upload) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useNewCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.NewCsvImportMutation,
    TError,
    Types.NewCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["NewCsvImport"],
    mutationFn: (variables?: Types.NewCsvImportMutationVariables) =>
      publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
        NewCsvImportDocument,
        variables,
      )(),
    ...options,
  })
}

useNewCsvImportMutation.fetcher = (
  variables: Types.NewCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.NewCsvImportMutation, Types.NewCsvImportMutationVariables>(
    NewCsvImportDocument,
    variables,
    options,
  )

export const SetCsvImportSchemaDocument = `
    mutation SetCsvImportSchema($csvImportPid: String!, $schemaPid: String!) {
  setCsvImportSchema(csvImportPid: $csvImportPid, schemaPid: $schemaPid) {
    __typename
  }
}
    `

export const useSetCsvImportSchemaMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportSchemaMutation,
    TError,
    Types.SetCsvImportSchemaMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportSchema"],
    mutationFn: (variables?: Types.SetCsvImportSchemaMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportSchemaMutation,
        Types.SetCsvImportSchemaMutationVariables
      >(SetCsvImportSchemaDocument, variables)(),
    ...options,
  })
}

useSetCsvImportSchemaMutation.fetcher = (
  variables: Types.SetCsvImportSchemaMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportSchemaMutation,
    Types.SetCsvImportSchemaMutationVariables
  >(SetCsvImportSchemaDocument, variables, options)

export const UpdateCsvImportColumnMappingsDocument = `
    mutation UpdateCsvImportColumnMappings($csvImportPid: String!, $columnMappings: ColumnMappingsInput!) {
  updateCsvImportColumnMappings(
    pid: $csvImportPid
    columnMappings: $columnMappings
  ) {
    __typename
    ... on CsvImport {
      pid
      buildingTypes
      guessedBuildingTypeMappings {
        office
        multifamily
        hotel
        hospital
        outpatient
        retailStandalone
        retailStripMall
        quickServiceRestaurant
        fullServiceRestaurant
        primarySchool
        secondarySchool
        singleFamilyDetached
        warehouse
        dataCenter
        coldStorage
        industrial
        parkingLot
        supermarket
      }
    }
    ... on Error {
      message
    }
  }
}
    `

export const useUpdateCsvImportColumnMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCsvImportColumnMappingsMutation,
    TError,
    Types.UpdateCsvImportColumnMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCsvImportColumnMappings"],
    mutationFn: (variables?: Types.UpdateCsvImportColumnMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateCsvImportColumnMappingsMutation,
        Types.UpdateCsvImportColumnMappingsMutationVariables
      >(UpdateCsvImportColumnMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateCsvImportColumnMappingsMutation.fetcher = (
  variables: Types.UpdateCsvImportColumnMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCsvImportColumnMappingsMutation,
    Types.UpdateCsvImportColumnMappingsMutationVariables
  >(UpdateCsvImportColumnMappingsDocument, variables, options)

export const AddLabelToCsvImportDocument = `
    mutation AddLabelToCsvImport($csvImportPid: String!, $labelPid: String!) {
  addLabelToCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useAddLabelToCsvImportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddLabelToCsvImportMutation,
    TError,
    Types.AddLabelToCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["AddLabelToCsvImport"],
    mutationFn: (variables?: Types.AddLabelToCsvImportMutationVariables) =>
      publicFetcher<
        Types.AddLabelToCsvImportMutation,
        Types.AddLabelToCsvImportMutationVariables
      >(AddLabelToCsvImportDocument, variables)(),
    ...options,
  })
}

useAddLabelToCsvImportMutation.fetcher = (
  variables: Types.AddLabelToCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddLabelToCsvImportMutation,
    Types.AddLabelToCsvImportMutationVariables
  >(AddLabelToCsvImportDocument, variables, options)

export const RemoveLabelFromCsvImportDocument = `
    mutation RemoveLabelFromCsvImport($csvImportPid: String!, $labelPid: String!) {
  removeLabelFromCsvImport(csvImportPid: $csvImportPid, labelPid: $labelPid) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useRemoveLabelFromCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveLabelFromCsvImportMutation,
    TError,
    Types.RemoveLabelFromCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveLabelFromCsvImport"],
    mutationFn: (variables?: Types.RemoveLabelFromCsvImportMutationVariables) =>
      publicFetcher<
        Types.RemoveLabelFromCsvImportMutation,
        Types.RemoveLabelFromCsvImportMutationVariables
      >(RemoveLabelFromCsvImportDocument, variables)(),
    ...options,
  })
}

useRemoveLabelFromCsvImportMutation.fetcher = (
  variables: Types.RemoveLabelFromCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveLabelFromCsvImportMutation,
    Types.RemoveLabelFromCsvImportMutationVariables
  >(RemoveLabelFromCsvImportDocument, variables, options)

export const CreateLabelForCsvImportDocument = `
    mutation CreateLabelForCsvImport($csvImportPid: String!, $labelName: String!, $labelColor: LabelColor) {
  createAndApplyLabelToCsvImport(
    csvImportPid: $csvImportPid
    labelName: $labelName
    labelColor: $labelColor
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useCreateLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateLabelForCsvImportMutation,
    TError,
    Types.CreateLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateLabelForCsvImport"],
    mutationFn: (variables?: Types.CreateLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.CreateLabelForCsvImportMutation,
        Types.CreateLabelForCsvImportMutationVariables
      >(CreateLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useCreateLabelForCsvImportMutation.fetcher = (
  variables: Types.CreateLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateLabelForCsvImportMutation,
    Types.CreateLabelForCsvImportMutationVariables
  >(CreateLabelForCsvImportDocument, variables, options)

export const DeleteLabelForCsvImportDocument = `
    mutation DeleteLabelForCsvImport($csvImportPid: String!, $labelPid: String!) {
  deleteLabelAndRemoveFromCsvImport(
    csvImportPid: $csvImportPid
    labelPid: $labelPid
  ) {
    __typename
    ... on CsvImport {
      ...CsvImportLabels
    }
    ... on Error {
      message
    }
  }
}
    ${CsvImportLabelsFragmentDoc}`

export const useDeleteLabelForCsvImportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteLabelForCsvImportMutation,
    TError,
    Types.DeleteLabelForCsvImportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteLabelForCsvImport"],
    mutationFn: (variables?: Types.DeleteLabelForCsvImportMutationVariables) =>
      publicFetcher<
        Types.DeleteLabelForCsvImportMutation,
        Types.DeleteLabelForCsvImportMutationVariables
      >(DeleteLabelForCsvImportDocument, variables)(),
    ...options,
  })
}

useDeleteLabelForCsvImportMutation.fetcher = (
  variables: Types.DeleteLabelForCsvImportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteLabelForCsvImportMutation,
    Types.DeleteLabelForCsvImportMutationVariables
  >(DeleteLabelForCsvImportDocument, variables, options)

export const UpdateBuildingTypeMappingsDocument = `
    mutation UpdateBuildingTypeMappings($csvImportPid: String!, $mappings: BuildingTypeMappingsInput!) {
  updateBuildingTypeMappings(csvImportPid: $csvImportPid, mappings: $mappings) {
    __typename
    ... on CsvImport {
      pid
    }
    ... on Error {
      message
    }
  }
}
    `

export const useUpdateBuildingTypeMappingsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UpdateBuildingTypeMappingsMutation,
    TError,
    Types.UpdateBuildingTypeMappingsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBuildingTypeMappingsMutation,
    TError,
    Types.UpdateBuildingTypeMappingsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBuildingTypeMappings"],
    mutationFn: (variables?: Types.UpdateBuildingTypeMappingsMutationVariables) =>
      publicFetcher<
        Types.UpdateBuildingTypeMappingsMutation,
        Types.UpdateBuildingTypeMappingsMutationVariables
      >(UpdateBuildingTypeMappingsDocument, variables)(),
    ...options,
  })
}

useUpdateBuildingTypeMappingsMutation.fetcher = (
  variables: Types.UpdateBuildingTypeMappingsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateBuildingTypeMappingsMutation,
    Types.UpdateBuildingTypeMappingsMutationVariables
  >(UpdateBuildingTypeMappingsDocument, variables, options)

export const DropCsvImportRowDocument = `
    mutation DropCsvImportRow($rowId: Int!) {
  dropCsvImportRow(rowId: $rowId) {
    id
    droppedAt
  }
}
    `

export const useDropCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DropCsvImportRowMutation,
    TError,
    Types.DropCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["DropCsvImportRow"],
    mutationFn: (variables?: Types.DropCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.DropCsvImportRowMutation,
        Types.DropCsvImportRowMutationVariables
      >(DropCsvImportRowDocument, variables)(),
    ...options,
  })
}

useDropCsvImportRowMutation.fetcher = (
  variables: Types.DropCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DropCsvImportRowMutation,
    Types.DropCsvImportRowMutationVariables
  >(DropCsvImportRowDocument, variables, options)

export const AmendCsvImportRowDocument = `
    mutation AmendCsvImportRow($rowId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  amendCsvImportRow(rowId: $rowId, amendments: $amendments) {
    id
    amendments {
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `

export const useAmendCsvImportRowMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AmendCsvImportRowMutation,
    TError,
    Types.AmendCsvImportRowMutationVariables,
    TContext
  >({
    mutationKey: ["AmendCsvImportRow"],
    mutationFn: (variables?: Types.AmendCsvImportRowMutationVariables) =>
      publicFetcher<
        Types.AmendCsvImportRowMutation,
        Types.AmendCsvImportRowMutationVariables
      >(AmendCsvImportRowDocument, variables)(),
    ...options,
  })
}

useAmendCsvImportRowMutation.fetcher = (
  variables: Types.AmendCsvImportRowMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AmendCsvImportRowMutation,
    Types.AmendCsvImportRowMutationVariables
  >(AmendCsvImportRowDocument, variables, options)

export const SetCsvImportRowBuildingDataDocument = `
    mutation SetCsvImportRowBuildingData($rowId: Int!, $buildingDataId: Int!) {
  setCsvImportRowBuildingData(rowId: $rowId, buildingDataId: $buildingDataId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
  }
}
    `

export const useSetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCsvImportRowBuildingDataMutation,
    TError,
    Types.SetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["SetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.SetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.SetCsvImportRowBuildingDataMutation,
        Types.SetCsvImportRowBuildingDataMutationVariables
      >(SetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useSetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.SetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCsvImportRowBuildingDataMutation,
    Types.SetCsvImportRowBuildingDataMutationVariables
  >(SetCsvImportRowBuildingDataDocument, variables, options)

export const UnsetCsvImportRowBuildingDataDocument = `
    mutation UnsetCsvImportRowBuildingData($rowId: Int!) {
  unsetCsvImportRowBuildingData(rowId: $rowId) {
    id
    buildingData {
      id
      propertyName
      fullAddress
      streetAddress
      city
      state
      zipCode
      buildingType
      sqft
      roofSqft
      numStories
      latitude
      longitude
      buildingFootprintGeojson
      yearBuilt
      numUnitsTotal
      numUnitsStudio
      numUnits1Bd
      numUnits2Bd
      numUnits3Bd
      numUnits4Bd
    }
    amendments {
      buildingType
      customerId
      latitude
      longitude
      name
      numStories
      roofSqft
      sqft
      yearBuilt
      mfUnits {
        numUnitsTotal
        numUnitsStudio
        numUnits1Bd
        numUnits2Bd
        numUnits3Bd
        numUnits4Bd
      }
    }
  }
}
    `

export const useUnsetCsvImportRowBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UnsetCsvImportRowBuildingDataMutation,
    TError,
    Types.UnsetCsvImportRowBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["UnsetCsvImportRowBuildingData"],
    mutationFn: (variables?: Types.UnsetCsvImportRowBuildingDataMutationVariables) =>
      publicFetcher<
        Types.UnsetCsvImportRowBuildingDataMutation,
        Types.UnsetCsvImportRowBuildingDataMutationVariables
      >(UnsetCsvImportRowBuildingDataDocument, variables)(),
    ...options,
  })
}

useUnsetCsvImportRowBuildingDataMutation.fetcher = (
  variables: Types.UnsetCsvImportRowBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UnsetCsvImportRowBuildingDataMutation,
    Types.UnsetCsvImportRowBuildingDataMutationVariables
  >(UnsetCsvImportRowBuildingDataDocument, variables, options)

export const CreateThirdPartySupplierDocument = `
    mutation CreateThirdPartySupplier($name: String!) {
  createThirdPartySupplier(name: $name) {
    id
    pid
    name
  }
}
    `

export const useCreateThirdPartySupplierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateThirdPartySupplierMutation,
    TError,
    Types.CreateThirdPartySupplierMutationVariables,
    TContext
  >({
    mutationKey: ["CreateThirdPartySupplier"],
    mutationFn: (variables?: Types.CreateThirdPartySupplierMutationVariables) =>
      publicFetcher<
        Types.CreateThirdPartySupplierMutation,
        Types.CreateThirdPartySupplierMutationVariables
      >(CreateThirdPartySupplierDocument, variables)(),
    ...options,
  })
}

useCreateThirdPartySupplierMutation.fetcher = (
  variables: Types.CreateThirdPartySupplierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateThirdPartySupplierMutation,
    Types.CreateThirdPartySupplierMutationVariables
  >(CreateThirdPartySupplierDocument, variables, options)

export const AddMeterDocument = `
    mutation AddMeter($sitePid: String!, $loadCategory: LoadCategory!, $label: String, $buildingType: BuildingType, $tariffPid: String) {
  billingMeters(sitePid: $sitePid) {
    addMeter(
      loadCategory: $loadCategory
      label: $label
      buildingType: $buildingType
      tariffPid: $tariffPid
    ) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useAddMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddMeterMutation,
    TError,
    Types.AddMeterMutationVariables,
    TContext
  >({
    mutationKey: ["AddMeter"],
    mutationFn: (variables?: Types.AddMeterMutationVariables) =>
      publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
        AddMeterDocument,
        variables,
      )(),
    ...options,
  })
}

useAddMeterMutation.fetcher = (
  variables: Types.AddMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddMeterMutation, Types.AddMeterMutationVariables>(
    AddMeterDocument,
    variables,
    options,
  )

export const EditBillingMeterAnnualDocument = `
    mutation EditBillingMeterAnnual($sitePid: String!, $meterPid: String!, $kwh: Float, $kw: Float, $costUsd: Float) {
  billingMeters(sitePid: $sitePid) {
    editAnnual(meterPid: $meterPid, kwh: $kwh, kw: $kw, costUsd: $costUsd) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useEditBillingMeterAnnualMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterAnnualMutation,
    TError,
    Types.EditBillingMeterAnnualMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterAnnual"],
    mutationFn: (variables?: Types.EditBillingMeterAnnualMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterAnnualMutation,
        Types.EditBillingMeterAnnualMutationVariables
      >(EditBillingMeterAnnualDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterAnnualMutation.fetcher = (
  variables: Types.EditBillingMeterAnnualMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterAnnualMutation,
    Types.EditBillingMeterAnnualMutationVariables
  >(EditBillingMeterAnnualDocument, variables, options)

export const EditBillingMeterLabelDocument = `
    mutation EditBillingMeterLabel($sitePid: String!, $meterPid: String!, $label: String!) {
  billingMeters(sitePid: $sitePid) {
    editLabel(meterPid: $meterPid, label: $label) {
      ...MutBillingMeter
    }
  }
}
    ${MutBillingMeterFragmentDoc}`

export const useEditBillingMeterLabelMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterLabelMutation,
    TError,
    Types.EditBillingMeterLabelMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterLabel"],
    mutationFn: (variables?: Types.EditBillingMeterLabelMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterLabelMutation,
        Types.EditBillingMeterLabelMutationVariables
      >(EditBillingMeterLabelDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterLabelMutation.fetcher = (
  variables: Types.EditBillingMeterLabelMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterLabelMutation,
    Types.EditBillingMeterLabelMutationVariables
  >(EditBillingMeterLabelDocument, variables, options)

export const EditBillingMeterBuildingTypeDocument = `
    mutation EditBillingMeterBuildingType($sitePid: String!, $meterPid: String!, $buildingType: BuildingType!) {
  billingMeters(sitePid: $sitePid) {
    editBuildingType(meterPid: $meterPid, buildingType: $buildingType) {
      __typename
    }
  }
}
    `

export const useEditBillingMeterBuildingTypeMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBillingMeterBuildingTypeMutation,
    TError,
    Types.EditBillingMeterBuildingTypeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterBuildingTypeMutation,
    TError,
    Types.EditBillingMeterBuildingTypeMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterBuildingType"],
    mutationFn: (variables?: Types.EditBillingMeterBuildingTypeMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterBuildingTypeMutation,
        Types.EditBillingMeterBuildingTypeMutationVariables
      >(EditBillingMeterBuildingTypeDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterBuildingTypeMutation.fetcher = (
  variables: Types.EditBillingMeterBuildingTypeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterBuildingTypeMutation,
    Types.EditBillingMeterBuildingTypeMutationVariables
  >(EditBillingMeterBuildingTypeDocument, variables, options)

export const EditBillingMeterTariffDocument = `
    mutation EditBillingMeterTariff($sitePid: String!, $meterPid: String!, $tariffPid: String!) {
  billingMeters(sitePid: $sitePid) {
    editBauTariff(meterPid: $meterPid, tariffPid: $tariffPid) {
      __typename
    }
  }
}
    `

export const useEditBillingMeterTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterTariffMutation,
    TError,
    Types.EditBillingMeterTariffMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterTariff"],
    mutationFn: (variables?: Types.EditBillingMeterTariffMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterTariffMutation,
        Types.EditBillingMeterTariffMutationVariables
      >(EditBillingMeterTariffDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterTariffMutation.fetcher = (
  variables: Types.EditBillingMeterTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterTariffMutation,
    Types.EditBillingMeterTariffMutationVariables
  >(EditBillingMeterTariffDocument, variables, options)

export const AddBillingMeterIntervalsDocument = `
    mutation AddBillingMeterIntervals($sitePid: String!, $input: BillingIntervalsExtraction!) {
  billingMeters(sitePid: $sitePid) {
    addIntervals(input: $input)
  }
}
    `

export const useAddBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterIntervalsMutation,
    TError,
    Types.AddBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterIntervals"],
    mutationFn: (variables?: Types.AddBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterIntervalsMutation,
        Types.AddBillingMeterIntervalsMutationVariables
      >(AddBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterIntervalsMutation.fetcher = (
  variables: Types.AddBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterIntervalsMutation,
    Types.AddBillingMeterIntervalsMutationVariables
  >(AddBillingMeterIntervalsDocument, variables, options)

export const AddBillingMeterMonthliesDocument = `
    mutation AddBillingMeterMonthlies($sitePid: String!, $input: MonthlyExtraction!) {
  billingMeters(sitePid: $sitePid) {
    addMonthlies(input: $input)
  }
}
    `

export const useAddBillingMeterMonthliesMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBillingMeterMonthliesMutation,
    TError,
    Types.AddBillingMeterMonthliesMutationVariables,
    TContext
  >({
    mutationKey: ["AddBillingMeterMonthlies"],
    mutationFn: (variables?: Types.AddBillingMeterMonthliesMutationVariables) =>
      publicFetcher<
        Types.AddBillingMeterMonthliesMutation,
        Types.AddBillingMeterMonthliesMutationVariables
      >(AddBillingMeterMonthliesDocument, variables)(),
    ...options,
  })
}

useAddBillingMeterMonthliesMutation.fetcher = (
  variables: Types.AddBillingMeterMonthliesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBillingMeterMonthliesMutation,
    Types.AddBillingMeterMonthliesMutationVariables
  >(AddBillingMeterMonthliesDocument, variables, options)

export const BillingMeterEditTenantDetailsDocument = `
    mutation BillingMeterEditTenantDetails($sitePid: String!, $meterPid: String!, $input: MFUnitBreakdownInput!) {
  billingMeters(sitePid: $sitePid) {
    editTenantDetails(meterPid: $meterPid, input: $input)
  }
}
    `

export const useBillingMeterEditTenantDetailsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.BillingMeterEditTenantDetailsMutation,
    TError,
    Types.BillingMeterEditTenantDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["BillingMeterEditTenantDetails"],
    mutationFn: (variables?: Types.BillingMeterEditTenantDetailsMutationVariables) =>
      publicFetcher<
        Types.BillingMeterEditTenantDetailsMutation,
        Types.BillingMeterEditTenantDetailsMutationVariables
      >(BillingMeterEditTenantDetailsDocument, variables)(),
    ...options,
  })
}

useBillingMeterEditTenantDetailsMutation.fetcher = (
  variables: Types.BillingMeterEditTenantDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterEditTenantDetailsMutation,
    Types.BillingMeterEditTenantDetailsMutationVariables
  >(BillingMeterEditTenantDetailsDocument, variables, options)

export const RemoveBillingMeterIntervalsDocument = `
    mutation RemoveBillingMeterIntervals($sitePid: String!, $meterPid: String!, $intervalsId: Int!) {
  billingMeters(sitePid: $sitePid) {
    removeIntervals(meterPid: $meterPid, intervalsId: $intervalsId)
  }
}
    `

export const useRemoveBillingMeterIntervalsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterIntervalsMutation,
    TError,
    Types.RemoveBillingMeterIntervalsMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterIntervals"],
    mutationFn: (variables?: Types.RemoveBillingMeterIntervalsMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterIntervalsMutation,
        Types.RemoveBillingMeterIntervalsMutationVariables
      >(RemoveBillingMeterIntervalsDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterIntervalsMutation.fetcher = (
  variables: Types.RemoveBillingMeterIntervalsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterIntervalsMutation,
    Types.RemoveBillingMeterIntervalsMutationVariables
  >(RemoveBillingMeterIntervalsDocument, variables, options)

export const SetArchivedBillingMeterDocument = `
    mutation SetArchivedBillingMeter($sitePid: String!, $meterPid: String!, $archived: Boolean!) {
  billingMeters(sitePid: $sitePid) {
    editArchived(meterPid: $meterPid, archived: $archived) {
      __typename
    }
  }
}
    `

export const useSetArchivedBillingMeterMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchivedBillingMeterMutation,
    TError,
    Types.SetArchivedBillingMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchivedBillingMeter"],
    mutationFn: (variables?: Types.SetArchivedBillingMeterMutationVariables) =>
      publicFetcher<
        Types.SetArchivedBillingMeterMutation,
        Types.SetArchivedBillingMeterMutationVariables
      >(SetArchivedBillingMeterDocument, variables)(),
    ...options,
  })
}

useSetArchivedBillingMeterMutation.fetcher = (
  variables: Types.SetArchivedBillingMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchivedBillingMeterMutation,
    Types.SetArchivedBillingMeterMutationVariables
  >(SetArchivedBillingMeterDocument, variables, options)

export const EditBillingMeterMonthDocument = `
    mutation EditBillingMeterMonth($sitePid: String!, $meterPid: String!, $start: Date!, $end: Date!, $totalKwh: Float!, $peakKw: Float, $totalCostUsd: Float, $supplyCostUsd: Float, $transmissionCostUsd: Float) {
  billingMeters(sitePid: $sitePid) {
    editMonth(
      meterPid: $meterPid
      start: $start
      end: $end
      totalKwh: $totalKwh
      peakKw: $peakKw
      totalCostUsd: $totalCostUsd
      supplyCostUsd: $supplyCostUsd
      transmissionCostUsd: $transmissionCostUsd
    ) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useEditBillingMeterMonthMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBillingMeterMonthMutation,
    TError,
    Types.EditBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["EditBillingMeterMonth"],
    mutationFn: (variables?: Types.EditBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.EditBillingMeterMonthMutation,
        Types.EditBillingMeterMonthMutationVariables
      >(EditBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useEditBillingMeterMonthMutation.fetcher = (
  variables: Types.EditBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBillingMeterMonthMutation,
    Types.EditBillingMeterMonthMutationVariables
  >(EditBillingMeterMonthDocument, variables, options)

export const RemoveBillingMeterMonthDocument = `
    mutation RemoveBillingMeterMonth($sitePid: String!, $meterPid: String!, $monthId: Int!) {
  billingMeters(sitePid: $sitePid) {
    removeMonth(meterPid: $meterPid, monthId: $monthId) {
      __typename
    }
  }
}
    `

export const useRemoveBillingMeterMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBillingMeterMonthMutation,
    TError,
    Types.RemoveBillingMeterMonthMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBillingMeterMonth"],
    mutationFn: (variables?: Types.RemoveBillingMeterMonthMutationVariables) =>
      publicFetcher<
        Types.RemoveBillingMeterMonthMutation,
        Types.RemoveBillingMeterMonthMutationVariables
      >(RemoveBillingMeterMonthDocument, variables)(),
    ...options,
  })
}

useRemoveBillingMeterMonthMutation.fetcher = (
  variables: Types.RemoveBillingMeterMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBillingMeterMonthMutation,
    Types.RemoveBillingMeterMonthMutationVariables
  >(RemoveBillingMeterMonthDocument, variables, options)

export const MoveDocumentBillingMonthDocument = `
    mutation MoveDocumentBillingMonth($sitePid: String!, $billingMonthId: Int!, $kind: BillType!, $fromMeterPid: String, $toMeterPid: String) {
  billingMeters(sitePid: $sitePid) {
    moveDocumentBillingMonth(
      dbmId: $billingMonthId
      kind: $kind
      fromMeterPid: $fromMeterPid
      toMeterPid: $toMeterPid
    )
  }
}
    `

export const useMoveDocumentBillingMonthMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.MoveDocumentBillingMonthMutation,
    TError,
    Types.MoveDocumentBillingMonthMutationVariables,
    TContext
  >({
    mutationKey: ["MoveDocumentBillingMonth"],
    mutationFn: (variables?: Types.MoveDocumentBillingMonthMutationVariables) =>
      publicFetcher<
        Types.MoveDocumentBillingMonthMutation,
        Types.MoveDocumentBillingMonthMutationVariables
      >(MoveDocumentBillingMonthDocument, variables)(),
    ...options,
  })
}

useMoveDocumentBillingMonthMutation.fetcher = (
  variables: Types.MoveDocumentBillingMonthMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MoveDocumentBillingMonthMutation,
    Types.MoveDocumentBillingMonthMutationVariables
  >(MoveDocumentBillingMonthDocument, variables, options)

export const CreateBuildingDataFromGooglePlaceDocument = `
    mutation CreateBuildingDataFromGooglePlace($placeId: String!) {
  buildingData {
    createFromGooglePlace(placeId: $placeId) {
      __typename
      ... on BuildingData {
        id
        fullAddress
        buildingType
        propertyName
        roofSqft
        sqft
        numStories
        latitude
        longitude
        buildingFootprintGeojson
        yearBuilt
        numUnitsTotal
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateBuildingDataFromGooglePlaceMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    TError,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBuildingDataFromGooglePlace"],
    mutationFn: (
      variables?: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
    ) =>
      publicFetcher<
        Types.CreateBuildingDataFromGooglePlaceMutation,
        Types.CreateBuildingDataFromGooglePlaceMutationVariables
      >(CreateBuildingDataFromGooglePlaceDocument, variables)(),
    ...options,
  })
}

useCreateBuildingDataFromGooglePlaceMutation.fetcher = (
  variables: Types.CreateBuildingDataFromGooglePlaceMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBuildingDataFromGooglePlaceMutation,
    Types.CreateBuildingDataFromGooglePlaceMutationVariables
  >(CreateBuildingDataFromGooglePlaceDocument, variables, options)

export const CreateSiteFromBuildingDataDocument = `
    mutation CreateSiteFromBuildingData($portfolioPid: String!, $buildingDataId: Int!, $amendments: BuildingDataAmendmentsInput!) {
  buildingData {
    createSite(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
      amendments: $amendments
    ) {
      __typename
      ... on SiteRef {
        pid
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreateSiteFromBuildingDataMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateSiteFromBuildingDataMutation,
    TError,
    Types.CreateSiteFromBuildingDataMutationVariables,
    TContext
  >({
    mutationKey: ["CreateSiteFromBuildingData"],
    mutationFn: (variables?: Types.CreateSiteFromBuildingDataMutationVariables) =>
      publicFetcher<
        Types.CreateSiteFromBuildingDataMutation,
        Types.CreateSiteFromBuildingDataMutationVariables
      >(CreateSiteFromBuildingDataDocument, variables)(),
    ...options,
  })
}

useCreateSiteFromBuildingDataMutation.fetcher = (
  variables: Types.CreateSiteFromBuildingDataMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateSiteFromBuildingDataMutation,
    Types.CreateSiteFromBuildingDataMutationVariables
  >(CreateSiteFromBuildingDataDocument, variables, options)

export const CreateBundleParentDocument = `
    mutation CreateBundleParent($orgSlug: String!, $name: String!, $type: FinProductType!, $note: String!, $pricingNote: String!, $shortPricingNote: String, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      createParent(
        name: $name
        type: $type
        note: $note
        pricingNote: $pricingNote
        shortPricingNote: $shortPricingNote
        biddingOpenAt: $biddingOpenAt
        biddingClosedAt: $biddingClosedAt
      ) {
        id
        pid
        name
      }
    }
  }
}
    `

export const useCreateBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateBundleParentMutation,
    TError,
    Types.CreateBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBundleParentMutation,
    TError,
    Types.CreateBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBundleParent"],
    mutationFn: (variables?: Types.CreateBundleParentMutationVariables) =>
      publicFetcher<
        Types.CreateBundleParentMutation,
        Types.CreateBundleParentMutationVariables
      >(CreateBundleParentDocument, variables)(),
    ...options,
  })
}

useCreateBundleParentMutation.fetcher = (
  variables: Types.CreateBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBundleParentMutation,
    Types.CreateBundleParentMutationVariables
  >(CreateBundleParentDocument, variables, options)

export const EditBundleParentDocument = `
    mutation EditBundleParent($orgSlug: String!, $bundleParentPid: String!, $name: String!, $note: String!, $biddingOpenAt: DateTime, $biddingClosedAt: DateTime) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setName(name: $name)
        setNote(note: $note)
        setBiddingOpenAt(biddingOpenAt: $biddingOpenAt)
        setBiddingClosedAt(biddingClosedAt: $biddingClosedAt)
      }
    }
  }
}
    `

export const useEditBundleParentMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBundleParentMutation,
    TError,
    Types.EditBundleParentMutationVariables,
    TContext
  >({
    mutationKey: ["EditBundleParent"],
    mutationFn: (variables?: Types.EditBundleParentMutationVariables) =>
      publicFetcher<
        Types.EditBundleParentMutation,
        Types.EditBundleParentMutationVariables
      >(EditBundleParentDocument, variables)(),
    ...options,
  })
}

useEditBundleParentMutation.fetcher = (
  variables: Types.EditBundleParentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBundleParentMutation,
    Types.EditBundleParentMutationVariables
  >(EditBundleParentDocument, variables, options)

export const EditBundleParentPricingDocument = `
    mutation EditBundleParentPricing($orgSlug: String!, $bundleParentPid: String!, $pricingNote: String!, $shortPricingNote: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setPricingNote(pricingNote: $pricingNote)
        setShortPricingNote(shortPricingNote: $shortPricingNote)
      }
    }
  }
}
    `

export const useEditBundleParentPricingMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.EditBundleParentPricingMutation,
    TError,
    Types.EditBundleParentPricingMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditBundleParentPricingMutation,
    TError,
    Types.EditBundleParentPricingMutationVariables,
    TContext
  >({
    mutationKey: ["EditBundleParentPricing"],
    mutationFn: (variables?: Types.EditBundleParentPricingMutationVariables) =>
      publicFetcher<
        Types.EditBundleParentPricingMutation,
        Types.EditBundleParentPricingMutationVariables
      >(EditBundleParentPricingDocument, variables)(),
    ...options,
  })
}

useEditBundleParentPricingMutation.fetcher = (
  variables: Types.EditBundleParentPricingMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditBundleParentPricingMutation,
    Types.EditBundleParentPricingMutationVariables
  >(EditBundleParentPricingDocument, variables, options)

export const AddBundleParentFinancierDocument = `
    mutation AddBundleParentFinancier($orgSlug: String!, $bundleParentPid: String!, $financierPids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addFinanciers(financierPids: $financierPids)
      }
    }
  }
}
    `

export const useAddBundleParentFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentFinancierMutation,
    TError,
    Types.AddBundleParentFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentFinancier"],
    mutationFn: (variables?: Types.AddBundleParentFinancierMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentFinancierMutation,
        Types.AddBundleParentFinancierMutationVariables
      >(AddBundleParentFinancierDocument, variables)(),
    ...options,
  })
}

useAddBundleParentFinancierMutation.fetcher = (
  variables: Types.AddBundleParentFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentFinancierMutation,
    Types.AddBundleParentFinancierMutationVariables
  >(AddBundleParentFinancierDocument, variables, options)

export const AddBundleParentSiteDocument = `
    mutation AddBundleParentSite($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $projectParams: ProjectParamsInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        upsertSite(sitePid: $sitePid, projectParams: $projectParams)
      }
    }
  }
}
    `

export const useAddBundleParentSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentSiteMutation,
    TError,
    Types.AddBundleParentSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentSiteMutation,
    TError,
    Types.AddBundleParentSiteMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentSite"],
    mutationFn: (variables?: Types.AddBundleParentSiteMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentSiteMutation,
        Types.AddBundleParentSiteMutationVariables
      >(AddBundleParentSiteDocument, variables)(),
    ...options,
  })
}

useAddBundleParentSiteMutation.fetcher = (
  variables: Types.AddBundleParentSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentSiteMutation,
    Types.AddBundleParentSiteMutationVariables
  >(AddBundleParentSiteDocument, variables, options)

export const CreateBundleParentSystemDocument = `
    mutation CreateBundleParentSystem($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSystem(sitePid: $sitePid, input: $input) {
          system {
            pid
          }
          lite {
            pid
          }
        }
      }
    }
  }
}
    `

export const useCreateBundleParentSystemMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBundleParentSystemMutation,
    TError,
    Types.CreateBundleParentSystemMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBundleParentSystem"],
    mutationFn: (variables?: Types.CreateBundleParentSystemMutationVariables) =>
      publicFetcher<
        Types.CreateBundleParentSystemMutation,
        Types.CreateBundleParentSystemMutationVariables
      >(CreateBundleParentSystemDocument, variables)(),
    ...options,
  })
}

useCreateBundleParentSystemMutation.fetcher = (
  variables: Types.CreateBundleParentSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBundleParentSystemMutation,
    Types.CreateBundleParentSystemMutationVariables
  >(CreateBundleParentSystemDocument, variables, options)

export const RemoveBundleParentSiteDocument = `
    mutation RemoveBundleParentSite($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        removeSite(sitePid: $sitePid)
      }
    }
  }
}
    `

export const useRemoveBundleParentSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBundleParentSiteMutation,
    TError,
    Types.RemoveBundleParentSiteMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBundleParentSite"],
    mutationFn: (variables?: Types.RemoveBundleParentSiteMutationVariables) =>
      publicFetcher<
        Types.RemoveBundleParentSiteMutation,
        Types.RemoveBundleParentSiteMutationVariables
      >(RemoveBundleParentSiteDocument, variables)(),
    ...options,
  })
}

useRemoveBundleParentSiteMutation.fetcher = (
  variables: Types.RemoveBundleParentSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBundleParentSiteMutation,
    Types.RemoveBundleParentSiteMutationVariables
  >(RemoveBundleParentSiteDocument, variables, options)

export const SetBundleParentAdminNoteDocument = `
    mutation SetBundleParentAdminNote($orgSlug: String!, $bundleParentPid: String!, $adminOnlyNote: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setAdminOnlyNote(adminOnlyNote: $adminOnlyNote)
      }
    }
  }
}
    `

export const useSetBundleParentAdminNoteMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentAdminNoteMutation,
    TError,
    Types.SetBundleParentAdminNoteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentAdminNoteMutation,
    TError,
    Types.SetBundleParentAdminNoteMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentAdminNote"],
    mutationFn: (variables?: Types.SetBundleParentAdminNoteMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentAdminNoteMutation,
        Types.SetBundleParentAdminNoteMutationVariables
      >(SetBundleParentAdminNoteDocument, variables)(),
    ...options,
  })
}

useSetBundleParentAdminNoteMutation.fetcher = (
  variables: Types.SetBundleParentAdminNoteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentAdminNoteMutation,
    Types.SetBundleParentAdminNoteMutationVariables
  >(SetBundleParentAdminNoteDocument, variables, options)

export const LinkBundleParentNotionPageDocument = `
    mutation LinkBundleParentNotionPage($orgSlug: String!, $bundleParentPid: String!, $notionPageUrl: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        linkNotionPage(notionPageUrl: $notionPageUrl)
      }
    }
  }
}
    `

export const useLinkBundleParentNotionPageMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.LinkBundleParentNotionPageMutation,
    TError,
    Types.LinkBundleParentNotionPageMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.LinkBundleParentNotionPageMutation,
    TError,
    Types.LinkBundleParentNotionPageMutationVariables,
    TContext
  >({
    mutationKey: ["LinkBundleParentNotionPage"],
    mutationFn: (variables?: Types.LinkBundleParentNotionPageMutationVariables) =>
      publicFetcher<
        Types.LinkBundleParentNotionPageMutation,
        Types.LinkBundleParentNotionPageMutationVariables
      >(LinkBundleParentNotionPageDocument, variables)(),
    ...options,
  })
}

useLinkBundleParentNotionPageMutation.fetcher = (
  variables: Types.LinkBundleParentNotionPageMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LinkBundleParentNotionPageMutation,
    Types.LinkBundleParentNotionPageMutationVariables
  >(LinkBundleParentNotionPageDocument, variables, options)

export const SetBundleParentBidRemindersSentDocument = `
    mutation SetBundleParentBidRemindersSent($orgSlug: String!, $bundleParentPid: String!, $bidRemindersSent: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setBidRemindersSent(bidRemindersSent: $bidRemindersSent)
      }
    }
  }
}
    `

export const useSetBundleParentBidRemindersSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentBidRemindersSentMutation,
    TError,
    Types.SetBundleParentBidRemindersSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentBidRemindersSentMutation,
    TError,
    Types.SetBundleParentBidRemindersSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentBidRemindersSent"],
    mutationFn: (variables?: Types.SetBundleParentBidRemindersSentMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentBidRemindersSentMutation,
        Types.SetBundleParentBidRemindersSentMutationVariables
      >(SetBundleParentBidRemindersSentDocument, variables)(),
    ...options,
  })
}

useSetBundleParentBidRemindersSentMutation.fetcher = (
  variables: Types.SetBundleParentBidRemindersSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentBidRemindersSentMutation,
    Types.SetBundleParentBidRemindersSentMutationVariables
  >(SetBundleParentBidRemindersSentDocument, variables, options)

export const SetBundleParentLossNoticesSentDocument = `
    mutation SetBundleParentLossNoticesSent($orgSlug: String!, $bundleParentPid: String!, $lossNoticesSent: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setLossNoticesSent(lossNoticesSent: $lossNoticesSent)
      }
    }
  }
}
    `

export const useSetBundleParentLossNoticesSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentLossNoticesSentMutation,
    TError,
    Types.SetBundleParentLossNoticesSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentLossNoticesSentMutation,
    TError,
    Types.SetBundleParentLossNoticesSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentLossNoticesSent"],
    mutationFn: (variables?: Types.SetBundleParentLossNoticesSentMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentLossNoticesSentMutation,
        Types.SetBundleParentLossNoticesSentMutationVariables
      >(SetBundleParentLossNoticesSentDocument, variables)(),
    ...options,
  })
}

useSetBundleParentLossNoticesSentMutation.fetcher = (
  variables: Types.SetBundleParentLossNoticesSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentLossNoticesSentMutation,
    Types.SetBundleParentLossNoticesSentMutationVariables
  >(SetBundleParentLossNoticesSentDocument, variables, options)

export const SetProjectChangeOrderSentDocument = `
    mutation SetProjectChangeOrderSent($projectPid: String!, $changeOrderSent: Boolean!) {
  project(projectPid: $projectPid) {
    setChangeOrderSent(changeOrderSent: $changeOrderSent)
  }
}
    `

export const useSetProjectChangeOrderSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectChangeOrderSentMutation,
    TError,
    Types.SetProjectChangeOrderSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectChangeOrderSentMutation,
    TError,
    Types.SetProjectChangeOrderSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectChangeOrderSent"],
    mutationFn: (variables?: Types.SetProjectChangeOrderSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectChangeOrderSentMutation,
        Types.SetProjectChangeOrderSentMutationVariables
      >(SetProjectChangeOrderSentDocument, variables)(),
    ...options,
  })
}

useSetProjectChangeOrderSentMutation.fetcher = (
  variables: Types.SetProjectChangeOrderSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectChangeOrderSentMutation,
    Types.SetProjectChangeOrderSentMutationVariables
  >(SetProjectChangeOrderSentDocument, variables, options)

export const SetProjectHostAwardSentDocument = `
    mutation SetProjectHostAwardSent($projectPid: String!, $hostAwardSent: Boolean!) {
  project(projectPid: $projectPid) {
    setHostAwardSent(hostAwardSent: $hostAwardSent)
  }
}
    `

export const useSetProjectHostAwardSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectHostAwardSentMutation,
    TError,
    Types.SetProjectHostAwardSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectHostAwardSentMutation,
    TError,
    Types.SetProjectHostAwardSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectHostAwardSent"],
    mutationFn: (variables?: Types.SetProjectHostAwardSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectHostAwardSentMutation,
        Types.SetProjectHostAwardSentMutationVariables
      >(SetProjectHostAwardSentDocument, variables)(),
    ...options,
  })
}

useSetProjectHostAwardSentMutation.fetcher = (
  variables: Types.SetProjectHostAwardSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectHostAwardSentMutation,
    Types.SetProjectHostAwardSentMutationVariables
  >(SetProjectHostAwardSentDocument, variables, options)

export const SetProjectScopeOfWorkSentDocument = `
    mutation SetProjectScopeOfWorkSent($projectPid: String!, $scopeOfWorkSent: Boolean!) {
  project(projectPid: $projectPid) {
    setScopeOfWorkSent(scopeOfWorkSent: $scopeOfWorkSent)
  }
}
    `

export const useSetProjectScopeOfWorkSentMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetProjectScopeOfWorkSentMutation,
    TError,
    Types.SetProjectScopeOfWorkSentMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetProjectScopeOfWorkSentMutation,
    TError,
    Types.SetProjectScopeOfWorkSentMutationVariables,
    TContext
  >({
    mutationKey: ["SetProjectScopeOfWorkSent"],
    mutationFn: (variables?: Types.SetProjectScopeOfWorkSentMutationVariables) =>
      publicFetcher<
        Types.SetProjectScopeOfWorkSentMutation,
        Types.SetProjectScopeOfWorkSentMutationVariables
      >(SetProjectScopeOfWorkSentDocument, variables)(),
    ...options,
  })
}

useSetProjectScopeOfWorkSentMutation.fetcher = (
  variables: Types.SetProjectScopeOfWorkSentMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetProjectScopeOfWorkSentMutation,
    Types.SetProjectScopeOfWorkSentMutationVariables
  >(SetProjectScopeOfWorkSentDocument, variables, options)

export const AddInvestorBidDocument = `
    mutation AddInvestorBid($orgSlug: String!, $bundlePid: String!, $input: InvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addInvestorBid(input: $input) {
        __typename
        ... on InvestorBid {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useAddInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidMutation,
    TError,
    Types.AddInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBid"],
    mutationFn: (variables?: Types.AddInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidMutation,
        Types.AddInvestorBidMutationVariables
      >(AddInvestorBidDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidMutation.fetcher = (
  variables: Types.AddInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddInvestorBidMutation, Types.AddInvestorBidMutationVariables>(
    AddInvestorBidDocument,
    variables,
    options,
  )

export const EditInvestorBidDocument = `
    mutation EditInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $input: InvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      editInvestorBid(bidPid: $bidPid, input: $input) {
        __typename
        ... on InvestorBid {
          ppa {
            pid
          }
          cs {
            pid
          }
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useEditInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidMutation,
    TError,
    Types.EditInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBid"],
    mutationFn: (variables?: Types.EditInvestorBidMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidMutation,
        Types.EditInvestorBidMutationVariables
      >(EditInvestorBidDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidMutation.fetcher = (
  variables: Types.EditInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EditInvestorBidMutation, Types.EditInvestorBidMutationVariables>(
    EditInvestorBidDocument,
    variables,
    options,
  )

export const DuplicateInvestorBidDocument = `
    mutation DuplicateInvestorBid($orgSlug: String!, $bundlePid: String!, $input: DuplicateInvestorBidInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      duplicateInvestorBid(input: $input) {
        ppa {
          pid
        }
        cs {
          pid
        }
      }
    }
  }
}
    `

export const useDuplicateInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DuplicateInvestorBidMutation,
    TError,
    Types.DuplicateInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["DuplicateInvestorBid"],
    mutationFn: (variables?: Types.DuplicateInvestorBidMutationVariables) =>
      publicFetcher<
        Types.DuplicateInvestorBidMutation,
        Types.DuplicateInvestorBidMutationVariables
      >(DuplicateInvestorBidDocument, variables)(),
    ...options,
  })
}

useDuplicateInvestorBidMutation.fetcher = (
  variables: Types.DuplicateInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DuplicateInvestorBidMutation,
    Types.DuplicateInvestorBidMutationVariables
  >(DuplicateInvestorBidDocument, variables, options)

export const AddSystemDocument = `
    mutation AddSystem($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $input: AddSystemInput!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addSystem(sitePid: $sitePid, input: $input) {
        system {
          pid
        }
        lite {
          pid
        }
      }
    }
  }
}
    `

export const useAddSystemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddSystemMutation,
    TError,
    Types.AddSystemMutationVariables,
    TContext
  >({
    mutationKey: ["AddSystem"],
    mutationFn: (variables?: Types.AddSystemMutationVariables) =>
      publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
        AddSystemDocument,
        variables,
      )(),
    ...options,
  })
}

useAddSystemMutation.fetcher = (
  variables: Types.AddSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddSystemMutation, Types.AddSystemMutationVariables>(
    AddSystemDocument,
    variables,
    options,
  )

export const UpdateEvOverridesDocument = `
    mutation UpdateEVOverrides($sitePid: String!, $projectStartDate: DateTime, $discountRate: Float, $usdPerKwhMarkup: Float, $incentivePct: Float, $salesTaxPct: Float, $omUsdPerChargerPerYr: Float, $rateManagementUsdPerChargerPerYr: Float, $cmsUsdPerChargerPerYr: Float, $demandUsdPerKw: Float, $installType: ChargerInstallType, $termYears: Int, $lowEnergyUsdPerKwh: Float, $highEnergyUsdPerKwh: Float, $onPeakChargingPct: Float, $numChargers: Float, $chargerLocation: ChargerLocation, $chargerKw: Float, $milesDrivenPerYear: Float, $milesPerKwh: Float, $maxUtilization: Float, $transactionFeePct: Float, $onSiteChargingPct: Float, $yr0NumTenantEvs: Int, $evGrowthPct: Float, $incentiveCapNumChargers: Int, $maxEvAdoptionPct: Float, $installCostUsd: Float) {
  evOverrides(sitePid: $sitePid) {
    update(
      projectStartDate: $projectStartDate
      discountRate: $discountRate
      usdPerKwhMarkup: $usdPerKwhMarkup
      incentivePct: $incentivePct
      salesTaxPct: $salesTaxPct
      omUsdPerChargerPerYr: $omUsdPerChargerPerYr
      rateManagementUsdPerChargerPerYr: $rateManagementUsdPerChargerPerYr
      cmsUsdPerChargerPerYr: $cmsUsdPerChargerPerYr
      demandUsdPerKw: $demandUsdPerKw
      installType: $installType
      termYears: $termYears
      lowEnergyUsdPerKwh: $lowEnergyUsdPerKwh
      highEnergyUsdPerKwh: $highEnergyUsdPerKwh
      onPeakChargingPct: $onPeakChargingPct
      numChargers: $numChargers
      chargerLocation: $chargerLocation
      chargerKw: $chargerKw
      milesDrivenPerYear: $milesDrivenPerYear
      milesPerKwh: $milesPerKwh
      maxUtilization: $maxUtilization
      transactionFeePct: $transactionFeePct
      onSiteChargingPct: $onSiteChargingPct
      yr0NumTenantEvs: $yr0NumTenantEvs
      evGrowthPct: $evGrowthPct
      incentiveCapNumChargers: $incentiveCapNumChargers
      maxEvAdoptionPct: $maxEvAdoptionPct
      installCostUsd: $installCostUsd
    )
  }
}
    `

export const useUpdateEvOverridesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateEvOverridesMutation,
    TError,
    Types.UpdateEvOverridesMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateEVOverrides"],
    mutationFn: (variables?: Types.UpdateEvOverridesMutationVariables) =>
      publicFetcher<
        Types.UpdateEvOverridesMutation,
        Types.UpdateEvOverridesMutationVariables
      >(UpdateEvOverridesDocument, variables)(),
    ...options,
  })
}

useUpdateEvOverridesMutation.fetcher = (
  variables: Types.UpdateEvOverridesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateEvOverridesMutation,
    Types.UpdateEvOverridesMutationVariables
  >(UpdateEvOverridesDocument, variables, options)

export const ArchiveSiteDocument = `
    mutation ArchiveSite($sitePid: String!, $reason: StatusReason!) {
  archiveSite(sitePid: $sitePid, reason: $reason) {
    __typename
  }
}
    `

export const useArchiveSiteMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveSiteMutation,
    TError,
    Types.ArchiveSiteMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveSite"],
    mutationFn: (variables?: Types.ArchiveSiteMutationVariables) =>
      publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
        ArchiveSiteDocument,
        variables,
      )(),
    ...options,
  })
}

useArchiveSiteMutation.fetcher = (
  variables: Types.ArchiveSiteMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ArchiveSiteMutation, Types.ArchiveSiteMutationVariables>(
    ArchiveSiteDocument,
    variables,
    options,
  )

export const ResolveSiteFlagDocument = `
    mutation ResolveSiteFlag($orgSlug: String!, $sitePid: String!, $flagType: SiteFlagType!) {
  orgBySlug(slug: $orgSlug) {
    flags {
      resolveSiteFlag(sitePid: $sitePid, flagType: $flagType) {
        __typename
      }
    }
  }
}
    `

export const useResolveSiteFlagMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ResolveSiteFlagMutation,
    TError,
    Types.ResolveSiteFlagMutationVariables,
    TContext
  >({
    mutationKey: ["ResolveSiteFlag"],
    mutationFn: (variables?: Types.ResolveSiteFlagMutationVariables) =>
      publicFetcher<
        Types.ResolveSiteFlagMutation,
        Types.ResolveSiteFlagMutationVariables
      >(ResolveSiteFlagDocument, variables)(),
    ...options,
  })
}

useResolveSiteFlagMutation.fetcher = (
  variables: Types.ResolveSiteFlagMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResolveSiteFlagMutation, Types.ResolveSiteFlagMutationVariables>(
    ResolveSiteFlagDocument,
    variables,
    options,
  )

export const CreatePortfolioDocument = `
    mutation CreatePortfolio($orgSlug: String!, $name: String!) {
  orgBySlug(slug: $orgSlug) {
    createPortfolio(name: $name) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useCreatePortfolioMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreatePortfolioMutation,
    TError,
    Types.CreatePortfolioMutationVariables,
    TContext
  >({
    mutationKey: ["CreatePortfolio"],
    mutationFn: (variables?: Types.CreatePortfolioMutationVariables) =>
      publicFetcher<
        Types.CreatePortfolioMutation,
        Types.CreatePortfolioMutationVariables
      >(CreatePortfolioDocument, variables)(),
    ...options,
  })
}

useCreatePortfolioMutation.fetcher = (
  variables: Types.CreatePortfolioMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CreatePortfolioMutation, Types.CreatePortfolioMutationVariables>(
    CreatePortfolioDocument,
    variables,
    options,
  )

export const GrantRoleDocument = `
    mutation GrantRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      grantRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useGrantRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.GrantRoleMutation,
    TError,
    Types.GrantRoleMutationVariables,
    TContext
  >({
    mutationKey: ["GrantRole"],
    mutationFn: (variables?: Types.GrantRoleMutationVariables) =>
      publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
        GrantRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useGrantRoleMutation.fetcher = (
  variables: Types.GrantRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GrantRoleMutation, Types.GrantRoleMutationVariables>(
    GrantRoleDocument,
    variables,
    options,
  )

export const RevokeRoleDocument = `
    mutation RevokeRole($orgSlug: String!, $input: UserRoleInput!) {
  orgBySlug(slug: $orgSlug) {
    roles {
      revokeRole(input: $input) {
        __typename
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useRevokeRoleMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RevokeRoleMutation,
    TError,
    Types.RevokeRoleMutationVariables,
    TContext
  >({
    mutationKey: ["RevokeRole"],
    mutationFn: (variables?: Types.RevokeRoleMutationVariables) =>
      publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
        RevokeRoleDocument,
        variables,
      )(),
    ...options,
  })
}

useRevokeRoleMutation.fetcher = (
  variables: Types.RevokeRoleMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevokeRoleMutation, Types.RevokeRoleMutationVariables>(
    RevokeRoleDocument,
    variables,
    options,
  )

export const UpdateInvestorProfileDocument = `
    mutation UpdateInvestorProfile($orgSlug: String!, $hq: String, $yearFounded: Int, $website: String, $managementTeam: [String!]!, $description: String, $distinguishingFeatures: String, $investorBacking: String, $longTermOwnershipApproach: String, $subscriptionMgmtApproach: String, $committedFunding: String, $experiences: [ExperienceInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateProfile(
        hq: $hq
        yearFounded: $yearFounded
        website: $website
        managementTeam: $managementTeam
        description: $description
        distinguishingFeatures: $distinguishingFeatures
        investorBacking: $investorBacking
        longTermOwnershipApproach: $longTermOwnershipApproach
        subscriptionMgmtApproach: $subscriptionMgmtApproach
        committedFunding: $committedFunding
        experiences: $experiences
      )
    }
  }
}
    `

export const useUpdateInvestorProfileMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorProfileMutation,
    TError,
    Types.UpdateInvestorProfileMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorProfile"],
    mutationFn: (variables?: Types.UpdateInvestorProfileMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorProfileMutation,
        Types.UpdateInvestorProfileMutationVariables
      >(UpdateInvestorProfileDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorProfileMutation.fetcher = (
  variables: Types.UpdateInvestorProfileMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorProfileMutation,
    Types.UpdateInvestorProfileMutationVariables
  >(UpdateInvestorProfileDocument, variables, options)

export const UpdateInvestorBuyBoxDocument = `
    mutation UpdateInvestorBuyBox($orgSlug: String!, $buyBoxes: [BuyBoxInput!]!) {
  orgBySlug(slug: $orgSlug) {
    investorProfile {
      updateBuyBox(buyBoxes: $buyBoxes)
    }
  }
}
    `

export const useUpdateInvestorBuyBoxMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateInvestorBuyBoxMutation,
    TError,
    Types.UpdateInvestorBuyBoxMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateInvestorBuyBox"],
    mutationFn: (variables?: Types.UpdateInvestorBuyBoxMutationVariables) =>
      publicFetcher<
        Types.UpdateInvestorBuyBoxMutation,
        Types.UpdateInvestorBuyBoxMutationVariables
      >(UpdateInvestorBuyBoxDocument, variables)(),
    ...options,
  })
}

useUpdateInvestorBuyBoxMutation.fetcher = (
  variables: Types.UpdateInvestorBuyBoxMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateInvestorBuyBoxMutation,
    Types.UpdateInvestorBuyBoxMutationVariables
  >(UpdateInvestorBuyBoxDocument, variables, options)

export const EvButtonDocument = `
    mutation EVButton($sitePid: String!, $button: String!) {
  evButton(sitePid: $sitePid) {
    press(button: $button)
  }
}
    `

export const useEvButtonMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EvButtonMutation,
    TError,
    Types.EvButtonMutationVariables,
    TContext
  >({
    mutationKey: ["EVButton"],
    mutationFn: (variables?: Types.EvButtonMutationVariables) =>
      publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
        EvButtonDocument,
        variables,
      )(),
    ...options,
  })
}

useEvButtonMutation.fetcher = (
  variables: Types.EvButtonMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvButtonMutation, Types.EvButtonMutationVariables>(
    EvButtonDocument,
    variables,
    options,
  )

export const SetArchiveInvestorBidDocument = `
    mutation SetArchiveInvestorBid($orgSlug: String!, $bundlePid: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      archiveInvestorBid(archive: $value, bidPid: $bidPid)
    }
  }
}
    `

export const useSetArchiveInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveInvestorBidMutation,
    TError,
    Types.SetArchiveInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveInvestorBid"],
    mutationFn: (variables?: Types.SetArchiveInvestorBidMutationVariables) =>
      publicFetcher<
        Types.SetArchiveInvestorBidMutation,
        Types.SetArchiveInvestorBidMutationVariables
      >(SetArchiveInvestorBidDocument, variables)(),
    ...options,
  })
}

useSetArchiveInvestorBidMutation.fetcher = (
  variables: Types.SetArchiveInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveInvestorBidMutation,
    Types.SetArchiveInvestorBidMutationVariables
  >(SetArchiveInvestorBidDocument, variables, options)

export const SetBundleRowOptOutDocument = `
    mutation SetBundleRowOptOut($orgSlug: String!, $bundlePid: String!, $bundleRowId: Int!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: {bundleRowId: $bundleRowId}
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBundleRowOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleRowOptOutMutation,
    TError,
    Types.SetBundleRowOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleRowOptOut"],
    mutationFn: (variables?: Types.SetBundleRowOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleRowOptOutMutation,
        Types.SetBundleRowOptOutMutationVariables
      >(SetBundleRowOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleRowOptOutMutation.fetcher = (
  variables: Types.SetBundleRowOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleRowOptOutMutation,
    Types.SetBundleRowOptOutMutationVariables
  >(SetBundleRowOptOutDocument, variables, options)

export const SetBundleOptOutDocument = `
    mutation SetBundleOptOut($orgSlug: String!, $bundlePid: String!, $value: Boolean!, $reason: InvestorOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleOptOut(
        optOut: $value
        input: {bundlePid: $bundlePid}
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBundleOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleOptOutMutation,
    TError,
    Types.SetBundleOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleOptOut"],
    mutationFn: (variables?: Types.SetBundleOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBundleOptOutMutation,
        Types.SetBundleOptOutMutationVariables
      >(SetBundleOptOutDocument, variables)(),
    ...options,
  })
}

useSetBundleOptOutMutation.fetcher = (
  variables: Types.SetBundleOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SetBundleOptOutMutation, Types.SetBundleOptOutMutationVariables>(
    SetBundleOptOutDocument,
    variables,
    options,
  )

export const SetBidSpecOptOutDocument = `
    mutation SetBidSpecOptOut($orgSlug: String!, $bundlePid: String!, $bidSpecPid: String!, $value: Boolean!, $reason: SpecOptOutReason, $customReason: String) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      toggleSpecOptOut(
        optOut: $value
        bidSpecPid: $bidSpecPid
        reason: $reason
        customReason: $customReason
      )
    }
  }
}
    `

export const useSetBidSpecOptOutMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBidSpecOptOutMutation,
    TError,
    Types.SetBidSpecOptOutMutationVariables,
    TContext
  >({
    mutationKey: ["SetBidSpecOptOut"],
    mutationFn: (variables?: Types.SetBidSpecOptOutMutationVariables) =>
      publicFetcher<
        Types.SetBidSpecOptOutMutation,
        Types.SetBidSpecOptOutMutationVariables
      >(SetBidSpecOptOutDocument, variables)(),
    ...options,
  })
}

useSetBidSpecOptOutMutation.fetcher = (
  variables: Types.SetBidSpecOptOutMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBidSpecOptOutMutation,
    Types.SetBidSpecOptOutMutationVariables
  >(SetBidSpecOptOutDocument, variables, options)

export const SubmitBundleBidsDocument = `
    mutation SubmitBundleBids($orgSlug: String!, $bundlePid: String!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      submitBids(pricingTermsAgreed: true)
    }
  }
}
    `

export const useSubmitBundleBidsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SubmitBundleBidsMutation,
    TError,
    Types.SubmitBundleBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SubmitBundleBids"],
    mutationFn: (variables?: Types.SubmitBundleBidsMutationVariables) =>
      publicFetcher<
        Types.SubmitBundleBidsMutation,
        Types.SubmitBundleBidsMutationVariables
      >(SubmitBundleBidsDocument, variables)(),
    ...options,
  })
}

useSubmitBundleBidsMutation.fetcher = (
  variables: Types.SubmitBundleBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SubmitBundleBidsMutation,
    Types.SubmitBundleBidsMutationVariables
  >(SubmitBundleBidsDocument, variables, options)

export const AddHelioscopeDocument = `
    mutation AddHelioscope($sitePid: String!, $bundlePid: String, $reportPdf: Upload!, $productionCsv: Upload) {
  addHelioscope(
    sitePid: $sitePid
    bundlePid: $bundlePid
    reportPdf: $reportPdf
    productionCsv: $productionCsv
  ) {
    __typename
    ... on Helioscope2 {
      ...Helioscope2Ref
    }
    ... on Error {
      message
    }
  }
}
    ${Helioscope2RefFragmentDoc}`

export const useAddHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddHelioscopeMutation,
    TError,
    Types.AddHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["AddHelioscope"],
    mutationFn: (variables?: Types.AddHelioscopeMutationVariables) =>
      publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
        AddHelioscopeDocument,
        variables,
      )(),
    ...options,
  })
}

useAddHelioscopeMutation.fetcher = (
  variables: Types.AddHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddHelioscopeMutation, Types.AddHelioscopeMutationVariables>(
    AddHelioscopeDocument,
    variables,
    options,
  )

export const SetArchiveBidSpecDocument = `
    mutation SetArchiveBidSpec($orgSlug: String!, $bundleParentPid: String!, $specPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        archiveSpec(specPid: $specPid, archive: $value)
      }
    }
  }
}
    `

export const useSetArchiveBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetArchiveBidSpecMutation,
    TError,
    Types.SetArchiveBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["SetArchiveBidSpec"],
    mutationFn: (variables?: Types.SetArchiveBidSpecMutationVariables) =>
      publicFetcher<
        Types.SetArchiveBidSpecMutation,
        Types.SetArchiveBidSpecMutationVariables
      >(SetArchiveBidSpecDocument, variables)(),
    ...options,
  })
}

useSetArchiveBidSpecMutation.fetcher = (
  variables: Types.SetArchiveBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetArchiveBidSpecMutation,
    Types.SetArchiveBidSpecMutationVariables
  >(SetArchiveBidSpecDocument, variables, options)

export const SetPackageSiteNotesDocument = `
    mutation SetPackageSiteNotes($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $notes: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setRowNotes(sitePid: $sitePid, notes: $notes)
      }
    }
  }
}
    `

export const useSetPackageSiteNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageSiteNotesMutation,
    TError,
    Types.SetPackageSiteNotesMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageSiteNotes"],
    mutationFn: (variables?: Types.SetPackageSiteNotesMutationVariables) =>
      publicFetcher<
        Types.SetPackageSiteNotesMutation,
        Types.SetPackageSiteNotesMutationVariables
      >(SetPackageSiteNotesDocument, variables)(),
    ...options,
  })
}

useSetPackageSiteNotesMutation.fetcher = (
  variables: Types.SetPackageSiteNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageSiteNotesMutation,
    Types.SetPackageSiteNotesMutationVariables
  >(SetPackageSiteNotesDocument, variables, options)

export const AddInvestorBidSpecDocument = `
    mutation AddInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addSpec(sitePid: $sitePid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
      }
    }
  }
}
    `

export const useAddInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddInvestorBidSpecMutation,
    TError,
    Types.AddInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["AddInvestorBidSpec"],
    mutationFn: (variables?: Types.AddInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.AddInvestorBidSpecMutation,
        Types.AddInvestorBidSpecMutationVariables
      >(AddInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useAddInvestorBidSpecMutation.fetcher = (
  variables: Types.AddInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddInvestorBidSpecMutation,
    Types.AddInvestorBidSpecMutationVariables
  >(AddInvestorBidSpecDocument, variables, options)

export const EditInvestorBidSpecDocument = `
    mutation EditInvestorBidSpec($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $specPid: String!, $input: BidSpecInput!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editSpec(specPid: $specPid, sitePid: $sitePid, input: $input) {
          ppa {
            pid
          }
          cs {
            pid
          }
          cash {
            pid
          }
        }
      }
    }
  }
}
    `

export const useEditInvestorBidSpecMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditInvestorBidSpecMutation,
    TError,
    Types.EditInvestorBidSpecMutationVariables,
    TContext
  >({
    mutationKey: ["EditInvestorBidSpec"],
    mutationFn: (variables?: Types.EditInvestorBidSpecMutationVariables) =>
      publicFetcher<
        Types.EditInvestorBidSpecMutation,
        Types.EditInvestorBidSpecMutationVariables
      >(EditInvestorBidSpecDocument, variables)(),
    ...options,
  })
}

useEditInvestorBidSpecMutation.fetcher = (
  variables: Types.EditInvestorBidSpecMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditInvestorBidSpecMutation,
    Types.EditInvestorBidSpecMutationVariables
  >(EditInvestorBidSpecDocument, variables, options)

export const EnqueueTaskDocument = `
    mutation EnqueueTask($params: TaskParamsInput!) {
  tasks {
    enqueue(params: $params) {
      __typename
      ... on Task {
        id
        name
        status
      }
      ... on Error {
        message
      }
    }
  }
}
    `

export const useEnqueueTaskMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EnqueueTaskMutation,
    TError,
    Types.EnqueueTaskMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EnqueueTaskMutation,
    TError,
    Types.EnqueueTaskMutationVariables,
    TContext
  >({
    mutationKey: ["EnqueueTask"],
    mutationFn: (variables?: Types.EnqueueTaskMutationVariables) =>
      publicFetcher<Types.EnqueueTaskMutation, Types.EnqueueTaskMutationVariables>(
        EnqueueTaskDocument,
        variables,
      )(),
    ...options,
  })
}

useEnqueueTaskMutation.fetcher = (
  variables: Types.EnqueueTaskMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EnqueueTaskMutation, Types.EnqueueTaskMutationVariables>(
    EnqueueTaskDocument,
    variables,
    options,
  )

export const AddBundleParentEtbDocument = `
    mutation AddBundleParentEtb($orgSlug: String!, $bundleParentPid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        addEtb(sitePid: $sitePid, upload: $upload) {
          ...EtbExtract
        }
      }
    }
  }
}
    ${EtbExtractFragmentDoc}`

export const useAddBundleParentEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleParentEtbMutation,
    TError,
    Types.AddBundleParentEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleParentEtb"],
    mutationFn: (variables?: Types.AddBundleParentEtbMutationVariables) =>
      publicFetcher<
        Types.AddBundleParentEtbMutation,
        Types.AddBundleParentEtbMutationVariables
      >(AddBundleParentEtbDocument, variables)(),
    ...options,
  })
}

useAddBundleParentEtbMutation.fetcher = (
  variables: Types.AddBundleParentEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBundleParentEtbMutation,
    Types.AddBundleParentEtbMutationVariables
  >(AddBundleParentEtbDocument, variables, options)

export const AddBundleEtbDocument = `
    mutation AddBundleEtb($orgSlug: String!, $bundlePid: String!, $sitePid: String!, $upload: Upload!) {
  orgBySlug(slug: $orgSlug) {
    financierBundle(bundlePid: $bundlePid) {
      addEtb(sitePid: $sitePid, upload: $upload) {
        ...EtbExtract
      }
    }
  }
}
    ${EtbExtractFragmentDoc}`

export const useAddBundleEtbMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBundleEtbMutation,
    TError,
    Types.AddBundleEtbMutationVariables,
    TContext
  >({
    mutationKey: ["AddBundleEtb"],
    mutationFn: (variables?: Types.AddBundleEtbMutationVariables) =>
      publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
        AddBundleEtbDocument,
        variables,
      )(),
    ...options,
  })
}

useAddBundleEtbMutation.fetcher = (
  variables: Types.AddBundleEtbMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddBundleEtbMutation, Types.AddBundleEtbMutationVariables>(
    AddBundleEtbDocument,
    variables,
    options,
  )

export const SetPackageRowStatusDocument = `
    mutation SetPackageRowStatus($bundleParentPid: String!, $sitePid: String!, $status: PackageRowStatus, $closed: Boolean, $closeReason: PackageRowCloseReason, $closeCustomReason: String) {
  setPackageRowStatus(
    bundleParentPid: $bundleParentPid
    sitePid: $sitePid
    status: $status
    closed: $closed
    closeReason: $closeReason
    customCloseReason: $closeCustomReason
  )
}
    `

export const useSetPackageRowStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetPackageRowStatusMutation,
    TError,
    Types.SetPackageRowStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetPackageRowStatus"],
    mutationFn: (variables?: Types.SetPackageRowStatusMutationVariables) =>
      publicFetcher<
        Types.SetPackageRowStatusMutation,
        Types.SetPackageRowStatusMutationVariables
      >(SetPackageRowStatusDocument, variables)(),
    ...options,
  })
}

useSetPackageRowStatusMutation.fetcher = (
  variables: Types.SetPackageRowStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetPackageRowStatusMutation,
    Types.SetPackageRowStatusMutationVariables
  >(SetPackageRowStatusDocument, variables, options)

export const AwardInvestorBidDocument = `
    mutation AwardInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    awardInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `

export const useAwardInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AwardInvestorBidMutation,
    TError,
    Types.AwardInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["AwardInvestorBid"],
    mutationFn: (variables?: Types.AwardInvestorBidMutationVariables) =>
      publicFetcher<
        Types.AwardInvestorBidMutation,
        Types.AwardInvestorBidMutationVariables
      >(AwardInvestorBidDocument, variables)(),
    ...options,
  })
}

useAwardInvestorBidMutation.fetcher = (
  variables: Types.AwardInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardInvestorBidMutation,
    Types.AwardInvestorBidMutationVariables
  >(AwardInvestorBidDocument, variables, options)

export const RecommendInvestorBidDocument = `
    mutation RecommendInvestorBid($orgSlug: String!, $bidPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    recommendInvestorBid(bidPid: $bidPid, value: $value)
  }
}
    `

export const useRecommendInvestorBidMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RecommendInvestorBidMutation,
    TError,
    Types.RecommendInvestorBidMutationVariables,
    TContext
  >({
    mutationKey: ["RecommendInvestorBid"],
    mutationFn: (variables?: Types.RecommendInvestorBidMutationVariables) =>
      publicFetcher<
        Types.RecommendInvestorBidMutation,
        Types.RecommendInvestorBidMutationVariables
      >(RecommendInvestorBidDocument, variables)(),
    ...options,
  })
}

useRecommendInvestorBidMutation.fetcher = (
  variables: Types.RecommendInvestorBidMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RecommendInvestorBidMutation,
    Types.RecommendInvestorBidMutationVariables
  >(RecommendInvestorBidDocument, variables, options)

export const SetBundleParentHiddenFromFinancierDocument = `
    mutation SetBundleParentHiddenFromFinancier($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromFinancier(hidden: $value)
      }
    }
  }
}
    `

export const useSetBundleParentHiddenFromFinancierMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromFinancierMutation,
    TError,
    Types.SetBundleParentHiddenFromFinancierMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromFinancier"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromFinancierMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromFinancierMutation,
        Types.SetBundleParentHiddenFromFinancierMutationVariables
      >(SetBundleParentHiddenFromFinancierDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromFinancierMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromFinancierMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromFinancierMutation,
    Types.SetBundleParentHiddenFromFinancierMutationVariables
  >(SetBundleParentHiddenFromFinancierDocument, variables, options)

export const SetBundleParentHiddenFromReceiverDocument = `
    mutation SetBundleParentHiddenFromReceiver($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setHiddenFromRecvr(hidden: $value)
      }
    }
  }
}
    `

export const useSetBundleParentHiddenFromReceiverMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentHiddenFromReceiverMutation,
    TError,
    Types.SetBundleParentHiddenFromReceiverMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentHiddenFromReceiver"],
    mutationFn: (
      variables?: Types.SetBundleParentHiddenFromReceiverMutationVariables,
    ) =>
      publicFetcher<
        Types.SetBundleParentHiddenFromReceiverMutation,
        Types.SetBundleParentHiddenFromReceiverMutationVariables
      >(SetBundleParentHiddenFromReceiverDocument, variables)(),
    ...options,
  })
}

useSetBundleParentHiddenFromReceiverMutation.fetcher = (
  variables: Types.SetBundleParentHiddenFromReceiverMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentHiddenFromReceiverMutation,
    Types.SetBundleParentHiddenFromReceiverMutationVariables
  >(SetBundleParentHiddenFromReceiverDocument, variables, options)

export const SetBundleParentCanAddNewBidsDocument = `
    mutation SetBundleParentCanAddNewBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanAddNewBids(can: $value)
      }
    }
  }
}
    `

export const useSetBundleParentCanAddNewBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanAddNewBidsMutation,
    TError,
    Types.SetBundleParentCanAddNewBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanAddNewBids"],
    mutationFn: (variables?: Types.SetBundleParentCanAddNewBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanAddNewBidsMutation,
        Types.SetBundleParentCanAddNewBidsMutationVariables
      >(SetBundleParentCanAddNewBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanAddNewBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanAddNewBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanAddNewBidsMutation,
    Types.SetBundleParentCanAddNewBidsMutationVariables
  >(SetBundleParentCanAddNewBidsDocument, variables, options)

export const SetBundleParentCanEditBidsDocument = `
    mutation SetBundleParentCanEditBids($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setCanEditBids(can: $value)
      }
    }
  }
}
    `

export const useSetBundleParentCanEditBidsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentCanEditBidsMutation,
    TError,
    Types.SetBundleParentCanEditBidsMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentCanEditBids"],
    mutationFn: (variables?: Types.SetBundleParentCanEditBidsMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentCanEditBidsMutation,
        Types.SetBundleParentCanEditBidsMutationVariables
      >(SetBundleParentCanEditBidsDocument, variables)(),
    ...options,
  })
}

useSetBundleParentCanEditBidsMutation.fetcher = (
  variables: Types.SetBundleParentCanEditBidsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentCanEditBidsMutation,
    Types.SetBundleParentCanEditBidsMutationVariables
  >(SetBundleParentCanEditBidsDocument, variables, options)

export const SetBundleParentShowAllBidsReportDocument = `
    mutation SetBundleParentShowAllBidsReport($orgSlug: String!, $bundleParentPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setShowAllBidsReport(value: $value)
      }
    }
  }
}
    `

export const useSetBundleParentShowAllBidsReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentShowAllBidsReportMutation,
    TError,
    Types.SetBundleParentShowAllBidsReportMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentShowAllBidsReport"],
    mutationFn: (variables?: Types.SetBundleParentShowAllBidsReportMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentShowAllBidsReportMutation,
        Types.SetBundleParentShowAllBidsReportMutationVariables
      >(SetBundleParentShowAllBidsReportDocument, variables)(),
    ...options,
  })
}

useSetBundleParentShowAllBidsReportMutation.fetcher = (
  variables: Types.SetBundleParentShowAllBidsReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentShowAllBidsReportMutation,
    Types.SetBundleParentShowAllBidsReportMutationVariables
  >(SetBundleParentShowAllBidsReportDocument, variables, options)

export const SetBundleParentOpsOwnerDocument = `
    mutation SetBundleParentOpsOwner($orgSlug: String!, $bundleParentPid: String!, $opsOwnerPid: String) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        setOpsOwner(opsOwnerPid: $opsOwnerPid)
      }
    }
  }
}
    `

export const useSetBundleParentOpsOwnerMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetBundleParentOpsOwnerMutation,
    TError,
    Types.SetBundleParentOpsOwnerMutationVariables,
    TContext
  >({
    mutationKey: ["SetBundleParentOpsOwner"],
    mutationFn: (variables?: Types.SetBundleParentOpsOwnerMutationVariables) =>
      publicFetcher<
        Types.SetBundleParentOpsOwnerMutation,
        Types.SetBundleParentOpsOwnerMutationVariables
      >(SetBundleParentOpsOwnerDocument, variables)(),
    ...options,
  })
}

useSetBundleParentOpsOwnerMutation.fetcher = (
  variables: Types.SetBundleParentOpsOwnerMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetBundleParentOpsOwnerMutation,
    Types.SetBundleParentOpsOwnerMutationVariables
  >(SetBundleParentOpsOwnerDocument, variables, options)

export const UpdateSiteDetailsDocument = `
    mutation UpdateSiteDetails($sitePid: String!, $input: UpdateDetailsInput!, $sourceType: ChangeSourceType, $sourceId: String) {
  siteDetails(sitePid: $sitePid) {
    updateDetails(input: $input, sourceType: $sourceType, sourceId: $sourceId)
  }
}
    `

export const useUpdateSiteDetailsMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateSiteDetailsMutation,
    TError,
    Types.UpdateSiteDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateSiteDetails"],
    mutationFn: (variables?: Types.UpdateSiteDetailsMutationVariables) =>
      publicFetcher<
        Types.UpdateSiteDetailsMutation,
        Types.UpdateSiteDetailsMutationVariables
      >(UpdateSiteDetailsDocument, variables)(),
    ...options,
  })
}

useUpdateSiteDetailsMutation.fetcher = (
  variables: Types.UpdateSiteDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateSiteDetailsMutation,
    Types.UpdateSiteDetailsMutationVariables
  >(UpdateSiteDetailsDocument, variables, options)

export const CreateCustomBidReportDocument = `
    mutation CreateCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        createCustomBidReport(
          name: $name
          developerNotes: $developerNotes
          buildingOwnerNotes: $buildingOwnerNotes
        ) {
          pid
          name
          notes
          buildingOwnerNotes
        }
      }
    }
  }
}
    `

export const useCreateCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomBidReportMutation,
    TError,
    Types.CreateCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomBidReport"],
    mutationFn: (variables?: Types.CreateCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.CreateCustomBidReportMutation,
        Types.CreateCustomBidReportMutationVariables
      >(CreateCustomBidReportDocument, variables)(),
    ...options,
  })
}

useCreateCustomBidReportMutation.fetcher = (
  variables: Types.CreateCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomBidReportMutation,
    Types.CreateCustomBidReportMutationVariables
  >(CreateCustomBidReportDocument, variables, options)

export const EditCustomBidReportDocument = `
    mutation EditCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $name: String!, $developerNotes: String!, $buildingOwnerNotes: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setName(value: $name)
          setDeveloperNotes(value: $developerNotes)
          setBuildingOwnerNotes(value: $buildingOwnerNotes)
        }
      }
    }
  }
}
    `

export const useEditCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.EditCustomBidReportMutation,
    TError,
    Types.EditCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["EditCustomBidReport"],
    mutationFn: (variables?: Types.EditCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.EditCustomBidReportMutation,
        Types.EditCustomBidReportMutationVariables
      >(EditCustomBidReportDocument, variables)(),
    ...options,
  })
}

useEditCustomBidReportMutation.fetcher = (
  variables: Types.EditCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.EditCustomBidReportMutation,
    Types.EditCustomBidReportMutationVariables
  >(EditCustomBidReportDocument, variables, options)

export const SetCustomBidReportVisibleToDevelopersDocument = `
    mutation SetCustomBidReportVisibleToDevelopers($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToDevelopers(value: $value)
        }
      }
    }
  }
}
    `

export const useSetCustomBidReportVisibleToDevelopersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    TError,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToDevelopers"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToDevelopersMutation,
        Types.SetCustomBidReportVisibleToDevelopersMutationVariables
      >(SetCustomBidReportVisibleToDevelopersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToDevelopersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToDevelopersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToDevelopersMutation,
    Types.SetCustomBidReportVisibleToDevelopersMutationVariables
  >(SetCustomBidReportVisibleToDevelopersDocument, variables, options)

export const SetCustomBidReportVisibleToBuildingOwnersDocument = `
    mutation SetCustomBidReportVisibleToBuildingOwners($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $value: Boolean!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          setVisibleToBuildingOwners(value: $value)
        }
      }
    }
  }
}
    `

export const useSetCustomBidReportVisibleToBuildingOwnersMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    TError,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    TContext
  >({
    mutationKey: ["SetCustomBidReportVisibleToBuildingOwners"],
    mutationFn: (
      variables?: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
    ) =>
      publicFetcher<
        Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
        Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
      >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables)(),
    ...options,
  })
}

useSetCustomBidReportVisibleToBuildingOwnersMutation.fetcher = (
  variables: Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetCustomBidReportVisibleToBuildingOwnersMutation,
    Types.SetCustomBidReportVisibleToBuildingOwnersMutationVariables
  >(SetCustomBidReportVisibleToBuildingOwnersDocument, variables, options)

export const DeleteCustomBidReportDocument = `
    mutation DeleteCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          delete
        }
      }
    }
  }
}
    `

export const useDeleteCustomBidReportMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.DeleteCustomBidReportMutation,
    TError,
    Types.DeleteCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["DeleteCustomBidReport"],
    mutationFn: (variables?: Types.DeleteCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.DeleteCustomBidReportMutation,
        Types.DeleteCustomBidReportMutationVariables
      >(DeleteCustomBidReportDocument, variables)(),
    ...options,
  })
}

useDeleteCustomBidReportMutation.fetcher = (
  variables: Types.DeleteCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeleteCustomBidReportMutation,
    Types.DeleteCustomBidReportMutationVariables
  >(DeleteCustomBidReportDocument, variables, options)

export const AddBidToCustomBidReportDocument = `
    mutation AddBidToCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          upsertBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `

export const useAddBidToCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddBidToCustomBidReportMutation,
    TError,
    Types.AddBidToCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["AddBidToCustomBidReport"],
    mutationFn: (variables?: Types.AddBidToCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.AddBidToCustomBidReportMutation,
        Types.AddBidToCustomBidReportMutationVariables
      >(AddBidToCustomBidReportDocument, variables)(),
    ...options,
  })
}

useAddBidToCustomBidReportMutation.fetcher = (
  variables: Types.AddBidToCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddBidToCustomBidReportMutation,
    Types.AddBidToCustomBidReportMutationVariables
  >(AddBidToCustomBidReportDocument, variables, options)

export const RemoveBidFromCustomBidReportDocument = `
    mutation RemoveBidFromCustomBidReport($orgSlug: String!, $bundleParentPid: String!, $reportPid: String!, $bidPid: String!) {
  orgBySlug(slug: $orgSlug) {
    bundle {
      editParent(pid: $bundleParentPid) {
        editCustomBidReport(pid: $reportPid) {
          removeBid(bidPid: $bidPid)
        }
      }
    }
  }
}
    `

export const useRemoveBidFromCustomBidReportMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.RemoveBidFromCustomBidReportMutation,
    TError,
    Types.RemoveBidFromCustomBidReportMutationVariables,
    TContext
  >({
    mutationKey: ["RemoveBidFromCustomBidReport"],
    mutationFn: (variables?: Types.RemoveBidFromCustomBidReportMutationVariables) =>
      publicFetcher<
        Types.RemoveBidFromCustomBidReportMutation,
        Types.RemoveBidFromCustomBidReportMutationVariables
      >(RemoveBidFromCustomBidReportDocument, variables)(),
    ...options,
  })
}

useRemoveBidFromCustomBidReportMutation.fetcher = (
  variables: Types.RemoveBidFromCustomBidReportMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RemoveBidFromCustomBidReportMutation,
    Types.RemoveBidFromCustomBidReportMutationVariables
  >(RemoveBidFromCustomBidReportDocument, variables, options)

export const UpdateCustomTariffDocument = `
    mutation UpdateCustomTariff($sitePid: String!, $customTariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!, $name: String!) {
  customTariffs(sitePid: $sitePid) {
    update(
      customTariffId: $customTariffId
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
      name: $name
    ) {
      id
    }
  }
}
    `

export const useUpdateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateCustomTariffMutation,
    TError,
    Types.UpdateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateCustomTariff"],
    mutationFn: (variables?: Types.UpdateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.UpdateCustomTariffMutation,
        Types.UpdateCustomTariffMutationVariables
      >(UpdateCustomTariffDocument, variables)(),
    ...options,
  })
}

useUpdateCustomTariffMutation.fetcher = (
  variables: Types.UpdateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateCustomTariffMutation,
    Types.UpdateCustomTariffMutationVariables
  >(UpdateCustomTariffDocument, variables, options)

export const CreateCustomTariffDocument = `
    mutation CreateCustomTariff($sitePid: String!, $name: String!, $rateId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  customTariffs(sitePid: $sitePid) {
    create(
      name: $name
      rateId: $rateId
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
    ) {
      id
    }
  }
}
    `

export const useCreateCustomTariffMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffMutation,
    TError,
    Types.CreateCustomTariffMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariff"],
    mutationFn: (variables?: Types.CreateCustomTariffMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffMutation,
        Types.CreateCustomTariffMutationVariables
      >(CreateCustomTariffDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffMutation.fetcher = (
  variables: Types.CreateCustomTariffMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffMutation,
    Types.CreateCustomTariffMutationVariables
  >(CreateCustomTariffDocument, variables, options)

export const CreateCustomTariffScratchDocument = `
    mutation CreateCustomTariffScratch($sitePid: String!, $name: String!, $tariffClass: TariffClass!, $utilityPid: String!, $addRateOps: [AddRateInput!]!) {
  customTariffs(sitePid: $sitePid) {
    createFromScratch(
      name: $name
      tariffClass: $tariffClass
      utilityPid: $utilityPid
      addRateOps: $addRateOps
    ) {
      id
    }
  }
}
    `

export const useCreateCustomTariffScratchMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateCustomTariffScratchMutation,
    TError,
    Types.CreateCustomTariffScratchMutationVariables,
    TContext
  >({
    mutationKey: ["CreateCustomTariffScratch"],
    mutationFn: (variables?: Types.CreateCustomTariffScratchMutationVariables) =>
      publicFetcher<
        Types.CreateCustomTariffScratchMutation,
        Types.CreateCustomTariffScratchMutationVariables
      >(CreateCustomTariffScratchDocument, variables)(),
    ...options,
  })
}

useCreateCustomTariffScratchMutation.fetcher = (
  variables: Types.CreateCustomTariffScratchMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateCustomTariffScratchMutation,
    Types.CreateCustomTariffScratchMutationVariables
  >(CreateCustomTariffScratchDocument, variables, options)

export const SetIsBehindTheMeterDocument = `
    mutation SetIsBehindTheMeter($sitePid: String!, $isBehindTheMeter: Boolean) {
  setIsBehindTheMeter(sitePid: $sitePid, isBehindTheMeter: $isBehindTheMeter)
}
    `

export const useSetIsBehindTheMeterMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetIsBehindTheMeterMutation,
    TError,
    Types.SetIsBehindTheMeterMutationVariables,
    TContext
  >({
    mutationKey: ["SetIsBehindTheMeter"],
    mutationFn: (variables?: Types.SetIsBehindTheMeterMutationVariables) =>
      publicFetcher<
        Types.SetIsBehindTheMeterMutation,
        Types.SetIsBehindTheMeterMutationVariables
      >(SetIsBehindTheMeterDocument, variables)(),
    ...options,
  })
}

useSetIsBehindTheMeterMutation.fetcher = (
  variables: Types.SetIsBehindTheMeterMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetIsBehindTheMeterMutation,
    Types.SetIsBehindTheMeterMutationVariables
  >(SetIsBehindTheMeterDocument, variables, options)

export const FinalizeEnergyPageUploadsDocument = `
    mutation FinalizeEnergyPageUploads($sitePid: String!) {
  finalizeEnergyPageUpload(sitePid: $sitePid)
}
    `

export const useFinalizeEnergyPageUploadsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.FinalizeEnergyPageUploadsMutation,
    TError,
    Types.FinalizeEnergyPageUploadsMutationVariables,
    TContext
  >({
    mutationKey: ["FinalizeEnergyPageUploads"],
    mutationFn: (variables?: Types.FinalizeEnergyPageUploadsMutationVariables) =>
      publicFetcher<
        Types.FinalizeEnergyPageUploadsMutation,
        Types.FinalizeEnergyPageUploadsMutationVariables
      >(FinalizeEnergyPageUploadsDocument, variables)(),
    ...options,
  })
}

useFinalizeEnergyPageUploadsMutation.fetcher = (
  variables: Types.FinalizeEnergyPageUploadsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinalizeEnergyPageUploadsMutation,
    Types.FinalizeEnergyPageUploadsMutationVariables
  >(FinalizeEnergyPageUploadsDocument, variables, options)

export const CreateEmailDraftDocument = `
    mutation CreateEmailDraft($draftParams: DraftEmailInput!) {
  admin {
    googleApi {
      draftEmail(draftParams: $draftParams)
    }
  }
}
    `

export const useCreateEmailDraftMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateEmailDraftMutation,
    TError,
    Types.CreateEmailDraftMutationVariables,
    TContext
  >({
    mutationKey: ["CreateEmailDraft"],
    mutationFn: (variables?: Types.CreateEmailDraftMutationVariables) =>
      publicFetcher<
        Types.CreateEmailDraftMutation,
        Types.CreateEmailDraftMutationVariables
      >(CreateEmailDraftDocument, variables)(),
    ...options,
  })
}

useCreateEmailDraftMutation.fetcher = (
  variables: Types.CreateEmailDraftMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateEmailDraftMutation,
    Types.CreateEmailDraftMutationVariables
  >(CreateEmailDraftDocument, variables, options)

export const AddFinmodelOverrideDocument = `
    mutation AddFinmodelOverride($sitePid: String, $portfolioPid: String, $overrideInput: FinmodelMutationInput!) {
  finmodel {
    addOverride(
      sitePid: $sitePid
      portfolioPid: $portfolioPid
      overrideInput: $overrideInput
    ) {
      __typename
      ... on Error {
        message
      }
      ... on FinmodelInputOverride {
        inputName
        inputValue
      }
    }
  }
}
    `

export const useAddFinmodelOverrideMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddFinmodelOverrideMutation,
    TError,
    Types.AddFinmodelOverrideMutationVariables,
    TContext
  >({
    mutationKey: ["AddFinmodelOverride"],
    mutationFn: (variables?: Types.AddFinmodelOverrideMutationVariables) =>
      publicFetcher<
        Types.AddFinmodelOverrideMutation,
        Types.AddFinmodelOverrideMutationVariables
      >(AddFinmodelOverrideDocument, variables)(),
    ...options,
  })
}

useAddFinmodelOverrideMutation.fetcher = (
  variables: Types.AddFinmodelOverrideMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AddFinmodelOverrideMutation,
    Types.AddFinmodelOverrideMutationVariables
  >(AddFinmodelOverrideDocument, variables, options)

export const SetEnergyProjectMilestoneDocument = `
    mutation SetEnergyProjectMilestone($projectPid: String!, $milestoneType: MilestoneType!, $milestoneInput: ProjectMilestoneInput!) {
  project(projectPid: $projectPid) {
    setMilestone(milestoneType: $milestoneType, milestoneInput: $milestoneInput)
  }
}
    `

export const useSetEnergyProjectMilestoneMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectMilestoneMutation,
    TError,
    Types.SetEnergyProjectMilestoneMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectMilestoneMutation,
    TError,
    Types.SetEnergyProjectMilestoneMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectMilestone"],
    mutationFn: (variables?: Types.SetEnergyProjectMilestoneMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectMilestoneMutation,
        Types.SetEnergyProjectMilestoneMutationVariables
      >(SetEnergyProjectMilestoneDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectMilestoneMutation.fetcher = (
  variables: Types.SetEnergyProjectMilestoneMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectMilestoneMutation,
    Types.SetEnergyProjectMilestoneMutationVariables
  >(SetEnergyProjectMilestoneDocument, variables, options)

export const SetEnergyProjectSystemDocument = `
    mutation SetEnergyProjectSystem($projectPid: String!, $input: AddSystemInput!) {
  project(projectPid: $projectPid) {
    setSystem(input: $input) {
      system {
        pid
      }
      lite {
        pid
      }
    }
  }
}
    `

export const useSetEnergyProjectSystemMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectSystemMutation,
    TError,
    Types.SetEnergyProjectSystemMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectSystemMutation,
    TError,
    Types.SetEnergyProjectSystemMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectSystem"],
    mutationFn: (variables?: Types.SetEnergyProjectSystemMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectSystemMutation,
        Types.SetEnergyProjectSystemMutationVariables
      >(SetEnergyProjectSystemDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectSystemMutation.fetcher = (
  variables: Types.SetEnergyProjectSystemMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectSystemMutation,
    Types.SetEnergyProjectSystemMutationVariables
  >(SetEnergyProjectSystemDocument, variables, options)

export const SetEnergyProjectDetailsDocument = `
    mutation SetEnergyProjectDetails($projectPid: String!, $input: EnergyProjectDetails!) {
  project(projectPid: $projectPid) {
    setProjectDetails(input: $input) {
      __typename
      ... on Error {
        message
      }
    }
  }
}
    `

export const useSetEnergyProjectDetailsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectDetailsMutation,
    TError,
    Types.SetEnergyProjectDetailsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectDetailsMutation,
    TError,
    Types.SetEnergyProjectDetailsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectDetails"],
    mutationFn: (variables?: Types.SetEnergyProjectDetailsMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectDetailsMutation,
        Types.SetEnergyProjectDetailsMutationVariables
      >(SetEnergyProjectDetailsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectDetailsMutation.fetcher = (
  variables: Types.SetEnergyProjectDetailsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectDetailsMutation,
    Types.SetEnergyProjectDetailsMutationVariables
  >(SetEnergyProjectDetailsDocument, variables, options)

export const SetEnergyProjectCsSpecificationsDocument = `
    mutation SetEnergyProjectCSSpecifications($projectPid: String!, $input: CSProjectSpecificationInput!) {
  project(projectPid: $projectPid) {
    setCsSpecificationDetails(input: $input)
  }
}
    `

export const useSetEnergyProjectCsSpecificationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectCsSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCsSpecificationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectCsSpecificationsMutation,
    TError,
    Types.SetEnergyProjectCsSpecificationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectCSSpecifications"],
    mutationFn: (variables?: Types.SetEnergyProjectCsSpecificationsMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectCsSpecificationsMutation,
        Types.SetEnergyProjectCsSpecificationsMutationVariables
      >(SetEnergyProjectCsSpecificationsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectCsSpecificationsMutation.fetcher = (
  variables: Types.SetEnergyProjectCsSpecificationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectCsSpecificationsMutation,
    Types.SetEnergyProjectCsSpecificationsMutationVariables
  >(SetEnergyProjectCsSpecificationsDocument, variables, options)

export const SetEnergyProjectPpaSpecificationsDocument = `
    mutation SetEnergyProjectPPASpecifications($projectPid: String!, $input: PPAProjectSpecificationInput!) {
  project(projectPid: $projectPid) {
    setPpaSpecificationDetails(input: $input)
  }
}
    `

export const useSetEnergyProjectPpaSpecificationsMutation = <
  TError = unknown,
  TContext = unknown,
>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    TError,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    TError,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectPPASpecifications"],
    mutationFn: (
      variables?: Types.SetEnergyProjectPpaSpecificationsMutationVariables,
    ) =>
      publicFetcher<
        Types.SetEnergyProjectPpaSpecificationsMutation,
        Types.SetEnergyProjectPpaSpecificationsMutationVariables
      >(SetEnergyProjectPpaSpecificationsDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectPpaSpecificationsMutation.fetcher = (
  variables: Types.SetEnergyProjectPpaSpecificationsMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectPpaSpecificationsMutation,
    Types.SetEnergyProjectPpaSpecificationsMutationVariables
  >(SetEnergyProjectPpaSpecificationsDocument, variables, options)

export const SetEnergyProjectStatusDocument = `
    mutation SetEnergyProjectStatus($projectPid: String!, $status: ProjectStatus, $closed: Boolean, $closeReason: ProjectCloseReason, $closeCustomReason: String) {
  project(projectPid: $projectPid) {
    setProjectStatus(
      status: $status
      closed: $closed
      closeReason: $closeReason
      customCloseReason: $closeCustomReason
    )
  }
}
    `

export const useSetEnergyProjectStatusMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.SetEnergyProjectStatusMutation,
    TError,
    Types.SetEnergyProjectStatusMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.SetEnergyProjectStatusMutation,
    TError,
    Types.SetEnergyProjectStatusMutationVariables,
    TContext
  >({
    mutationKey: ["SetEnergyProjectStatus"],
    mutationFn: (variables?: Types.SetEnergyProjectStatusMutationVariables) =>
      publicFetcher<
        Types.SetEnergyProjectStatusMutation,
        Types.SetEnergyProjectStatusMutationVariables
      >(SetEnergyProjectStatusDocument, variables)(),
    ...options,
  })
}

useSetEnergyProjectStatusMutation.fetcher = (
  variables: Types.SetEnergyProjectStatusMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SetEnergyProjectStatusMutation,
    Types.SetEnergyProjectStatusMutationVariables
  >(SetEnergyProjectStatusDocument, variables, options)

export const ArchiveEnergyProjectDocument = `
    mutation ArchiveEnergyProject($projectPid: String!, $archive: Boolean!) {
  project(projectPid: $projectPid) {
    archiveProject(archive: $archive)
  }
}
    `

export const useArchiveEnergyProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.ArchiveEnergyProjectMutation,
    TError,
    Types.ArchiveEnergyProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.ArchiveEnergyProjectMutation,
    TError,
    Types.ArchiveEnergyProjectMutationVariables,
    TContext
  >({
    mutationKey: ["ArchiveEnergyProject"],
    mutationFn: (variables?: Types.ArchiveEnergyProjectMutationVariables) =>
      publicFetcher<
        Types.ArchiveEnergyProjectMutation,
        Types.ArchiveEnergyProjectMutationVariables
      >(ArchiveEnergyProjectDocument, variables)(),
    ...options,
  })
}

useArchiveEnergyProjectMutation.fetcher = (
  variables: Types.ArchiveEnergyProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ArchiveEnergyProjectMutation,
    Types.ArchiveEnergyProjectMutationVariables
  >(ArchiveEnergyProjectDocument, variables, options)

export const CreateFtmProgramDocument = `
    mutation CreateFtmProgram($program: FTMProgramInput!) {
  ftmPrograms {
    new(program: $program) {
      state
      name
    }
  }
}
    `

export const useCreateFtmProgramMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateFtmProgramMutation,
    TError,
    Types.CreateFtmProgramMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateFtmProgramMutation,
    TError,
    Types.CreateFtmProgramMutationVariables,
    TContext
  >({
    mutationKey: ["CreateFtmProgram"],
    mutationFn: (variables?: Types.CreateFtmProgramMutationVariables) =>
      publicFetcher<
        Types.CreateFtmProgramMutation,
        Types.CreateFtmProgramMutationVariables
      >(CreateFtmProgramDocument, variables)(),
    ...options,
  })
}

useCreateFtmProgramMutation.fetcher = (
  variables: Types.CreateFtmProgramMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateFtmProgramMutation,
    Types.CreateFtmProgramMutationVariables
  >(CreateFtmProgramDocument, variables, options)

export const CreateBatteryProductDocument = `
    mutation CreateBatteryProduct($input: BatteryProductAdminInput!) {
  batteryProducts {
    create(input: $input) {
      id
      sizeKw
      sizeKwh
      vendor
      equipmentCost
      laborCost
      includeInOptimizer
      disabled
    }
  }
}
    `

export const useCreateBatteryProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.CreateBatteryProductMutation,
    TError,
    Types.CreateBatteryProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.CreateBatteryProductMutation,
    TError,
    Types.CreateBatteryProductMutationVariables,
    TContext
  >({
    mutationKey: ["CreateBatteryProduct"],
    mutationFn: (variables?: Types.CreateBatteryProductMutationVariables) =>
      publicFetcher<
        Types.CreateBatteryProductMutation,
        Types.CreateBatteryProductMutationVariables
      >(CreateBatteryProductDocument, variables)(),
    ...options,
  })
}

useCreateBatteryProductMutation.fetcher = (
  variables: Types.CreateBatteryProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CreateBatteryProductMutation,
    Types.CreateBatteryProductMutationVariables
  >(CreateBatteryProductDocument, variables, options)

export const UpdateBatteryProductDocument = `
    mutation UpdateBatteryProduct($id: Int!, $input: BatteryProductAdminInput!) {
  batteryProducts {
    update(id: $id, input: $input) {
      id
      sizeKw
      sizeKwh
      vendor
      equipmentCost
      laborCost
      includeInOptimizer
      disabled
    }
  }
}
    `

export const useUpdateBatteryProductMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateBatteryProductMutation,
    TError,
    Types.UpdateBatteryProductMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateBatteryProductMutation,
    TError,
    Types.UpdateBatteryProductMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateBatteryProduct"],
    mutationFn: (variables?: Types.UpdateBatteryProductMutationVariables) =>
      publicFetcher<
        Types.UpdateBatteryProductMutation,
        Types.UpdateBatteryProductMutationVariables
      >(UpdateBatteryProductDocument, variables)(),
    ...options,
  })
}

useUpdateBatteryProductMutation.fetcher = (
  variables: Types.UpdateBatteryProductMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateBatteryProductMutation,
    Types.UpdateBatteryProductMutationVariables
  >(UpdateBatteryProductDocument, variables, options)

export const AddProjectDocument = `
    mutation AddProject($sitePid: String!, $input: ProjectInput!) {
  addProject(sitePid: $sitePid, input: $input) {
    __typename
    ... on EnergyProject {
      ...EnergyProject
    }
    ... on Error {
      message
    }
  }
}
    ${EnergyProjectFragmentDoc}
${UserRefFragmentDoc}
${EnergyProjectCsSpecificationFragmentDoc}
${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${EnergyProjectPpaSpecificationFragmentDoc}
${SystemFragmentDoc}
${MilestoneSummaryFragmentDoc}
${ProgressNotesFragmentDoc}`

export const useAddProjectMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.AddProjectMutation,
    TError,
    Types.AddProjectMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.AddProjectMutation,
    TError,
    Types.AddProjectMutationVariables,
    TContext
  >({
    mutationKey: ["AddProject"],
    mutationFn: (variables?: Types.AddProjectMutationVariables) =>
      publicFetcher<Types.AddProjectMutation, Types.AddProjectMutationVariables>(
        AddProjectDocument,
        variables,
      )(),
    ...options,
  })
}

useAddProjectMutation.fetcher = (
  variables: Types.AddProjectMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AddProjectMutation, Types.AddProjectMutationVariables>(
    AddProjectDocument,
    variables,
    options,
  )

export const UpdateProgressNotesDocument = `
    mutation UpdateProgressNotes($projectPid: String!, $input: ProgressNotesInput!) {
  progressNotes {
    updateProgressNotes(projectPid: $projectPid, input: $input) {
      ... on ProgressNotesType {
        ...ProgressNotes
      }
      ... on Error {
        message
      }
    }
  }
}
    ${ProgressNotesFragmentDoc}`

export const useUpdateProgressNotesMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UpdateProgressNotesMutation,
    TError,
    Types.UpdateProgressNotesMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UpdateProgressNotesMutation,
    TError,
    Types.UpdateProgressNotesMutationVariables,
    TContext
  >({
    mutationKey: ["UpdateProgressNotes"],
    mutationFn: (variables?: Types.UpdateProgressNotesMutationVariables) =>
      publicFetcher<
        Types.UpdateProgressNotesMutation,
        Types.UpdateProgressNotesMutationVariables
      >(UpdateProgressNotesDocument, variables)(),
    ...options,
  })
}

useUpdateProgressNotesMutation.fetcher = (
  variables: Types.UpdateProgressNotesMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UpdateProgressNotesMutation,
    Types.UpdateProgressNotesMutationVariables
  >(UpdateProgressNotesDocument, variables, options)

export const UploadAdminHelioscopeDocument = `
    mutation UploadAdminHelioscope($input: HelioscopeUploadInput!) {
  admin {
    helioscopes {
      uploadHelioscope(uploadInput: $input) {
        __typename
        ... on Helioscope {
          pid
        }
        ... on Error {
          message
        }
      }
    }
  }
}
    `

export const useUploadAdminHelioscopeMutation = <TError = unknown, TContext = unknown>(
  options?: UseMutationOptions<
    Types.UploadAdminHelioscopeMutation,
    TError,
    Types.UploadAdminHelioscopeMutationVariables,
    TContext
  >,
) => {
  return useMutation<
    Types.UploadAdminHelioscopeMutation,
    TError,
    Types.UploadAdminHelioscopeMutationVariables,
    TContext
  >({
    mutationKey: ["UploadAdminHelioscope"],
    mutationFn: (variables?: Types.UploadAdminHelioscopeMutationVariables) =>
      publicFetcher<
        Types.UploadAdminHelioscopeMutation,
        Types.UploadAdminHelioscopeMutationVariables
      >(UploadAdminHelioscopeDocument, variables)(),
    ...options,
  })
}

useUploadAdminHelioscopeMutation.fetcher = (
  variables: Types.UploadAdminHelioscopeMutationVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.UploadAdminHelioscopeMutation,
    Types.UploadAdminHelioscopeMutationVariables
  >(UploadAdminHelioscopeDocument, variables, options)

export const ListUtilitiesDocument = `
    query ListUtilities($zipCode: String!, $state: String!) {
  utilitiesForZip(zipCode: $zipCode) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
  utilitiesForState(state: $state) {
    __typename
    ... on UtilityList {
      refs {
        id
        pid
        name
        shortName
      }
    }
    ... on Error {
      message
    }
  }
}
    `

export const useListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ListUtilitiesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilities", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useListUtilitiesQuery.getKey = (variables: Types.ListUtilitiesQueryVariables) => [
  "ListUtilities",
  variables,
]

useListUtilitiesQuery.rootKey = "ListUtilities"

export const useSuspenseListUtilitiesQuery = <
  TData = Types.ListUtilitiesQuery,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ListUtilitiesQuery, TError, TData>({
    queryKey: ["ListUtilitiesSuspense", variables],
    queryFn: publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
      ListUtilitiesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilitiesSuspense", variables]

useSuspenseListUtilitiesQuery.rootKey = "ListUtilities"

export const useInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infinite", variables]

useInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

export const useSuspenseInfiniteListUtilitiesQuery = <
  TData = InfiniteData<Types.ListUtilitiesQuery>,
  TError = unknown,
>(
  variables: Types.ListUtilitiesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ListUtilitiesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ListUtilitiesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ListUtilitiesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ListUtilities.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
            ListUtilitiesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteListUtilitiesQuery.getKey = (
  variables: Types.ListUtilitiesQueryVariables,
) => ["ListUtilities.infiniteSuspense", variables]

useSuspenseInfiniteListUtilitiesQuery.rootKey = "ListUtilities.infinite"

useListUtilitiesQuery.fetcher = (
  variables: Types.ListUtilitiesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ListUtilitiesQuery, Types.ListUtilitiesQueryVariables>(
    ListUtilitiesDocument,
    variables,
    options,
  )

export const OrgSettingsDetailBySlugDocument = `
    query OrgSettingsDetailBySlug($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    slug
    irrFlavor
    isDeveloper
    currentUserPermissions
    users {
      ...UserRef
    }
    portfolios {
      pid
      title
    }
    userRoles {
      user {
        ...UserRef
      }
      role
    }
    investorProfile {
      updatedAt
      updatedByName
      hq
      yearFounded
      website
      managementTeam
      description
      distinguishingFeatures
      investorBacking
      longTermOwnershipApproach
      subscriptionMgmtApproach
      committedFunding
      experiences {
        state
        ftmEnergizedPvKw
        ftmAwardedPvKw
        btmEnergizedPvKw
        btmAwardedPvKw
      }
      buyBoxes {
        state
        ftmMinPortfolioSize
        ftmMinProjectSize
        ftmSeekingProjects
        btmMinPortfolioSize
        btmMinProjectSize
        btmSeekingProjects
        utilityProgramNotes
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug", variables]

useOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useSuspenseOrgSettingsDetailBySlugQuery = <
  TData = Types.OrgSettingsDetailBySlugQuery,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>({
    queryKey: ["OrgSettingsDetailBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSettingsDetailBySlugQuery,
      Types.OrgSettingsDetailBySlugQueryVariables
    >(OrgSettingsDetailBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlugSuspense", variables]

useSuspenseOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug"

export const useInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSettingsDetailBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infinite", variables]

useInfiniteOrgSettingsDetailBySlugQuery.rootKey = "OrgSettingsDetailBySlug.infinite"

export const useSuspenseInfiniteOrgSettingsDetailBySlugQuery = <
  TData = InfiniteData<Types.OrgSettingsDetailBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSettingsDetailBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSettingsDetailBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSettingsDetailBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "OrgSettingsDetailBySlug.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSettingsDetailBySlugQuery,
            Types.OrgSettingsDetailBySlugQueryVariables
          >(OrgSettingsDetailBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.getKey = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
) => ["OrgSettingsDetailBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSettingsDetailBySlugQuery.rootKey =
  "OrgSettingsDetailBySlug.infinite"

useOrgSettingsDetailBySlugQuery.fetcher = (
  variables: Types.OrgSettingsDetailBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSettingsDetailBySlugQuery,
    Types.OrgSettingsDetailBySlugQueryVariables
  >(OrgSettingsDetailBySlugDocument, variables, options)

export const OrgUsersDocument = `
    query OrgUsers($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    name
    currentUserPermissions
    portfolios {
      pid
      title
    }
    orgUsers {
      user {
        pid
        name
        firstName
        email
      }
      userRoles {
        role
        portfolio {
          pid
          title
        }
      }
    }
    unclaimedInvites {
      dateCreated
      createdBy {
        ...UserRef
      }
      portfolio {
        pid
        title
      }
      targetUser {
        ...UserRef
      }
    }
  }
}
    ${UserRefFragmentDoc}`

export const useOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsers", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers",
  variables,
]

useOrgUsersQuery.rootKey = "OrgUsers"

export const useSuspenseOrgUsersQuery = <TData = Types.OrgUsersQuery, TError = unknown>(
  variables: Types.OrgUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgUsersQuery, TError, TData>({
    queryKey: ["OrgUsersSuspense", variables],
    queryFn: publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
      OrgUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsersSuspense",
  variables,
]

useSuspenseOrgUsersQuery.rootKey = "OrgUsers"

export const useInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infinite",
  variables,
]

useInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

export const useSuspenseInfiniteOrgUsersQuery = <
  TData = InfiniteData<Types.OrgUsersQuery>,
  TError = unknown,
>(
  variables: Types.OrgUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
            OrgUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgUsersQuery.getKey = (variables: Types.OrgUsersQueryVariables) => [
  "OrgUsers.infiniteSuspense",
  variables,
]

useSuspenseInfiniteOrgUsersQuery.rootKey = "OrgUsers.infinite"

useOrgUsersQuery.fetcher = (
  variables: Types.OrgUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgUsersQuery, Types.OrgUsersQueryVariables>(
    OrgUsersDocument,
    variables,
    options,
  )

export const AdminPortfolioUsersDocument = `
    query AdminPortfolioUsers($portfolioPid: String!) {
  admin {
    portfolio(pid: $portfolioPid) {
      pid
      title
      users {
        ...AdminUserRef
      }
    }
  }
}
    ${AdminUserRefFragmentDoc}`

export const useAdminPortfolioUsersQuery = <
  TData = Types.AdminPortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AdminPortfolioUsersQuery, TError, TData>({
    queryKey: ["AdminPortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioUsersQuery,
      Types.AdminPortfolioUsersQueryVariables
    >(AdminPortfolioUsersDocument, variables),
    ...options,
  })
}

useAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers", variables]

useAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers"

export const useSuspenseAdminPortfolioUsersQuery = <
  TData = Types.AdminPortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminPortfolioUsersQuery, TError, TData>({
    queryKey: ["AdminPortfolioUsersSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminPortfolioUsersQuery,
      Types.AdminPortfolioUsersQueryVariables
    >(AdminPortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspenseAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsersSuspense", variables]

useSuspenseAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers"

export const useInfiniteAdminPortfolioUsersQuery = <
  TData = InfiniteData<Types.AdminPortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminPortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminPortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioUsersQuery,
            Types.AdminPortfolioUsersQueryVariables
          >(AdminPortfolioUsersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers.infinite", variables]

useInfiniteAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers.infinite"

export const useSuspenseInfiniteAdminPortfolioUsersQuery = <
  TData = InfiniteData<Types.AdminPortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminPortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminPortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminPortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminPortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AdminPortfolioUsers.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminPortfolioUsersQuery,
            Types.AdminPortfolioUsersQueryVariables
          >(AdminPortfolioUsersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminPortfolioUsersQuery.getKey = (
  variables: Types.AdminPortfolioUsersQueryVariables,
) => ["AdminPortfolioUsers.infiniteSuspense", variables]

useSuspenseInfiniteAdminPortfolioUsersQuery.rootKey = "AdminPortfolioUsers.infinite"

useAdminPortfolioUsersQuery.fetcher = (
  variables: Types.AdminPortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AdminPortfolioUsersQuery,
    Types.AdminPortfolioUsersQueryVariables
  >(AdminPortfolioUsersDocument, variables, options)

export const PortfolioUsersDocument = `
    query PortfolioUsers($portfolioPid: String!) {
  portfolio(pid: $portfolioPid) {
    pid
    title
    users {
      ...UserRef
    }
  }
}
    ${UserRefFragmentDoc}`

export const usePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PortfolioUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsers", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

usePortfolioUsersQuery.getKey = (variables: Types.PortfolioUsersQueryVariables) => [
  "PortfolioUsers",
  variables,
]

usePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useSuspensePortfolioUsersQuery = <
  TData = Types.PortfolioUsersQuery,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioUsersQuery, TError, TData>({
    queryKey: ["PortfolioUsersSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioUsersQuery,
      Types.PortfolioUsersQueryVariables
    >(PortfolioUsersDocument, variables),
    ...options,
  })
}

useSuspensePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsersSuspense", variables]

useSuspensePortfolioUsersQuery.rootKey = "PortfolioUsers"

export const useInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infinite", variables]

useInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

export const useSuspenseInfinitePortfolioUsersQuery = <
  TData = InfiniteData<Types.PortfolioUsersQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
            PortfolioUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioUsersQuery.getKey = (
  variables: Types.PortfolioUsersQueryVariables,
) => ["PortfolioUsers.infiniteSuspense", variables]

useSuspenseInfinitePortfolioUsersQuery.rootKey = "PortfolioUsers.infinite"

usePortfolioUsersQuery.fetcher = (
  variables: Types.PortfolioUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PortfolioUsersQuery, Types.PortfolioUsersQueryVariables>(
    PortfolioUsersDocument,
    variables,
    options,
  )

export const OrgSiteLabelsBySlugDocument = `
    query OrgSiteLabelsBySlug($orgSlug: String) {
  orgLabels(slug: $orgSlug) {
    ...SiteLabelRef
  }
}
    ${SiteLabelRefFragmentDoc}`

export const useOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlug"]
        : ["OrgSiteLabelsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteLabelsBySlug"] : ["OrgSiteLabelsBySlug", variables]

useOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useSuspenseOrgSiteLabelsBySlugQuery = <
  TData = Types.OrgSiteLabelsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteLabelsBySlugSuspense"]
        : ["OrgSiteLabelsBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteLabelsBySlugQuery,
      Types.OrgSiteLabelsBySlugQueryVariables
    >(OrgSiteLabelsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlugSuspense"]
    : ["OrgSiteLabelsBySlugSuspense", variables]

useSuspenseOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug"

export const useInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infinite"]
            : ["OrgSiteLabelsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infinite"]
    : ["OrgSiteLabelsBySlug.infinite", variables]

useInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteLabelsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteLabelsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteLabelsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteLabelsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteLabelsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteLabelsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteLabelsBySlug.infiniteSuspense"]
            : ["OrgSiteLabelsBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteLabelsBySlugQuery,
            Types.OrgSiteLabelsBySlugQueryVariables
          >(OrgSiteLabelsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.getKey = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteLabelsBySlug.infiniteSuspense"]
    : ["OrgSiteLabelsBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteLabelsBySlugQuery.rootKey = "OrgSiteLabelsBySlug.infinite"

useOrgSiteLabelsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteLabelsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OrgSiteLabelsBySlugQuery,
    Types.OrgSiteLabelsBySlugQueryVariables
  >(OrgSiteLabelsBySlugDocument, variables, options)

export const OrgSiteDocsBySlugDocument = `
    query OrgSiteDocsBySlug($orgSlug: String) {
  orgSiteDocuments(slug: $orgSlug) {
    sitePid
    documents {
      ...Document
    }
  }
}
    ${DocumentFragmentDoc}`

export const useOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlug"]
        : ["OrgSiteDocsBySlug", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined ? ["OrgSiteDocsBySlug"] : ["OrgSiteDocsBySlug", variables]

useOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useSuspenseOrgSiteDocsBySlugQuery = <
  TData = Types.OrgSiteDocsBySlugQuery,
  TError = unknown,
>(
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["OrgSiteDocsBySlugSuspense"]
        : ["OrgSiteDocsBySlugSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteDocsBySlugQuery,
      Types.OrgSiteDocsBySlugQueryVariables
    >(OrgSiteDocsBySlugDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlugSuspense"]
    : ["OrgSiteDocsBySlugSuspense", variables]

useSuspenseOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug"

export const useInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infinite"]
            : ["OrgSiteDocsBySlug.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infinite"]
    : ["OrgSiteDocsBySlug.infinite", variables]

useInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

export const useSuspenseInfiniteOrgSiteDocsBySlugQuery = <
  TData = InfiniteData<Types.OrgSiteDocsBySlugQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteDocsBySlugQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteDocsBySlugQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteDocsBySlugQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteDocsBySlugQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["OrgSiteDocsBySlug.infiniteSuspense"]
            : ["OrgSiteDocsBySlug.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteDocsBySlugQuery,
            Types.OrgSiteDocsBySlugQueryVariables
          >(OrgSiteDocsBySlugDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteDocsBySlugQuery.getKey = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
) =>
  variables === undefined
    ? ["OrgSiteDocsBySlug.infiniteSuspense"]
    : ["OrgSiteDocsBySlug.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteDocsBySlugQuery.rootKey = "OrgSiteDocsBySlug.infinite"

useOrgSiteDocsBySlugQuery.fetcher = (
  variables?: Types.OrgSiteDocsBySlugQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteDocsBySlugQuery, Types.OrgSiteDocsBySlugQueryVariables>(
    OrgSiteDocsBySlugDocument,
    variables,
    options,
  )

export const GetVersionDocument = `
    query GetVersion {
  schemaVersion {
    version
  }
}
    `

export const useGetVersionQuery = <TData = Types.GetVersionQuery, TError = unknown>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetVersionQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetVersionQuery, TError, TData>({
    queryKey: variables === undefined ? ["GetVersion"] : ["GetVersion", variables],
    queryFn: publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion"] : ["GetVersion", variables]

useGetVersionQuery.rootKey = "GetVersion"

export const useSuspenseGetVersionQuery = <
  TData = Types.GetVersionQuery,
  TError = unknown,
>(
  variables?: Types.GetVersionQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetVersionQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["GetVersionSuspense"]
        : ["GetVersionSuspense", variables],
    queryFn: publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
      GetVersionDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersionSuspense"] : ["GetVersionSuspense", variables]

useSuspenseGetVersionQuery.rootKey = "GetVersion"

export const useInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infinite"]
            : ["GetVersion.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetVersionQuery.getKey = (variables?: Types.GetVersionQueryVariables) =>
  variables === undefined ? ["GetVersion.infinite"] : ["GetVersion.infinite", variables]

useInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

export const useSuspenseInfiniteGetVersionQuery = <
  TData = InfiniteData<Types.GetVersionQuery>,
  TError = unknown,
>(
  variables: Types.GetVersionQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetVersionQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetVersionQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetVersionQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["GetVersion.infiniteSuspense"]
            : ["GetVersion.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
            GetVersionDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetVersionQuery.getKey = (
  variables?: Types.GetVersionQueryVariables,
) =>
  variables === undefined
    ? ["GetVersion.infiniteSuspense"]
    : ["GetVersion.infiniteSuspense", variables]

useSuspenseInfiniteGetVersionQuery.rootKey = "GetVersion.infinite"

useGetVersionQuery.fetcher = (
  variables?: Types.GetVersionQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetVersionQuery, Types.GetVersionQueryVariables>(
    GetVersionDocument,
    variables,
    options,
  )

export const CurrentUserDocument = `
    query CurrentUser {
  currentUser {
    pid
    isAuthenticated
    isAdmin
    email
    name
    org {
      pid
      slug
      name
      logoUrl
      logomarkUrl
      irrFlavor
      inactivityTimeoutMs
    }
    orgs {
      pid
      name
      slug
    }
  }
}
    `

export const useCurrentUserQuery = <TData = Types.CurrentUserQuery, TError = unknown>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<UseQueryOptions<Types.CurrentUserQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.CurrentUserQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey: variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUser"] : ["CurrentUser", variables]

useCurrentUserQuery.rootKey = "CurrentUser"

export const useSuspenseCurrentUserQuery = <
  TData = Types.CurrentUserQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserSuspense"]
        : ["CurrentUserSuspense", variables],
    queryFn: publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
      CurrentUserDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined ? ["CurrentUserSuspense"] : ["CurrentUserSuspense", variables]

useSuspenseCurrentUserQuery.rootKey = "CurrentUser"

export const useInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infinite"]
            : ["CurrentUser.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserQuery.getKey = (variables?: Types.CurrentUserQueryVariables) =>
  variables === undefined
    ? ["CurrentUser.infinite"]
    : ["CurrentUser.infinite", variables]

useInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

export const useSuspenseInfiniteCurrentUserQuery = <
  TData = InfiniteData<Types.CurrentUserQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUser.infiniteSuspense"]
            : ["CurrentUser.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
            CurrentUserDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserQuery.getKey = (
  variables?: Types.CurrentUserQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUser.infiniteSuspense"]
    : ["CurrentUser.infiniteSuspense", variables]

useSuspenseInfiniteCurrentUserQuery.rootKey = "CurrentUser.infinite"

useCurrentUserQuery.fetcher = (
  variables?: Types.CurrentUserQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CurrentUserQuery, Types.CurrentUserQueryVariables>(
    CurrentUserDocument,
    variables,
    options,
  )

export const PropertyHubViewDocument = `
    query PropertyHubView($orgSlug: String!, $filterBy: OrgSiteTableFilter!, $sortBy: [OrgSiteTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const usePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubView", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

usePropertyHubViewQuery.getKey = (variables: Types.PropertyHubViewQueryVariables) => [
  "PropertyHubView",
  variables,
]

usePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useSuspensePropertyHubViewQuery = <
  TData = Types.PropertyHubViewQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubViewQuery, TError, TData>({
    queryKey: ["PropertyHubViewSuspense", variables],
    queryFn: publicFetcher<
      Types.PropertyHubViewQuery,
      Types.PropertyHubViewQueryVariables
    >(PropertyHubViewDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubViewSuspense", variables]

useSuspensePropertyHubViewQuery.rootKey = "PropertyHubView"

export const useInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infinite", variables]

useInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

export const useSuspenseInfinitePropertyHubViewQuery = <
  TData = InfiniteData<Types.PropertyHubViewQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubViewQuery,
            Types.PropertyHubViewQueryVariables
          >(PropertyHubViewDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubViewQuery.getKey = (
  variables: Types.PropertyHubViewQueryVariables,
) => ["PropertyHubView.infiniteSuspense", variables]

useSuspenseInfinitePropertyHubViewQuery.rootKey = "PropertyHubView.infinite"

usePropertyHubViewQuery.fetcher = (
  variables: Types.PropertyHubViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubViewQuery, Types.PropertyHubViewQueryVariables>(
    PropertyHubViewDocument,
    variables,
    options,
  )

export const PropertyHubRowsDocument = `
    query PropertyHubRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    sites {
      rows(keys: $keys) {
        pid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        ownerName
        customerId
        reonomyPropertyId
        reonomyPropertyLink
        tenants
        portfolio
        status
        statusReason
        statusCustomReason
        flagSummary {
          __typename
          utilityNotFound
          tariffNotFound
          improbableEnergyCosts
          existingSolarLikely
          duplicateAddressFound
        }
        otherPortfolios
        salePlans
        salePlannedYear
        mortgageLender
        sqft
        buildingType
        buildingClass
        numStories
        numUnitsTotal
        yearBuilt
        buildingFootprintSqft
        buildingFootprintRoofSqftError
        buildingSqftRoofSqftError
        buildingFootprintCount
        existingSolarDetectionScore
        inEnergyCommunity
        inLowIncomeCommunity
        roofCondition
        roofReplacedYr
        roofSqft
        usableRoofSqft
        metering
        loadOrigin
        utilityName
        utilityCagr
        utilityCagrFromYear
        utilityCagrToYear
        meterCount
        loadKwh
        loadUsd
        loadUsdPerKwh
        soleTariffName
        bauCo2EmissionsTons
        bauCo2EmissionsTonsPerMwh
        ppaOfftaker
        ppaSignatory
        occupancyType
        tenantLeaseEnds
        leaseType
        tenantInterest
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        noiRangeMin
        noiRangeMax
        noiSelfMax
        irrSelfMax
        solarLocationSelfMax
        includeStorageSelfMax
        pvSizeKwSelfMax
        storageSizeKwSelfMax
        storageSizeKwhSelfMax
        leDevFeeSelfMax
        gpvSelfMax
        lifetimeCapexSelfMax
        lifetimeUtilitySavingsSelfMax
        co2EmissionsReductionTonnesSelfMax
        noiPpaMax
        solarLocationPpaMax
        includeStoragePpaMax
        pvSizeKwPpaMax
        storageSizeKwPpaMax
        storageSizeKwhPpaMax
        leDevFeePpaMax
        gpvPpaMax
        lifetimeUtilitySavingsPpaMax
        costPerKwhPpaMax
        ppaEscPpaMax
        yearOneOfftakerSavingsPpaMax
        co2EmissionsReductionTonnesPpaMax
        noiLoanMax
        leDevFeeLoanMax
        gpvLoanMax
        noiLeaseMax
        noiCsMax
        pvSizeKwCsMax
        roofMaxPvKw
        groundMaxPvKw
        carportMaxPvKw
        physicalCheck {
          roofIsGood
          carportIsGood
          groundmountIsGood
          existingSolar
          unobstructedFlatRoof
          badRoof
        }
        marketplaceStatus
      }
    }
  }
}
    `

export const usePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRows", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

usePropertyHubRowsQuery.getKey = (variables: Types.PropertyHubRowsQueryVariables) => [
  "PropertyHubRows",
  variables,
]

usePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useSuspensePropertyHubRowsQuery = <
  TData = Types.PropertyHubRowsQuery,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PropertyHubRowsQuery, TError, TData>({
    queryKey: ["PropertyHubRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.PropertyHubRowsQuery,
      Types.PropertyHubRowsQueryVariables
    >(PropertyHubRowsDocument, variables),
    ...options,
  })
}

useSuspensePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRowsSuspense", variables]

useSuspensePropertyHubRowsQuery.rootKey = "PropertyHubRows"

export const useInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infinite", variables]

useInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

export const useSuspenseInfinitePropertyHubRowsQuery = <
  TData = InfiniteData<Types.PropertyHubRowsQuery>,
  TError = unknown,
>(
  variables: Types.PropertyHubRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PropertyHubRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PropertyHubRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PropertyHubRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PropertyHubRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PropertyHubRowsQuery,
            Types.PropertyHubRowsQueryVariables
          >(PropertyHubRowsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePropertyHubRowsQuery.getKey = (
  variables: Types.PropertyHubRowsQueryVariables,
) => ["PropertyHubRows.infiniteSuspense", variables]

useSuspenseInfinitePropertyHubRowsQuery.rootKey = "PropertyHubRows.infinite"

usePropertyHubRowsQuery.fetcher = (
  variables: Types.PropertyHubRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.PropertyHubRowsQuery, Types.PropertyHubRowsQueryVariables>(
    PropertyHubRowsDocument,
    variables,
    options,
  )

export const OrgPropertySearchDocument = `
    query OrgPropertySearch($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    siteSearchTerms {
      propertyName
      address
      pid
    }
  }
}
    `

export const useOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearch", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch", variables]

useOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useSuspenseOrgPropertySearchQuery = <
  TData = Types.OrgPropertySearchQuery,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgPropertySearchQuery, TError, TData>({
    queryKey: ["OrgPropertySearchSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgPropertySearchQuery,
      Types.OrgPropertySearchQueryVariables
    >(OrgPropertySearchDocument, variables),
    ...options,
  })
}

useSuspenseOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearchSuspense", variables]

useSuspenseOrgPropertySearchQuery.rootKey = "OrgPropertySearch"

export const useInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infinite", variables]

useInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

export const useSuspenseInfiniteOrgPropertySearchQuery = <
  TData = InfiniteData<Types.OrgPropertySearchQuery>,
  TError = unknown,
>(
  variables: Types.OrgPropertySearchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgPropertySearchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgPropertySearchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgPropertySearchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgPropertySearch.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgPropertySearchQuery,
            Types.OrgPropertySearchQueryVariables
          >(OrgPropertySearchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgPropertySearchQuery.getKey = (
  variables: Types.OrgPropertySearchQueryVariables,
) => ["OrgPropertySearch.infiniteSuspense", variables]

useSuspenseInfiniteOrgPropertySearchQuery.rootKey = "OrgPropertySearch.infinite"

useOrgPropertySearchQuery.fetcher = (
  variables: Types.OrgPropertySearchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgPropertySearchQuery, Types.OrgPropertySearchQueryVariables>(
    OrgPropertySearchDocument,
    variables,
    options,
  )

export const OrgHomepageDocument = `
    query OrgHomepage($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    portfolioPdfReportHref
    mapView {
      pid
      lat
      lon
    }
  }
}
    `

export const useOrgHomepageQuery = <TData = Types.OrgHomepageQuery, TError = unknown>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgHomepageQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgHomepageQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepage", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage",
  variables,
]

useOrgHomepageQuery.rootKey = "OrgHomepage"

export const useSuspenseOrgHomepageQuery = <
  TData = Types.OrgHomepageQuery,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgHomepageQuery, TError, TData>({
    queryKey: ["OrgHomepageSuspense", variables],
    queryFn: publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
      OrgHomepageDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepageSuspense",
  variables,
]

useSuspenseOrgHomepageQuery.rootKey = "OrgHomepage"

export const useInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgHomepageQuery.getKey = (variables: Types.OrgHomepageQueryVariables) => [
  "OrgHomepage.infinite",
  variables,
]

useInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

export const useSuspenseInfiniteOrgHomepageQuery = <
  TData = InfiniteData<Types.OrgHomepageQuery>,
  TError = unknown,
>(
  variables: Types.OrgHomepageQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgHomepageQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgHomepageQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgHomepageQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgHomepage.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
            OrgHomepageDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgHomepageQuery.getKey = (
  variables: Types.OrgHomepageQueryVariables,
) => ["OrgHomepage.infiniteSuspense", variables]

useSuspenseInfiniteOrgHomepageQuery.rootKey = "OrgHomepage.infinite"

useOrgHomepageQuery.fetcher = (
  variables: Types.OrgHomepageQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgHomepageQuery, Types.OrgHomepageQueryVariables>(
    OrgHomepageDocument,
    variables,
    options,
  )

export const OrgFacetsDocument = `
    query OrgFacets($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        stats {
          portfolios
          coveredStates
          buildingClasses
          buildingTypes
          counties
          cbsaNames
          assetManagers
          utilityNames
          ownerNames
        }
      }
    }
    finproducts {
      id
      type
      name
    }
  }
}
    `

export const useOrgFacetsQuery = <TData = Types.OrgFacetsQuery, TError = unknown>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgFacetsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacets", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets",
  variables,
]

useOrgFacetsQuery.rootKey = "OrgFacets"

export const useSuspenseOrgFacetsQuery = <
  TData = Types.OrgFacetsQuery,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgFacetsQuery, TError, TData>({
    queryKey: ["OrgFacetsSuspense", variables],
    queryFn: publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
      OrgFacetsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacetsSuspense",
  variables,
]

useSuspenseOrgFacetsQuery.rootKey = "OrgFacets"

export const useInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgFacetsQuery.getKey = (variables: Types.OrgFacetsQueryVariables) => [
  "OrgFacets.infinite",
  variables,
]

useInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

export const useSuspenseInfiniteOrgFacetsQuery = <
  TData = InfiniteData<Types.OrgFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OrgFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgFacets.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
            OrgFacetsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgFacetsQuery.getKey = (
  variables: Types.OrgFacetsQueryVariables,
) => ["OrgFacets.infiniteSuspense", variables]

useSuspenseInfiniteOrgFacetsQuery.rootKey = "OrgFacets.infinite"

useOrgFacetsQuery.fetcher = (
  variables: Types.OrgFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgFacetsQuery, Types.OrgFacetsQueryVariables>(
    OrgFacetsDocument,
    variables,
    options,
  )

export const OrgSiteTableDocument = `
    query OrgSiteTable($orgSlug: String!, $filterBy: OrgSiteTableFilter, $sortBy: [OrgSiteTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    sites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          annualNoi
          co2EmissionsReductionTonnes
          maxPvSizeKw
        }
      }
    }
  }
}
    `

export const useOrgSiteTableQuery = <TData = Types.OrgSiteTableQuery, TError = unknown>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTable", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable",
  variables,
]

useOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useSuspenseOrgSiteTableQuery = <
  TData = Types.OrgSiteTableQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableQuery, TError, TData>({
    queryKey: ["OrgSiteTableSuspense", variables],
    queryFn: publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
      OrgSiteTableDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTableSuspense",
  variables,
]

useSuspenseOrgSiteTableQuery.rootKey = "OrgSiteTable"

export const useInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableQuery.getKey = (variables: Types.OrgSiteTableQueryVariables) => [
  "OrgSiteTable.infinite",
  variables,
]

useInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

export const useSuspenseInfiniteOrgSiteTableQuery = <
  TData = InfiniteData<Types.OrgSiteTableQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTable.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
            OrgSiteTableDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableQuery.getKey = (
  variables: Types.OrgSiteTableQueryVariables,
) => ["OrgSiteTable.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteTableQuery.rootKey = "OrgSiteTable.infinite"

useOrgSiteTableQuery.fetcher = (
  variables: Types.OrgSiteTableQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableQuery, Types.OrgSiteTableQueryVariables>(
    OrgSiteTableDocument,
    variables,
    options,
  )

export const PortfolioPdfReportStatsDocument = `
    query PortfolioPdfReportStats($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    mapView {
      pid
      lat
      lon
    }
    sites {
      view(
        filterBy: {status: {value: ACTIVE}}
        sortBy: [{attr: NOI_CS_MAX, order: DESC}]
      ) {
        keys
        count
        stats {
          annualNoi
          maxPvSizeKw
          co2EmissionsReductionTonnes
        }
      }
      highlight: view(
        filterBy: {status: {value: ACTIVE}, irrSelfMax: {gte: 0.1}, noiPpaMax: {gte: 0}, noiCsMax: {gte: 0}}
        sortBy: [{attr: NOI_CS_MAX, order: DESC}]
      ) {
        keys
        count
      }
      cs: view(
        filterBy: {status: {value: ACTIVE}, noiCsMax: {gte: 20000}}
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      csAfter: view(
        filterBy: {status: {value: ACTIVE}, noiCsMax: {gte: 33333}}
        sortBy: []
      ) {
        count
        stats {
          csAnnualNoi
          csMaxPvSizeKw
        }
      }
      ppa: view(filterBy: {status: {value: ACTIVE}, noiPpaMax: {gte: 0}}, sortBy: []) {
        count
        stats {
          ppaAnnualNoi
          ppaMaxPvSizeKw
          ppaCo2EmissionsReductionTonnes
        }
      }
      cash: view(
        filterBy: {status: {value: ACTIVE}, irrSelfMax: {gte: 0.1}}
        sortBy: []
      ) {
        count
        stats {
          cashAnnualNoi
          cashMaxPvSizeKw
          cashCo2EmissionsReductionTonnes
        }
      }
    }
    bestCsSite {
      pid
      name
      streetAddress
      city
      state
      zipCode
      noiCsMax
    }
  }
}
    `

export const usePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStats", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

usePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats", variables]

usePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useSuspensePortfolioPdfReportStatsQuery = <
  TData = Types.PortfolioPdfReportStatsQuery,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>({
    queryKey: ["PortfolioPdfReportStatsSuspense", variables],
    queryFn: publicFetcher<
      Types.PortfolioPdfReportStatsQuery,
      Types.PortfolioPdfReportStatsQueryVariables
    >(PortfolioPdfReportStatsDocument, variables),
    ...options,
  })
}

useSuspensePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStatsSuspense", variables]

useSuspensePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats"

export const useInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["PortfolioPdfReportStats.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infinite", variables]

useInfinitePortfolioPdfReportStatsQuery.rootKey = "PortfolioPdfReportStats.infinite"

export const useSuspenseInfinitePortfolioPdfReportStatsQuery = <
  TData = InfiniteData<Types.PortfolioPdfReportStatsQuery>,
  TError = unknown,
>(
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.PortfolioPdfReportStatsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.PortfolioPdfReportStatsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.PortfolioPdfReportStatsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "PortfolioPdfReportStats.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.PortfolioPdfReportStatsQuery,
            Types.PortfolioPdfReportStatsQueryVariables
          >(PortfolioPdfReportStatsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfinitePortfolioPdfReportStatsQuery.getKey = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
) => ["PortfolioPdfReportStats.infiniteSuspense", variables]

useSuspenseInfinitePortfolioPdfReportStatsQuery.rootKey =
  "PortfolioPdfReportStats.infinite"

usePortfolioPdfReportStatsQuery.fetcher = (
  variables: Types.PortfolioPdfReportStatsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.PortfolioPdfReportStatsQuery,
    Types.PortfolioPdfReportStatsQueryVariables
  >(PortfolioPdfReportStatsDocument, variables, options)

export const OrgSiteTableRowsDocument = `
    query OrgSiteTableRows($orgSlug: String!, $propertyPids: [String!]!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    sites {
      rows(keys: $propertyPids) {
        pid
        name
        portfolio
        streetAddress
        city
        state
        zipCode
        noiRangeMin
        noiRangeMax
        latitude
        longitude
        irrSelfMax
        noiLoanMax
        noiPpaMax
        noiCsMax
        energyThings {
          canRooftopSolar
          canCarportSolar
          canGroundmountSolar
          canStorage
        }
        financingThings {
          canSelfInvest
          canLoan
          canPpa
          canLease
          canCommunitySolar
        }
        footprint {
          latitude
          longitude
          roofShape
          roofShapeGeojson
        }
      }
    }
  }
}
    `

export const useOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRows", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useOrgSiteTableRowsQuery.getKey = (variables: Types.OrgSiteTableRowsQueryVariables) => [
  "OrgSiteTableRows",
  variables,
]

useOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useSuspenseOrgSiteTableRowsQuery = <
  TData = Types.OrgSiteTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgSiteTableRowsQuery, TError, TData>({
    queryKey: ["OrgSiteTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.OrgSiteTableRowsQuery,
      Types.OrgSiteTableRowsQueryVariables
    >(OrgSiteTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRowsSuspense", variables]

useSuspenseOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows"

export const useInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infinite", variables]

useInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

export const useSuspenseInfiniteOrgSiteTableRowsQuery = <
  TData = InfiniteData<Types.OrgSiteTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OrgSiteTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgSiteTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgSiteTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgSiteTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgSiteTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OrgSiteTableRowsQuery,
            Types.OrgSiteTableRowsQueryVariables
          >(OrgSiteTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgSiteTableRowsQuery.getKey = (
  variables: Types.OrgSiteTableRowsQueryVariables,
) => ["OrgSiteTableRows.infiniteSuspense", variables]

useSuspenseInfiniteOrgSiteTableRowsQuery.rootKey = "OrgSiteTableRows.infinite"

useOrgSiteTableRowsQuery.fetcher = (
  variables: Types.OrgSiteTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgSiteTableRowsQuery, Types.OrgSiteTableRowsQueryVariables>(
    OrgSiteTableRowsDocument,
    variables,
    options,
  )

export const RevampSiteDocument = `
    query RevampSite($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      ...RevampSiteBasics
      status
      statusDisplayText
      flags {
        type
      }
      feasibility {
        economics
        physical
      }
      labels {
        name
        pid
        themeColor
      }
      biddingConfigResult {
        id
      }
      selectedConfigResult {
        ...Result
      }
      selectedResults {
        ...Result
      }
      highlightedResults {
        ...Result
      }
      activeResults {
        ...Result
      }
      resultPerLocation {
        ...ResultRef
        helioscope {
          ...Helioscope
        }
      }
    }
  }
}
    ${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${UserRefFragmentDoc}
${FootprintFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultRefFragmentDoc}`

export const useRevampSiteQuery = <TData = Types.RevampSiteQuery, TError = unknown>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<UseQueryOptions<Types.RevampSiteQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSite", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite",
  variables,
]

useRevampSiteQuery.rootKey = "RevampSite"

export const useSuspenseRevampSiteQuery = <
  TData = Types.RevampSiteQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteQuery, TError, TData>({
    queryKey: ["RevampSiteSuspense", variables],
    queryFn: publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
      RevampSiteDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSiteSuspense",
  variables,
]

useSuspenseRevampSiteQuery.rootKey = "RevampSite"

export const useInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteQuery.getKey = (variables: Types.RevampSiteQueryVariables) => [
  "RevampSite.infinite",
  variables,
]

useInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

export const useSuspenseInfiniteRevampSiteQuery = <
  TData = InfiniteData<Types.RevampSiteQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSite.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
            RevampSiteDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteQuery.getKey = (
  variables: Types.RevampSiteQueryVariables,
) => ["RevampSite.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteQuery.rootKey = "RevampSite.infinite"

useRevampSiteQuery.fetcher = (
  variables: Types.RevampSiteQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.RevampSiteQuery, Types.RevampSiteQueryVariables>(
    RevampSiteDocument,
    variables,
    options,
  )

export const ResultDetailDocument = `
    query ResultDetail($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    site(pid: $sitePid) {
      pid
      name
      roofMaxPvKw
      footprint {
        ...Footprint
      }
      result(pid: $resultPid) {
        ...Result
      }
    }
  }
}
    ${FootprintFragmentDoc}
${UserRefFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useResultDetailQuery = <TData = Types.ResultDetailQuery, TError = unknown>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetail", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail",
  variables,
]

useResultDetailQuery.rootKey = "ResultDetail"

export const useSuspenseResultDetailQuery = <
  TData = Types.ResultDetailQuery,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultDetailQuery, TError, TData>({
    queryKey: ["ResultDetailSuspense", variables],
    queryFn: publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
      ResultDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetailSuspense",
  variables,
]

useSuspenseResultDetailQuery.rootKey = "ResultDetail"

export const useInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultDetailQuery.getKey = (variables: Types.ResultDetailQueryVariables) => [
  "ResultDetail.infinite",
  variables,
]

useInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

export const useSuspenseInfiniteResultDetailQuery = <
  TData = InfiniteData<Types.ResultDetailQuery>,
  TError = unknown,
>(
  variables: Types.ResultDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
            ResultDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultDetailQuery.getKey = (
  variables: Types.ResultDetailQueryVariables,
) => ["ResultDetail.infiniteSuspense", variables]

useSuspenseInfiniteResultDetailQuery.rootKey = "ResultDetail.infinite"

useResultDetailQuery.fetcher = (
  variables: Types.ResultDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultDetailQuery, Types.ResultDetailQueryVariables>(
    ResultDetailDocument,
    variables,
    options,
  )

export const ResultCashFlowDocument = `
    query ResultCashFlow($orgSlug: String!, $sitePid: String!, $resultPid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      result(pid: $resultPid) {
        result {
          __typename
          ... on RevampFinmodel {
            buildingOwnerXlsx
          }
        }
      }
    }
  }
}
    `

export const useResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ResultCashFlowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlow", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useResultCashFlowQuery.getKey = (variables: Types.ResultCashFlowQueryVariables) => [
  "ResultCashFlow",
  variables,
]

useResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useSuspenseResultCashFlowQuery = <
  TData = Types.ResultCashFlowQuery,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ResultCashFlowQuery, TError, TData>({
    queryKey: ["ResultCashFlowSuspense", variables],
    queryFn: publicFetcher<
      Types.ResultCashFlowQuery,
      Types.ResultCashFlowQueryVariables
    >(ResultCashFlowDocument, variables),
    ...options,
  })
}

useSuspenseResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlowSuspense", variables]

useSuspenseResultCashFlowQuery.rootKey = "ResultCashFlow"

export const useInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infinite", variables]

useInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

export const useSuspenseInfiniteResultCashFlowQuery = <
  TData = InfiniteData<Types.ResultCashFlowQuery>,
  TError = unknown,
>(
  variables: Types.ResultCashFlowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ResultCashFlowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ResultCashFlowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ResultCashFlowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ResultCashFlow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
            ResultCashFlowDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteResultCashFlowQuery.getKey = (
  variables: Types.ResultCashFlowQueryVariables,
) => ["ResultCashFlow.infiniteSuspense", variables]

useSuspenseInfiniteResultCashFlowQuery.rootKey = "ResultCashFlow.infinite"

useResultCashFlowQuery.fetcher = (
  variables: Types.ResultCashFlowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ResultCashFlowQuery, Types.ResultCashFlowQueryVariables>(
    ResultCashFlowDocument,
    variables,
    options,
  )

export const RevampSiteDocumentsDocument = `
    query RevampSiteDocuments($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    site(pid: $sitePid) {
      pid
      documents {
        canDelete
        contentType
        dateCreated
        fileName
        ownerName
        pid
        tag
        downloadHref
      }
    }
  }
}
    `

export const useRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocuments", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments", variables]

useRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useSuspenseRevampSiteDocumentsQuery = <
  TData = Types.RevampSiteDocumentsQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteDocumentsQuery, TError, TData>({
    queryKey: ["RevampSiteDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteDocumentsQuery,
      Types.RevampSiteDocumentsQueryVariables
    >(RevampSiteDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocumentsSuspense", variables]

useSuspenseRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments"

export const useInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infinite", variables]

useInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

export const useSuspenseInfiniteRevampSiteDocumentsQuery = <
  TData = InfiniteData<Types.RevampSiteDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteDocumentsQuery,
            Types.RevampSiteDocumentsQueryVariables
          >(RevampSiteDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteDocumentsQuery.getKey = (
  variables: Types.RevampSiteDocumentsQueryVariables,
) => ["RevampSiteDocuments.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteDocumentsQuery.rootKey = "RevampSiteDocuments.infinite"

useRevampSiteDocumentsQuery.fetcher = (
  variables: Types.RevampSiteDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteDocumentsQuery,
    Types.RevampSiteDocumentsQueryVariables
  >(RevampSiteDocumentsDocument, variables, options)

export const SiteProjectDocumentsDocument = `
    query SiteProjectDocuments($sitePid: String!, $orgSlug: String!) {
  projects {
    siteProjects(sitePid: $sitePid) {
      pid
      name
      finproductType
      projectSpecification {
        ppa {
          system {
            totalPvSizeKw
          }
        }
        cs {
          systemLite {
            totalPvSizeKw
          }
        }
      }
      documents(orgSlug: $orgSlug) {
        canDelete
        contentType
        dateCreated
        fileName
        ownerName
        pid
        tag
        downloadHref
      }
    }
  }
}
    `

export const useSiteProjectDocumentsQuery = <
  TData = Types.SiteProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteProjectDocumentsQuery, TError, TData>({
    queryKey: ["SiteProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.SiteProjectDocumentsQuery,
      Types.SiteProjectDocumentsQueryVariables
    >(SiteProjectDocumentsDocument, variables),
    ...options,
  })
}

useSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments", variables]

useSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments"

export const useSuspenseSiteProjectDocumentsQuery = <
  TData = Types.SiteProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteProjectDocumentsQuery, TError, TData>({
    queryKey: ["SiteProjectDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteProjectDocumentsQuery,
      Types.SiteProjectDocumentsQueryVariables
    >(SiteProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocumentsSuspense", variables]

useSuspenseSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments"

export const useInfiniteSiteProjectDocumentsQuery = <
  TData = InfiniteData<Types.SiteProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteProjectDocumentsQuery,
            Types.SiteProjectDocumentsQueryVariables
          >(SiteProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments.infinite", variables]

useInfiniteSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments.infinite"

export const useSuspenseInfiniteSiteProjectDocumentsQuery = <
  TData = InfiniteData<Types.SiteProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteProjectDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteProjectDocumentsQuery,
            Types.SiteProjectDocumentsQueryVariables
          >(SiteProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteProjectDocumentsQuery.getKey = (
  variables: Types.SiteProjectDocumentsQueryVariables,
) => ["SiteProjectDocuments.infiniteSuspense", variables]

useSuspenseInfiniteSiteProjectDocumentsQuery.rootKey = "SiteProjectDocuments.infinite"

useSiteProjectDocumentsQuery.fetcher = (
  variables: Types.SiteProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteProjectDocumentsQuery,
    Types.SiteProjectDocumentsQueryVariables
  >(SiteProjectDocumentsDocument, variables, options)

export const RevampSiteBelowTheFoldDocument = `
    query RevampSiteBelowTheFold($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      recommendedResult {
        ...Result
        ...ResultWithEnergyData
      }
      policies {
        ...PolicyBrief
      }
    }
  }
}
    ${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}
${ResultWithEnergyDataFragmentDoc}
${PolicyBriefFragmentDoc}`

export const useRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFold", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold", variables]

useRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useSuspenseRevampSiteBelowTheFoldQuery = <
  TData = Types.RevampSiteBelowTheFoldQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>({
    queryKey: ["RevampSiteBelowTheFoldSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBelowTheFoldQuery,
      Types.RevampSiteBelowTheFoldQueryVariables
    >(RevampSiteBelowTheFoldDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFoldSuspense", variables]

useSuspenseRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold"

export const useInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBelowTheFold.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infinite", variables]

useInfiniteRevampSiteBelowTheFoldQuery.rootKey = "RevampSiteBelowTheFold.infinite"

export const useSuspenseInfiniteRevampSiteBelowTheFoldQuery = <
  TData = InfiniteData<Types.RevampSiteBelowTheFoldQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBelowTheFoldQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBelowTheFoldQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBelowTheFoldQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteBelowTheFold.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBelowTheFoldQuery,
            Types.RevampSiteBelowTheFoldQueryVariables
          >(RevampSiteBelowTheFoldDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.getKey = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
) => ["RevampSiteBelowTheFold.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteBelowTheFoldQuery.rootKey =
  "RevampSiteBelowTheFold.infinite"

useRevampSiteBelowTheFoldQuery.fetcher = (
  variables: Types.RevampSiteBelowTheFoldQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBelowTheFoldQuery,
    Types.RevampSiteBelowTheFoldQueryVariables
  >(RevampSiteBelowTheFoldDocument, variables, options)

export const RevampSiteBillingMetersDocument = `
    query RevampSiteBillingMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeter
      }
    }
  }
}
    ${BillingMeterFragmentDoc}
${FancyFloatFragmentDoc}
${BillingMonthViewFragmentDoc}
${ProviderFragmentDoc}
${UserRefFragmentDoc}
${BillingMeterProjectedFragmentDoc}
${BillGroupFragmentDoc}
${GroupedLineItemFragmentDoc}
${BillingIntervalViewFragmentDoc}`

export const useRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMeters", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters", variables]

useRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useSuspenseRevampSiteBillingMetersQuery = <
  TData = Types.RevampSiteBillingMetersQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteBillingMetersQuery, TError, TData>({
    queryKey: ["RevampSiteBillingMetersSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteBillingMetersQuery,
      Types.RevampSiteBillingMetersQueryVariables
    >(RevampSiteBillingMetersDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMetersSuspense", variables]

useSuspenseRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters"

export const useInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteBillingMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infinite", variables]

useInfiniteRevampSiteBillingMetersQuery.rootKey = "RevampSiteBillingMeters.infinite"

export const useSuspenseInfiniteRevampSiteBillingMetersQuery = <
  TData = InfiniteData<Types.RevampSiteBillingMetersQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteBillingMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteBillingMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteBillingMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteBillingMeters.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteBillingMetersQuery,
            Types.RevampSiteBillingMetersQueryVariables
          >(RevampSiteBillingMetersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteBillingMetersQuery.getKey = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
) => ["RevampSiteBillingMeters.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteBillingMetersQuery.rootKey =
  "RevampSiteBillingMeters.infinite"

useRevampSiteBillingMetersQuery.fetcher = (
  variables: Types.RevampSiteBillingMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteBillingMetersQuery,
    Types.RevampSiteBillingMetersQueryVariables
  >(RevampSiteBillingMetersDocument, variables, options)

export const RevampSiteGridEscalatorDocument = `
    query RevampSiteGridEscalator($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      gridEscalator
    }
  }
}
    `

export const useRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalator", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator", variables]

useRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useSuspenseRevampSiteGridEscalatorQuery = <
  TData = Types.RevampSiteGridEscalatorQuery,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>({
    queryKey: ["RevampSiteGridEscalatorSuspense", variables],
    queryFn: publicFetcher<
      Types.RevampSiteGridEscalatorQuery,
      Types.RevampSiteGridEscalatorQueryVariables
    >(RevampSiteGridEscalatorDocument, variables),
    ...options,
  })
}

useSuspenseRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalatorSuspense", variables]

useSuspenseRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator"

export const useInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["RevampSiteGridEscalator.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infinite", variables]

useInfiniteRevampSiteGridEscalatorQuery.rootKey = "RevampSiteGridEscalator.infinite"

export const useSuspenseInfiniteRevampSiteGridEscalatorQuery = <
  TData = InfiniteData<Types.RevampSiteGridEscalatorQuery>,
  TError = unknown,
>(
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.RevampSiteGridEscalatorQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.RevampSiteGridEscalatorQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.RevampSiteGridEscalatorQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "RevampSiteGridEscalator.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.RevampSiteGridEscalatorQuery,
            Types.RevampSiteGridEscalatorQueryVariables
          >(RevampSiteGridEscalatorDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteRevampSiteGridEscalatorQuery.getKey = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
) => ["RevampSiteGridEscalator.infiniteSuspense", variables]

useSuspenseInfiniteRevampSiteGridEscalatorQuery.rootKey =
  "RevampSiteGridEscalator.infinite"

useRevampSiteGridEscalatorQuery.fetcher = (
  variables: Types.RevampSiteGridEscalatorQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.RevampSiteGridEscalatorQuery,
    Types.RevampSiteGridEscalatorQueryVariables
  >(RevampSiteGridEscalatorDocument, variables, options)

export const SiteMetersDocument = `
    query SiteMeters($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      billingMeters {
        ...BillingMeterOption
      }
    }
  }
}
    ${BillingMeterOptionFragmentDoc}`

export const useSiteMetersQuery = <TData = Types.SiteMetersQuery, TError = unknown>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteMetersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMeters", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters",
  variables,
]

useSiteMetersQuery.rootKey = "SiteMeters"

export const useSuspenseSiteMetersQuery = <
  TData = Types.SiteMetersQuery,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteMetersQuery, TError, TData>({
    queryKey: ["SiteMetersSuspense", variables],
    queryFn: publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
      SiteMetersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMetersSuspense",
  variables,
]

useSuspenseSiteMetersQuery.rootKey = "SiteMeters"

export const useInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteMetersQuery.getKey = (variables: Types.SiteMetersQueryVariables) => [
  "SiteMeters.infinite",
  variables,
]

useInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

export const useSuspenseInfiniteSiteMetersQuery = <
  TData = InfiniteData<Types.SiteMetersQuery>,
  TError = unknown,
>(
  variables: Types.SiteMetersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteMetersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteMetersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteMetersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteMeters.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
            SiteMetersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteMetersQuery.getKey = (
  variables: Types.SiteMetersQueryVariables,
) => ["SiteMeters.infiniteSuspense", variables]

useSuspenseInfiniteSiteMetersQuery.rootKey = "SiteMeters.infinite"

useSiteMetersQuery.fetcher = (
  variables: Types.SiteMetersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteMetersQuery, Types.SiteMetersQueryVariables>(
    SiteMetersDocument,
    variables,
    options,
  )

export const BillingMeterDocumentParseRequestsDocument = `
    query BillingMeterDocumentParseRequests($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    site(pid: $sitePid) {
      documentParseRequests {
        ...DocumentParseRequest
      }
    }
  }
}
    ${DocumentParseRequestFragmentDoc}`

export const useBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequests", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests", variables]

useBillingMeterDocumentParseRequestsQuery.rootKey = "BillingMeterDocumentParseRequests"

export const useSuspenseBillingMeterDocumentParseRequestsQuery = <
  TData = Types.BillingMeterDocumentParseRequestsQuery,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>({
    queryKey: ["BillingMeterDocumentParseRequestsSuspense", variables],
    queryFn: publicFetcher<
      Types.BillingMeterDocumentParseRequestsQuery,
      Types.BillingMeterDocumentParseRequestsQueryVariables
    >(BillingMeterDocumentParseRequestsDocument, variables),
    ...options,
  })
}

useSuspenseBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequestsSuspense", variables]

useSuspenseBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests"

export const useInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BillingMeterDocumentParseRequestsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infinite", variables]

useInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

export const useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery = <
  TData = InfiniteData<Types.BillingMeterDocumentParseRequestsQuery>,
  TError = unknown,
>(
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BillingMeterDocumentParseRequestsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.BillingMeterDocumentParseRequestsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BillingMeterDocumentParseRequests.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BillingMeterDocumentParseRequestsQuery,
            Types.BillingMeterDocumentParseRequestsQueryVariables
          >(BillingMeterDocumentParseRequestsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.getKey = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
) => ["BillingMeterDocumentParseRequests.infiniteSuspense", variables]

useSuspenseInfiniteBillingMeterDocumentParseRequestsQuery.rootKey =
  "BillingMeterDocumentParseRequests.infinite"

useBillingMeterDocumentParseRequestsQuery.fetcher = (
  variables: Types.BillingMeterDocumentParseRequestsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BillingMeterDocumentParseRequestsQuery,
    Types.BillingMeterDocumentParseRequestsQueryVariables
  >(BillingMeterDocumentParseRequestsDocument, variables, options)

export const OrgDetailDocument = `
    query OrgDetail($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    pid
    slug
    name
    logomarkUrl
    logoUrl
  }
}
    `

export const useOrgDetailQuery = <TData = Types.OrgDetailQuery, TError = unknown>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgDetailQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetail", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail",
  variables,
]

useOrgDetailQuery.rootKey = "OrgDetail"

export const useSuspenseOrgDetailQuery = <
  TData = Types.OrgDetailQuery,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgDetailQuery, TError, TData>({
    queryKey: ["OrgDetailSuspense", variables],
    queryFn: publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
      OrgDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetailSuspense",
  variables,
]

useSuspenseOrgDetailQuery.rootKey = "OrgDetail"

export const useInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgDetailQuery.getKey = (variables: Types.OrgDetailQueryVariables) => [
  "OrgDetail.infinite",
  variables,
]

useInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

export const useSuspenseInfiniteOrgDetailQuery = <
  TData = InfiniteData<Types.OrgDetailQuery>,
  TError = unknown,
>(
  variables: Types.OrgDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
            OrgDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgDetailQuery.getKey = (
  variables: Types.OrgDetailQueryVariables,
) => ["OrgDetail.infiniteSuspense", variables]

useSuspenseInfiniteOrgDetailQuery.rootKey = "OrgDetail.infinite"

useOrgDetailQuery.fetcher = (
  variables: Types.OrgDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgDetailQuery, Types.OrgDetailQueryVariables>(
    OrgDetailDocument,
    variables,
    options,
  )

export const MultiMeterDetailDocument = `
    query MultiMeterDetail($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    site(pid: $sitePid) {
      ...SiteMeterSet
    }
  }
}
    ${SiteMeterSetFragmentDoc}
${SiteBauMeterFragmentDoc}
${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetail", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useMultiMeterDetailQuery.getKey = (variables: Types.MultiMeterDetailQueryVariables) => [
  "MultiMeterDetail",
  variables,
]

useMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useSuspenseMultiMeterDetailQuery = <
  TData = Types.MultiMeterDetailQuery,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MultiMeterDetailQuery, TError, TData>({
    queryKey: ["MultiMeterDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MultiMeterDetailQuery,
      Types.MultiMeterDetailQueryVariables
    >(MultiMeterDetailDocument, variables),
    ...options,
  })
}

useSuspenseMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetailSuspense", variables]

useSuspenseMultiMeterDetailQuery.rootKey = "MultiMeterDetail"

export const useInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infinite", variables]

useInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

export const useSuspenseInfiniteMultiMeterDetailQuery = <
  TData = InfiniteData<Types.MultiMeterDetailQuery>,
  TError = unknown,
>(
  variables: Types.MultiMeterDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MultiMeterDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MultiMeterDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MultiMeterDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MultiMeterDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MultiMeterDetailQuery,
            Types.MultiMeterDetailQueryVariables
          >(MultiMeterDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMultiMeterDetailQuery.getKey = (
  variables: Types.MultiMeterDetailQueryVariables,
) => ["MultiMeterDetail.infiniteSuspense", variables]

useSuspenseInfiniteMultiMeterDetailQuery.rootKey = "MultiMeterDetail.infinite"

useMultiMeterDetailQuery.fetcher = (
  variables: Types.MultiMeterDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.MultiMeterDetailQuery, Types.MultiMeterDetailQueryVariables>(
    MultiMeterDetailDocument,
    variables,
    options,
  )

export const OrgBundlesDocument = `
    query OrgBundles($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    pid
    name
    logoUrl
    bundles {
      ...BundleListItem
      rows {
        ...BundleRowDetail
      }
    }
  }
}
    ${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${CashInvestorBidFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${CashBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useOrgBundlesQuery = <TData = Types.OrgBundlesQuery, TError = unknown>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgBundlesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundles", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles",
  variables,
]

useOrgBundlesQuery.rootKey = "OrgBundles"

export const useSuspenseOrgBundlesQuery = <
  TData = Types.OrgBundlesQuery,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgBundlesQuery, TError, TData>({
    queryKey: ["OrgBundlesSuspense", variables],
    queryFn: publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
      OrgBundlesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundlesSuspense",
  variables,
]

useSuspenseOrgBundlesQuery.rootKey = "OrgBundles"

export const useInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgBundlesQuery.getKey = (variables: Types.OrgBundlesQueryVariables) => [
  "OrgBundles.infinite",
  variables,
]

useInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

export const useSuspenseInfiniteOrgBundlesQuery = <
  TData = InfiniteData<Types.OrgBundlesQuery>,
  TError = unknown,
>(
  variables: Types.OrgBundlesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgBundlesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgBundlesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgBundlesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgBundles.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
            OrgBundlesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgBundlesQuery.getKey = (
  variables: Types.OrgBundlesQueryVariables,
) => ["OrgBundles.infiniteSuspense", variables]

useSuspenseInfiniteOrgBundlesQuery.rootKey = "OrgBundles.infinite"

useOrgBundlesQuery.fetcher = (
  variables: Types.OrgBundlesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgBundlesQuery, Types.OrgBundlesQueryVariables>(
    OrgBundlesDocument,
    variables,
    options,
  )

export const SiteBidsDocument = `
    query SiteBids($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      bidPackages {
        ...BasicBidPackage
        row(pid: $sitePid) {
          status
          closedAt
          closeReason
          closeCustomReason
        }
      }
      selectedConfigResult {
        ...Result
      }
      biddingConfigResult {
        ...Result
      }
    }
  }
}
    ${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}
${ResultFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${RevampCsFragmentDoc}
${HelioscopeFragmentDoc}`

export const useSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteBidsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBids", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids",
  variables,
]

useSiteBidsQuery.rootKey = "SiteBids"

export const useSuspenseSiteBidsQuery = <TData = Types.SiteBidsQuery, TError = unknown>(
  variables: Types.SiteBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsQuery, TError, TData>({
    queryKey: ["SiteBidsSuspense", variables],
    queryFn: publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
      SiteBidsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBidsSuspense",
  variables,
]

useSuspenseSiteBidsQuery.rootKey = "SiteBids"

export const useInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infinite",
  variables,
]

useInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

export const useSuspenseInfiniteSiteBidsQuery = <
  TData = InfiniteData<Types.SiteBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBids.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
            SiteBidsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsQuery.getKey = (variables: Types.SiteBidsQueryVariables) => [
  "SiteBids.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteBidsQuery.rootKey = "SiteBids.infinite"

useSiteBidsQuery.fetcher = (
  variables: Types.SiteBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsQuery, Types.SiteBidsQueryVariables>(
    SiteBidsDocument,
    variables,
    options,
  )

export const FinancierBundleRowDocument = `
    query FinancierBundleRow($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowForFinancier(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    sitePid: $sitePid
  ) {
    ...BundleRow
    helioscopes {
      ...Helioscope
    }
  }
  isAwardee(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid)
  awardedBidFinmodel(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid) {
    ...RevampFinmodel
  }
}
    ${BundleRowFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${UserRefFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}
${HelioscopeFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useFinancierBundleRowQuery = <
  TData = Types.FinancierBundleRowQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleRowQuery, TError, TData>({
    queryKey: ["FinancierBundleRow", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowQuery,
      Types.FinancierBundleRowQueryVariables
    >(FinancierBundleRowDocument, variables),
    ...options,
  })
}

useFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow", variables]

useFinancierBundleRowQuery.rootKey = "FinancierBundleRow"

export const useSuspenseFinancierBundleRowQuery = <
  TData = Types.FinancierBundleRowQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleRowQuery, TError, TData>({
    queryKey: ["FinancierBundleRowSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowQuery,
      Types.FinancierBundleRowQueryVariables
    >(FinancierBundleRowDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRowSuspense", variables]

useSuspenseFinancierBundleRowQuery.rootKey = "FinancierBundleRow"

export const useInfiniteFinancierBundleRowQuery = <
  TData = InfiniteData<Types.FinancierBundleRowQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierBundleRow.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowQuery,
            Types.FinancierBundleRowQueryVariables
          >(FinancierBundleRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow.infinite", variables]

useInfiniteFinancierBundleRowQuery.rootKey = "FinancierBundleRow.infinite"

export const useSuspenseInfiniteFinancierBundleRowQuery = <
  TData = InfiniteData<Types.FinancierBundleRowQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FinancierBundleRowQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FinancierBundleRowQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["FinancierBundleRow.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowQuery,
            Types.FinancierBundleRowQueryVariables
          >(FinancierBundleRowDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleRowQuery.getKey = (
  variables: Types.FinancierBundleRowQueryVariables,
) => ["FinancierBundleRow.infiniteSuspense", variables]

useSuspenseInfiniteFinancierBundleRowQuery.rootKey = "FinancierBundleRow.infinite"

useFinancierBundleRowQuery.fetcher = (
  variables: Types.FinancierBundleRowQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.FinancierBundleRowQuery, Types.FinancierBundleRowQueryVariables>(
    FinancierBundleRowDocument,
    variables,
    options,
  )

export const FinancierBundleRowDocumentsDocument = `
    query FinancierBundleRowDocuments($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowForFinancier(
    orgSlug: $orgSlug
    bundlePid: $bundlePid
    sitePid: $sitePid
  ) {
    site {
      pid
      documents {
        ...Document
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useFinancierBundleRowDocumentsQuery = <
  TData = Types.FinancierBundleRowDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleRowDocuments", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowDocumentsQuery,
      Types.FinancierBundleRowDocumentsQueryVariables
    >(FinancierBundleRowDocumentsDocument, variables),
    ...options,
  })
}

useFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments", variables]

useFinancierBundleRowDocumentsQuery.rootKey = "FinancierBundleRowDocuments"

export const useSuspenseFinancierBundleRowDocumentsQuery = <
  TData = Types.FinancierBundleRowDocumentsQuery,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>({
    queryKey: ["FinancierBundleRowDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.FinancierBundleRowDocumentsQuery,
      Types.FinancierBundleRowDocumentsQueryVariables
    >(FinancierBundleRowDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocumentsSuspense", variables]

useSuspenseFinancierBundleRowDocumentsQuery.rootKey = "FinancierBundleRowDocuments"

export const useInfiniteFinancierBundleRowDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleRowDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FinancierBundleRowDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FinancierBundleRowDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleRowDocuments.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowDocumentsQuery,
            Types.FinancierBundleRowDocumentsQueryVariables
          >(FinancierBundleRowDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments.infinite", variables]

useInfiniteFinancierBundleRowDocumentsQuery.rootKey =
  "FinancierBundleRowDocuments.infinite"

export const useSuspenseInfiniteFinancierBundleRowDocumentsQuery = <
  TData = InfiniteData<Types.FinancierBundleRowDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FinancierBundleRowDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.FinancierBundleRowDocumentsQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "FinancierBundleRowDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FinancierBundleRowDocumentsQuery,
            Types.FinancierBundleRowDocumentsQueryVariables
          >(FinancierBundleRowDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFinancierBundleRowDocumentsQuery.getKey = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
) => ["FinancierBundleRowDocuments.infiniteSuspense", variables]

useSuspenseInfiniteFinancierBundleRowDocumentsQuery.rootKey =
  "FinancierBundleRowDocuments.infinite"

useFinancierBundleRowDocumentsQuery.fetcher = (
  variables: Types.FinancierBundleRowDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FinancierBundleRowDocumentsQuery,
    Types.FinancierBundleRowDocumentsQueryVariables
  >(FinancierBundleRowDocumentsDocument, variables, options)

export const ProjectDetailDocument = `
    query ProjectDetail($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      finproductType
      site {
        ...RevampSiteBasics
        billingMeters {
          ...BillingMeterOption
        }
      }
      helioscopes {
        ...Helioscope2
      }
      awardedBidFinmodel {
        ...RevampFinmodel
      }
    }
  }
}
    ${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${UserRefFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}
${Helioscope2FragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}`

export const useProjectDetailQuery = <
  TData = Types.ProjectDetailQuery,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDetailQuery, TError, TData>({
    queryKey: ["ProjectDetail", variables],
    queryFn: publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
      ProjectDetailDocument,
      variables,
    ),
    ...options,
  })
}

useProjectDetailQuery.getKey = (variables: Types.ProjectDetailQueryVariables) => [
  "ProjectDetail",
  variables,
]

useProjectDetailQuery.rootKey = "ProjectDetail"

export const useSuspenseProjectDetailQuery = <
  TData = Types.ProjectDetailQuery,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDetailQuery, TError, TData>({
    queryKey: ["ProjectDetailSuspense", variables],
    queryFn: publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
      ProjectDetailDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetailSuspense", variables]

useSuspenseProjectDetailQuery.rootKey = "ProjectDetail"

export const useInfiniteProjectDetailQuery = <
  TData = InfiniteData<Types.ProjectDetailQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
            ProjectDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetail.infinite", variables]

useInfiniteProjectDetailQuery.rootKey = "ProjectDetail.infinite"

export const useSuspenseInfiniteProjectDetailQuery = <
  TData = InfiniteData<Types.ProjectDetailQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
            ProjectDetailDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDetailQuery.getKey = (
  variables: Types.ProjectDetailQueryVariables,
) => ["ProjectDetail.infiniteSuspense", variables]

useSuspenseInfiniteProjectDetailQuery.rootKey = "ProjectDetail.infinite"

useProjectDetailQuery.fetcher = (
  variables: Types.ProjectDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectDetailQuery, Types.ProjectDetailQueryVariables>(
    ProjectDetailDocument,
    variables,
    options,
  )

export const ProjectAllDocumentsDocument = `
    query ProjectAllDocuments($orgSlug: String!, $projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      site {
        pid
        documents {
          ...Document
        }
      }
      documents(orgSlug: $orgSlug) {
        ...Document
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useProjectAllDocumentsQuery = <
  TData = Types.ProjectAllDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectAllDocumentsQuery, TError, TData>({
    queryKey: ["ProjectAllDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectAllDocumentsQuery,
      Types.ProjectAllDocumentsQueryVariables
    >(ProjectAllDocumentsDocument, variables),
    ...options,
  })
}

useProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments", variables]

useProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments"

export const useSuspenseProjectAllDocumentsQuery = <
  TData = Types.ProjectAllDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectAllDocumentsQuery, TError, TData>({
    queryKey: ["ProjectAllDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectAllDocumentsQuery,
      Types.ProjectAllDocumentsQueryVariables
    >(ProjectAllDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocumentsSuspense", variables]

useSuspenseProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments"

export const useInfiniteProjectAllDocumentsQuery = <
  TData = InfiniteData<Types.ProjectAllDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectAllDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectAllDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectAllDocumentsQuery,
            Types.ProjectAllDocumentsQueryVariables
          >(ProjectAllDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments.infinite", variables]

useInfiniteProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments.infinite"

export const useSuspenseInfiniteProjectAllDocumentsQuery = <
  TData = InfiniteData<Types.ProjectAllDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectAllDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectAllDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectAllDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectAllDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectAllDocuments.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectAllDocumentsQuery,
            Types.ProjectAllDocumentsQueryVariables
          >(ProjectAllDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectAllDocumentsQuery.getKey = (
  variables: Types.ProjectAllDocumentsQueryVariables,
) => ["ProjectAllDocuments.infiniteSuspense", variables]

useSuspenseInfiniteProjectAllDocumentsQuery.rootKey = "ProjectAllDocuments.infinite"

useProjectAllDocumentsQuery.fetcher = (
  variables: Types.ProjectAllDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectAllDocumentsQuery,
    Types.ProjectAllDocumentsQueryVariables
  >(ProjectAllDocumentsDocument, variables, options)

export const ProjectDocumentsBatchDocument = `
    query ProjectDocumentsBatch($orgSlug: String!, $projectPids: [String!]!, $tags: [DocumentTag!]) {
  projects {
    documentsBatch(projectPids: $projectPids, orgSlug: $orgSlug, tags: $tags) {
      projectPid
      documents {
        ...Document
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useProjectDocumentsBatchQuery = <
  TData = Types.ProjectDocumentsBatchQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDocumentsBatchQuery, TError, TData>({
    queryKey: ["ProjectDocumentsBatch", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsBatchQuery,
      Types.ProjectDocumentsBatchQueryVariables
    >(ProjectDocumentsBatchDocument, variables),
    ...options,
  })
}

useProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch", variables]

useProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch"

export const useSuspenseProjectDocumentsBatchQuery = <
  TData = Types.ProjectDocumentsBatchQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDocumentsBatchQuery, TError, TData>({
    queryKey: ["ProjectDocumentsBatchSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsBatchQuery,
      Types.ProjectDocumentsBatchQueryVariables
    >(ProjectDocumentsBatchDocument, variables),
    ...options,
  })
}

useSuspenseProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatchSuspense", variables]

useSuspenseProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch"

export const useInfiniteProjectDocumentsBatchQuery = <
  TData = InfiniteData<Types.ProjectDocumentsBatchQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDocumentsBatchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocumentsBatch.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsBatchQuery,
            Types.ProjectDocumentsBatchQueryVariables
          >(ProjectDocumentsBatchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch.infinite", variables]

useInfiniteProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch.infinite"

export const useSuspenseInfiniteProjectDocumentsBatchQuery = <
  TData = InfiniteData<Types.ProjectDocumentsBatchQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDocumentsBatchQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDocumentsBatchQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDocumentsBatchQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectDocumentsBatch.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsBatchQuery,
            Types.ProjectDocumentsBatchQueryVariables
          >(ProjectDocumentsBatchDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDocumentsBatchQuery.getKey = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
) => ["ProjectDocumentsBatch.infiniteSuspense", variables]

useSuspenseInfiniteProjectDocumentsBatchQuery.rootKey = "ProjectDocumentsBatch.infinite"

useProjectDocumentsBatchQuery.fetcher = (
  variables: Types.ProjectDocumentsBatchQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectDocumentsBatchQuery,
    Types.ProjectDocumentsBatchQueryVariables
  >(ProjectDocumentsBatchDocument, variables, options)

export const ProjectMultiMeterDocument = `
    query ProjectMultiMeter($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      site {
        ...SiteMeterSet
      }
    }
  }
}
    ${SiteMeterSetFragmentDoc}
${SiteBauMeterFragmentDoc}
${FormattedBillFragmentDoc}
${BillSectionFragmentDoc}
${DecodedRateFragmentDoc}
${CostOrTaxItemFragmentDoc}`

export const useProjectMultiMeterQuery = <
  TData = Types.ProjectMultiMeterQuery,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectMultiMeterQuery, TError, TData>({
    queryKey: ["ProjectMultiMeter", variables],
    queryFn: publicFetcher<
      Types.ProjectMultiMeterQuery,
      Types.ProjectMultiMeterQueryVariables
    >(ProjectMultiMeterDocument, variables),
    ...options,
  })
}

useProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter", variables]

useProjectMultiMeterQuery.rootKey = "ProjectMultiMeter"

export const useSuspenseProjectMultiMeterQuery = <
  TData = Types.ProjectMultiMeterQuery,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectMultiMeterQuery, TError, TData>({
    queryKey: ["ProjectMultiMeterSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectMultiMeterQuery,
      Types.ProjectMultiMeterQueryVariables
    >(ProjectMultiMeterDocument, variables),
    ...options,
  })
}

useSuspenseProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeterSuspense", variables]

useSuspenseProjectMultiMeterQuery.rootKey = "ProjectMultiMeter"

export const useInfiniteProjectMultiMeterQuery = <
  TData = InfiniteData<Types.ProjectMultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectMultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectMultiMeter.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMultiMeterQuery,
            Types.ProjectMultiMeterQueryVariables
          >(ProjectMultiMeterDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter.infinite", variables]

useInfiniteProjectMultiMeterQuery.rootKey = "ProjectMultiMeter.infinite"

export const useSuspenseInfiniteProjectMultiMeterQuery = <
  TData = InfiniteData<Types.ProjectMultiMeterQuery>,
  TError = unknown,
>(
  variables: Types.ProjectMultiMeterQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectMultiMeterQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectMultiMeterQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectMultiMeterQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectMultiMeter.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectMultiMeterQuery,
            Types.ProjectMultiMeterQueryVariables
          >(ProjectMultiMeterDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectMultiMeterQuery.getKey = (
  variables: Types.ProjectMultiMeterQueryVariables,
) => ["ProjectMultiMeter.infiniteSuspense", variables]

useSuspenseInfiniteProjectMultiMeterQuery.rootKey = "ProjectMultiMeter.infinite"

useProjectMultiMeterQuery.fetcher = (
  variables: Types.ProjectMultiMeterQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectMultiMeterQuery, Types.ProjectMultiMeterQueryVariables>(
    ProjectMultiMeterDocument,
    variables,
    options,
  )

export const SidebarDataDocument = `
    query SidebarData($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    sites {
      view {
        count
      }
    }
    shine {
      stats {
        numProperties
      }
    }
    isDeveloper
  }
}
    `

export const useSidebarDataQuery = <TData = Types.SidebarDataQuery, TError = unknown>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<UseQueryOptions<Types.SidebarDataQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SidebarDataQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarData", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData",
  variables,
]

useSidebarDataQuery.rootKey = "SidebarData"

export const useSuspenseSidebarDataQuery = <
  TData = Types.SidebarDataQuery,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SidebarDataQuery, TError, TData>({
    queryKey: ["SidebarDataSuspense", variables],
    queryFn: publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
      SidebarDataDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarDataSuspense",
  variables,
]

useSuspenseSidebarDataQuery.rootKey = "SidebarData"

export const useInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSidebarDataQuery.getKey = (variables: Types.SidebarDataQueryVariables) => [
  "SidebarData.infinite",
  variables,
]

useInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

export const useSuspenseInfiniteSidebarDataQuery = <
  TData = InfiniteData<Types.SidebarDataQuery>,
  TError = unknown,
>(
  variables: Types.SidebarDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SidebarDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SidebarDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SidebarDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SidebarData.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
            SidebarDataDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSidebarDataQuery.getKey = (
  variables: Types.SidebarDataQueryVariables,
) => ["SidebarData.infiniteSuspense", variables]

useSuspenseInfiniteSidebarDataQuery.rootKey = "SidebarData.infinite"

useSidebarDataQuery.fetcher = (
  variables: Types.SidebarDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SidebarDataQuery, Types.SidebarDataQueryVariables>(
    SidebarDataDocument,
    variables,
    options,
  )

export const ThirdPartySuppliersDocument = `
    query ThirdPartySuppliers {
  thirdPartySuppliers {
    id
    pid
    name
  }
}
    `

export const useThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliers"]
        : ["ThirdPartySuppliers", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined ? ["ThirdPartySuppliers"] : ["ThirdPartySuppliers", variables]

useThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useSuspenseThirdPartySuppliersQuery = <
  TData = Types.ThirdPartySuppliersQuery,
  TError = unknown,
>(
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ThirdPartySuppliersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["ThirdPartySuppliersSuspense"]
        : ["ThirdPartySuppliersSuspense", variables],
    queryFn: publicFetcher<
      Types.ThirdPartySuppliersQuery,
      Types.ThirdPartySuppliersQueryVariables
    >(ThirdPartySuppliersDocument, variables),
    ...options,
  })
}

useSuspenseThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliersSuspense"]
    : ["ThirdPartySuppliersSuspense", variables]

useSuspenseThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers"

export const useInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infinite"]
            : ["ThirdPartySuppliers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infinite"]
    : ["ThirdPartySuppliers.infinite", variables]

useInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

export const useSuspenseInfiniteThirdPartySuppliersQuery = <
  TData = InfiniteData<Types.ThirdPartySuppliersQuery>,
  TError = unknown,
>(
  variables: Types.ThirdPartySuppliersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ThirdPartySuppliersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ThirdPartySuppliersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ThirdPartySuppliersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["ThirdPartySuppliers.infiniteSuspense"]
            : ["ThirdPartySuppliers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ThirdPartySuppliersQuery,
            Types.ThirdPartySuppliersQueryVariables
          >(ThirdPartySuppliersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteThirdPartySuppliersQuery.getKey = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
) =>
  variables === undefined
    ? ["ThirdPartySuppliers.infiniteSuspense"]
    : ["ThirdPartySuppliers.infiniteSuspense", variables]

useSuspenseInfiniteThirdPartySuppliersQuery.rootKey = "ThirdPartySuppliers.infinite"

useThirdPartySuppliersQuery.fetcher = (
  variables?: Types.ThirdPartySuppliersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ThirdPartySuppliersQuery,
    Types.ThirdPartySuppliersQueryVariables
  >(ThirdPartySuppliersDocument, variables, options)

export const ShineSiteDetailDocument = `
    query ShineSiteDetail($orgSlug: String!, $propertyPid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $propertyPid) {
      pid
      name
      fullAddress
      footprint {
        ...Footprint
      }
      shine {
        status
        stats {
          ...ShineStats
        }
      }
    }
  }
}
    ${FootprintFragmentDoc}
${UserRefFragmentDoc}
${ShineStatsFragmentDoc}`

export const useShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetail", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useShineSiteDetailQuery.getKey = (variables: Types.ShineSiteDetailQueryVariables) => [
  "ShineSiteDetail",
  variables,
]

useShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useSuspenseShineSiteDetailQuery = <
  TData = Types.ShineSiteDetailQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailQuery, TError, TData>({
    queryKey: ["ShineSiteDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailQuery,
      Types.ShineSiteDetailQueryVariables
    >(ShineSiteDetailDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetailSuspense", variables]

useSuspenseShineSiteDetailQuery.rootKey = "ShineSiteDetail"

export const useInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infinite", variables]

useInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

export const useSuspenseInfiniteShineSiteDetailQuery = <
  TData = InfiniteData<Types.ShineSiteDetailQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailQuery,
            Types.ShineSiteDetailQueryVariables
          >(ShineSiteDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailQuery.getKey = (
  variables: Types.ShineSiteDetailQueryVariables,
) => ["ShineSiteDetail.infiniteSuspense", variables]

useSuspenseInfiniteShineSiteDetailQuery.rootKey = "ShineSiteDetail.infinite"

useShineSiteDetailQuery.fetcher = (
  variables: Types.ShineSiteDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ShineSiteDetailQuery, Types.ShineSiteDetailQueryVariables>(
    ShineSiteDetailDocument,
    variables,
    options,
  )

export const OrgShineDocument = `
    query OrgShine($orgSlug: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    name
    slug
    shine {
      status
      stats {
        ...ShineStats
      }
      units(duration: $duration) {
        ...ShineUnit
      }
      siteRefs {
        pid
        lat
        lon
        monthlyGrossRevenue
        status
      }
    }
  }
}
    ${ShineStatsFragmentDoc}
${ShineUnitFragmentDoc}`

export const useOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<UseQueryOptions<Types.OrgShineQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShine", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine",
  variables,
]

useOrgShineQuery.rootKey = "OrgShine"

export const useSuspenseOrgShineQuery = <TData = Types.OrgShineQuery, TError = unknown>(
  variables: Types.OrgShineQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OrgShineQuery, TError, TData>({
    queryKey: ["OrgShineSuspense", variables],
    queryFn: publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
      OrgShineDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShineSuspense",
  variables,
]

useSuspenseOrgShineQuery.rootKey = "OrgShine"

export const useInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infinite",
  variables,
]

useInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

export const useSuspenseInfiniteOrgShineQuery = <
  TData = InfiniteData<Types.OrgShineQuery>,
  TError = unknown,
>(
  variables: Types.OrgShineQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OrgShineQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OrgShineQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OrgShineQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OrgShine.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
            OrgShineDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOrgShineQuery.getKey = (variables: Types.OrgShineQueryVariables) => [
  "OrgShine.infiniteSuspense",
  variables,
]

useSuspenseInfiniteOrgShineQuery.rootKey = "OrgShine.infinite"

useOrgShineQuery.fetcher = (
  variables: Types.OrgShineQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.OrgShineQuery, Types.OrgShineQueryVariables>(
    OrgShineDocument,
    variables,
    options,
  )

export const ShineSiteDetailUnitsDocument = `
    query ShineSiteDetailUnits($orgSlug: String!, $propertyPid: String!, $duration: ShineDuration!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $propertyPid) {
      pid
      shine {
        units(duration: $duration) {
          ...ShineUnit
        }
      }
    }
  }
}
    ${ShineUnitFragmentDoc}`

export const useShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnits", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits", variables]

useShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useSuspenseShineSiteDetailUnitsQuery = <
  TData = Types.ShineSiteDetailUnitsQuery,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>({
    queryKey: ["ShineSiteDetailUnitsSuspense", variables],
    queryFn: publicFetcher<
      Types.ShineSiteDetailUnitsQuery,
      Types.ShineSiteDetailUnitsQueryVariables
    >(ShineSiteDetailUnitsDocument, variables),
    ...options,
  })
}

useSuspenseShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnitsSuspense", variables]

useSuspenseShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits"

export const useInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ShineSiteDetailUnits.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infinite", variables]

useInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

export const useSuspenseInfiniteShineSiteDetailUnitsQuery = <
  TData = InfiniteData<Types.ShineSiteDetailUnitsQuery>,
  TError = unknown,
>(
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ShineSiteDetailUnitsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ShineSiteDetailUnitsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ShineSiteDetailUnitsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ShineSiteDetailUnits.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ShineSiteDetailUnitsQuery,
            Types.ShineSiteDetailUnitsQueryVariables
          >(ShineSiteDetailUnitsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteShineSiteDetailUnitsQuery.getKey = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
) => ["ShineSiteDetailUnits.infiniteSuspense", variables]

useSuspenseInfiniteShineSiteDetailUnitsQuery.rootKey = "ShineSiteDetailUnits.infinite"

useShineSiteDetailUnitsQuery.fetcher = (
  variables: Types.ShineSiteDetailUnitsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ShineSiteDetailUnitsQuery,
    Types.ShineSiteDetailUnitsQueryVariables
  >(ShineSiteDetailUnitsDocument, variables, options)

export const SiteSolarPanelVisualDocument = `
    query SiteSolarPanelVisual($orgSlug: String!, $propertyPid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      visuals {
        ...SiteVisuals
      }
    }
  }
}
    ${SiteVisualsFragmentDoc}`

export const useSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisual", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual", variables]

useSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useSuspenseSiteSolarPanelVisualQuery = <
  TData = Types.SiteSolarPanelVisualQuery,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteSolarPanelVisualQuery, TError, TData>({
    queryKey: ["SiteSolarPanelVisualSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteSolarPanelVisualQuery,
      Types.SiteSolarPanelVisualQueryVariables
    >(SiteSolarPanelVisualDocument, variables),
    ...options,
  })
}

useSuspenseSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisualSuspense", variables]

useSuspenseSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual"

export const useInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteSolarPanelVisual.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infinite", variables]

useInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

export const useSuspenseInfiniteSiteSolarPanelVisualQuery = <
  TData = InfiniteData<Types.SiteSolarPanelVisualQuery>,
  TError = unknown,
>(
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteSolarPanelVisualQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteSolarPanelVisualQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteSolarPanelVisualQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteSolarPanelVisual.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteSolarPanelVisualQuery,
            Types.SiteSolarPanelVisualQueryVariables
          >(SiteSolarPanelVisualDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteSolarPanelVisualQuery.getKey = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
) => ["SiteSolarPanelVisual.infiniteSuspense", variables]

useSuspenseInfiniteSiteSolarPanelVisualQuery.rootKey = "SiteSolarPanelVisual.infinite"

useSiteSolarPanelVisualQuery.fetcher = (
  variables: Types.SiteSolarPanelVisualQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteSolarPanelVisualQuery,
    Types.SiteSolarPanelVisualQueryVariables
  >(SiteSolarPanelVisualDocument, variables, options)

export const CsvImportsListDocument = `
    query CsvImportsList($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      all {
        pid
        fileName
        createdAt
        createdBy {
          email
        }
        portfolio {
          pid
          title
        }
        rowStats {
          total
          imported
        }
      }
    }
  }
}
    `

export const useCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportsListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsList", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useCsvImportsListQuery.getKey = (variables: Types.CsvImportsListQueryVariables) => [
  "CsvImportsList",
  variables,
]

useCsvImportsListQuery.rootKey = "CsvImportsList"

export const useSuspenseCsvImportsListQuery = <
  TData = Types.CsvImportsListQuery,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportsListQuery, TError, TData>({
    queryKey: ["CsvImportsListSuspense", variables],
    queryFn: publicFetcher<
      Types.CsvImportsListQuery,
      Types.CsvImportsListQueryVariables
    >(CsvImportsListDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsListSuspense", variables]

useSuspenseCsvImportsListQuery.rootKey = "CsvImportsList"

export const useInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infinite", variables]

useInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

export const useSuspenseInfiniteCsvImportsListQuery = <
  TData = InfiniteData<Types.CsvImportsListQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportsListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportsListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportsListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportsListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportsList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
            CsvImportsListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportsListQuery.getKey = (
  variables: Types.CsvImportsListQueryVariables,
) => ["CsvImportsList.infiniteSuspense", variables]

useSuspenseInfiniteCsvImportsListQuery.rootKey = "CsvImportsList.infinite"

useCsvImportsListQuery.fetcher = (
  variables: Types.CsvImportsListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportsListQuery, Types.CsvImportsListQueryVariables>(
    CsvImportsListDocument,
    variables,
    options,
  )

export const SiteEvDocument = `
    query SiteEV($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    site(pid: $sitePid) {
      evCalc {
        ...BasicEVCalc
      }
      fullAddress
      yearBuilt
      numParkingSpaces
      buildingClass
      numUnitsTotal
    }
  }
}
    ${BasicEvCalcFragmentDoc}`

export const useSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<UseQueryOptions<Types.SiteEvQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEV", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => ["SiteEV", variables]

useSiteEvQuery.rootKey = "SiteEV"

export const useSuspenseSiteEvQuery = <TData = Types.SiteEvQuery, TError = unknown>(
  variables: Types.SiteEvQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvQuery, TError, TData>({
    queryKey: ["SiteEVSuspense", variables],
    queryFn: publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
      SiteEvDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEVSuspense",
  variables,
]

useSuspenseSiteEvQuery.rootKey = "SiteEV"

export const useInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infinite",
  variables,
]

useInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

export const useSuspenseInfiniteSiteEvQuery = <
  TData = InfiniteData<Types.SiteEvQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEV.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(SiteEvDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvQuery.getKey = (variables: Types.SiteEvQueryVariables) => [
  "SiteEV.infiniteSuspense",
  variables,
]

useSuspenseInfiniteSiteEvQuery.rootKey = "SiteEV.infinite"

useSiteEvQuery.fetcher = (
  variables: Types.SiteEvQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvQuery, Types.SiteEvQueryVariables>(
    SiteEvDocument,
    variables,
    options,
  )

export const SiteEvIncentivesDocument = `
    query SiteEvIncentives($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evCalc {
        incentives {
          programName
          state
          url
          eligibleAreas
          incentiveAmount
          programConstraints
          incentiveCap
          calculation
        }
      }
    }
  }
}
    `

export const useSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentives", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSiteEvIncentivesQuery.getKey = (variables: Types.SiteEvIncentivesQueryVariables) => [
  "SiteEvIncentives",
  variables,
]

useSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useSuspenseSiteEvIncentivesQuery = <
  TData = Types.SiteEvIncentivesQuery,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteEvIncentivesQuery, TError, TData>({
    queryKey: ["SiteEvIncentivesSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteEvIncentivesQuery,
      Types.SiteEvIncentivesQueryVariables
    >(SiteEvIncentivesDocument, variables),
    ...options,
  })
}

useSuspenseSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentivesSuspense", variables]

useSuspenseSiteEvIncentivesQuery.rootKey = "SiteEvIncentives"

export const useInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infinite", variables]

useInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

export const useSuspenseInfiniteSiteEvIncentivesQuery = <
  TData = InfiniteData<Types.SiteEvIncentivesQuery>,
  TError = unknown,
>(
  variables: Types.SiteEvIncentivesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteEvIncentivesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteEvIncentivesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteEvIncentivesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteEvIncentives.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteEvIncentivesQuery,
            Types.SiteEvIncentivesQueryVariables
          >(SiteEvIncentivesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteEvIncentivesQuery.getKey = (
  variables: Types.SiteEvIncentivesQueryVariables,
) => ["SiteEvIncentives.infiniteSuspense", variables]

useSuspenseInfiniteSiteEvIncentivesQuery.rootKey = "SiteEvIncentives.infinite"

useSiteEvIncentivesQuery.fetcher = (
  variables: Types.SiteEvIncentivesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteEvIncentivesQuery, Types.SiteEvIncentivesQueryVariables>(
    SiteEvIncentivesDocument,
    variables,
    options,
  )

export const SiteRecalculatingDocument = `
    query SiteRecalculating($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      isRecalculating
    }
  }
}
    `

export const useSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculating", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating", variables]

useSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useSuspenseSiteRecalculatingQuery = <
  TData = Types.SiteRecalculatingQuery,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteRecalculatingQuery, TError, TData>({
    queryKey: ["SiteRecalculatingSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteRecalculatingQuery,
      Types.SiteRecalculatingQueryVariables
    >(SiteRecalculatingDocument, variables),
    ...options,
  })
}

useSuspenseSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculatingSuspense", variables]

useSuspenseSiteRecalculatingQuery.rootKey = "SiteRecalculating"

export const useInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infinite", variables]

useInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

export const useSuspenseInfiniteSiteRecalculatingQuery = <
  TData = InfiniteData<Types.SiteRecalculatingQuery>,
  TError = unknown,
>(
  variables: Types.SiteRecalculatingQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteRecalculatingQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteRecalculatingQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteRecalculatingQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteRecalculating.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteRecalculatingQuery,
            Types.SiteRecalculatingQueryVariables
          >(SiteRecalculatingDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteRecalculatingQuery.getKey = (
  variables: Types.SiteRecalculatingQueryVariables,
) => ["SiteRecalculating.infiniteSuspense", variables]

useSuspenseInfiniteSiteRecalculatingQuery.rootKey = "SiteRecalculating.infinite"

useSiteRecalculatingQuery.fetcher = (
  variables: Types.SiteRecalculatingQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteRecalculatingQuery, Types.SiteRecalculatingQueryVariables>(
    SiteRecalculatingDocument,
    variables,
    options,
  )

export const ScenarioListDocument = `
    query ScenarioList($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      fullAddress
      scenarios {
        name
        pid
        solarLocation
        loadCategory
        includeStorage
      }
    }
  }
}
    `

export const useScenarioListQuery = <TData = Types.ScenarioListQuery, TError = unknown>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ScenarioListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioList", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList",
  variables,
]

useScenarioListQuery.rootKey = "ScenarioList"

export const useSuspenseScenarioListQuery = <
  TData = Types.ScenarioListQuery,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ScenarioListQuery, TError, TData>({
    queryKey: ["ScenarioListSuspense", variables],
    queryFn: publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
      ScenarioListDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioListSuspense",
  variables,
]

useSuspenseScenarioListQuery.rootKey = "ScenarioList"

export const useInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteScenarioListQuery.getKey = (variables: Types.ScenarioListQueryVariables) => [
  "ScenarioList.infinite",
  variables,
]

useInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

export const useSuspenseInfiniteScenarioListQuery = <
  TData = InfiniteData<Types.ScenarioListQuery>,
  TError = unknown,
>(
  variables: Types.ScenarioListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ScenarioListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ScenarioListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ScenarioListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ScenarioList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
            ScenarioListDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteScenarioListQuery.getKey = (
  variables: Types.ScenarioListQueryVariables,
) => ["ScenarioList.infiniteSuspense", variables]

useSuspenseInfiniteScenarioListQuery.rootKey = "ScenarioList.infinite"

useScenarioListQuery.fetcher = (
  variables: Types.ScenarioListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ScenarioListQuery, Types.ScenarioListQueryVariables>(
    ScenarioListDocument,
    variables,
    options,
  )

export const SiteFromPortfolioBuildingDataDocument = `
    query SiteFromPortfolioBuildingData($orgSlug: String!, $portfolioPid: String!, $buildingDataId: Int!) {
  orgBySlug(slug: $orgSlug) {
    siteFromPortfolioBuildingData(
      portfolioPid: $portfolioPid
      buildingDataId: $buildingDataId
    ) {
      __typename
      ... on SiteRef {
        pid
      }
    }
  }
}
    `

export const useSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingData", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData", variables]

useSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useSuspenseSiteFromPortfolioBuildingDataQuery = <
  TData = Types.SiteFromPortfolioBuildingDataQuery,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>({
    queryKey: ["SiteFromPortfolioBuildingDataSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteFromPortfolioBuildingDataQuery,
      Types.SiteFromPortfolioBuildingDataQueryVariables
    >(SiteFromPortfolioBuildingDataDocument, variables),
    ...options,
  })
}

useSuspenseSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingDataSuspense", variables]

useSuspenseSiteFromPortfolioBuildingDataQuery.rootKey = "SiteFromPortfolioBuildingData"

export const useInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFromPortfolioBuildingDataQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infinite", variables]

useInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

export const useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery = <
  TData = InfiniteData<Types.SiteFromPortfolioBuildingDataQuery>,
  TError = unknown,
>(
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFromPortfolioBuildingDataQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.SiteFromPortfolioBuildingDataQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "SiteFromPortfolioBuildingData.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFromPortfolioBuildingDataQuery,
            Types.SiteFromPortfolioBuildingDataQueryVariables
          >(SiteFromPortfolioBuildingDataDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.getKey = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
) => ["SiteFromPortfolioBuildingData.infiniteSuspense", variables]

useSuspenseInfiniteSiteFromPortfolioBuildingDataQuery.rootKey =
  "SiteFromPortfolioBuildingData.infinite"

useSiteFromPortfolioBuildingDataQuery.fetcher = (
  variables: Types.SiteFromPortfolioBuildingDataQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.SiteFromPortfolioBuildingDataQuery,
    Types.SiteFromPortfolioBuildingDataQueryVariables
  >(SiteFromPortfolioBuildingDataDocument, variables, options)

export const UtilityTariffsDocument = `
    query UtilityTariffs($utilityPid: String!) {
  tariffs2(utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    ${PublicTariffFragmentDoc}`

export const useUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.UtilityTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffs", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useUtilityTariffsQuery.getKey = (variables: Types.UtilityTariffsQueryVariables) => [
  "UtilityTariffs",
  variables,
]

useUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useSuspenseUtilityTariffsQuery = <
  TData = Types.UtilityTariffsQuery,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.UtilityTariffsQuery, TError, TData>({
    queryKey: ["UtilityTariffsSuspense", variables],
    queryFn: publicFetcher<
      Types.UtilityTariffsQuery,
      Types.UtilityTariffsQueryVariables
    >(UtilityTariffsDocument, variables),
    ...options,
  })
}

useSuspenseUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffsSuspense", variables]

useSuspenseUtilityTariffsQuery.rootKey = "UtilityTariffs"

export const useInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infinite", variables]

useInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

export const useSuspenseInfiniteUtilityTariffsQuery = <
  TData = InfiniteData<Types.UtilityTariffsQuery>,
  TError = unknown,
>(
  variables: Types.UtilityTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.UtilityTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.UtilityTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.UtilityTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["UtilityTariffs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
            UtilityTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteUtilityTariffsQuery.getKey = (
  variables: Types.UtilityTariffsQueryVariables,
) => ["UtilityTariffs.infiniteSuspense", variables]

useSuspenseInfiniteUtilityTariffsQuery.rootKey = "UtilityTariffs.infinite"

useUtilityTariffsQuery.fetcher = (
  variables: Types.UtilityTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.UtilityTariffsQuery, Types.UtilityTariffsQueryVariables>(
    UtilityTariffsDocument,
    variables,
    options,
  )

export const CustomTariffsDocument = `
    query CustomTariffs($sitePid: String!, $utilityPid: String!) {
  customTariffs(sitePid: $sitePid, utilityPid: $utilityPid) {
    ...PublicTariff
  }
}
    ${PublicTariffFragmentDoc}`

export const useCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffs", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useCustomTariffsQuery.getKey = (variables: Types.CustomTariffsQueryVariables) => [
  "CustomTariffs",
  variables,
]

useCustomTariffsQuery.rootKey = "CustomTariffs"

export const useSuspenseCustomTariffsQuery = <
  TData = Types.CustomTariffsQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffsQuery, TError, TData>({
    queryKey: ["CustomTariffsSuspense", variables],
    queryFn: publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
      CustomTariffsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffsSuspense", variables]

useSuspenseCustomTariffsQuery.rootKey = "CustomTariffs"

export const useInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infinite", variables]

useInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

export const useSuspenseInfiniteCustomTariffsQuery = <
  TData = InfiniteData<Types.CustomTariffsQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffs.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
            CustomTariffsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffsQuery.getKey = (
  variables: Types.CustomTariffsQueryVariables,
) => ["CustomTariffs.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffsQuery.rootKey = "CustomTariffs.infinite"

useCustomTariffsQuery.fetcher = (
  variables: Types.CustomTariffsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffsQuery, Types.CustomTariffsQueryVariables>(
    CustomTariffsDocument,
    variables,
    options,
  )

export const CustomTariffDetailDocument = `
    query CustomTariffDetail($orgSlug: String!, $propertyPid: String!, $tariffId: Int!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        code
        customerClass
        ops {
          ...TariffOp
        }
        preOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
        postOpDetails {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
        }
      }
    }
  }
}
    ${TariffOpFragmentDoc}
${CustomRateFragmentDoc}
${RateFragmentDoc}
${RateBandFragmentDoc}
${SeasonFragmentDoc}`

export const useCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetail", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail", variables]

useCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useSuspenseCustomTariffDetailQuery = <
  TData = Types.CustomTariffDetailQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffDetailQuery, TError, TData>({
    queryKey: ["CustomTariffDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffDetailQuery,
      Types.CustomTariffDetailQueryVariables
    >(CustomTariffDetailDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetailSuspense", variables]

useSuspenseCustomTariffDetailQuery.rootKey = "CustomTariffDetail"

export const useInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infinite", variables]

useInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

export const useSuspenseInfiniteCustomTariffDetailQuery = <
  TData = InfiniteData<Types.CustomTariffDetailQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffDetailQuery,
            Types.CustomTariffDetailQueryVariables
          >(CustomTariffDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffDetailQuery.getKey = (
  variables: Types.CustomTariffDetailQueryVariables,
) => ["CustomTariffDetail.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffDetailQuery.rootKey = "CustomTariffDetail.infinite"

useCustomTariffDetailQuery.fetcher = (
  variables: Types.CustomTariffDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CustomTariffDetailQuery, Types.CustomTariffDetailQueryVariables>(
    CustomTariffDetailDocument,
    variables,
    options,
  )

export const CustomTariffPreviewDocument = `
    query CustomTariffPreview($orgSlug: String!, $propertyPid: String!, $tariffId: Int!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $propertyPid) {
      customTariff(tariffId: $tariffId) {
        id
        name
        previewDetails(
          addRateOps: $addRateOps
          subRateOps: $subRateOps
          propertyOps: $propertyOps
        ) {
          allRates {
            ...Rate
          }
          rates {
            ...Rate
          }
          properties {
            keyName
            dataType
            propertyValue
            propertyValues
            formulaDetail
          }
        }
      }
    }
  }
}
    ${RateFragmentDoc}
${RateBandFragmentDoc}
${SeasonFragmentDoc}`

export const useCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreview", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview", variables]

useCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useSuspenseCustomTariffPreviewQuery = <
  TData = Types.CustomTariffPreviewQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffPreviewQuery, TError, TData>({
    queryKey: ["CustomTariffPreviewSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffPreviewQuery,
      Types.CustomTariffPreviewQueryVariables
    >(CustomTariffPreviewDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreviewSuspense", variables]

useSuspenseCustomTariffPreviewQuery.rootKey = "CustomTariffPreview"

export const useInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffPreview.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infinite", variables]

useInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

export const useSuspenseInfiniteCustomTariffPreviewQuery = <
  TData = InfiniteData<Types.CustomTariffPreviewQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffPreviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffPreviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffPreviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffPreviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CustomTariffPreview.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffPreviewQuery,
            Types.CustomTariffPreviewQueryVariables
          >(CustomTariffPreviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffPreviewQuery.getKey = (
  variables: Types.CustomTariffPreviewQueryVariables,
) => ["CustomTariffPreview.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffPreviewQuery.rootKey = "CustomTariffPreview.infinite"

useCustomTariffPreviewQuery.fetcher = (
  variables: Types.CustomTariffPreviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffPreviewQuery,
    Types.CustomTariffPreviewQueryVariables
  >(CustomTariffPreviewDocument, variables, options)

export const CustomTariffBillCalcDocument = `
    query CustomTariffBillCalc($propertyPid: String!, $customTariffId: Int!, $bauMeterPid: String!, $addRateOps: [AddRateInput!]!, $subRateOps: [SubRateInput!]!, $propertyOps: [OverridePropertyInput!]!) {
  customTariff(sitePid: $propertyPid, customTariffId: $customTariffId) {
    billCalc(
      bauMeterPid: $bauMeterPid
      addRateOps: $addRateOps
      subRateOps: $subRateOps
      propertyOps: $propertyOps
    ) {
      start
      end
      summary {
        chargeType
        costUsd
      }
      details {
        group
        items {
          cost
          usage
          name
          rate
          unit
        }
      }
    }
  }
}
    `

export const useCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalc", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc", variables]

useCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useSuspenseCustomTariffBillCalcQuery = <
  TData = Types.CustomTariffBillCalcQuery,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CustomTariffBillCalcQuery, TError, TData>({
    queryKey: ["CustomTariffBillCalcSuspense", variables],
    queryFn: publicFetcher<
      Types.CustomTariffBillCalcQuery,
      Types.CustomTariffBillCalcQueryVariables
    >(CustomTariffBillCalcDocument, variables),
    ...options,
  })
}

useSuspenseCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalcSuspense", variables]

useSuspenseCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc"

export const useInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CustomTariffBillCalc.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infinite", variables]

useInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

export const useSuspenseInfiniteCustomTariffBillCalcQuery = <
  TData = InfiniteData<Types.CustomTariffBillCalcQuery>,
  TError = unknown,
>(
  variables: Types.CustomTariffBillCalcQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CustomTariffBillCalcQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CustomTariffBillCalcQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CustomTariffBillCalcQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CustomTariffBillCalc.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CustomTariffBillCalcQuery,
            Types.CustomTariffBillCalcQueryVariables
          >(CustomTariffBillCalcDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCustomTariffBillCalcQuery.getKey = (
  variables: Types.CustomTariffBillCalcQueryVariables,
) => ["CustomTariffBillCalc.infiniteSuspense", variables]

useSuspenseInfiniteCustomTariffBillCalcQuery.rootKey = "CustomTariffBillCalc.infinite"

useCustomTariffBillCalcQuery.fetcher = (
  variables: Types.CustomTariffBillCalcQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CustomTariffBillCalcQuery,
    Types.CustomTariffBillCalcQueryVariables
  >(CustomTariffBillCalcDocument, variables, options)

export const BundleParentsDocument = `
    query BundleParents($orgSlug: String!) {
  bundleParents(orgSlug: $orgSlug) {
    ...BasicBundleParent
  }
}
    ${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}`

export const useBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParents", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useBundleParentsQuery.getKey = (variables: Types.BundleParentsQueryVariables) => [
  "BundleParents",
  variables,
]

useBundleParentsQuery.rootKey = "BundleParents"

export const useSuspenseBundleParentsQuery = <
  TData = Types.BundleParentsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentsQuery, TError, TData>({
    queryKey: ["BundleParentsSuspense", variables],
    queryFn: publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
      BundleParentsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParentsSuspense", variables]

useSuspenseBundleParentsQuery.rootKey = "BundleParents"

export const useInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infinite", variables]

useInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

export const useSuspenseInfiniteBundleParentsQuery = <
  TData = InfiniteData<Types.BundleParentsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParents.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
            BundleParentsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentsQuery.getKey = (
  variables: Types.BundleParentsQueryVariables,
) => ["BundleParents.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentsQuery.rootKey = "BundleParents.infinite"

useBundleParentsQuery.fetcher = (
  variables: Types.BundleParentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentsQuery, Types.BundleParentsQueryVariables>(
    BundleParentsDocument,
    variables,
    options,
  )

export const BundleParentDetailDocument = `
    query BundleParentDetail($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    ...BundleParent
    projectDocuments {
      projectAssociations {
        project {
          pid
          name
        }
        viewableByDeveloper
        viewableByBuildingOwner
      }
      document {
        id
        pid
        tag
        fileName
        contentType
        dateCreated
        downloadHref
        canDelete
        ownerName
      }
    }
    bundles {
      ...BundleListItem
      rows {
        ...BundleRowDetail
      }
    }
  }
}
    ${BundleParentFragmentDoc}
${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${SystemLiteFragmentDoc}
${CashBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${RevampCsFragmentDoc}
${CashInvestorBidFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetail", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail", variables]

useBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useSuspenseBundleParentDetailQuery = <
  TData = Types.BundleParentDetailQuery,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentDetailQuery, TError, TData>({
    queryKey: ["BundleParentDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentDetailQuery,
      Types.BundleParentDetailQueryVariables
    >(BundleParentDetailDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetailSuspense", variables]

useSuspenseBundleParentDetailQuery.rootKey = "BundleParentDetail"

export const useInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infinite", variables]

useInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

export const useSuspenseInfiniteBundleParentDetailQuery = <
  TData = InfiniteData<Types.BundleParentDetailQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentDetailQuery,
            Types.BundleParentDetailQueryVariables
          >(BundleParentDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentDetailQuery.getKey = (
  variables: Types.BundleParentDetailQueryVariables,
) => ["BundleParentDetail.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentDetailQuery.rootKey = "BundleParentDetail.infinite"

useBundleParentDetailQuery.fetcher = (
  variables: Types.BundleParentDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentDetailQuery, Types.BundleParentDetailQueryVariables>(
    BundleParentDetailDocument,
    variables,
    options,
  )

export const BundleParentReportsDocument = `
    query BundleParentReports($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    pid
    type
    customReports {
      ...CustomBidReport
    }
  }
}
    ${CustomBidReportFragmentDoc}`

export const useBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReports", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports", variables]

useBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useSuspenseBundleParentReportsQuery = <
  TData = Types.BundleParentReportsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentReportsQuery, TError, TData>({
    queryKey: ["BundleParentReportsSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentReportsQuery,
      Types.BundleParentReportsQueryVariables
    >(BundleParentReportsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReportsSuspense", variables]

useSuspenseBundleParentReportsQuery.rootKey = "BundleParentReports"

export const useInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infinite", variables]

useInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

export const useSuspenseInfiniteBundleParentReportsQuery = <
  TData = InfiniteData<Types.BundleParentReportsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BundleParentReports.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentReportsQuery,
            Types.BundleParentReportsQueryVariables
          >(BundleParentReportsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentReportsQuery.getKey = (
  variables: Types.BundleParentReportsQueryVariables,
) => ["BundleParentReports.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentReportsQuery.rootKey = "BundleParentReports.infinite"

useBundleParentReportsQuery.fetcher = (
  variables: Types.BundleParentReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BundleParentReportsQuery,
    Types.BundleParentReportsQueryVariables
  >(BundleParentReportsDocument, variables, options)

export const BidReportsDocument = `
    query BidReports {
  bidReports {
    pid
    name
    notes
    buildingOwnerNotes
    parent {
      pid
    }
    bids {
      ppa {
        pid
      }
      cs {
        pid
      }
    }
  }
}
    `

export const useBidReportsQuery = <TData = Types.BidReportsQuery, TError = unknown>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<UseQueryOptions<Types.BidReportsQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidReportsQuery, TError, TData>({
    queryKey: variables === undefined ? ["BidReports"] : ["BidReports", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports"] : ["BidReports", variables]

useBidReportsQuery.rootKey = "BidReports"

export const useSuspenseBidReportsQuery = <
  TData = Types.BidReportsQuery,
  TError = unknown,
>(
  variables?: Types.BidReportsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidReportsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidReportsSuspense"]
        : ["BidReportsSuspense", variables],
    queryFn: publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
      BidReportsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReportsSuspense"] : ["BidReportsSuspense", variables]

useSuspenseBidReportsQuery.rootKey = "BidReports"

export const useInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infinite"]
            : ["BidReports.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidReportsQuery.getKey = (variables?: Types.BidReportsQueryVariables) =>
  variables === undefined ? ["BidReports.infinite"] : ["BidReports.infinite", variables]

useInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

export const useSuspenseInfiniteBidReportsQuery = <
  TData = InfiniteData<Types.BidReportsQuery>,
  TError = unknown,
>(
  variables: Types.BidReportsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidReportsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidReportsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidReportsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidReports.infiniteSuspense"]
            : ["BidReports.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
            BidReportsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidReportsQuery.getKey = (
  variables?: Types.BidReportsQueryVariables,
) =>
  variables === undefined
    ? ["BidReports.infiniteSuspense"]
    : ["BidReports.infiniteSuspense", variables]

useSuspenseInfiniteBidReportsQuery.rootKey = "BidReports.infinite"

useBidReportsQuery.fetcher = (
  variables?: Types.BidReportsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidReportsQuery, Types.BidReportsQueryVariables>(
    BidReportsDocument,
    variables,
    options,
  )

export const BundleParentBasicsDocument = `
    query BundleParentBasics($orgSlug: String!, $pid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $pid) {
    ...BasicBundleParent
    publicUrl
    totalPvSizeKwDc
    bundles {
      pid
      financier {
        pid
        name
        orgSlug
        orgUsers {
          user {
            ...UserRef
          }
          userRoles {
            role
          }
        }
      }
    }
  }
}
    ${BasicBundleParentFragmentDoc}
${UserRefFragmentDoc}`

export const useBundleParentBasicsQuery = <
  TData = Types.BundleParentBasicsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BundleParentBasicsQuery, TError, TData>({
    queryKey: ["BundleParentBasics", variables],
    queryFn: publicFetcher<
      Types.BundleParentBasicsQuery,
      Types.BundleParentBasicsQueryVariables
    >(BundleParentBasicsDocument, variables),
    ...options,
  })
}

useBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics", variables]

useBundleParentBasicsQuery.rootKey = "BundleParentBasics"

export const useSuspenseBundleParentBasicsQuery = <
  TData = Types.BundleParentBasicsQuery,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BundleParentBasicsQuery, TError, TData>({
    queryKey: ["BundleParentBasicsSuspense", variables],
    queryFn: publicFetcher<
      Types.BundleParentBasicsQuery,
      Types.BundleParentBasicsQueryVariables
    >(BundleParentBasicsDocument, variables),
    ...options,
  })
}

useSuspenseBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasicsSuspense", variables]

useSuspenseBundleParentBasicsQuery.rootKey = "BundleParentBasics"

export const useInfiniteBundleParentBasicsQuery = <
  TData = InfiniteData<Types.BundleParentBasicsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BundleParentBasicsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentBasics.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentBasicsQuery,
            Types.BundleParentBasicsQueryVariables
          >(BundleParentBasicsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics.infinite", variables]

useInfiniteBundleParentBasicsQuery.rootKey = "BundleParentBasics.infinite"

export const useSuspenseInfiniteBundleParentBasicsQuery = <
  TData = InfiniteData<Types.BundleParentBasicsQuery>,
  TError = unknown,
>(
  variables: Types.BundleParentBasicsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BundleParentBasicsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BundleParentBasicsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BundleParentBasicsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BundleParentBasics.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BundleParentBasicsQuery,
            Types.BundleParentBasicsQueryVariables
          >(BundleParentBasicsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBundleParentBasicsQuery.getKey = (
  variables: Types.BundleParentBasicsQueryVariables,
) => ["BundleParentBasics.infiniteSuspense", variables]

useSuspenseInfiniteBundleParentBasicsQuery.rootKey = "BundleParentBasics.infinite"

useBundleParentBasicsQuery.fetcher = (
  variables: Types.BundleParentBasicsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BundleParentBasicsQuery, Types.BundleParentBasicsQueryVariables>(
    BundleParentBasicsDocument,
    variables,
    options,
  )

export const EvOverridesDocument = `
    query EVOverrides($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    site(pid: $sitePid) {
      evOverrides {
        current {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
        defaults {
          chargerKw
          chargerLocation
          cmsUsdPerChargerPerYr
          demandUsdPerKw
          discountRate
          evGrowthPct
          highEnergyUsdPerKwh
          incentivePct
          installType
          lowEnergyUsdPerKwh
          maxUtilization
          milesDrivenPerYear
          milesPerKwh
          numChargers
          omUsdPerChargerPerYr
          onPeakChargingPct
          onSiteChargingPct
          projectStartDate
          rateManagementUsdPerChargerPerYr
          salesTaxPct
          termYears
          transactionFeePct
          usdPerKwhMarkup
          yr0NumTenantEvs
          incentiveCapNumChargers
          maxEvAdoptionPct
          installCostUsd
        }
      }
    }
  }
}
    `

export const useEvOverridesQuery = <TData = Types.EvOverridesQuery, TError = unknown>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<UseQueryOptions<Types.EvOverridesQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EvOverridesQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverrides", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides",
  variables,
]

useEvOverridesQuery.rootKey = "EVOverrides"

export const useSuspenseEvOverridesQuery = <
  TData = Types.EvOverridesQuery,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EvOverridesQuery, TError, TData>({
    queryKey: ["EVOverridesSuspense", variables],
    queryFn: publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
      EvOverridesDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverridesSuspense",
  variables,
]

useSuspenseEvOverridesQuery.rootKey = "EVOverrides"

export const useInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEvOverridesQuery.getKey = (variables: Types.EvOverridesQueryVariables) => [
  "EVOverrides.infinite",
  variables,
]

useInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

export const useSuspenseInfiniteEvOverridesQuery = <
  TData = InfiniteData<Types.EvOverridesQuery>,
  TError = unknown,
>(
  variables: Types.EvOverridesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EvOverridesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EvOverridesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EvOverridesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["EVOverrides.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
            EvOverridesDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEvOverridesQuery.getKey = (
  variables: Types.EvOverridesQueryVariables,
) => ["EVOverrides.infiniteSuspense", variables]

useSuspenseInfiniteEvOverridesQuery.rootKey = "EVOverrides.infinite"

useEvOverridesQuery.fetcher = (
  variables: Types.EvOverridesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EvOverridesQuery, Types.EvOverridesQueryVariables>(
    EvOverridesDocument,
    variables,
    options,
  )

export const InvestorBundleListDocument = `
    query InvestorBundleList($orgSlug: String!) {
  bundlesForFinancier2(slug: $orgSlug) {
    ...BundleListItem
  }
}
    ${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}`

export const useInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.InvestorBundleListQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleList", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList", variables]

useInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useSuspenseInvestorBundleListQuery = <
  TData = Types.InvestorBundleListQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleListQuery, TError, TData>({
    queryKey: ["InvestorBundleListSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleListQuery,
      Types.InvestorBundleListQueryVariables
    >(InvestorBundleListDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleListSuspense", variables]

useSuspenseInvestorBundleListQuery.rootKey = "InvestorBundleList"

export const useInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infinite", variables]

useInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

export const useSuspenseInfiniteInvestorBundleListQuery = <
  TData = InfiniteData<Types.InvestorBundleListQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleListQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleListQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleListQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleListQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleList.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleListQuery,
            Types.InvestorBundleListQueryVariables
          >(InvestorBundleListDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleListQuery.getKey = (
  variables: Types.InvestorBundleListQueryVariables,
) => ["InvestorBundleList.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleListQuery.rootKey = "InvestorBundleList.infinite"

useInvestorBundleListQuery.fetcher = (
  variables: Types.InvestorBundleListQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.InvestorBundleListQuery, Types.InvestorBundleListQueryVariables>(
    InvestorBundleListDocument,
    variables,
    options,
  )

export const InvestorBundleDetailDocument = `
    query InvestorBundleDetail($orgSlug: String!, $bundlePid: String!) {
  bundleDetail(slug: $orgSlug, bundlePid: $bundlePid) {
    ...BundleDetail
  }
}
    ${BundleDetailFragmentDoc}
${BundleListItemFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${CashInvestorBidFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${CashBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${BidReportFragmentDoc}
${CsSiteBidReportFragmentDoc}
${BoxplotStatsFragmentDoc}
${PpaSiteBidReportFragmentDoc}
${CashSiteBidReportFragmentDoc}`

export const useInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail", variables]

useInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useSuspenseInvestorBundleDetailQuery = <
  TData = Types.InvestorBundleDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleDetailQuery,
      Types.InvestorBundleDetailQueryVariables
    >(InvestorBundleDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetailSuspense", variables]

useSuspenseInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail"

export const useInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infinite", variables]

useInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

export const useSuspenseInfiniteInvestorBundleDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InvestorBundleDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleDetailQuery,
            Types.InvestorBundleDetailQueryVariables
          >(InvestorBundleDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleDetailQuery.getKey = (
  variables: Types.InvestorBundleDetailQueryVariables,
) => ["InvestorBundleDetail.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleDetailQuery.rootKey = "InvestorBundleDetail.infinite"

useInvestorBundleDetailQuery.fetcher = (
  variables: Types.InvestorBundleDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleDetailQuery,
    Types.InvestorBundleDetailQueryVariables
  >(InvestorBundleDetailDocument, variables, options)

export const InvestorBundleRowDetailDocument = `
    query InvestorBundleRowDetail($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  bundleRowDetail(orgSlug: $orgSlug, bundlePid: $bundlePid, sitePid: $sitePid) {
    ...BundleRowDetail
  }
}
    ${BundleRowDetailFragmentDoc}
${BundleDetailRowFragmentDoc}
${InvestorOptOutFragmentDoc}
${UserRefFragmentDoc}
${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${CashInvestorBidFragmentDoc}
${BidSpecFragmentDoc}
${PpaBidSpecFragmentDoc}
${CsBidSpecFragmentDoc}
${CashBidSpecFragmentDoc}
${InvestorBidSpecOptOutFragmentDoc}
${RevampSiteBasicsFragmentDoc}
${SiteConfigFragmentDoc}
${FootprintFragmentDoc}
${BillingMeterOptionFragmentDoc}`

export const useInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetail", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail", variables]

useInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useSuspenseInvestorBundleRowDetailQuery = <
  TData = Types.InvestorBundleRowDetailQuery,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.InvestorBundleRowDetailQuery, TError, TData>({
    queryKey: ["InvestorBundleRowDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.InvestorBundleRowDetailQuery,
      Types.InvestorBundleRowDetailQueryVariables
    >(InvestorBundleRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetailSuspense", variables]

useSuspenseInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail"

export const useInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["InvestorBundleRowDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infinite", variables]

useInfiniteInvestorBundleRowDetailQuery.rootKey = "InvestorBundleRowDetail.infinite"

export const useSuspenseInfiniteInvestorBundleRowDetailQuery = <
  TData = InfiniteData<Types.InvestorBundleRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.InvestorBundleRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.InvestorBundleRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.InvestorBundleRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "InvestorBundleRowDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.InvestorBundleRowDetailQuery,
            Types.InvestorBundleRowDetailQueryVariables
          >(InvestorBundleRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteInvestorBundleRowDetailQuery.getKey = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
) => ["InvestorBundleRowDetail.infiniteSuspense", variables]

useSuspenseInfiniteInvestorBundleRowDetailQuery.rootKey =
  "InvestorBundleRowDetail.infinite"

useInvestorBundleRowDetailQuery.fetcher = (
  variables: Types.InvestorBundleRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.InvestorBundleRowDetailQuery,
    Types.InvestorBundleRowDetailQueryVariables
  >(InvestorBundleRowDetailDocument, variables, options)

export const GetTaskDocument = `
    query GetTask($taskId: Int!) {
  tasks {
    one(taskId: $taskId) {
      __typename
      ... on Task {
        id
        name
        status
      }
    }
  }
}
    `

export const useGetTaskQuery = <TData = Types.GetTaskQuery, TError = unknown>(
  variables: Types.GetTaskQueryVariables,
  options?: Omit<UseQueryOptions<Types.GetTaskQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.GetTaskQuery, TError, TData>({
    queryKey: ["GetTask", variables],
    queryFn: publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
      GetTaskDocument,
      variables,
    ),
    ...options,
  })
}

useGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask",
  variables,
]

useGetTaskQuery.rootKey = "GetTask"

export const useSuspenseGetTaskQuery = <TData = Types.GetTaskQuery, TError = unknown>(
  variables: Types.GetTaskQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.GetTaskQuery, TError, TData>({
    queryKey: ["GetTaskSuspense", variables],
    queryFn: publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
      GetTaskDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTaskSuspense",
  variables,
]

useSuspenseGetTaskQuery.rootKey = "GetTask"

export const useInfiniteGetTaskQuery = <
  TData = InfiniteData<Types.GetTaskQuery>,
  TError = unknown,
>(
  variables: Types.GetTaskQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.GetTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetTask.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
            GetTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask.infinite",
  variables,
]

useInfiniteGetTaskQuery.rootKey = "GetTask.infinite"

export const useSuspenseInfiniteGetTaskQuery = <
  TData = InfiniteData<Types.GetTaskQuery>,
  TError = unknown,
>(
  variables: Types.GetTaskQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.GetTaskQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.GetTaskQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.GetTaskQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["GetTask.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
            GetTaskDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteGetTaskQuery.getKey = (variables: Types.GetTaskQueryVariables) => [
  "GetTask.infiniteSuspense",
  variables,
]

useSuspenseInfiniteGetTaskQuery.rootKey = "GetTask.infinite"

useGetTaskQuery.fetcher = (
  variables: Types.GetTaskQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.GetTaskQuery, Types.GetTaskQueryVariables>(
    GetTaskDocument,
    variables,
    options,
  )

export const CsvImportDetailDocument = `
    query CsvImportDetail($orgSlug: String!, $csvImportPid: String!) {
  orgBySlug(slug: $orgSlug) {
    csvImports {
      one(pid: $csvImportPid) {
        pid
        rawCsvWithPidsHref
        columnIds
        columnMappings {
          assetManagerColumnId
          buildingTypeColumnId
          cityColumnId
          customerIdColumnId
          latitudeColumnId
          longitudeColumnId
          nameColumnId
          numStoriesColumnId
          numUnits1BdColumnId
          numUnits2BdColumnId
          numUnits3BdColumnId
          numUnits4BdColumnId
          numUnitsStudioColumnId
          numUnitsTotalColumnId
          sqftColumnId
          stateColumnId
          streetAddressColumnId
          yearBuiltColumnId
          zipCodeColumnId
        }
        buildingTypes
        buildingTypeMappings {
          office
          multifamily
          hotel
          hospital
          outpatient
          retailStandalone
          retailStripMall
          quickServiceRestaurant
          fullServiceRestaurant
          primarySchool
          secondarySchool
          singleFamilyDetached
          warehouse
          dataCenter
          coldStorage
          industrial
          parkingLot
          supermarket
        }
        guessedSchema {
          pid
          origin
        }
        labels {
          pid
          themeColor
          name
        }
        rows {
          id
          droppedAt
          buildingData {
            id
            propertyName
            fullAddress
            streetAddress
            city
            state
            zipCode
            buildingType
            sqft
            roofSqft
            numStories
            latitude
            longitude
            buildingFootprintGeojson
            yearBuilt
            numUnitsTotal
            numUnitsStudio
            numUnits1Bd
            numUnits2Bd
            numUnits3Bd
            numUnits4Bd
          }
          amendments {
            assetManager
            buildingType
            customerId
            latitude
            longitude
            name
            numStories
            roofSqft
            sqft
            yearBuilt
            mfUnits {
              numUnitsTotal
              numUnitsStudio
              numUnits1Bd
              numUnits2Bd
              numUnits3Bd
              numUnits4Bd
            }
          }
          site {
            pid
          }
          dirty {
            ...DirtyRowData
          }
          errors {
            type
            message
          }
          warnings {
            type
            message
          }
        }
      }
    }
  }
}
    ${DirtyRowDataFragmentDoc}`

export const useCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.CsvImportDetailQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetail", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useCsvImportDetailQuery.getKey = (variables: Types.CsvImportDetailQueryVariables) => [
  "CsvImportDetail",
  variables,
]

useCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useSuspenseCsvImportDetailQuery = <
  TData = Types.CsvImportDetailQuery,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CsvImportDetailQuery, TError, TData>({
    queryKey: ["CsvImportDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.CsvImportDetailQuery,
      Types.CsvImportDetailQueryVariables
    >(CsvImportDetailDocument, variables),
    ...options,
  })
}

useSuspenseCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetailSuspense", variables]

useSuspenseCsvImportDetailQuery.rootKey = "CsvImportDetail"

export const useInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infinite", variables]

useInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

export const useSuspenseInfiniteCsvImportDetailQuery = <
  TData = InfiniteData<Types.CsvImportDetailQuery>,
  TError = unknown,
>(
  variables: Types.CsvImportDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CsvImportDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CsvImportDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CsvImportDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CsvImportDetail.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CsvImportDetailQuery,
            Types.CsvImportDetailQueryVariables
          >(CsvImportDetailDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCsvImportDetailQuery.getKey = (
  variables: Types.CsvImportDetailQueryVariables,
) => ["CsvImportDetail.infiniteSuspense", variables]

useSuspenseInfiniteCsvImportDetailQuery.rootKey = "CsvImportDetail.infinite"

useCsvImportDetailQuery.fetcher = (
  variables: Types.CsvImportDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.CsvImportDetailQuery, Types.CsvImportDetailQueryVariables>(
    CsvImportDetailDocument,
    variables,
    options,
  )

export const MarketplacePackagesDocument = `
    query MarketplacePackages($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    bidPackages {
      ...BasicBidPackage
    }
  }
}
    ${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}`

export const useMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackages", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages", variables]

useMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useSuspenseMarketplacePackagesQuery = <
  TData = Types.MarketplacePackagesQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackagesQuery, TError, TData>({
    queryKey: ["MarketplacePackagesSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackagesQuery,
      Types.MarketplacePackagesQueryVariables
    >(MarketplacePackagesDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackagesSuspense", variables]

useSuspenseMarketplacePackagesQuery.rootKey = "MarketplacePackages"

export const useInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackages.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infinite", variables]

useInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

export const useSuspenseInfiniteMarketplacePackagesQuery = <
  TData = InfiniteData<Types.MarketplacePackagesQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackagesQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackagesQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackagesQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackagesQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackages.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackagesQuery,
            Types.MarketplacePackagesQueryVariables
          >(MarketplacePackagesDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackagesQuery.getKey = (
  variables: Types.MarketplacePackagesQueryVariables,
) => ["MarketplacePackages.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackagesQuery.rootKey = "MarketplacePackages.infinite"

useMarketplacePackagesQuery.fetcher = (
  variables: Types.MarketplacePackagesQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackagesQuery,
    Types.MarketplacePackagesQueryVariables
  >(MarketplacePackagesDocument, variables, options)

export const MarketplaceSpecsViewDocument = `
    query MarketplaceSpecsView($orgSlug: String!, $filterBy: BidSpecsTableFilter!, $sortBy: [BidSpecsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView", variables]

useMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useSuspenseMarketplaceSpecsViewQuery = <
  TData = Types.MarketplaceSpecsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsViewQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsViewQuery,
      Types.MarketplaceSpecsViewQueryVariables
    >(MarketplaceSpecsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsViewSuspense", variables]

useSuspenseMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView"

export const useInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infinite", variables]

useInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

export const useSuspenseInfiniteMarketplaceSpecsViewQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSpecsView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsViewQuery,
            Types.MarketplaceSpecsViewQueryVariables
          >(MarketplaceSpecsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsViewQuery.getKey = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
) => ["MarketplaceSpecsView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSpecsViewQuery.rootKey = "MarketplaceSpecsView.infinite"

useMarketplaceSpecsViewQuery.fetcher = (
  variables: Types.MarketplaceSpecsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsViewQuery,
    Types.MarketplaceSpecsViewQueryVariables
  >(MarketplaceSpecsViewDocument, variables, options)

export const MarketplaceSpecsRowsDocument = `
    query MarketplaceSpecsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSpecs {
      rows(keys: $keys) {
        pid
        sitePid
        bidName
        bidPackagePid
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        csSystem {
          ...SystemLite
        }
        ppaSystem {
          ...System
        }
        csRoofRentEscalator
        ppaRoofRentEscalator
        ppaPriceEscalator
        csRoofRentY1Usd
        ppaRoofRentY1Usd
        ppaPriceY1Usd
        termYears
      }
    }
  }
}
    ${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${SystemFragmentDoc}`

export const useMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows", variables]

useMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useSuspenseMarketplaceSpecsRowsQuery = <
  TData = Types.MarketplaceSpecsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSpecsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSpecsRowsQuery,
      Types.MarketplaceSpecsRowsQueryVariables
    >(MarketplaceSpecsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRowsSuspense", variables]

useSuspenseMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows"

export const useInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSpecsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infinite", variables]

useInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

export const useSuspenseInfiniteMarketplaceSpecsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSpecsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSpecsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSpecsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSpecsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSpecsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSpecsRowsQuery,
            Types.MarketplaceSpecsRowsQueryVariables
          >(MarketplaceSpecsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSpecsRowsQuery.getKey = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
) => ["MarketplaceSpecsRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSpecsRowsQuery.rootKey = "MarketplaceSpecsRows.infinite"

useMarketplaceSpecsRowsQuery.fetcher = (
  variables: Types.MarketplaceSpecsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSpecsRowsQuery,
    Types.MarketplaceSpecsRowsQueryVariables
  >(MarketplaceSpecsRowsDocument, variables, options)

export const MarketplaceSitesViewDocument = `
    query MarketplaceSitesView($orgSlug: String!, $filterBy: BidPackageSitesTableFilter!, $sortBy: [BidPackageSitesTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    name
    bidPackageSites {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView", variables]

useMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useSuspenseMarketplaceSitesViewQuery = <
  TData = Types.MarketplaceSitesViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesViewQuery, TError, TData>({
    queryKey: ["MarketplaceSitesViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesViewQuery,
      Types.MarketplaceSitesViewQueryVariables
    >(MarketplaceSitesViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesViewSuspense", variables]

useSuspenseMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView"

export const useInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infinite", variables]

useInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

export const useSuspenseInfiniteMarketplaceSitesViewQuery = <
  TData = InfiniteData<Types.MarketplaceSitesViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSitesView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesViewQuery,
            Types.MarketplaceSitesViewQueryVariables
          >(MarketplaceSitesViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesViewQuery.getKey = (
  variables: Types.MarketplaceSitesViewQueryVariables,
) => ["MarketplaceSitesView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSitesViewQuery.rootKey = "MarketplaceSitesView.infinite"

useMarketplaceSitesViewQuery.fetcher = (
  variables: Types.MarketplaceSitesViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesViewQuery,
    Types.MarketplaceSitesViewQueryVariables
  >(MarketplaceSitesViewDocument, variables, options)

export const MarketplaceSitesRowsDocument = `
    query MarketplaceSitesRows($orgSlug: String!, $keys: [Int!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageSites {
      rows(keys: $keys) {
        id
        sitePid
        bidPackagePid
        bidPackageName
        name
        city
        state
        zipCode
        streetAddress
        county
        cbsaName
        assetManager
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        buildingType
        yearBuilt
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        numUnitsTotal
        buildingClass
        roofReplacedYr
        roofCondition
        salePlans
        buildingLegalName
        propertyFundName
        leaseSignatory
        counselApproval
        leaseApprovers
        externalLeaseApprovers
        needsTenantApproval
        roofRightsHolder
        propertyLenders
        propertyInsurer
        constructionEmail
        propertySiteVisitContacts
        leasePropertyLegalDescription
        leaseLegalOwnerName
        signatureBlocks
        isFannieMaeLender
        isFmGlobalInsurer
        financierCount
        bidCount
        ppaNoiMin
        ppaNoiAvg
        ppaNoiMax
        csNoiMin
        csNoiAvg
        csNoiMax
        targetUtility {
          pid
          name
        }
        awardedDeveloper
        awardedBidPvSizeKwDc
        awardedBidPvSizeKwAc
        awardedBidPvTypes
        csEstimatedAwardDate
        estimatedNtp
        estimatedCod
        csNpv
        roofRentY1Usd
        roofRentEscalator
        noiLifetimeUsd
        financingType
      }
    }
  }
}
    `

export const useMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows", variables]

useMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useSuspenseMarketplaceSitesRowsQuery = <
  TData = Types.MarketplaceSitesRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceSitesRowsQuery, TError, TData>({
    queryKey: ["MarketplaceSitesRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceSitesRowsQuery,
      Types.MarketplaceSitesRowsQueryVariables
    >(MarketplaceSitesRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRowsSuspense", variables]

useSuspenseMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows"

export const useInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceSitesRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infinite", variables]

useInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

export const useSuspenseInfiniteMarketplaceSitesRowsQuery = <
  TData = InfiniteData<Types.MarketplaceSitesRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceSitesRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceSitesRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceSitesRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceSitesRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceSitesRowsQuery,
            Types.MarketplaceSitesRowsQueryVariables
          >(MarketplaceSitesRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceSitesRowsQuery.getKey = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
) => ["MarketplaceSitesRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceSitesRowsQuery.rootKey = "MarketplaceSitesRows.infinite"

useMarketplaceSitesRowsQuery.fetcher = (
  variables: Types.MarketplaceSitesRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceSitesRowsQuery,
    Types.MarketplaceSitesRowsQueryVariables
  >(MarketplaceSitesRowsDocument, variables, options)

export const MarketplaceBidsViewDocument = `
    query MarketplaceBidsView($orgSlug: String!, $filterBy: InvestorBidsTableFilter!, $sortBy: [InvestorBidsTableSort!]) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsView", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView", variables]

useMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useSuspenseMarketplaceBidsViewQuery = <
  TData = Types.MarketplaceBidsViewQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsViewQuery, TError, TData>({
    queryKey: ["MarketplaceBidsViewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsViewQuery,
      Types.MarketplaceBidsViewQueryVariables
    >(MarketplaceBidsViewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsViewSuspense", variables]

useSuspenseMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView"

export const useInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infinite", variables]

useInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

export const useSuspenseInfiniteMarketplaceBidsViewQuery = <
  TData = InfiniteData<Types.MarketplaceBidsViewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsViewQuery,
            Types.MarketplaceBidsViewQueryVariables
          >(MarketplaceBidsViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsViewQuery.getKey = (
  variables: Types.MarketplaceBidsViewQueryVariables,
) => ["MarketplaceBidsView.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsViewQuery.rootKey = "MarketplaceBidsView.infinite"

useMarketplaceBidsViewQuery.fetcher = (
  variables: Types.MarketplaceBidsViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsViewQuery,
    Types.MarketplaceBidsViewQueryVariables
  >(MarketplaceBidsViewDocument, variables, options)

export const MarketplaceBidsFacetsDocument = `
    query MarketplaceBidsFacets($orgSlug: String!, $filterBy: InvestorBidsTableFilter!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      view(filterBy: $filterBy) {
        stats {
          financiers
          siteNames
          siteStates
          siteStreetAddresses
        }
      }
    }
  }
}
    `

export const useMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacets", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets", variables]

useMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useSuspenseMarketplaceBidsFacetsQuery = <
  TData = Types.MarketplaceBidsFacetsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsFacetsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsFacetsQuery,
      Types.MarketplaceBidsFacetsQueryVariables
    >(MarketplaceBidsFacetsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacetsSuspense", variables]

useSuspenseMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets"

export const useInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infinite", variables]

useInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

export const useSuspenseInfiniteMarketplaceBidsFacetsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsFacetsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsFacets.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsFacetsQuery,
            Types.MarketplaceBidsFacetsQueryVariables
          >(MarketplaceBidsFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsFacetsQuery.getKey = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
) => ["MarketplaceBidsFacets.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsFacetsQuery.rootKey = "MarketplaceBidsFacets.infinite"

useMarketplaceBidsFacetsQuery.fetcher = (
  variables: Types.MarketplaceBidsFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsFacetsQuery,
    Types.MarketplaceBidsFacetsQueryVariables
  >(MarketplaceBidsFacetsDocument, variables, options)

export const MarketplaceBidsRowsDocument = `
    query MarketplaceBidsRows($orgSlug: String!, $keys: [String!]!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackageBids {
      rows(keys: $keys) {
        pid
        bidName
        sitePid
        bundleRowPid
        bundleParentPid
        name
        streetAddress
        city
        state
        zipCode
        latitude
        longitude
        county
        cbsaName
        assetManager
        packageRowIsClosed
        packageRowStatus
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        isAwardedBid
        isRecommendedBid
        bidPackagePid
        bidPackageName
        biddingOpenAt
        biddingClosedAt
        type
        csEstimatedAwardDate
        estimatedCod
        estimatedNtp
        itcPercentageAssumption
        interconnectionCostUsdPerWatt
        interconnectionCostUtilityMinUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostNonUtilityUsd
        interconnectionNotes
        interconnectionRepriceProcessNotes
        incentivesNotes
        otherNotes
        termYears
        recOwner
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        roofReplacementYear
        csRoofRentEscalator
        csRoofRentY1Usd
        csBuildingOwnerNoiLifetimeUsd
        csRoofRentNpv
        systemLite {
          id
          ...SystemLite
        }
        ppaRoofRentEscalator
        ppaRoofRentY1Usd
        ppaPriceEscalator
        ppaPriceY1Usd
        cashTotalPvCostUsd
        cashTotalStorageCostUsd
        cashNpv
        cashPostTaxItcDeprIrr
        system {
          id
          ...System
        }
        financierPid
        financierName
        financierLogoUrl
        financierLogomarkUrl
        ppaBuildingOwnerNoiLifetimeUsd
        ppaOfftakerLifetimeSavingsUsd
        ppaYearOneOfftakerSavingsUsd
        ppaYearOneRoofRentalFee
        itcAdders
        itcObbbStrategy
        itcObbbCustomStrategy
        recValueUsdPerMwh
        estimatedIxApproval
        csSubscriberDiscountPct
        csFtmProgramName
      }
    }
  }
}
    ${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${SystemFragmentDoc}`

export const useMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRows", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows", variables]

useMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useSuspenseMarketplaceBidsRowsQuery = <
  TData = Types.MarketplaceBidsRowsQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidsRowsQuery, TError, TData>({
    queryKey: ["MarketplaceBidsRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidsRowsQuery,
      Types.MarketplaceBidsRowsQueryVariables
    >(MarketplaceBidsRowsDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRowsSuspense", variables]

useSuspenseMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows"

export const useInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidsRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infinite", variables]

useInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

export const useSuspenseInfiniteMarketplaceBidsRowsQuery = <
  TData = InfiniteData<Types.MarketplaceBidsRowsQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidsRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidsRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidsRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidsRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidsRowsQuery,
            Types.MarketplaceBidsRowsQueryVariables
          >(MarketplaceBidsRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidsRowsQuery.getKey = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
) => ["MarketplaceBidsRows.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidsRowsQuery.rootKey = "MarketplaceBidsRows.infinite"

useMarketplaceBidsRowsQuery.fetcher = (
  variables: Types.MarketplaceBidsRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidsRowsQuery,
    Types.MarketplaceBidsRowsQueryVariables
  >(MarketplaceBidsRowsDocument, variables, options)

export const MarketplacePackageDetailDocument = `
    query MarketplacePackageDetail($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
    }
  }
}
    ${BidPackageDetailFragmentDoc}
${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}
${PackageDocumentFragmentDoc}`

export const useMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail", variables]

useMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useSuspenseMarketplacePackageDetailQuery = <
  TData = Types.MarketplacePackageDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageDetailQuery,
      Types.MarketplacePackageDetailQueryVariables
    >(MarketplacePackageDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetailSuspense", variables]

useSuspenseMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail"

export const useInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplacePackageDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infinite", variables]

useInfiniteMarketplacePackageDetailQuery.rootKey = "MarketplacePackageDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplacePackageDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplacePackageDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageDetailQuery,
            Types.MarketplacePackageDetailQueryVariables
          >(MarketplacePackageDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageDetailQuery.getKey = (
  variables: Types.MarketplacePackageDetailQueryVariables,
) => ["MarketplacePackageDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageDetailQuery.rootKey =
  "MarketplacePackageDetail.infinite"

useMarketplacePackageDetailQuery.fetcher = (
  variables: Types.MarketplacePackageDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageDetailQuery,
    Types.MarketplacePackageDetailQueryVariables
  >(MarketplacePackageDetailDocument, variables, options)

export const MarketplacePackageBidReviewDocument = `
    query MarketplacePackageBidReview($orgSlug: String!, $bundlePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      bidReview {
        ...BidReview
      }
    }
  }
}
    ${BidReviewFragmentDoc}
${BidReportFragmentDoc}
${CsSiteBidReportFragmentDoc}
${InvestorProfileFragmentDoc}
${BoxplotStatsFragmentDoc}
${PpaSiteBidReportFragmentDoc}
${CashSiteBidReportFragmentDoc}`

export const useMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReview", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview", variables]

useMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useSuspenseMarketplacePackageBidReviewQuery = <
  TData = Types.MarketplacePackageBidReviewQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>({
    queryKey: ["MarketplacePackageBidReviewSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageBidReviewQuery,
      Types.MarketplacePackageBidReviewQueryVariables
    >(MarketplacePackageBidReviewDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReviewSuspense", variables]

useSuspenseMarketplacePackageBidReviewQuery.rootKey = "MarketplacePackageBidReview"

export const useInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageBidReviewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageBidReviewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infinite", variables]

useInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

export const useSuspenseInfiniteMarketplacePackageBidReviewQuery = <
  TData = InfiniteData<Types.MarketplacePackageBidReviewQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageBidReviewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageBidReviewQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageBidReview.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageBidReviewQuery,
            Types.MarketplacePackageBidReviewQueryVariables
          >(MarketplacePackageBidReviewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageBidReviewQuery.getKey = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
) => ["MarketplacePackageBidReview.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageBidReviewQuery.rootKey =
  "MarketplacePackageBidReview.infinite"

useMarketplacePackageBidReviewQuery.fetcher = (
  variables: Types.MarketplacePackageBidReviewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageBidReviewQuery,
    Types.MarketplacePackageBidReviewQueryVariables
  >(MarketplacePackageBidReviewDocument, variables, options)

export const MarketplacePackageRowDetailDocument = `
    query MarketplacePackageRowDetail($orgSlug: String!, $bundlePid: String!, $sitePid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bidPackage(pid: $bundlePid) {
      ...BidPackageDetail
      row(pid: $sitePid) {
        status
        closedAt
        closeReason
        closeCustomReason
        site {
          pid
          name
          streetAddress
          city
          state
          zipCode
          county
          fullAddress
          targetUtility {
            name
          }
          buildingType
          yearBuilt
          sqft
          numStories
          buildingClass
          roofSqft
          siteDetails {
            roofReplacedYr
            roofCondition
          }
          roofMaxPvKw
          meterSet {
            loadKwh
            totalCost
          }
        }
      }
    }
  }
}
    ${BidPackageDetailFragmentDoc}
${BasicBidPackageFragmentDoc}
${InvestorProfileFragmentDoc}
${PackageDocumentFragmentDoc}`

export const useMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail", variables]

useMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useSuspenseMarketplacePackageRowDetailQuery = <
  TData = Types.MarketplacePackageRowDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>({
    queryKey: ["MarketplacePackageRowDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplacePackageRowDetailQuery,
      Types.MarketplacePackageRowDetailQueryVariables
    >(MarketplacePackageRowDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetailSuspense", variables]

useSuspenseMarketplacePackageRowDetailQuery.rootKey = "MarketplacePackageRowDetail"

export const useInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplacePackageRowDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplacePackageRowDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infinite", variables]

useInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

export const useSuspenseInfiniteMarketplacePackageRowDetailQuery = <
  TData = InfiniteData<Types.MarketplacePackageRowDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplacePackageRowDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.MarketplacePackageRowDetailQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplacePackageRowDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplacePackageRowDetailQuery,
            Types.MarketplacePackageRowDetailQueryVariables
          >(MarketplacePackageRowDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplacePackageRowDetailQuery.getKey = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
) => ["MarketplacePackageRowDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplacePackageRowDetailQuery.rootKey =
  "MarketplacePackageRowDetail.infinite"

useMarketplacePackageRowDetailQuery.fetcher = (
  variables: Types.MarketplacePackageRowDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplacePackageRowDetailQuery,
    Types.MarketplacePackageRowDetailQueryVariables
  >(MarketplacePackageRowDetailDocument, variables, options)

export const MarketplaceBidDetailDocument = `
    query MarketplaceBidDetail($orgSlug: String!, $bidPid: String!) {
  org: orgBySlug(slug: $orgSlug) {
    bid(pid: $bidPid) {
      ...InvestorBid
      ppa {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
      cs {
        site {
          ...BidRevampSite
        }
        financier {
          pid
          name
          orgSlug
          logoUrl
          logomarkUrl
        }
      }
      cash {
        site {
          ...BidRevampSite
        }
      }
    }
  }
}
    ${InvestorBidFragmentDoc}
${PpaInvestorBidFragmentDoc}
${InvestorProfileFragmentDoc}
${SystemFragmentDoc}
${PvSystemSpecFragmentDoc}
${RevampFinmodelFragmentDoc}
${KwhFlowFragmentDoc}
${RevampSystemFragmentDoc}
${FinmodelCostFragmentDoc}
${CsInvestorBidFragmentDoc}
${SystemLiteFragmentDoc}
${RevampCsFragmentDoc}
${CashInvestorBidFragmentDoc}
${BidRevampSiteFragmentDoc}`

export const useMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetail", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail", variables]

useMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useSuspenseMarketplaceBidDetailQuery = <
  TData = Types.MarketplaceBidDetailQuery,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.MarketplaceBidDetailQuery, TError, TData>({
    queryKey: ["MarketplaceBidDetailSuspense", variables],
    queryFn: publicFetcher<
      Types.MarketplaceBidDetailQuery,
      Types.MarketplaceBidDetailQueryVariables
    >(MarketplaceBidDetailDocument, variables),
    ...options,
  })
}

useSuspenseMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetailSuspense", variables]

useSuspenseMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail"

export const useInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["MarketplaceBidDetail.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infinite", variables]

useInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

export const useSuspenseInfiniteMarketplaceBidDetailQuery = <
  TData = InfiniteData<Types.MarketplaceBidDetailQuery>,
  TError = unknown,
>(
  variables: Types.MarketplaceBidDetailQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.MarketplaceBidDetailQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.MarketplaceBidDetailQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.MarketplaceBidDetailQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "MarketplaceBidDetail.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.MarketplaceBidDetailQuery,
            Types.MarketplaceBidDetailQueryVariables
          >(MarketplaceBidDetailDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteMarketplaceBidDetailQuery.getKey = (
  variables: Types.MarketplaceBidDetailQueryVariables,
) => ["MarketplaceBidDetail.infiniteSuspense", variables]

useSuspenseInfiniteMarketplaceBidDetailQuery.rootKey = "MarketplaceBidDetail.infinite"

useMarketplaceBidDetailQuery.fetcher = (
  variables: Types.MarketplaceBidDetailQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.MarketplaceBidDetailQuery,
    Types.MarketplaceBidDetailQueryVariables
  >(MarketplaceBidDetailDocument, variables, options)

export const CurrentUserGoogleAccessDocument = `
    query CurrentUserGoogleAccess {
  currentUser {
    pid
    hasGoogleAccessToken
  }
}
    `

export const useCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccess"]
        : ["CurrentUserGoogleAccess", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess"]
    : ["CurrentUserGoogleAccess", variables]

useCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useSuspenseCurrentUserGoogleAccessQuery = <
  TData = Types.CurrentUserGoogleAccessQuery,
  TError = unknown,
>(
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["CurrentUserGoogleAccessSuspense"]
        : ["CurrentUserGoogleAccessSuspense", variables],
    queryFn: publicFetcher<
      Types.CurrentUserGoogleAccessQuery,
      Types.CurrentUserGoogleAccessQueryVariables
    >(CurrentUserGoogleAccessDocument, variables),
    ...options,
  })
}

useSuspenseCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccessSuspense"]
    : ["CurrentUserGoogleAccessSuspense", variables]

useSuspenseCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess"

export const useInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infinite"]
            : ["CurrentUserGoogleAccess.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infinite"]
    : ["CurrentUserGoogleAccess.infinite", variables]

useInfiniteCurrentUserGoogleAccessQuery.rootKey = "CurrentUserGoogleAccess.infinite"

export const useSuspenseInfiniteCurrentUserGoogleAccessQuery = <
  TData = InfiniteData<Types.CurrentUserGoogleAccessQuery>,
  TError = unknown,
>(
  variables: Types.CurrentUserGoogleAccessQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CurrentUserGoogleAccessQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CurrentUserGoogleAccessQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CurrentUserGoogleAccessQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["CurrentUserGoogleAccess.infiniteSuspense"]
            : ["CurrentUserGoogleAccess.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CurrentUserGoogleAccessQuery,
            Types.CurrentUserGoogleAccessQueryVariables
          >(CurrentUserGoogleAccessDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCurrentUserGoogleAccessQuery.getKey = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
) =>
  variables === undefined
    ? ["CurrentUserGoogleAccess.infiniteSuspense"]
    : ["CurrentUserGoogleAccess.infiniteSuspense", variables]

useSuspenseInfiniteCurrentUserGoogleAccessQuery.rootKey =
  "CurrentUserGoogleAccess.infinite"

useCurrentUserGoogleAccessQuery.fetcher = (
  variables?: Types.CurrentUserGoogleAccessQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CurrentUserGoogleAccessQuery,
    Types.CurrentUserGoogleAccessQueryVariables
  >(CurrentUserGoogleAccessDocument, variables, options)

export const AwardedProjectsTableViewDocument = `
    query AwardedProjectsTableView($orgSlug: String!, $filterBy: AwardedProjectTableFilter, $sortBy: [AwardedProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    awardedProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
        stats {
          buildingOwners
        }
      }
    }
  }
}
    `

export const useAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView", variables]

useAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useSuspenseAwardedProjectsTableViewQuery = <
  TData = Types.AwardedProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableViewQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableViewQuery,
      Types.AwardedProjectsTableViewQueryVariables
    >(AwardedProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableViewSuspense", variables]

useSuspenseAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView"

export const useInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infinite", variables]

useInfiniteAwardedProjectsTableViewQuery.rootKey = "AwardedProjectsTableView.infinite"

export const useSuspenseInfiniteAwardedProjectsTableViewQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AwardedProjectsTableView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableViewQuery,
            Types.AwardedProjectsTableViewQueryVariables
          >(AwardedProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableViewQuery.getKey = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
) => ["AwardedProjectsTableView.infiniteSuspense", variables]

useSuspenseInfiniteAwardedProjectsTableViewQuery.rootKey =
  "AwardedProjectsTableView.infinite"

useAwardedProjectsTableViewQuery.fetcher = (
  variables: Types.AwardedProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableViewQuery,
    Types.AwardedProjectsTableViewQueryVariables
  >(AwardedProjectsTableViewDocument, variables, options)

export const AwardedProjectsTableRowsDocument = `
    query AwardedProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    awardedProjects {
      rows(keys: $keys) {
        id
        orgName
        projectPid
        sitePid
        bundlePid
        name
        streetAddress
        city
        state
        zipCode
        projectName
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        lumenRevenue
        finproductType
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        rofoSignedDate
        rofoSignedEstimated
        rofoSignedActual
        preBidMeetingDate
        preBidMeetingEstimated
        preBidMeetingActual
        biddingOpenedDate
        biddingOpenedEstimated
        biddingOpenedActual
        biddingClosedDate
        biddingClosedEstimated
        biddingClosedActual
        bidsPresentedDate
        bidsPresentedEstimated
        bidsPresentedActual
        ixSubmittedDate
        ixSubmittedEstimated
        ixSubmittedActual
        ixApprovedDate
        ixApprovedEstimated
        ixApprovedActual
        csProgramSubmittedDate
        csProgramSubmittedEstimated
        csProgramSubmittedActual
        csProgramApprovedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        awardedToDeveloperDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        leaseSignedDate
        leaseSignedEstimated
        leaseSignedActual
        ntpDate
        ntpEstimated
        ntpActual
        codDate
        codEstimated
        codActual
      }
    }
  }
}
    `

export const useAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows", variables]

useAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useSuspenseAwardedProjectsTableRowsQuery = <
  TData = Types.AwardedProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>({
    queryKey: ["AwardedProjectsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.AwardedProjectsTableRowsQuery,
      Types.AwardedProjectsTableRowsQueryVariables
    >(AwardedProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRowsSuspense", variables]

useSuspenseAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows"

export const useInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AwardedProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infinite", variables]

useInfiniteAwardedProjectsTableRowsQuery.rootKey = "AwardedProjectsTableRows.infinite"

export const useSuspenseInfiniteAwardedProjectsTableRowsQuery = <
  TData = InfiniteData<Types.AwardedProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AwardedProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AwardedProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AwardedProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "AwardedProjectsTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AwardedProjectsTableRowsQuery,
            Types.AwardedProjectsTableRowsQueryVariables
          >(AwardedProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAwardedProjectsTableRowsQuery.getKey = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
) => ["AwardedProjectsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteAwardedProjectsTableRowsQuery.rootKey =
  "AwardedProjectsTableRows.infinite"

useAwardedProjectsTableRowsQuery.fetcher = (
  variables: Types.AwardedProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.AwardedProjectsTableRowsQuery,
    Types.AwardedProjectsTableRowsQueryVariables
  >(AwardedProjectsTableRowsDocument, variables, options)

export const OwnerProjectsTableViewDocument = `
    query OwnerProjectsTableView($orgSlug: String!, $filterBy: OwnerProjectTableFilter, $sortBy: [OwnerProjectTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    pid
    name
    ownerProjects {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useOwnerProjectsTableViewQuery = <
  TData = Types.OwnerProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableViewQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableView", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableViewQuery,
      Types.OwnerProjectsTableViewQueryVariables
    >(OwnerProjectsTableViewDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView", variables]

useOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView"

export const useSuspenseOwnerProjectsTableViewQuery = <
  TData = Types.OwnerProjectsTableViewQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableViewQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableViewQuery,
      Types.OwnerProjectsTableViewQueryVariables
    >(OwnerProjectsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableViewSuspense", variables]

useSuspenseOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView"

export const useInfiniteOwnerProjectsTableViewQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableViewQuery,
            Types.OwnerProjectsTableViewQueryVariables
          >(OwnerProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView.infinite", variables]

useInfiniteOwnerProjectsTableViewQuery.rootKey = "OwnerProjectsTableView.infinite"

export const useSuspenseInfiniteOwnerProjectsTableViewQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "OwnerProjectsTableView.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableViewQuery,
            Types.OwnerProjectsTableViewQueryVariables
          >(OwnerProjectsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableViewQuery.getKey = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
) => ["OwnerProjectsTableView.infiniteSuspense", variables]

useSuspenseInfiniteOwnerProjectsTableViewQuery.rootKey =
  "OwnerProjectsTableView.infinite"

useOwnerProjectsTableViewQuery.fetcher = (
  variables: Types.OwnerProjectsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableViewQuery,
    Types.OwnerProjectsTableViewQueryVariables
  >(OwnerProjectsTableViewDocument, variables, options)

export const OwnerProjectsTableFacetsDocument = `
    query OwnerProjectsTableFacets($orgSlug: String!) {
  orgBySlug(slug: $orgSlug) {
    facets: ownerProjects {
      view {
        count
        stats {
          awardedDevelopers
          states
        }
      }
    }
    PREPARING_LAUNCH: ownerProjects {
      view(
        filterBy: {projectStatus: {value: PREPARING_LAUNCH}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    VETTING_INTEREST: ownerProjects {
      view(
        filterBy: {projectStatus: {value: VETTING_INTEREST}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    COLLECTING_BIDS: ownerProjects {
      view(
        filterBy: {projectStatus: {value: COLLECTING_BIDS}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    REVIEWING_BIDS: ownerProjects {
      view(
        filterBy: {projectStatus: {value: REVIEWING_BIDS}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    NEGOTIATING_CONTRACTS: ownerProjects {
      view(
        filterBy: {projectStatus: {value: NEGOTIATING_CONTRACTS}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    DEVELOPING_PROJECT: ownerProjects {
      view(
        filterBy: {projectStatus: {value: DEVELOPING_PROJECT}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
    SHIPPED: ownerProjects {
      view(
        filterBy: {projectStatus: {value: SHIPPED}, projectIsClosed: {value: false}}
      ) {
        count
      }
    }
  }
}
    `

export const useOwnerProjectsTableFacetsQuery = <
  TData = Types.OwnerProjectsTableFacetsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableFacets", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableFacetsQuery,
      Types.OwnerProjectsTableFacetsQueryVariables
    >(OwnerProjectsTableFacetsDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets", variables]

useOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets"

export const useSuspenseOwnerProjectsTableFacetsQuery = <
  TData = Types.OwnerProjectsTableFacetsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableFacetsSuspense", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableFacetsQuery,
      Types.OwnerProjectsTableFacetsQueryVariables
    >(OwnerProjectsTableFacetsDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacetsSuspense", variables]

useSuspenseOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets"

export const useInfiniteOwnerProjectsTableFacetsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableFacetsQuery,
            Types.OwnerProjectsTableFacetsQueryVariables
          >(OwnerProjectsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets.infinite", variables]

useInfiniteOwnerProjectsTableFacetsQuery.rootKey = "OwnerProjectsTableFacets.infinite"

export const useSuspenseInfiniteOwnerProjectsTableFacetsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "OwnerProjectsTableFacets.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableFacetsQuery,
            Types.OwnerProjectsTableFacetsQueryVariables
          >(OwnerProjectsTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableFacetsQuery.getKey = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
) => ["OwnerProjectsTableFacets.infiniteSuspense", variables]

useSuspenseInfiniteOwnerProjectsTableFacetsQuery.rootKey =
  "OwnerProjectsTableFacets.infinite"

useOwnerProjectsTableFacetsQuery.fetcher = (
  variables: Types.OwnerProjectsTableFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableFacetsQuery,
    Types.OwnerProjectsTableFacetsQueryVariables
  >(OwnerProjectsTableFacetsDocument, variables, options)

export const OwnerProjectsTableRowsDocument = `
    query OwnerProjectsTableRows($orgSlug: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    name
    ownerProjects {
      rows(keys: $keys) {
        id
        developerName
        projectPid
        sitePid
        name
        projectName
        streetAddress
        city
        state
        zipCode
        projectStatus
        projectIsClosed
        projectClosedAt
        projectCloseReason
        projectCloseCustomReason
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        utilityName
        buildingType
        sqft
        buildingFootprintSqft
        roofSqft
        usableRoofSqft
        numStories
        buildingClass
        yearBuilt
        assetManager
        roofReplacedYr
        roofCondition
        projectValueY1Usd
        roofRentY1Usd
        roofRentEscalator
        ppaPriceEscalator
        ppaPriceY1Usd
        termYears
        financingType
        specFinproductType
        awardedToDeveloperDate
        awardedToDeveloperEstimated
        awardedToDeveloperActual
        leaseSignedDate
        leaseSignedEstimated
        leaseSignedActual
        ntpDate
        ntpEstimated
        ntpActual
        codDate
        codEstimated
        codActual
        csProgramApprovedDate
        csProgramApprovedEstimated
        csProgramApprovedActual
        ixApprovedDate
        ixApprovedEstimated
        ixApprovedActual
        bidPackagePid
        bidPackageName
        storageSizeKwh
        progressNotesPid
        progressNotes
        hasAction
        progressActionItem
        progressActionItemUpdatedAt
        progressPriority
        isFannieMaeLender
        propertyLenders
        isFmGlobalInsurer
        propertyInsurer
        propertyFundName
        leaseSignatory
      }
    }
  }
}
    `

export const useOwnerProjectsTableRowsQuery = <
  TData = Types.OwnerProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableRows", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableRowsQuery,
      Types.OwnerProjectsTableRowsQueryVariables
    >(OwnerProjectsTableRowsDocument, variables),
    ...options,
  })
}

useOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows", variables]

useOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows"

export const useSuspenseOwnerProjectsTableRowsQuery = <
  TData = Types.OwnerProjectsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>({
    queryKey: ["OwnerProjectsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.OwnerProjectsTableRowsQuery,
      Types.OwnerProjectsTableRowsQueryVariables
    >(OwnerProjectsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRowsSuspense", variables]

useSuspenseOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows"

export const useInfiniteOwnerProjectsTableRowsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["OwnerProjectsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableRowsQuery,
            Types.OwnerProjectsTableRowsQueryVariables
          >(OwnerProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows.infinite", variables]

useInfiniteOwnerProjectsTableRowsQuery.rootKey = "OwnerProjectsTableRows.infinite"

export const useSuspenseInfiniteOwnerProjectsTableRowsQuery = <
  TData = InfiniteData<Types.OwnerProjectsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.OwnerProjectsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.OwnerProjectsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.OwnerProjectsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "OwnerProjectsTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.OwnerProjectsTableRowsQuery,
            Types.OwnerProjectsTableRowsQueryVariables
          >(OwnerProjectsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteOwnerProjectsTableRowsQuery.getKey = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
) => ["OwnerProjectsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteOwnerProjectsTableRowsQuery.rootKey =
  "OwnerProjectsTableRows.infinite"

useOwnerProjectsTableRowsQuery.fetcher = (
  variables: Types.OwnerProjectsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.OwnerProjectsTableRowsQuery,
    Types.OwnerProjectsTableRowsQueryVariables
  >(OwnerProjectsTableRowsDocument, variables, options)

export const SiteBidsTableViewDocument = `
    query SiteBidsTableView($orgSlug: String!, $sitePid: String!, $filterBy: SiteBidsTableFilter, $sortBy: [SiteBidsTableSort!]) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableView", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView", variables]

useSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useSuspenseSiteBidsTableViewQuery = <
  TData = Types.SiteBidsTableViewQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableViewQuery, TError, TData>({
    queryKey: ["SiteBidsTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableViewQuery,
      Types.SiteBidsTableViewQueryVariables
    >(SiteBidsTableViewDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableViewSuspense", variables]

useSuspenseSiteBidsTableViewQuery.rootKey = "SiteBidsTableView"

export const useInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infinite", variables]

useInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

export const useSuspenseInfiniteSiteBidsTableViewQuery = <
  TData = InfiniteData<Types.SiteBidsTableViewQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableViewQuery,
            Types.SiteBidsTableViewQueryVariables
          >(SiteBidsTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableViewQuery.getKey = (
  variables: Types.SiteBidsTableViewQueryVariables,
) => ["SiteBidsTableView.infiniteSuspense", variables]

useSuspenseInfiniteSiteBidsTableViewQuery.rootKey = "SiteBidsTableView.infinite"

useSiteBidsTableViewQuery.fetcher = (
  variables: Types.SiteBidsTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableViewQuery, Types.SiteBidsTableViewQueryVariables>(
    SiteBidsTableViewDocument,
    variables,
    options,
  )

export const SiteBidsTableRowsDocument = `
    query SiteBidsTableRows($orgSlug: String!, $sitePid: String!, $keys: [Int!]!) {
  orgBySlug(slug: $orgSlug) {
    siteBids(sitePid: $sitePid) {
      rows(keys: $keys) {
        id
        bidPackagePid
        bundlePid
        bidPackageName
        name
        isAwarded
        isCustom
        packageRowStatus
        packageRowIsClosed
        packageRowClosedAt
        packageRowCloseReason
        packageRowCloseCustomReason
        estimatedCod
        estimatedNtp
        csEstimatedAwardDate
        pvSizeKwDc
        pvSizeKwAc
        pvTypes
        termYears
        roofRentY1Usd
        roofRentEscalator
        buildingOwnerExtraPaymentsUsd
        extraPaymentsNotes
        buildingOwnerNoiLifetimeUsd
        interconnectionCostUtilityMaxUsd
        interconnectionCostUtilityMinUsd
        interconnectionCostNonUtilityUsd
        interconnectionCostUsdPerWatt
        interconnectionNotes
        itcPercentageAssumption
        recOwner
        incentivesNotes
        otherNotes
      }
    }
  }
}
    `

export const useSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRows", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows", variables]

useSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useSuspenseSiteBidsTableRowsQuery = <
  TData = Types.SiteBidsTableRowsQuery,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteBidsTableRowsQuery, TError, TData>({
    queryKey: ["SiteBidsTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteBidsTableRowsQuery,
      Types.SiteBidsTableRowsQueryVariables
    >(SiteBidsTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRowsSuspense", variables]

useSuspenseSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows"

export const useInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infinite", variables]

useInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

export const useSuspenseInfiniteSiteBidsTableRowsQuery = <
  TData = InfiniteData<Types.SiteBidsTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.SiteBidsTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteBidsTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteBidsTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteBidsTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteBidsTableRows.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteBidsTableRowsQuery,
            Types.SiteBidsTableRowsQueryVariables
          >(SiteBidsTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteBidsTableRowsQuery.getKey = (
  variables: Types.SiteBidsTableRowsQueryVariables,
) => ["SiteBidsTableRows.infiniteSuspense", variables]

useSuspenseInfiniteSiteBidsTableRowsQuery.rootKey = "SiteBidsTableRows.infinite"

useSiteBidsTableRowsQuery.fetcher = (
  variables: Types.SiteBidsTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteBidsTableRowsQuery, Types.SiteBidsTableRowsQueryVariables>(
    SiteBidsTableRowsDocument,
    variables,
    options,
  )

export const SiteFieldChangeLogDocument = `
    query SiteFieldChangeLog($orgSlug: String!, $sitePid: String!) {
  orgBySlug(slug: $orgSlug) {
    slug
    name
    pid
    site(pid: $sitePid) {
      pid
      fieldHistory {
        ...SiteFieldChangeLog
      }
      siteDetails {
        fieldHistory {
          ...SiteDetailsFieldChangeLog
        }
      }
    }
  }
}
    ${SiteFieldChangeLogFragmentDoc}
${FieldChangeLogHistoryFragmentDoc}
${ChangeLogEntryFragmentDoc}
${SiteDetailsFieldChangeLogFragmentDoc}`

export const useSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLog", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog", variables]

useSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useSuspenseSiteFieldChangeLogQuery = <
  TData = Types.SiteFieldChangeLogQuery,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteFieldChangeLogQuery, TError, TData>({
    queryKey: ["SiteFieldChangeLogSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteFieldChangeLogQuery,
      Types.SiteFieldChangeLogQueryVariables
    >(SiteFieldChangeLogDocument, variables),
    ...options,
  })
}

useSuspenseSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLogSuspense", variables]

useSuspenseSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog"

export const useInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infinite", variables]

useInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

export const useSuspenseInfiniteSiteFieldChangeLogQuery = <
  TData = InfiniteData<Types.SiteFieldChangeLogQuery>,
  TError = unknown,
>(
  variables: Types.SiteFieldChangeLogQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteFieldChangeLogQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteFieldChangeLogQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteFieldChangeLogQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteFieldChangeLog.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteFieldChangeLogQuery,
            Types.SiteFieldChangeLogQueryVariables
          >(SiteFieldChangeLogDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteFieldChangeLogQuery.getKey = (
  variables: Types.SiteFieldChangeLogQueryVariables,
) => ["SiteFieldChangeLog.infiniteSuspense", variables]

useSuspenseInfiniteSiteFieldChangeLogQuery.rootKey = "SiteFieldChangeLog.infinite"

useSiteFieldChangeLogQuery.fetcher = (
  variables: Types.SiteFieldChangeLogQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteFieldChangeLogQuery, Types.SiteFieldChangeLogQueryVariables>(
    SiteFieldChangeLogDocument,
    variables,
    options,
  )

export const AdminUsersDocument = `
    query AdminUsers {
  admin {
    activeAdmins {
      ...UserRef
    }
  }
}
    ${UserRefFragmentDoc}`

export const useAdminUsersQuery = <TData = Types.AdminUsersQuery, TError = unknown>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<UseQueryOptions<Types.AdminUsersQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey: variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers"] : ["AdminUsers", variables]

useAdminUsersQuery.rootKey = "AdminUsers"

export const useSuspenseAdminUsersQuery = <
  TData = Types.AdminUsersQuery,
  TError = unknown,
>(
  variables?: Types.AdminUsersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminUsersQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AdminUsersSuspense"]
        : ["AdminUsersSuspense", variables],
    queryFn: publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
      AdminUsersDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsersSuspense"] : ["AdminUsersSuspense", variables]

useSuspenseAdminUsersQuery.rootKey = "AdminUsers"

export const useInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infinite"]
            : ["AdminUsers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminUsersQuery.getKey = (variables?: Types.AdminUsersQueryVariables) =>
  variables === undefined ? ["AdminUsers.infinite"] : ["AdminUsers.infinite", variables]

useInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

export const useSuspenseInfiniteAdminUsersQuery = <
  TData = InfiniteData<Types.AdminUsersQuery>,
  TError = unknown,
>(
  variables: Types.AdminUsersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminUsersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminUsersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminUsersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AdminUsers.infiniteSuspense"]
            : ["AdminUsers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
            AdminUsersDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminUsersQuery.getKey = (
  variables?: Types.AdminUsersQueryVariables,
) =>
  variables === undefined
    ? ["AdminUsers.infiniteSuspense"]
    : ["AdminUsers.infiniteSuspense", variables]

useSuspenseInfiniteAdminUsersQuery.rootKey = "AdminUsers.infinite"

useAdminUsersQuery.fetcher = (
  variables?: Types.AdminUsersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminUsersQuery, Types.AdminUsersQueryVariables>(
    AdminUsersDocument,
    variables,
    options,
  )

export const CollectingBidsNoticeDocument = `
    query CollectingBidsNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
  }
  orgBySlug(slug: $orgSlug) {
    orgUsers {
      ...OrgUser
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNotice", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice", variables]

useCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useSuspenseCollectingBidsNoticeQuery = <
  TData = Types.CollectingBidsNoticeQuery,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.CollectingBidsNoticeQuery, TError, TData>({
    queryKey: ["CollectingBidsNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.CollectingBidsNoticeQuery,
      Types.CollectingBidsNoticeQueryVariables
    >(CollectingBidsNoticeDocument, variables),
    ...options,
  })
}

useSuspenseCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNoticeSuspense", variables]

useSuspenseCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice"

export const useInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["CollectingBidsNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infinite", variables]

useInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

export const useSuspenseInfiniteCollectingBidsNoticeQuery = <
  TData = InfiniteData<Types.CollectingBidsNoticeQuery>,
  TError = unknown,
>(
  variables: Types.CollectingBidsNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.CollectingBidsNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.CollectingBidsNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.CollectingBidsNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "CollectingBidsNotice.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.CollectingBidsNoticeQuery,
            Types.CollectingBidsNoticeQueryVariables
          >(CollectingBidsNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteCollectingBidsNoticeQuery.getKey = (
  variables: Types.CollectingBidsNoticeQueryVariables,
) => ["CollectingBidsNotice.infiniteSuspense", variables]

useSuspenseInfiniteCollectingBidsNoticeQuery.rootKey = "CollectingBidsNotice.infinite"

useCollectingBidsNoticeQuery.fetcher = (
  variables: Types.CollectingBidsNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.CollectingBidsNoticeQuery,
    Types.CollectingBidsNoticeQueryVariables
  >(CollectingBidsNoticeDocument, variables, options)

export const LeaseExecutionDetailsRequestDocument = `
    query LeaseExecutionDetailsRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      id
      site {
        pid
        name
      }
      portfolio {
        pid
        title
      }
      awardedFinancier {
        name
      }
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequest", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest", variables]

useLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useSuspenseLeaseExecutionDetailsRequestQuery = <
  TData = Types.LeaseExecutionDetailsRequestQuery,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>({
    queryKey: ["LeaseExecutionDetailsRequestSuspense", variables],
    queryFn: publicFetcher<
      Types.LeaseExecutionDetailsRequestQuery,
      Types.LeaseExecutionDetailsRequestQueryVariables
    >(LeaseExecutionDetailsRequestDocument, variables),
    ...options,
  })
}

useSuspenseLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequestSuspense", variables]

useSuspenseLeaseExecutionDetailsRequestQuery.rootKey = "LeaseExecutionDetailsRequest"

export const useInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.LeaseExecutionDetailsRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.LeaseExecutionDetailsRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infinite",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infinite", variables]

useInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

export const useSuspenseInfiniteLeaseExecutionDetailsRequestQuery = <
  TData = InfiniteData<Types.LeaseExecutionDetailsRequestQuery>,
  TError = unknown,
>(
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.LeaseExecutionDetailsRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<
    Types.LeaseExecutionDetailsRequestQuery,
    TError,
    TData
  >(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "LeaseExecutionDetailsRequest.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.LeaseExecutionDetailsRequestQuery,
            Types.LeaseExecutionDetailsRequestQueryVariables
          >(LeaseExecutionDetailsRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.getKey = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
) => ["LeaseExecutionDetailsRequest.infiniteSuspense", variables]

useSuspenseInfiniteLeaseExecutionDetailsRequestQuery.rootKey =
  "LeaseExecutionDetailsRequest.infinite"

useLeaseExecutionDetailsRequestQuery.fetcher = (
  variables: Types.LeaseExecutionDetailsRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.LeaseExecutionDetailsRequestQuery,
    Types.LeaseExecutionDetailsRequestQueryVariables
  >(LeaseExecutionDetailsRequestDocument, variables, options)

export const DeveloperDiligenceRequestDocument = `
    query DeveloperDiligenceRequest($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      id
      site {
        pid
        name
      }
      awardedFinancier {
        name
      }
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequest", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest", variables]

useDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useSuspenseDeveloperDiligenceRequestQuery = <
  TData = Types.DeveloperDiligenceRequestQuery,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>({
    queryKey: ["DeveloperDiligenceRequestSuspense", variables],
    queryFn: publicFetcher<
      Types.DeveloperDiligenceRequestQuery,
      Types.DeveloperDiligenceRequestQueryVariables
    >(DeveloperDiligenceRequestDocument, variables),
    ...options,
  })
}

useSuspenseDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequestSuspense", variables]

useSuspenseDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest"

export const useInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.DeveloperDiligenceRequestQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["DeveloperDiligenceRequest.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infinite", variables]

useInfiniteDeveloperDiligenceRequestQuery.rootKey = "DeveloperDiligenceRequest.infinite"

export const useSuspenseInfiniteDeveloperDiligenceRequestQuery = <
  TData = InfiniteData<Types.DeveloperDiligenceRequestQuery>,
  TError = unknown,
>(
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.DeveloperDiligenceRequestQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.DeveloperDiligenceRequestQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "DeveloperDiligenceRequest.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.DeveloperDiligenceRequestQuery,
            Types.DeveloperDiligenceRequestQueryVariables
          >(DeveloperDiligenceRequestDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteDeveloperDiligenceRequestQuery.getKey = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
) => ["DeveloperDiligenceRequest.infiniteSuspense", variables]

useSuspenseInfiniteDeveloperDiligenceRequestQuery.rootKey =
  "DeveloperDiligenceRequest.infinite"

useDeveloperDiligenceRequestQuery.fetcher = (
  variables: Types.DeveloperDiligenceRequestQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.DeveloperDiligenceRequestQuery,
    Types.DeveloperDiligenceRequestQueryVariables
  >(DeveloperDiligenceRequestDocument, variables, options)

export const EmailSenderDocument = `
    query EmailSender {
  currentUser {
    firstName
    meetingSchedulingUrl
  }
  googleApi {
    sendAs {
      signature
    }
  }
}
    `

export const useEmailSenderQuery = <TData = Types.EmailSenderQuery, TError = unknown>(
  variables?: Types.EmailSenderQueryVariables,
  options?: Omit<UseQueryOptions<Types.EmailSenderQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.EmailSenderQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.EmailSenderQuery, TError, TData>({
    queryKey: variables === undefined ? ["EmailSender"] : ["EmailSender", variables],
    queryFn: publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
      EmailSenderDocument,
      variables,
    ),
    ...options,
  })
}

useEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined ? ["EmailSender"] : ["EmailSender", variables]

useEmailSenderQuery.rootKey = "EmailSender"

export const useSuspenseEmailSenderQuery = <
  TData = Types.EmailSenderQuery,
  TError = unknown,
>(
  variables?: Types.EmailSenderQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.EmailSenderQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["EmailSenderSuspense"]
        : ["EmailSenderSuspense", variables],
    queryFn: publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
      EmailSenderDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined ? ["EmailSenderSuspense"] : ["EmailSenderSuspense", variables]

useSuspenseEmailSenderQuery.rootKey = "EmailSender"

export const useInfiniteEmailSenderQuery = <
  TData = InfiniteData<Types.EmailSenderQuery>,
  TError = unknown,
>(
  variables: Types.EmailSenderQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.EmailSenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EmailSender.infinite"]
            : ["EmailSender.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
            EmailSenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteEmailSenderQuery.getKey = (variables?: Types.EmailSenderQueryVariables) =>
  variables === undefined
    ? ["EmailSender.infinite"]
    : ["EmailSender.infinite", variables]

useInfiniteEmailSenderQuery.rootKey = "EmailSender.infinite"

export const useSuspenseInfiniteEmailSenderQuery = <
  TData = InfiniteData<Types.EmailSenderQuery>,
  TError = unknown,
>(
  variables: Types.EmailSenderQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.EmailSenderQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.EmailSenderQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.EmailSenderQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["EmailSender.infiniteSuspense"]
            : ["EmailSender.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
            EmailSenderDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteEmailSenderQuery.getKey = (
  variables?: Types.EmailSenderQueryVariables,
) =>
  variables === undefined
    ? ["EmailSender.infiniteSuspense"]
    : ["EmailSender.infiniteSuspense", variables]

useSuspenseInfiniteEmailSenderQuery.rootKey = "EmailSender.infinite"

useEmailSenderQuery.fetcher = (
  variables?: Types.EmailSenderQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.EmailSenderQuery, Types.EmailSenderQueryVariables>(
    EmailSenderDocument,
    variables,
    options,
  )

export const ReminderToBidDocument = `
    query ReminderToBid($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    biddingClosedAt
    type
    totalPvSizeKwDc
    shortPricingNote
    recvr {
      slug
      name
    }
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
      totalBids
      optOut {
        archivedAt
      }
      archivedAt
      rows {
        specs {
          optOut {
            archivedAt
          }
        }
      }
    }
    rows {
      id
      specs {
        ppa {
          id
          system {
            pvSpecs {
              pvTypes
            }
          }
        }
        cs {
          id
          systemLite {
            pvSpecs {
              pvTypes
            }
          }
        }
      }
      site {
        state
      }
    }
  }
  currentUser {
    pid
    name
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ReminderToBidQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBid", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useReminderToBidQuery.getKey = (variables: Types.ReminderToBidQueryVariables) => [
  "ReminderToBid",
  variables,
]

useReminderToBidQuery.rootKey = "ReminderToBid"

export const useSuspenseReminderToBidQuery = <
  TData = Types.ReminderToBidQuery,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ReminderToBidQuery, TError, TData>({
    queryKey: ["ReminderToBidSuspense", variables],
    queryFn: publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
      ReminderToBidDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBidSuspense", variables]

useSuspenseReminderToBidQuery.rootKey = "ReminderToBid"

export const useInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infinite", variables]

useInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

export const useSuspenseInfiniteReminderToBidQuery = <
  TData = InfiniteData<Types.ReminderToBidQuery>,
  TError = unknown,
>(
  variables: Types.ReminderToBidQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ReminderToBidQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ReminderToBidQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ReminderToBidQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ReminderToBid.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
            ReminderToBidDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteReminderToBidQuery.getKey = (
  variables: Types.ReminderToBidQueryVariables,
) => ["ReminderToBid.infiniteSuspense", variables]

useSuspenseInfiniteReminderToBidQuery.rootKey = "ReminderToBid.infinite"

useReminderToBidQuery.fetcher = (
  variables: Types.ReminderToBidQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ReminderToBidQuery, Types.ReminderToBidQueryVariables>(
    ReminderToBidDocument,
    variables,
    options,
  )

export const BidResultForLosersDocument = `
    query BidResultForLosers($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    recvr {
      name
    }
    bundles {
      pid
      totalBids
      financier {
        pid
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
    }
    rows {
      id
      awardedFinancier {
        pid
      }
    }
  }
  currentUser {
    pid
    name
    meetingSchedulingUrl
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidResultForLosersQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosers", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers", variables]

useBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useSuspenseBidResultForLosersQuery = <
  TData = Types.BidResultForLosersQuery,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidResultForLosersQuery, TError, TData>({
    queryKey: ["BidResultForLosersSuspense", variables],
    queryFn: publicFetcher<
      Types.BidResultForLosersQuery,
      Types.BidResultForLosersQueryVariables
    >(BidResultForLosersDocument, variables),
    ...options,
  })
}

useSuspenseBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosersSuspense", variables]

useSuspenseBidResultForLosersQuery.rootKey = "BidResultForLosers"

export const useInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infinite", variables]

useInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

export const useSuspenseInfiniteBidResultForLosersQuery = <
  TData = InfiniteData<Types.BidResultForLosersQuery>,
  TError = unknown,
>(
  variables: Types.BidResultForLosersQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidResultForLosersQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidResultForLosersQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidResultForLosersQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidResultForLosers.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidResultForLosersQuery,
            Types.BidResultForLosersQueryVariables
          >(BidResultForLosersDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidResultForLosersQuery.getKey = (
  variables: Types.BidResultForLosersQueryVariables,
) => ["BidResultForLosers.infiniteSuspense", variables]

useSuspenseInfiniteBidResultForLosersQuery.rootKey = "BidResultForLosers.infinite"

useBidResultForLosersQuery.fetcher = (
  variables: Types.BidResultForLosersQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidResultForLosersQuery, Types.BidResultForLosersQueryVariables>(
    BidResultForLosersDocument,
    variables,
    options,
  )

export const BidLaunchNoticeDocument = `
    query BidLaunchNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    name
    totalPvSizeKwDc
    shortPricingNote
    biddingClosedAt
    type
    recvr {
      name
    }
    bundles {
      pid
      financier {
        orgSlug
        name
        orgUsers {
          ...OrgUser
        }
      }
    }
    rows {
      id
      specs {
        ppa {
          id
          system {
            pvSpecs {
              pvTypes
            }
          }
        }
        cs {
          id
          systemLite {
            pvSpecs {
              pvTypes
            }
          }
        }
      }
      site {
        state
      }
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNotice", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useBidLaunchNoticeQuery.getKey = (variables: Types.BidLaunchNoticeQueryVariables) => [
  "BidLaunchNotice",
  variables,
]

useBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useSuspenseBidLaunchNoticeQuery = <
  TData = Types.BidLaunchNoticeQuery,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidLaunchNoticeQuery, TError, TData>({
    queryKey: ["BidLaunchNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.BidLaunchNoticeQuery,
      Types.BidLaunchNoticeQueryVariables
    >(BidLaunchNoticeDocument, variables),
    ...options,
  })
}

useSuspenseBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNoticeSuspense", variables]

useSuspenseBidLaunchNoticeQuery.rootKey = "BidLaunchNotice"

export const useInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infinite", variables]

useInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

export const useSuspenseInfiniteBidLaunchNoticeQuery = <
  TData = InfiniteData<Types.BidLaunchNoticeQuery>,
  TError = unknown,
>(
  variables: Types.BidLaunchNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidLaunchNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidLaunchNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidLaunchNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidLaunchNotice.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidLaunchNoticeQuery,
            Types.BidLaunchNoticeQueryVariables
          >(BidLaunchNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidLaunchNoticeQuery.getKey = (
  variables: Types.BidLaunchNoticeQueryVariables,
) => ["BidLaunchNotice.infiniteSuspense", variables]

useSuspenseInfiniteBidLaunchNoticeQuery.rootKey = "BidLaunchNotice.infinite"

useBidLaunchNoticeQuery.fetcher = (
  variables: Types.BidLaunchNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BidLaunchNoticeQuery, Types.BidLaunchNoticeQueryVariables>(
    BidLaunchNoticeDocument,
    variables,
    options,
  )

export const HostAwardNoticeDocument = `
    query HostAwardNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      name
    }
    rows {
      awardedFinancier {
        name
        orgUsers {
          ...OrgUser
        }
      }
      portfolio {
        title
      }
      site {
        name
        buildingType
        state
      }
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNotice", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useHostAwardNoticeQuery.getKey = (variables: Types.HostAwardNoticeQueryVariables) => [
  "HostAwardNotice",
  variables,
]

useHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useSuspenseHostAwardNoticeQuery = <
  TData = Types.HostAwardNoticeQuery,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.HostAwardNoticeQuery, TError, TData>({
    queryKey: ["HostAwardNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.HostAwardNoticeQuery,
      Types.HostAwardNoticeQueryVariables
    >(HostAwardNoticeDocument, variables),
    ...options,
  })
}

useSuspenseHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNoticeSuspense", variables]

useSuspenseHostAwardNoticeQuery.rootKey = "HostAwardNotice"

export const useInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infinite", variables]

useInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

export const useSuspenseInfiniteHostAwardNoticeQuery = <
  TData = InfiniteData<Types.HostAwardNoticeQuery>,
  TError = unknown,
>(
  variables: Types.HostAwardNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.HostAwardNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.HostAwardNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.HostAwardNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["HostAwardNotice.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.HostAwardNoticeQuery,
            Types.HostAwardNoticeQueryVariables
          >(HostAwardNoticeDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteHostAwardNoticeQuery.getKey = (
  variables: Types.HostAwardNoticeQueryVariables,
) => ["HostAwardNotice.infiniteSuspense", variables]

useSuspenseInfiniteHostAwardNoticeQuery.rootKey = "HostAwardNotice.infinite"

useHostAwardNoticeQuery.fetcher = (
  variables: Types.HostAwardNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.HostAwardNoticeQuery, Types.HostAwardNoticeQueryVariables>(
    HostAwardNoticeDocument,
    variables,
    options,
  )

export const ProjectKickoffNoticeDocument = `
    query ProjectKickoffNotice($orgSlug: String!, $parentPid: String!) {
  bundleParentDetail(orgSlug: $orgSlug, parentPid: $parentPid) {
    pid
    recvr {
      orgUsers {
        ...OrgUser
      }
    }
    rows {
      awardedFinancier {
        name
      }
    }
  }
}
    ${OrgUserFragmentDoc}
${UserRefFragmentDoc}`

export const useProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNotice", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice", variables]

useProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useSuspenseProjectKickoffNoticeQuery = <
  TData = Types.ProjectKickoffNoticeQuery,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectKickoffNoticeQuery, TError, TData>({
    queryKey: ["ProjectKickoffNoticeSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectKickoffNoticeQuery,
      Types.ProjectKickoffNoticeQueryVariables
    >(ProjectKickoffNoticeDocument, variables),
    ...options,
  })
}

useSuspenseProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNoticeSuspense", variables]

useSuspenseProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice"

export const useInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectKickoffNotice.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infinite", variables]

useInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

export const useSuspenseInfiniteProjectKickoffNoticeQuery = <
  TData = InfiniteData<Types.ProjectKickoffNoticeQuery>,
  TError = unknown,
>(
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectKickoffNoticeQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectKickoffNoticeQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectKickoffNoticeQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "ProjectKickoffNotice.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectKickoffNoticeQuery,
            Types.ProjectKickoffNoticeQueryVariables
          >(ProjectKickoffNoticeDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectKickoffNoticeQuery.getKey = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
) => ["ProjectKickoffNotice.infiniteSuspense", variables]

useSuspenseInfiniteProjectKickoffNoticeQuery.rootKey = "ProjectKickoffNotice.infinite"

useProjectKickoffNoticeQuery.fetcher = (
  variables: Types.ProjectKickoffNoticeQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.ProjectKickoffNoticeQuery,
    Types.ProjectKickoffNoticeQueryVariables
  >(ProjectKickoffNoticeDocument, variables, options)

export const ProjectDocument = `
    query Project($projectPid: String!) {
  projects {
    project(projectPid: $projectPid) {
      ...EnergyProject
    }
  }
}
    ${EnergyProjectFragmentDoc}
${UserRefFragmentDoc}
${EnergyProjectCsSpecificationFragmentDoc}
${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${EnergyProjectPpaSpecificationFragmentDoc}
${SystemFragmentDoc}
${MilestoneSummaryFragmentDoc}
${ProgressNotesFragmentDoc}`

export const useProjectQuery = <TData = Types.ProjectQuery, TError = unknown>(
  variables: Types.ProjectQueryVariables,
  options?: Omit<UseQueryOptions<Types.ProjectQuery, TError, TData>, "queryKey"> & {
    queryKey?: UseQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectQuery, TError, TData>({
    queryKey: ["Project", variables],
    queryFn: publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
      ProjectDocument,
      variables,
    ),
    ...options,
  })
}

useProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project",
  variables,
]

useProjectQuery.rootKey = "Project"

export const useSuspenseProjectQuery = <TData = Types.ProjectQuery, TError = unknown>(
  variables: Types.ProjectQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectQuery, TError, TData>({
    queryKey: ["ProjectSuspense", variables],
    queryFn: publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
      ProjectDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "ProjectSuspense",
  variables,
]

useSuspenseProjectQuery.rootKey = "Project"

export const useInfiniteProjectQuery = <
  TData = InfiniteData<Types.ProjectQuery>,
  TError = unknown,
>(
  variables: Types.ProjectQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Project.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
            ProjectDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project.infinite",
  variables,
]

useInfiniteProjectQuery.rootKey = "Project.infinite"

export const useSuspenseInfiniteProjectQuery = <
  TData = InfiniteData<Types.ProjectQuery>,
  TError = unknown,
>(
  variables: Types.ProjectQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["Project.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
            ProjectDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectQuery.getKey = (variables: Types.ProjectQueryVariables) => [
  "Project.infiniteSuspense",
  variables,
]

useSuspenseInfiniteProjectQuery.rootKey = "Project.infinite"

useProjectQuery.fetcher = (
  variables: Types.ProjectQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectQuery, Types.ProjectQueryVariables>(
    ProjectDocument,
    variables,
    options,
  )

export const SiteProjectsDocument = `
    query SiteProjects($sitePid: String!) {
  projects {
    siteProjects(sitePid: $sitePid) {
      ...EnergyProject
    }
  }
}
    ${EnergyProjectFragmentDoc}
${UserRefFragmentDoc}
${EnergyProjectCsSpecificationFragmentDoc}
${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${EnergyProjectPpaSpecificationFragmentDoc}
${SystemFragmentDoc}
${MilestoneSummaryFragmentDoc}
${ProgressNotesFragmentDoc}`

export const useSiteProjectsQuery = <TData = Types.SiteProjectsQuery, TError = unknown>(
  variables: Types.SiteProjectsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteProjectsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteProjectsQuery, TError, TData>({
    queryKey: ["SiteProjects", variables],
    queryFn: publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
      SiteProjectsDocument,
      variables,
    ),
    ...options,
  })
}

useSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjects",
  variables,
]

useSiteProjectsQuery.rootKey = "SiteProjects"

export const useSuspenseSiteProjectsQuery = <
  TData = Types.SiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteProjectsQuery, TError, TData>({
    queryKey: ["SiteProjectsSuspense", variables],
    queryFn: publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
      SiteProjectsDocument,
      variables,
    ),
    ...options,
  })
}

useSuspenseSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjectsSuspense",
  variables,
]

useSuspenseSiteProjectsQuery.rootKey = "SiteProjects"

export const useInfiniteSiteProjectsQuery = <
  TData = InfiniteData<Types.SiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
            SiteProjectsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteProjectsQuery.getKey = (variables: Types.SiteProjectsQueryVariables) => [
  "SiteProjects.infinite",
  variables,
]

useInfiniteSiteProjectsQuery.rootKey = "SiteProjects.infinite"

export const useSuspenseInfiniteSiteProjectsQuery = <
  TData = InfiniteData<Types.SiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.SiteProjectsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteProjects.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
            SiteProjectsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteProjectsQuery.getKey = (
  variables: Types.SiteProjectsQueryVariables,
) => ["SiteProjects.infiniteSuspense", variables]

useSuspenseInfiniteSiteProjectsQuery.rootKey = "SiteProjects.infinite"

useSiteProjectsQuery.fetcher = (
  variables: Types.SiteProjectsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteProjectsQuery, Types.SiteProjectsQueryVariables>(
    SiteProjectsDocument,
    variables,
    options,
  )

export const AdminSiteProjectsDocument = `
    query AdminSiteProjects($sitePid: String!) {
  projects {
    adminSiteProjects(sitePid: $sitePid) {
      ...AdminEnergyProject
    }
  }
}
    ${AdminEnergyProjectFragmentDoc}
${EnergyProjectFragmentDoc}
${UserRefFragmentDoc}
${EnergyProjectCsSpecificationFragmentDoc}
${SystemLiteFragmentDoc}
${PvSystemSpecFragmentDoc}
${EnergyProjectPpaSpecificationFragmentDoc}
${SystemFragmentDoc}
${MilestoneSummaryFragmentDoc}
${ProgressNotesFragmentDoc}`

export const useAdminSiteProjectsQuery = <
  TData = Types.AdminSiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AdminSiteProjectsQuery, TError, TData>({
    queryKey: ["AdminSiteProjects", variables],
    queryFn: publicFetcher<
      Types.AdminSiteProjectsQuery,
      Types.AdminSiteProjectsQueryVariables
    >(AdminSiteProjectsDocument, variables),
    ...options,
  })
}

useAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects", variables]

useAdminSiteProjectsQuery.rootKey = "AdminSiteProjects"

export const useSuspenseAdminSiteProjectsQuery = <
  TData = Types.AdminSiteProjectsQuery,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AdminSiteProjectsQuery, TError, TData>({
    queryKey: ["AdminSiteProjectsSuspense", variables],
    queryFn: publicFetcher<
      Types.AdminSiteProjectsQuery,
      Types.AdminSiteProjectsQueryVariables
    >(AdminSiteProjectsDocument, variables),
    ...options,
  })
}

useSuspenseAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjectsSuspense", variables]

useSuspenseAdminSiteProjectsQuery.rootKey = "AdminSiteProjects"

export const useInfiniteAdminSiteProjectsQuery = <
  TData = InfiniteData<Types.AdminSiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AdminSiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteProjects.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteProjectsQuery,
            Types.AdminSiteProjectsQueryVariables
          >(AdminSiteProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects.infinite", variables]

useInfiniteAdminSiteProjectsQuery.rootKey = "AdminSiteProjects.infinite"

export const useSuspenseInfiniteAdminSiteProjectsQuery = <
  TData = InfiniteData<Types.AdminSiteProjectsQuery>,
  TError = unknown,
>(
  variables: Types.AdminSiteProjectsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AdminSiteProjectsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AdminSiteProjectsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AdminSiteProjectsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["AdminSiteProjects.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.AdminSiteProjectsQuery,
            Types.AdminSiteProjectsQueryVariables
          >(AdminSiteProjectsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAdminSiteProjectsQuery.getKey = (
  variables: Types.AdminSiteProjectsQueryVariables,
) => ["AdminSiteProjects.infiniteSuspense", variables]

useSuspenseInfiniteAdminSiteProjectsQuery.rootKey = "AdminSiteProjects.infinite"

useAdminSiteProjectsQuery.fetcher = (
  variables: Types.AdminSiteProjectsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AdminSiteProjectsQuery, Types.AdminSiteProjectsQueryVariables>(
    AdminSiteProjectsDocument,
    variables,
    options,
  )

export const AllFtmProgramsDocument = `
    query AllFtmPrograms {
  ftmPrograms {
    all {
      state
      name
    }
  }
}
    `

export const useAllFtmProgramsQuery = <
  TData = Types.AllFtmProgramsQuery,
  TError = unknown,
>(
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.AllFtmProgramsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables],
    queryFn: publicFetcher<
      Types.AllFtmProgramsQuery,
      Types.AllFtmProgramsQueryVariables
    >(AllFtmProgramsDocument, variables),
    ...options,
  })
}

useAllFtmProgramsQuery.getKey = (variables?: Types.AllFtmProgramsQueryVariables) =>
  variables === undefined ? ["AllFtmPrograms"] : ["AllFtmPrograms", variables]

useAllFtmProgramsQuery.rootKey = "AllFtmPrograms"

export const useSuspenseAllFtmProgramsQuery = <
  TData = Types.AllFtmProgramsQuery,
  TError = unknown,
>(
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.AllFtmProgramsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["AllFtmProgramsSuspense"]
        : ["AllFtmProgramsSuspense", variables],
    queryFn: publicFetcher<
      Types.AllFtmProgramsQuery,
      Types.AllFtmProgramsQueryVariables
    >(AllFtmProgramsDocument, variables),
    ...options,
  })
}

useSuspenseAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) =>
  variables === undefined
    ? ["AllFtmProgramsSuspense"]
    : ["AllFtmProgramsSuspense", variables]

useSuspenseAllFtmProgramsQuery.rootKey = "AllFtmPrograms"

export const useInfiniteAllFtmProgramsQuery = <
  TData = InfiniteData<Types.AllFtmProgramsQuery>,
  TError = unknown,
>(
  variables: Types.AllFtmProgramsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.AllFtmProgramsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllFtmPrograms.infinite"]
            : ["AllFtmPrograms.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
            AllFtmProgramsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) =>
  variables === undefined
    ? ["AllFtmPrograms.infinite"]
    : ["AllFtmPrograms.infinite", variables]

useInfiniteAllFtmProgramsQuery.rootKey = "AllFtmPrograms.infinite"

export const useSuspenseInfiniteAllFtmProgramsQuery = <
  TData = InfiniteData<Types.AllFtmProgramsQuery>,
  TError = unknown,
>(
  variables: Types.AllFtmProgramsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.AllFtmProgramsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.AllFtmProgramsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.AllFtmProgramsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["AllFtmPrograms.infiniteSuspense"]
            : ["AllFtmPrograms.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
            AllFtmProgramsDocument,
            { ...variables, ...(metaData.pageParam ?? {}) },
          )(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteAllFtmProgramsQuery.getKey = (
  variables?: Types.AllFtmProgramsQueryVariables,
) =>
  variables === undefined
    ? ["AllFtmPrograms.infiniteSuspense"]
    : ["AllFtmPrograms.infiniteSuspense", variables]

useSuspenseInfiniteAllFtmProgramsQuery.rootKey = "AllFtmPrograms.infinite"

useAllFtmProgramsQuery.fetcher = (
  variables?: Types.AllFtmProgramsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.AllFtmProgramsQuery, Types.AllFtmProgramsQueryVariables>(
    AllFtmProgramsDocument,
    variables,
    options,
  )

export const FtmProgramsForStateDocument = `
    query FtmProgramsForState($state: USState) {
  ftmPrograms {
    all(state: $state) {
      id
      name
    }
  }
}
    `

export const useFtmProgramsForStateQuery = <
  TData = Types.FtmProgramsForStateQuery,
  TError = unknown,
>(
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.FtmProgramsForStateQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FtmProgramsForState"]
        : ["FtmProgramsForState", variables],
    queryFn: publicFetcher<
      Types.FtmProgramsForStateQuery,
      Types.FtmProgramsForStateQueryVariables
    >(FtmProgramsForStateDocument, variables),
    ...options,
  })
}

useFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined ? ["FtmProgramsForState"] : ["FtmProgramsForState", variables]

useFtmProgramsForStateQuery.rootKey = "FtmProgramsForState"

export const useSuspenseFtmProgramsForStateQuery = <
  TData = Types.FtmProgramsForStateQuery,
  TError = unknown,
>(
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.FtmProgramsForStateQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["FtmProgramsForStateSuspense"]
        : ["FtmProgramsForStateSuspense", variables],
    queryFn: publicFetcher<
      Types.FtmProgramsForStateQuery,
      Types.FtmProgramsForStateQueryVariables
    >(FtmProgramsForStateDocument, variables),
    ...options,
  })
}

useSuspenseFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined
    ? ["FtmProgramsForStateSuspense"]
    : ["FtmProgramsForStateSuspense", variables]

useSuspenseFtmProgramsForStateQuery.rootKey = "FtmProgramsForState"

export const useInfiniteFtmProgramsForStateQuery = <
  TData = InfiniteData<Types.FtmProgramsForStateQuery>,
  TError = unknown,
>(
  variables: Types.FtmProgramsForStateQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.FtmProgramsForStateQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FtmProgramsForState.infinite"]
            : ["FtmProgramsForState.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FtmProgramsForStateQuery,
            Types.FtmProgramsForStateQueryVariables
          >(FtmProgramsForStateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined
    ? ["FtmProgramsForState.infinite"]
    : ["FtmProgramsForState.infinite", variables]

useInfiniteFtmProgramsForStateQuery.rootKey = "FtmProgramsForState.infinite"

export const useSuspenseInfiniteFtmProgramsForStateQuery = <
  TData = InfiniteData<Types.FtmProgramsForStateQuery>,
  TError = unknown,
>(
  variables: Types.FtmProgramsForStateQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.FtmProgramsForStateQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.FtmProgramsForStateQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.FtmProgramsForStateQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["FtmProgramsForState.infiniteSuspense"]
            : ["FtmProgramsForState.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.FtmProgramsForStateQuery,
            Types.FtmProgramsForStateQueryVariables
          >(FtmProgramsForStateDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteFtmProgramsForStateQuery.getKey = (
  variables?: Types.FtmProgramsForStateQueryVariables,
) =>
  variables === undefined
    ? ["FtmProgramsForState.infiniteSuspense"]
    : ["FtmProgramsForState.infiniteSuspense", variables]

useSuspenseInfiniteFtmProgramsForStateQuery.rootKey = "FtmProgramsForState.infinite"

useFtmProgramsForStateQuery.fetcher = (
  variables?: Types.FtmProgramsForStateQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.FtmProgramsForStateQuery,
    Types.FtmProgramsForStateQueryVariables
  >(FtmProgramsForStateDocument, variables, options)

export const BatteryProductsDocument = `
    query BatteryProducts {
  batteryProducts {
    id
    sizeKw
    sizeKwh
    vendor
    equipmentCost
    laborCost
    includeInOptimizer
    disabled
  }
}
    `

export const useBatteryProductsQuery = <
  TData = Types.BatteryProductsQuery,
  TError = unknown,
>(
  variables?: Types.BatteryProductsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.BatteryProductsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.BatteryProductsQuery, TError, TData>({
    queryKey:
      variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables],
    queryFn: publicFetcher<
      Types.BatteryProductsQuery,
      Types.BatteryProductsQueryVariables
    >(BatteryProductsDocument, variables),
    ...options,
  })
}

useBatteryProductsQuery.getKey = (variables?: Types.BatteryProductsQueryVariables) =>
  variables === undefined ? ["BatteryProducts"] : ["BatteryProducts", variables]

useBatteryProductsQuery.rootKey = "BatteryProducts"

export const useSuspenseBatteryProductsQuery = <
  TData = Types.BatteryProductsQuery,
  TError = unknown,
>(
  variables?: Types.BatteryProductsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BatteryProductsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BatteryProductsSuspense"]
        : ["BatteryProductsSuspense", variables],
    queryFn: publicFetcher<
      Types.BatteryProductsQuery,
      Types.BatteryProductsQueryVariables
    >(BatteryProductsDocument, variables),
    ...options,
  })
}

useSuspenseBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) =>
  variables === undefined
    ? ["BatteryProductsSuspense"]
    : ["BatteryProductsSuspense", variables]

useSuspenseBatteryProductsQuery.rootKey = "BatteryProducts"

export const useInfiniteBatteryProductsQuery = <
  TData = InfiniteData<Types.BatteryProductsQuery>,
  TError = unknown,
>(
  variables: Types.BatteryProductsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BatteryProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BatteryProducts.infinite"]
            : ["BatteryProducts.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatteryProductsQuery,
            Types.BatteryProductsQueryVariables
          >(BatteryProductsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) =>
  variables === undefined
    ? ["BatteryProducts.infinite"]
    : ["BatteryProducts.infinite", variables]

useInfiniteBatteryProductsQuery.rootKey = "BatteryProducts.infinite"

export const useSuspenseInfiniteBatteryProductsQuery = <
  TData = InfiniteData<Types.BatteryProductsQuery>,
  TError = unknown,
>(
  variables: Types.BatteryProductsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BatteryProductsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BatteryProductsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BatteryProductsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BatteryProducts.infiniteSuspense"]
            : ["BatteryProducts.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BatteryProductsQuery,
            Types.BatteryProductsQueryVariables
          >(BatteryProductsDocument, { ...variables, ...(metaData.pageParam ?? {}) })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBatteryProductsQuery.getKey = (
  variables?: Types.BatteryProductsQueryVariables,
) =>
  variables === undefined
    ? ["BatteryProducts.infiniteSuspense"]
    : ["BatteryProducts.infiniteSuspense", variables]

useSuspenseInfiniteBatteryProductsQuery.rootKey = "BatteryProducts.infinite"

useBatteryProductsQuery.fetcher = (
  variables?: Types.BatteryProductsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.BatteryProductsQuery, Types.BatteryProductsQueryVariables>(
    BatteryProductsDocument,
    variables,
    options,
  )

export const BidPackageTableFacetsDocument = `
    query BidPackageTableFacets($orgSlug: String) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      view {
        stats {
          buildingOwnerNames
          opsOwnerNames
        }
      }
    }
  }
}
    `

export const useBidPackageTableFacetsQuery = <
  TData = Types.BidPackageTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableFacets"]
        : ["BidPackageTableFacets", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableFacetsQuery,
      Types.BidPackageTableFacetsQueryVariables
    >(BidPackageTableFacetsDocument, variables),
    ...options,
  })
}

useBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets"]
    : ["BidPackageTableFacets", variables]

useBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets"

export const useSuspenseBidPackageTableFacetsQuery = <
  TData = Types.BidPackageTableFacetsQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableFacetsQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableFacetsSuspense"]
        : ["BidPackageTableFacetsSuspense", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableFacetsQuery,
      Types.BidPackageTableFacetsQueryVariables
    >(BidPackageTableFacetsDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacetsSuspense"]
    : ["BidPackageTableFacetsSuspense", variables]

useSuspenseBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets"

export const useInfiniteBidPackageTableFacetsQuery = <
  TData = InfiniteData<Types.BidPackageTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableFacetsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableFacets.infinite"]
            : ["BidPackageTableFacets.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableFacetsQuery,
            Types.BidPackageTableFacetsQueryVariables
          >(BidPackageTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets.infinite"]
    : ["BidPackageTableFacets.infinite", variables]

useInfiniteBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets.infinite"

export const useSuspenseInfiniteBidPackageTableFacetsQuery = <
  TData = InfiniteData<Types.BidPackageTableFacetsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableFacetsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableFacetsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableFacetsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableFacetsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableFacets.infiniteSuspense"]
            : ["BidPackageTableFacets.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableFacetsQuery,
            Types.BidPackageTableFacetsQueryVariables
          >(BidPackageTableFacetsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableFacetsQuery.getKey = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableFacets.infiniteSuspense"]
    : ["BidPackageTableFacets.infiniteSuspense", variables]

useSuspenseInfiniteBidPackageTableFacetsQuery.rootKey = "BidPackageTableFacets.infinite"

useBidPackageTableFacetsQuery.fetcher = (
  variables?: Types.BidPackageTableFacetsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableFacetsQuery,
    Types.BidPackageTableFacetsQueryVariables
  >(BidPackageTableFacetsDocument, variables, options)

export const BidPackageTableViewDocument = `
    query BidPackageTableView($orgSlug: String, $filterBy: BidPackageTableFilter, $sortBy: [BidPackageTableSort!]) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      view(filterBy: $filterBy, sortBy: $sortBy) {
        keys
        count
      }
    }
  }
}
    `

export const useBidPackageTableViewQuery = <
  TData = Types.BidPackageTableViewQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableView"]
        : ["BidPackageTableView", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableViewQuery,
      Types.BidPackageTableViewQueryVariables
    >(BidPackageTableViewDocument, variables),
    ...options,
  })
}

useBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined ? ["BidPackageTableView"] : ["BidPackageTableView", variables]

useBidPackageTableViewQuery.rootKey = "BidPackageTableView"

export const useSuspenseBidPackageTableViewQuery = <
  TData = Types.BidPackageTableViewQuery,
  TError = unknown,
>(
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableViewQuery, TError, TData>({
    queryKey:
      variables === undefined
        ? ["BidPackageTableViewSuspense"]
        : ["BidPackageTableViewSuspense", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableViewQuery,
      Types.BidPackageTableViewQueryVariables
    >(BidPackageTableViewDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableViewSuspense"]
    : ["BidPackageTableViewSuspense", variables]

useSuspenseBidPackageTableViewQuery.rootKey = "BidPackageTableView"

export const useInfiniteBidPackageTableViewQuery = <
  TData = InfiniteData<Types.BidPackageTableViewQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableViewQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableView.infinite"]
            : ["BidPackageTableView.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableViewQuery,
            Types.BidPackageTableViewQueryVariables
          >(BidPackageTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableView.infinite"]
    : ["BidPackageTableView.infinite", variables]

useInfiniteBidPackageTableViewQuery.rootKey = "BidPackageTableView.infinite"

export const useSuspenseInfiniteBidPackageTableViewQuery = <
  TData = InfiniteData<Types.BidPackageTableViewQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableViewQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableViewQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableViewQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableViewQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey:
          (optionsQueryKey ?? variables === undefined)
            ? ["BidPackageTableView.infiniteSuspense"]
            : ["BidPackageTableView.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableViewQuery,
            Types.BidPackageTableViewQueryVariables
          >(BidPackageTableViewDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableViewQuery.getKey = (
  variables?: Types.BidPackageTableViewQueryVariables,
) =>
  variables === undefined
    ? ["BidPackageTableView.infiniteSuspense"]
    : ["BidPackageTableView.infiniteSuspense", variables]

useSuspenseInfiniteBidPackageTableViewQuery.rootKey = "BidPackageTableView.infinite"

useBidPackageTableViewQuery.fetcher = (
  variables?: Types.BidPackageTableViewQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableViewQuery,
    Types.BidPackageTableViewQueryVariables
  >(BidPackageTableViewDocument, variables, options)

export const BidPackageTableRowsDocument = `
    query BidPackageTableRows($orgSlug: String, $keys: [String!]!) {
  bidPackages {
    table(orgSlug: $orgSlug) {
      rows(keys: $keys) {
        pid
        name
        buildingOwnerName
        orgSlug
        type
        projectCount
        opsOwnerName
        notionPageUrl
        biddingStatus
        invitedDeveloperCount
        participatingDeveloperCount
        biddingOpenAt
        biddingClosedAt
      }
    }
  }
}
    `

export const useBidPackageTableRowsQuery = <
  TData = Types.BidPackageTableRowsQuery,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useQuery<Types.BidPackageTableRowsQuery, TError, TData>({
    queryKey: ["BidPackageTableRows", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableRowsQuery,
      Types.BidPackageTableRowsQueryVariables
    >(BidPackageTableRowsDocument, variables),
    ...options,
  })
}

useBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows", variables]

useBidPackageTableRowsQuery.rootKey = "BidPackageTableRows"

export const useSuspenseBidPackageTableRowsQuery = <
  TData = Types.BidPackageTableRowsQuery,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.BidPackageTableRowsQuery, TError, TData>({
    queryKey: ["BidPackageTableRowsSuspense", variables],
    queryFn: publicFetcher<
      Types.BidPackageTableRowsQuery,
      Types.BidPackageTableRowsQueryVariables
    >(BidPackageTableRowsDocument, variables),
    ...options,
  })
}

useSuspenseBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRowsSuspense", variables]

useSuspenseBidPackageTableRowsQuery.rootKey = "BidPackageTableRows"

export const useInfiniteBidPackageTableRowsQuery = <
  TData = InfiniteData<Types.BidPackageTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.BidPackageTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["BidPackageTableRows.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableRowsQuery,
            Types.BidPackageTableRowsQueryVariables
          >(BidPackageTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows.infinite", variables]

useInfiniteBidPackageTableRowsQuery.rootKey = "BidPackageTableRows.infinite"

export const useSuspenseInfiniteBidPackageTableRowsQuery = <
  TData = InfiniteData<Types.BidPackageTableRowsQuery>,
  TError = unknown,
>(
  variables: Types.BidPackageTableRowsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.BidPackageTableRowsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.BidPackageTableRowsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.BidPackageTableRowsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? [
          "BidPackageTableRows.infiniteSuspense",
          variables,
        ],
        queryFn: (metaData) =>
          publicFetcher<
            Types.BidPackageTableRowsQuery,
            Types.BidPackageTableRowsQueryVariables
          >(BidPackageTableRowsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteBidPackageTableRowsQuery.getKey = (
  variables: Types.BidPackageTableRowsQueryVariables,
) => ["BidPackageTableRows.infiniteSuspense", variables]

useSuspenseInfiniteBidPackageTableRowsQuery.rootKey = "BidPackageTableRows.infinite"

useBidPackageTableRowsQuery.fetcher = (
  variables: Types.BidPackageTableRowsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<
    Types.BidPackageTableRowsQuery,
    Types.BidPackageTableRowsQueryVariables
  >(BidPackageTableRowsDocument, variables, options)

export const SiteInvestorBidsDocument = `
    query SiteInvestorBids($sitePid: String!) {
  admin {
    siteInvestorBids(sitePid: $sitePid) {
      pid
      bidType
      systemSizeKw
      developerName
      createdAt
    }
  }
}
    `

export const useSiteInvestorBidsQuery = <
  TData = Types.SiteInvestorBidsQuery,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.SiteInvestorBidsQuery, TError, TData>({
    queryKey: ["SiteInvestorBids", variables],
    queryFn: publicFetcher<
      Types.SiteInvestorBidsQuery,
      Types.SiteInvestorBidsQueryVariables
    >(SiteInvestorBidsDocument, variables),
    ...options,
  })
}

useSiteInvestorBidsQuery.getKey = (variables: Types.SiteInvestorBidsQueryVariables) => [
  "SiteInvestorBids",
  variables,
]

useSiteInvestorBidsQuery.rootKey = "SiteInvestorBids"

export const useSuspenseSiteInvestorBidsQuery = <
  TData = Types.SiteInvestorBidsQuery,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.SiteInvestorBidsQuery, TError, TData>({
    queryKey: ["SiteInvestorBidsSuspense", variables],
    queryFn: publicFetcher<
      Types.SiteInvestorBidsQuery,
      Types.SiteInvestorBidsQueryVariables
    >(SiteInvestorBidsDocument, variables),
    ...options,
  })
}

useSuspenseSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBidsSuspense", variables]

useSuspenseSiteInvestorBidsQuery.rootKey = "SiteInvestorBids"

export const useInfiniteSiteInvestorBidsQuery = <
  TData = InfiniteData<Types.SiteInvestorBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.SiteInvestorBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteInvestorBids.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteInvestorBidsQuery,
            Types.SiteInvestorBidsQueryVariables
          >(SiteInvestorBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBids.infinite", variables]

useInfiniteSiteInvestorBidsQuery.rootKey = "SiteInvestorBids.infinite"

export const useSuspenseInfiniteSiteInvestorBidsQuery = <
  TData = InfiniteData<Types.SiteInvestorBidsQuery>,
  TError = unknown,
>(
  variables: Types.SiteInvestorBidsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.SiteInvestorBidsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.SiteInvestorBidsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.SiteInvestorBidsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["SiteInvestorBids.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.SiteInvestorBidsQuery,
            Types.SiteInvestorBidsQueryVariables
          >(SiteInvestorBidsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteSiteInvestorBidsQuery.getKey = (
  variables: Types.SiteInvestorBidsQueryVariables,
) => ["SiteInvestorBids.infiniteSuspense", variables]

useSuspenseInfiniteSiteInvestorBidsQuery.rootKey = "SiteInvestorBids.infinite"

useSiteInvestorBidsQuery.fetcher = (
  variables: Types.SiteInvestorBidsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.SiteInvestorBidsQuery, Types.SiteInvestorBidsQueryVariables>(
    SiteInvestorBidsDocument,
    variables,
    options,
  )

export const ProjectDocumentsDocument = `
    query ProjectDocuments($projectPid: String!, $orgSlug: String!) {
  projects {
    project(projectPid: $projectPid) {
      pid
      documents(orgSlug: $orgSlug) {
        ...Document
      }
    }
  }
}
    ${DocumentFragmentDoc}`

export const useProjectDocumentsQuery = <
  TData = Types.ProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options?: Omit<
    UseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>["queryKey"]
  },
) => {
  return useQuery<Types.ProjectDocumentsQuery, TError, TData>({
    queryKey: ["ProjectDocuments", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsQuery,
      Types.ProjectDocumentsQueryVariables
    >(ProjectDocumentsDocument, variables),
    ...options,
  })
}

useProjectDocumentsQuery.getKey = (variables: Types.ProjectDocumentsQueryVariables) => [
  "ProjectDocuments",
  variables,
]

useProjectDocumentsQuery.rootKey = "ProjectDocuments"

export const useSuspenseProjectDocumentsQuery = <
  TData = Types.ProjectDocumentsQuery,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options?: Omit<
    UseSuspenseQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseQuery<Types.ProjectDocumentsQuery, TError, TData>({
    queryKey: ["ProjectDocumentsSuspense", variables],
    queryFn: publicFetcher<
      Types.ProjectDocumentsQuery,
      Types.ProjectDocumentsQueryVariables
    >(ProjectDocumentsDocument, variables),
    ...options,
  })
}

useSuspenseProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocumentsSuspense", variables]

useSuspenseProjectDocumentsQuery.rootKey = "ProjectDocuments"

export const useInfiniteProjectDocumentsQuery = <
  TData = InfiniteData<Types.ProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options: Omit<
    UseInfiniteQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseInfiniteQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useInfiniteQuery<Types.ProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocuments.infinite", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsQuery,
            Types.ProjectDocumentsQueryVariables
          >(ProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useInfiniteProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocuments.infinite", variables]

useInfiniteProjectDocumentsQuery.rootKey = "ProjectDocuments.infinite"

export const useSuspenseInfiniteProjectDocumentsQuery = <
  TData = InfiniteData<Types.ProjectDocumentsQuery>,
  TError = unknown,
>(
  variables: Types.ProjectDocumentsQueryVariables,
  options: Omit<
    UseSuspenseInfiniteQueryOptions<Types.ProjectDocumentsQuery, TError, TData>,
    "queryKey"
  > & {
    queryKey?: UseSuspenseInfiniteQueryOptions<
      Types.ProjectDocumentsQuery,
      TError,
      TData
    >["queryKey"]
  },
) => {
  return useSuspenseInfiniteQuery<Types.ProjectDocumentsQuery, TError, TData>(
    (() => {
      const { queryKey: optionsQueryKey, ...restOptions } = options
      return {
        queryKey: optionsQueryKey ?? ["ProjectDocuments.infiniteSuspense", variables],
        queryFn: (metaData) =>
          publicFetcher<
            Types.ProjectDocumentsQuery,
            Types.ProjectDocumentsQueryVariables
          >(ProjectDocumentsDocument, {
            ...variables,
            ...(metaData.pageParam ?? {}),
          })(),
        ...restOptions,
      }
    })(),
  )
}

useSuspenseInfiniteProjectDocumentsQuery.getKey = (
  variables: Types.ProjectDocumentsQueryVariables,
) => ["ProjectDocuments.infiniteSuspense", variables]

useSuspenseInfiniteProjectDocumentsQuery.rootKey = "ProjectDocuments.infinite"

useProjectDocumentsQuery.fetcher = (
  variables: Types.ProjectDocumentsQueryVariables,
  options?: RequestInit["headers"],
) =>
  publicFetcher<Types.ProjectDocumentsQuery, Types.ProjectDocumentsQueryVariables>(
    ProjectDocumentsDocument,
    variables,
    options,
  )
